import React, { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { QRCodeSVG } from "qrcode.react";
import { BudgetExpenseSection } from "@/components/admin/BudgetExpense";
import { Home, Calendar as CalendarIcon, Bell, Video, Heart, HandCoins, MapPin, Phone, Mail, Menu, X, Users, BookOpen, LogIn, LogOut, Settings, ChevronRight, Play, FileText, Send, CheckCircle, Check, AlertCircle, Loader2, Music, UserCheck, DollarSign, MessageSquare, Clock, Sparkles, Eye, EyeOff, Trash2, Edit, Pencil, Download, Share2, Upload, Plus, Info, Church, RefreshCw, Award, FolderOpen, Star, Wheat, Baby, XCircle, Archive, Moon, Sun, ExternalLink, GripVertical, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CCCLogo = "https://customer-assets.emergentagent.com/job_celestialapp-1/artifacts/sveqzlov_PHOTO-2025-12-13-23-31-20.jpg";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Theme Context for Dark Mode
const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      // Use the full endpoint to get permissions
      axios.get(`${API}/auth/me/full`).then(res => {
        setUser(res.data);
        setPermissions(res.data.permissions || {});
        setLoading(false);
      }).catch(() => {
        localStorage.removeItem("token");
        setToken(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    // Main admin has all permissions
    setPermissions(res.data.user.permissions || {
      events_read: true, events_write: true,
      announcements_read: true, announcements_write: true,
      sermons_read: true, sermons_write: true,
      hymns_read: true, hymns_write: true,
      bible_lessons_read: true, bible_lessons_write: true,
      prayers_read: true, prayers_write: true,
      contact_read: true, contact_write: true,
      ushers_access: true,
      attendance_read: true, attendance_write: true,
      finance_read: true, finance_write: true,
      expenses_read: true, expenses_write: true,
      members_read: true, members_write: true,
      birthdays_access: true,
      notifications_access: true,
      exports_download: true
    });
    return res.data;
  };

  const subAdminLogin = async (username, password) => {
    const res = await axios.post(`${API}/auth/sub-admin/login`, { username, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    setPermissions(res.data.user.permissions || {});
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setPermissions({});
  };

  const isMainAdmin = user?.role === "admin" || user?.role === "main_admin";
  const isSubAdmin = user?.role === "sub_admin";
  const isAdmin = isMainAdmin || isSubAdmin;
  
  const hasPermission = (perm) => {
    if (isMainAdmin) return true;
    return permissions[perm] === true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, login, subAdminLogin, logout, loading, 
      isAdmin, isMainAdmin, isSubAdmin, 
      permissions, hasPermission 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const [logoRolling, setLogoRolling] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (!logoRolling) {
      setLogoRolling(true);
      setTimeout(() => {
        setLogoRolling(false);
      }, 1200);
    }
  };
  
  return (
    <header className={`sticky top-0 z-50 shadow-xl ${isDarkMode ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600'}`}>
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-r from-purple-900/20 via-blue-900/10 to-indigo-900/20' : 'bg-gradient-to-r from-blue-400/20 via-cyan-400/10 to-purple-400/20'}`}></div>
      <div className="max-w-[1400px] mx-auto px-4 py-3 relative">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="header-logo">
            <div className="relative flex-shrink-0" onClick={handleLogoClick}>
              <div className={`absolute -inset-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full blur ${logoRolling ? 'opacity-80' : 'opacity-40 animate-pulse'}`}></div>
              <img 
                src={CCCLogo} 
                alt="CCC Logo" 
                className={`relative w-10 h-10 sm:w-14 sm:h-14 object-contain rounded-full shadow-lg border-2 border-white/50 bg-white logo-clickable ${logoRolling ? 'logo-rolling' : ''}`} 
                style={{imageRendering: 'high-quality'}} 
              />
            </div>
            <div>
              <h1 className="font-bold text-white text-sm sm:text-lg leading-tight drop-shadow-md">CCC Medway Central Parish</h1>
              <p className="text-xs sm:text-base text-cyan-100 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse" /> Blessed Community
              </p>
              <div className="mt-0.5 sm:mt-1">
                <p className="text-white font-semibold text-xs sm:text-sm">Welcome Home</p>
                <p className="text-cyan-100 text-[10px] sm:text-xs">We are blessed to have you!</p>
              </div>
            </div>
          </Link>
          <div className="flex flex-col items-end gap-1">
            {/* Phone and Email icons row - above Live Updates */}
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="ghost" className="rounded-full bg-white/20 text-white hover:bg-white/30 w-8 h-8 border border-white/30 backdrop-blur-sm shadow-lg">
                    <Phone className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-4" align="end">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-blue-600" /> Contact Us
                    </h4>
                    <div className="space-y-2">
                      <a href="tel:07507771597" className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-700">07507 771597</span>
                      </a>
                      <a href="tel:07367125017" className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-700">07367 125017</span>
                      </a>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="icon" variant="ghost" className="rounded-full bg-white/20 text-white hover:bg-white/30 w-8 h-8 border border-white/30 backdrop-blur-sm shadow-lg">
                    <Mail className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="end">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-600" /> Email Us
                    </h4>
                    <a href="mailto:info@cccmedwaycentralparish.com" className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-700 text-sm">info@cccmedwaycentralparish.com</span>
                    </a>
                  </div>
                </PopoverContent>
              </Popover>
              <Button 
                size="icon" 
                variant="ghost" 
                className="rounded-full bg-white/20 text-white hover:bg-white/30 w-8 h-8 border border-white/30 backdrop-blur-sm shadow-lg"
                onClick={() => navigate('/team')}
                title="Meet the Team"
              >
                <Users className="w-4 h-4" />
              </Button>
              {/* Dark Mode Toggle */}
              <Button 
                size="icon" 
                variant="ghost" 
                className={`rounded-full w-8 h-8 border backdrop-blur-sm shadow-lg transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-yellow-400/20 text-yellow-300 hover:bg-yellow-400/30 border-yellow-400/30' 
                    : 'bg-white/20 text-white hover:bg-white/30 border-white/30'
                }`}
                onClick={toggleDarkMode}
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
            </div>
            {/* Live Updates button - below icons */}
            <Button className="rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-blue-900 font-semibold hover:from-yellow-300 hover:to-orange-400 gap-2 shadow-lg border border-yellow-300/50 text-sm px-3 py-1 h-auto" onClick={() => navigate('/announcements')}>
              <Bell className="w-4 h-4" /> Live Updates
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

const BottomNav = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const navItems = [
    { path: "/", icon: Home, label: "Home" },
    { path: "/events", icon: CalendarIcon, label: "Events" },
    { path: "/hymns", icon: Music, label: "Hymns" },
    { path: "/prayer", icon: Heart, label: "Prayer" },
    { path: "/sermons", icon: Video, label: "Webcast" },
    { path: "/gallery", icon: ImageIcon, label: "Gallery" },
    { path: "/give", icon: HandCoins, label: "Give" },
    ...(isAdmin ? [{ path: "/admin", icon: Settings, label: "Admin" }] : []),
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200/50 shadow-2xl z-50">
      <div className="flex justify-center items-center py-2 sm:py-3 px-1 sm:px-2">
        <div className="flex items-center gap-0.5 sm:gap-2 bg-gray-50/80 rounded-full px-1.5 sm:px-2 py-1 sm:py-1.5 shadow-inner max-w-full overflow-x-auto">
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-full transition-all duration-300 flex-shrink-0 ${
                  isActive 
                    ? "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white shadow-lg shadow-purple-500/30" 
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`} 
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isActive ? "text-white" : ""}`} />
                <span className={`text-[10px] sm:text-sm font-medium whitespace-nowrap ${isActive ? "text-white" : ""}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

// Festive Season Detection & Messages
// Calculate Easter date using the Anonymous Gregorian algorithm
const getEasterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Helper to check if two dates are the same day
const isSameDay = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

const getFestiveSeason = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-12
  const day = today.getDate();
  
  // Calculate Easter and related dates for current year
  const easter = getEasterDate(year);
  const palmSunday = new Date(easter);
  palmSunday.setDate(easter.getDate() - 7);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  const pentecost = new Date(easter);
  pentecost.setDate(easter.getDate() + 49);
  
  // Holy Week range (Palm Sunday to Easter)
  const holyWeekStart = new Date(palmSunday);
  const holyWeekEnd = new Date(easter);
  
  // Only show banners on SPECIFIC Christian holidays (not general seasons)
  
  // Christmas Eve (Dec 24)
  if (month === 12 && day === 24) {
    return { 
      active: true, 
      name: "Christmas Eve", 
      emoji: "🌟", 
      message: "Glory to God in the highest! Merry Christmas Eve!",
      bgGradient: "from-red-600 via-green-600 to-red-600",
      verse: "For unto us a child is born, unto us a son is given. - Isaiah 9:6"
    };
  }
  
  // Christmas Day (Dec 25)
  if (month === 12 && day === 25) {
    return { 
      active: true, 
      name: "Christmas Day", 
      emoji: "🎄", 
      message: "Merry Christmas! Christ the Saviour is born!",
      bgGradient: "from-red-600 via-yellow-500 to-green-600",
      verse: "For God so loved the world that He gave His only begotten Son. - John 3:16"
    };
  }
  
  // Boxing Day (Dec 26)
  if (month === 12 && day === 26) {
    return { 
      active: true, 
      name: "Boxing Day", 
      emoji: "🎁", 
      message: "Happy Boxing Day! Continue celebrating Christ's birth!",
      bgGradient: "from-green-600 to-red-600",
      verse: "Every good gift and every perfect gift is from above. - James 1:17"
    };
  }
  
  // New Year's Eve (Dec 31)
  if (month === 12 && day === 31) {
    return { 
      active: true, 
      name: "New Year's Eve", 
      emoji: "🎆", 
      message: "Happy New Year's Eve! Let's thank God for this year!",
      bgGradient: "from-purple-600 via-pink-500 to-yellow-500",
      verse: "The Lord has done great things for us, and we are filled with joy. - Psalm 126:3"
    };
  }
  
  // New Year's Day (Jan 1)
  if (month === 1 && day === 1) {
    return { 
      active: true, 
      name: `Happy New Year ${year}!`, 
      emoji: "🎉", 
      message: `Happy New Year ${year}! God's blessings upon you!`,
      bgGradient: "from-yellow-500 via-orange-500 to-red-500",
      verse: "Behold, I am doing a new thing; now it springs forth. - Isaiah 43:19"
    };
  }
  
  // Palm Sunday
  if (isSameDay(today, palmSunday)) {
    return { 
      active: true, 
      name: "Palm Sunday", 
      emoji: "🌿", 
      message: "Hosanna! Blessed is He who comes in the name of the Lord!",
      bgGradient: "from-green-600 via-yellow-500 to-green-700",
      verse: "Hosanna to the Son of David! Blessed is He who comes in the name of the Lord! - Matthew 21:9"
    };
  }
  
  // Good Friday
  if (isSameDay(today, goodFriday)) {
    return { 
      active: true, 
      name: "Good Friday", 
      emoji: "✝️", 
      message: "Good Friday - Remembering Christ's sacrifice for us",
      bgGradient: "from-purple-900 to-gray-800",
      verse: "For God so loved the world that He gave His only begotten Son. - John 3:16"
    };
  }
  
  // Easter Sunday
  if (isSameDay(today, easter)) {
    return { 
      active: true, 
      name: "Easter Sunday", 
      emoji: "🌅", 
      message: "He is Risen! Happy Easter!",
      bgGradient: "from-yellow-400 via-pink-400 to-purple-500",
      verse: "He is not here; He has risen! - Luke 24:6"
    };
  }
  
  // Holy Week (between Palm Sunday and Easter, excluding specific days already handled)
  if (today >= holyWeekStart && today <= holyWeekEnd) {
    return { 
      active: true, 
      name: "Holy Week", 
      emoji: "🕊️", 
      message: "Holy Week - Preparing for Easter",
      bgGradient: "from-purple-600 to-indigo-600",
      verse: "Hosanna! Blessed is He who comes in the name of the Lord! - Mark 11:9"
    };
  }
  
  // Pentecost (50 days after Easter)
  if (isSameDay(today, pentecost)) {
    return { 
      active: true, 
      name: "Pentecost Sunday", 
      emoji: "🔥", 
      message: "Happy Pentecost! Celebrating the Holy Spirit!",
      bgGradient: "from-red-500 via-orange-500 to-yellow-500",
      verse: "And they were all filled with the Holy Spirit. - Acts 2:4"
    };
  }
  
  return { active: false };
};

// Festive Banner Component
const FestiveBanner = ({ festive, onClose }) => {
  if (!festive.active) return null;
  
  return (
    <div className={`relative bg-gradient-to-r ${festive.bgGradient} text-white py-4 px-4 text-center overflow-hidden`}>
      {/* Animated sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <span 
            key={i} 
            className="absolute text-2xl animate-pulse" 
            style={{ 
              left: `${Math.random() * 100}%`, 
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`
            }}
          >
            ✨
          </span>
        ))}
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-2">
          <span className="text-4xl animate-bounce">{festive.emoji}</span>
          <h2 className="text-xl sm:text-2xl font-bold drop-shadow-lg">{festive.name}</h2>
          <span className="text-4xl animate-bounce">{festive.emoji}</span>
        </div>
        <p className="text-lg font-medium mb-2">{festive.message}</p>
        <p className="text-sm opacity-90 italic">"{festive.verse}"</p>
      </div>
      
      <button 
        onClick={onClose} 
        className="absolute top-2 right-2 text-white/70 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  const [verse, setVerse] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [churchInfo, setChurchInfo] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);
  const [todayLessons, setTodayLessons] = useState(null);
  const [prophetLesson, setProphetLesson] = useState(null);
  const [bibleStudy, setBibleStudy] = useState(null);
  const [vineyardEvents, setVineyardEvents] = useState([]);
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [showFestive, setShowFestive] = useState(true);
  const [ministryInterest, setMinistryInterest] = useState({ open: false, ministry: "", name: "", phone: "" });
  const [showMinistryConfirmation, setShowMinistryConfirmation] = useState(false);
  const [submittingMinistry, setSubmittingMinistry] = useState(false);
  const [bibleVerseDialog, setBibleVerseDialog] = useState({ open: false, reference: "", verses: [], loading: false });
  
  const festive = getFestiveSeason();

  const fetchBibleVerse = async (reference) => {
    setBibleVerseDialog({ open: true, reference, verses: [], loading: true });
    try {
      // Clean up the reference for the API
      // Handle various formats like "Matthew 26: 20-35" or "Matthew 26:20-35"
      let cleanRef = reference.trim();
      // Remove extra spaces around colons and hyphens
      cleanRef = cleanRef.replace(/\s*:\s*/g, ':').replace(/\s*-\s*/g, '-');
      // Replace remaining spaces with + for URL encoding
      cleanRef = cleanRef.replace(/\s+/g, '+');
      
      const response = await fetch(`https://bible-api.com/${cleanRef}?translation=kjv`);
      const data = await response.json();
      if (data.verses && data.verses.length > 0) {
        setBibleVerseDialog({ open: true, reference, verses: data.verses, loading: false });
      } else if (data.text) {
        // Fallback to plain text if verses array not available
        setBibleVerseDialog({ open: true, reference, verses: [{ verse: 1, text: data.text }], loading: false });
      } else {
        setBibleVerseDialog({ open: true, reference, verses: [{ verse: 0, text: "Unable to fetch this verse. Please try again." }], loading: false });
      }
    } catch (err) {
      setBibleVerseDialog({ open: true, reference, verses: [{ verse: 0, text: "Unable to fetch verse. Please check your internet connection." }], loading: false });
    }
  };

  useEffect(() => {
    axios.get(`${API}/daily-inspiration`).then(res => setVerse(res.data)).catch(() => {});
    axios.get(`${API}/announcements`).then(res => setAnnouncements(res.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/church-info`).then(res => setChurchInfo(res.data)).catch(() => {});
    axios.get(`${API}/bible-lessons/current-week`).then(res => setTodayLessons(res.data)).catch(() => {});
    axios.get(`${API}/prophet-lessons/current-week`).then(res => setProphetLesson(res.data)).catch(() => {});
    axios.get(`${API}/bible-study/current`).then(res => setBibleStudy(res.data)).catch(() => {});
    axios.get(`${API}/vineyard-events`).then(res => setVineyardEvents(res.data)).catch(() => {});
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.contact || !contactForm.message) { toast.error("Please fill all fields"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent!");
      setContactForm({ name: "", contact: "", message: "" });
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  const handleMinistrySubmit = async () => {
    if (!ministryInterest.name || !ministryInterest.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmittingMinistry(true);
    try {
      await axios.post(`${API}/contact`, {
        name: ministryInterest.name,
        contact: ministryInterest.phone,
        message: `MINISTRY INTEREST: ${ministryInterest.ministry}\n\nI am interested in joining the ${ministryInterest.ministry}. Please contact me for more information.\n\nSubmitted via Ministry Interest Form`
      });
      setMinistryInterest({ open: false, ministry: "", name: "", phone: "" });
      setShowMinistryConfirmation(true);
    } catch {
      toast.error("Failed to submit. Please try again.");
    }
    setSubmittingMinistry(false);
  };

  return (
    <div className="pb-24" data-testid="home-page">
      {/* Ministry Interest Dialog */}
      <Dialog open={ministryInterest.open} onOpenChange={(open) => setMinistryInterest(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Users className="w-5 h-5" /> Ministry Interest Form
            </DialogTitle>
            <DialogDescription>
              Register your interest in joining a ministry. A department leader will contact you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-700 font-medium">Full Name *</Label>
              <Input 
                placeholder="Enter your full name" 
                value={ministryInterest.name} 
                onChange={e => setMinistryInterest(prev => ({...prev, name: e.target.value}))} 
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Contact Number *</Label>
              <Input 
                placeholder="Enter your phone number" 
                value={ministryInterest.phone} 
                onChange={e => setMinistryInterest(prev => ({...prev, phone: e.target.value}))} 
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Ministry of Interest</Label>
              <Input 
                value={ministryInterest.ministry} 
                readOnly 
                className="rounded-xl mt-1 bg-blue-50 text-blue-800 font-semibold"
              />
            </div>
            <Button 
              onClick={handleMinistrySubmit} 
              disabled={submittingMinistry}
              className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {submittingMinistry ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : "Submit Interest"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ministry Interest Confirmation Dialog */}
      <Dialog open={showMinistryConfirmation} onOpenChange={setShowMinistryConfirmation}>
        <DialogContent className="max-w-md">
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h2>
            <div className="text-gray-600 space-y-3 text-left bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
              <p className="font-medium text-gray-800">Thank you for showing interest in joining one of our ministries at CCC Medway Central Parish.</p>
              <p>We have received your details, and the ministry leader for the department you selected will contact you shortly.</p>
              <p>We are excited to help you take this next step in service, growth, and community.</p>
              <p>If you have any questions in the meantime, feel free to reach out.</p>
              <p>We look forward to welcoming you into the team.</p>
              <div className="pt-3 border-t border-blue-200 mt-3">
                <p className="font-semibold text-blue-700">God Blessing You</p>
                <p className="font-bold text-blue-800">CCC Medway Central Parish</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowMinistryConfirmation(false)} 
              className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 h-12 shadow-lg"
            >
              <Heart className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bible Verse Dialog */}
      <Dialog open={bibleVerseDialog.open} onOpenChange={(open) => setBibleVerseDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-800">
              <BookOpen className="w-5 h-5" /> 📖 {bibleVerseDialog.reference}
            </DialogTitle>
            <DialogDescription>King James Version (KJV)</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {bibleVerseDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                <span className="ml-3 text-gray-500">Loading verse...</span>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 space-y-3">
                {bibleVerseDialog.verses.map((v, idx) => (
                  <div key={idx} className="flex gap-3">
                    {v.verse > 0 && (
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center">
                        {v.verse}
                      </span>
                    )}
                    <p className="text-gray-800 leading-relaxed font-serif flex-1">
                      {v.text.trim()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setBibleVerseDialog({ open: false, reference: "", verses: [], loading: false })} className="rounded-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Festive Season Banner */}
      {showFestive && <FestiveBanner festive={festive} onClose={() => setShowFestive(false)} />}
      
      {/* Admin controls - only visible when logged in as admin */}
      {user && isAdmin && (
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex gap-2">
            <Button className="rounded-full bg-gradient-to-r from-orange-400 to-orange-500 text-white hover:opacity-90 gap-2" onClick={() => navigate('/admin')}><Settings className="w-4 h-4" /> Admin Panel</Button>
            <Button variant="outline" className="rounded-full" onClick={logout}><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Live Announcements Ticker */}
      {announcements.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl overflow-hidden shadow-md border border-gray-200">
            <div className="flex items-center">
              {/* LIVE indicator */}
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 bg-red-600 flex-shrink-0">
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full animate-pulse" />
                <span className="text-white font-bold text-xs sm:text-sm tracking-wide">LIVE</span>
              </div>
              <div className="hidden sm:flex items-center gap-2 px-3 py-3 bg-white border-r border-gray-200 flex-shrink-0">
                <Bell className="w-4 h-4 text-red-600" />
                <span className="font-semibold text-gray-800 text-sm whitespace-nowrap">Live Announcements</span>
              </div>
              {/* Scrolling ticker */}
              <div className="flex-1 overflow-hidden py-2 sm:py-3 bg-gradient-to-r from-indigo-900 to-blue-900">
                <div className="animate-marquee whitespace-nowrap flex items-center gap-4 sm:gap-8">
                  {[...announcements, ...announcements].map((item, i) => (
                    <div key={i} className="inline-flex items-center gap-2 sm:gap-3">
                      <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-yellow-400" />
                      <span className="text-white font-medium text-xs sm:text-sm">{item.title}</span>
                      <span className="px-1.5 sm:px-2 py-0.5 bg-purple-600 text-white text-[10px] sm:text-xs font-bold rounded-full">EVENT</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 mb-6">
        <div className="rainbow-border-card">
          <div className="flex flex-row items-center gap-4 p-6 bg-white rounded-2xl">
            <img src={CCCLogo} alt="CCC Logo" className="w-20 h-20 sm:w-24 sm:h-24 object-contain rounded-full shadow-lg flex-shrink-0" style={{imageRendering: 'high-quality'}} />
            <div className="flex-1">
              <h2 className="text-lg sm:text-2xl font-bold text-blue-700">Celestial Church of Christ — Medway Central Parish</h2>
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=176+Rainham+Rd,+Gillingham,+Kent,+ME5+7EN,+United+Kingdom"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 flex items-center gap-2 mt-1 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" /> 
                <span className="hover:underline text-sm sm:text-base">{churchInfo?.address || "176 Rainham Rd, Gillingham, Kent, ME5 7EN, United Kingdom"}</span>
              </a>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="link" className="text-blue-600 p-0 h-auto mt-2 font-semibold hover:text-blue-800">
                    <Info className="w-4 h-4 mr-1" /> About Us
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-blue-800 flex items-center gap-2">
                      <Church className="w-6 h-6" /> Our Church
                    </DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <div className="flex items-center gap-4 mb-4">
                      <img src={CCCLogo} alt="CCC Logo" className="w-20 h-20 object-contain rounded-full shadow-md" />
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">Celestial Church of Christ</h3>
                        <p className="text-blue-600 font-medium">Medway Central Parish</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                      <p className="text-gray-700 leading-relaxed text-base">
                        Our parish reflects the essence of a Bible-believing church, rooted in the teachings of our Lord and Saviour Jesus Christ. We are part of the one spiritual, global, united, and indivisible Holy Church — the Celestial Church of Christ — established by divine revelation on the 29th of September 1947 in Porto-Novo, Dahomey (now the Republic of Benin), through the vessel of Samuel Bilewu Joseph Oshoffa.
                      </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a 
                        href="https://www.google.com/maps/dir/?api=1&destination=176+Rainham+Rd,+Gillingham,+Kent,+ME5+7EN,+United+Kingdom"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-gray-600 text-sm hover:text-blue-600 transition-colors"
                      >
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span className="hover:underline">176 Rainham Rd, Gillingham, Kent, ME5 7EN, United Kingdom</span>
                      </a>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Lenten Season Banner - Displays until April 6th */}
      {new Date() <= new Date('2026-04-06T23:59:59') && (
        <div className="max-w-[1400px] mx-auto px-2 sm:px-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-purple-800 via-purple-700 to-indigo-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl overflow-hidden relative">
            {/* Decorative elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" />
            
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-6">
              {/* Left - Cross Icon and Title */}
              <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                <div className="w-10 h-14 sm:w-14 sm:h-20 bg-gradient-to-b from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                  <div className="text-white text-xl sm:text-3xl font-bold">✝</div>
                </div>
                <div>
                  <h3 className="text-white font-bold text-base sm:text-xl">Season of Lent</h3>
                  <span className="inline-block mt-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-purple-500/40 text-purple-100 text-[10px] sm:text-xs font-medium rounded-full backdrop-blur-sm">
                    40 Days of Reflection and Repentance
                  </span>
                </div>
              </div>
              
              {/* Center - Bible Verse */}
              <div className="flex-1 lg:px-6">
                <blockquote className="text-white/95 text-sm sm:text-lg lg:text-xl italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                  "Create in me a clean heart, O God, and renew a right spirit within me."
                </blockquote>
                <cite className="text-amber-300 font-medium mt-1 sm:mt-2 block text-xs sm:text-base">— Psalm 51:10</cite>
              </div>
              
              {/* Right - Upcoming Events */}
              <div className="w-full sm:w-auto flex-shrink-0 lg:border-l lg:border-purple-500/30 lg:pl-6 mt-2 sm:mt-0">
                <div className="flex items-center gap-2 text-purple-200 text-xs sm:text-sm mb-2 sm:mb-3">
                  <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Coming:</span>
                </div>
                <div className="flex sm:flex-col gap-2 overflow-x-auto pb-1 sm:pb-0">
                  <div className="flex items-center gap-2 sm:gap-3 bg-purple-500/20 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 backdrop-blur-sm flex-shrink-0">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-500/30 rounded-lg flex items-center justify-center">
                      <span className="text-yellow-300 text-sm sm:text-lg">☀️</span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-xs sm:text-sm">Easter Sunday</p>
                      <p className="text-purple-200 text-[10px] sm:text-xs">6 Apr</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 bg-purple-500/20 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 backdrop-blur-sm flex-shrink-0">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange-500/30 rounded-lg flex items-center justify-center">
                      <span className="text-orange-300 text-sm sm:text-lg">🌅</span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-xs sm:text-sm">Easter Monday</p>
                      <p className="text-purple-200 text-[10px] sm:text-xs">7 Apr</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {verse && (
        <div className="max-w-[1400px] mx-auto px-2 sm:px-4 mb-4 sm:mb-6">
          <div className="inspiration-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white relative overflow-hidden">
            {/* Main content - Inspiration left, Coming right */}
            <div className="flex items-stretch gap-4">
              {/* Left - Daily Inspiration */}
              <div className="flex-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/20 flex items-center justify-center"><BookOpen className="w-4 h-4 sm:w-5 sm:h-5" /></div>
                  <div><h3 className="font-bold text-sm sm:text-base">Daily Inspiration</h3><p className="text-xs sm:text-sm text-white/70">Theme: {verse.theme}</p></div>
                </div>
                <div className="mt-4 sm:mt-6">
                  <blockquote className="text-base sm:text-xl italic mb-2 sm:mb-3" style={{ fontFamily: 'Georgia, serif' }}>"{verse.verse}"</blockquote>
                  <cite className="text-yellow-300 font-medium text-sm sm:text-base">— {verse.reference}</cite>
                </div>
              </div>

              {/* Right - Coming Events */}
              <div className="w-40 sm:w-48 flex-shrink-0 flex flex-col items-end gap-3">
                <div className="flex items-center gap-1.5 text-white/70">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span className="text-xs sm:text-sm font-semibold">Coming:</span>
                </div>
                {(() => {
                  const now = new Date();
                  const events = [
                    { name: "Ascension Day", date: new Date(2026, 4, 14), icon: "☁️" },
                    { name: "Pentecost", date: new Date(2026, 4, 24), icon: "🔥" },
                  ].filter(e => e.date >= now);
                  return events.map((evt, i) => {
                    const daysLeft = Math.ceil((evt.date - now) / (1000*60*60*24));
                    return (
                      <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2.5 w-full">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{evt.icon}</span>
                          <div>
                            <p className="font-bold text-xs sm:text-sm text-white">{evt.name}</p>
                            <p className="text-white/70 text-[10px] sm:text-xs">
                              {evt.date.getDate()} {evt.date.toLocaleString('default',{month:'short'})}
                              {daysLeft > 0 && <span className="text-amber-300 ml-1 font-bold">({daysLeft}d)</span>}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-2 sm:px-4 mb-4 sm:mb-6">
        {/* Service Schedule Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center shadow-lg">
            <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-base sm:text-xl">Service Schedule</h3>
            <p className="text-xs sm:text-sm text-gray-500">Join us for worship</p>
          </div>
        </div>
        
        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {(() => {
            const services = [
              { 
                day: "SUNDAY", 
                name: "Sunday Morning Service", 
                time: "10:00 AM", 
                headerColor: "from-green-500 to-teal-500",
                serviceType: "Sunday Morning",
                showBibleLesson: true 
              },
              { 
                day: "SUNDAY", 
                name: "Sunday Evening Service", 
                time: "7:00 PM", 
                headerColor: "from-teal-500 to-cyan-500",
                serviceType: "Sunday Evening",
                showBibleLesson: true 
              },
              { 
                day: "TUESDAY", 
                name: "Bible Study", 
                subtitle: "Weekly Bible Study (8PM - 9PM)",
                time: "8:00 PM", 
                headerColor: "from-orange-500 to-red-500",
                serviceType: "Bible Study",
                showBibleStudy: true 
              },
              { 
                day: "WEDNESDAY", 
                name: "Seekers Service", 
                subtitle: "Mid-week seekers service",
                time: "9:00 AM", 
                headerColor: "from-sky-400 to-blue-500",
                serviceType: "Seekers Service",
                showBibleLesson: true 
              },
              { 
                day: "WEDNESDAY", 
                name: "Wednesday Mercy Day Service", 
                time: "7:00 PM", 
                headerColor: "from-blue-500 to-blue-600",
                serviceType: "Wednesday",
                showBibleLesson: true 
              },
              { 
                day: "FRIDAY", 
                name: "Friday Power Service", 
                time: "7:00 PM", 
                headerColor: "from-rose-500 to-pink-600",
                serviceType: "Friday",
                showBibleLesson: true 
              },
              { 
                day: "FRIDAY", 
                name: "Prophets Service", 
                time: "9:00 PM", 
                headerColor: "from-violet-500 to-purple-600",
                serviceType: "Friday Prophets",
                showProphetLesson: true 
              },
              { 
                day: "FIRST THURSDAY", 
                name: "New Moon Service (1st Thursday)", 
                time: "10:00 PM", 
                headerColor: "from-indigo-500 to-purple-600",
                serviceType: "New Moon",
                showBibleLesson: true
              }
            ];
            
            const lessonsByService = todayLessons?.lessons_by_service || {};
            
            return services.map((service, i) => {
              // Map frontend service types to API response keys
              const serviceKeyMap = {
                "Sunday Morning": "Sunday Morning (10AM)",
                "Sunday Evening": "Sunday Evening (6PM)",
                "Wednesday": "Wednesday",
                "Wednesday Morning": "Wednesday Morning",
                "Seekers Service": "Seekers Service",
                "Friday": "Friday",
                "Friday Prophets": "Friday",
                "New Moon": "New Moon",
                // Thursday Holy Week services
                "Washing of Feet": "Washing of Feet",
                "Holy Communion": "Holy Communion",
                "Lord's Supper": "Lord's Supper",
                "Holy Thursday": "Holy Thursday"
              };
              
              const apiKey = serviceKeyMap[service.serviceType] || service.serviceType;
              
              // For Holy Communion, also check Lord's Supper as fallback
              let matchingLessons = lessonsByService[apiKey] || lessonsByService[service.serviceType] || [];
              
              // For Holy Communion, fallback to Lord's Supper if no direct match
              if (service.serviceType === "Holy Communion" && !matchingLessons.length) {
                matchingLessons = lessonsByService["Lord's Supper"] || [];
              }
              
              // Also check for Holy Thursday lessons for all Thursday services
              if (service.isHolyThursday && !matchingLessons.length) {
                matchingLessons = lessonsByService["Holy Thursday"] || [];
              }
              
              // For Sunday services, also pull from Palm Sunday & Pentecost & special Harvest named services
              // so the title auto-swaps on those dates
              if (service.serviceType === "Sunday Morning") {
                const palm = lessonsByService["Palm Sunday Morning"] || [];
                const pent = lessonsByService["Pentecost Morning"] || [];
                const juvH = lessonsByService["Juvenile Harvest Morning"] || [];
                const adultH = lessonsByService["Adult Harvest Morning"] || [];
                const natH = lessonsByService["Harvest National Morning"] || [];
                matchingLessons = [...matchingLessons, ...palm, ...pent, ...juvH, ...adultH, ...natH];
              }
              if (service.serviceType === "Sunday Evening") {
                const palm = lessonsByService["Palm Sunday Evening"] || [];
                const pent = lessonsByService["Pentecost Evening"] || [];
                const juvH = lessonsByService["Juvenile Harvest Evening"] || [];
                const adultH = lessonsByService["Adult Harvest Evening"] || [];
                const natH = lessonsByService["Harvest National Evening"] || [];
                matchingLessons = [...matchingLessons, ...palm, ...pent, ...juvH, ...adultH, ...natH];
              }
              
              const todayDate = new Date().toISOString().split('T')[0];
              const sortedLessons = [...matchingLessons].sort((a, b) => {
                const diffA = Math.abs(new Date(a.date) - new Date(todayDate));
                const diffB = Math.abs(new Date(b.date) - new Date(todayDate));
                return diffA - diffB;
              });
              const currentLesson = sortedLessons.find(l => l.date >= todayDate) || sortedLessons[0];
              
              // Dynamically override card title/time when the upcoming lesson belongs to
              // a special-season service (Palm Sunday, Pentecost, Harvest) so the homepage rebadges itself.
              let displayName = service.name;
              let displayTime = service.time;
              if (currentLesson?.service_type === "Pentecost Morning") {
                displayName = "Pentecost Morning";
                displayTime = "10am";
              } else if (currentLesson?.service_type === "Pentecost Evening") {
                displayName = "Pentecost Evening";
                displayTime = "7pm";
              } else if (currentLesson?.service_type === "Palm Sunday Morning") {
                displayName = "Palm Sunday Morning";
                displayTime = "10am";
              } else if (currentLesson?.service_type === "Palm Sunday Evening") {
                displayName = "Palm Sunday Evening";
                displayTime = "7pm";
              } else if (currentLesson?.service_type === "Juvenile Harvest Morning") {
                displayName = "Juvenile Harvest Morning";
                displayTime = "10am";
              } else if (currentLesson?.service_type === "Juvenile Harvest Evening") {
                displayName = "Juvenile Harvest Evening";
                displayTime = "7pm";
              } else if (currentLesson?.service_type === "Adult Harvest Morning") {
                displayName = "Adult Harvest Morning";
                displayTime = "10am";
              } else if (currentLesson?.service_type === "Adult Harvest Evening") {
                displayName = "Adult Harvest Evening";
                displayTime = "7pm";
              } else if (currentLesson?.service_type === "Harvest National Morning") {
                displayName = "Harvest National Morning";
                displayTime = "10am";
              } else if (currentLesson?.service_type === "Harvest National Evening") {
                displayName = "Harvest National Evening";
                displayTime = "7pm";
              }
              
              return (
                <div key={i} className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300">
                  {/* Card Header */}
                  <div className={`bg-gradient-to-r ${service.headerColor} p-3 sm:p-4`}>
                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                      <div className="flex items-center gap-1.5 sm:gap-2 text-white text-xs sm:text-sm font-bold">
                        <CalendarIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {service.day}
                      </div>
                      {displayTime && (
                        <div className="flex items-center gap-1.5 bg-white/25 px-2 py-1 rounded-full">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          <span className="text-white text-xs sm:text-sm font-bold">{displayTime}</span>
                        </div>
                      )}
                    </div>
                    <h4 className="text-white font-bold text-sm sm:text-lg">{displayName}</h4>
                    {service.subtitle && (
                      <p className="text-white/80 text-xs sm:text-sm mt-0.5 sm:mt-1">{service.subtitle}</p>
                    )}
                  </div>
                  
                  {/* Card Body */}
                  <div className="p-3 sm:p-4">
                    {/* Bible Study Card - Special layout */}
                    {service.showBibleStudy && (
                      <>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-700 text-[10px] sm:text-xs font-semibold mb-2 sm:mb-3">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-orange-100 rounded flex items-center justify-center">
                            <BookOpen className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-orange-600" />
                          </div>
                          THIS WEEK'S STUDY
                          <span className="ml-auto bg-orange-100 text-orange-700 font-bold px-2 py-0.5 rounded text-[10px] sm:text-xs">{bibleStudy?.week_start_date || todayDate}</span>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 border border-yellow-100">
                          <p className="text-gray-600 text-xs sm:text-sm">Topic: <span className={bibleStudy?.topic ? "text-gray-800 font-medium" : "text-gray-400 italic"}>{bibleStudy?.topic || "To be announced"}</span></p>
                        </div>
                        {bibleStudy?.scripture ? (
                          <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3.5 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => fetchBibleVerse(bibleStudy.scripture)}>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-gray-800 font-bold text-sm sm:text-base">{bibleStudy.scripture}</p>
                              <p className="text-orange-500 text-xs sm:text-sm font-medium">Click to read →</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3.5 flex items-start gap-2 sm:gap-3">
                            <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-gray-400 italic text-sm">Scripture to be announced</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Regular Bible Lessons - but NOT for Good Friday which has special format */}
                    {service.showBibleLesson && !service.isGoodFriday && (
                      <>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-700 text-xs sm:text-sm font-semibold mb-2 sm:mb-3">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-100 rounded flex items-center justify-center">
                            <BookOpen className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600" />
                          </div>
                          THIS WEEK'S LESSONS
                          <span className="ml-auto bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded text-xs">{currentLesson?.date || todayDate}</span>
                        </div>
                        
                        {currentLesson?.first_lesson && (
                          <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3.5 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors mb-2"
                            onClick={() => fetchBibleVerse(currentLesson.first_lesson)}>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-gray-800 font-bold text-sm sm:text-base">1st: {currentLesson.first_lesson}</p>
                              <p className="text-blue-500 text-xs sm:text-sm font-medium">Click to read →</p>
                            </div>
                          </div>
                        )}
                        
                        {currentLesson?.second_lesson && (
                          <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3.5 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => fetchBibleVerse(currentLesson.second_lesson)}>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-gray-800 font-bold text-sm sm:text-base">2nd: {currentLesson.second_lesson}</p>
                              <p className="text-blue-500 text-xs sm:text-sm font-medium">Click to read →</p>
                            </div>
                          </div>
                        )}
                        
                        {!currentLesson?.first_lesson && !currentLesson?.second_lesson && currentLesson?.scripture && (
                          <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3.5 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => fetchBibleVerse(currentLesson.scripture)}>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-gray-800 font-bold text-sm sm:text-base">{currentLesson.scripture}</p>
                              <p className="text-blue-500 text-xs sm:text-sm font-medium">Click to read →</p>
                            </div>
                          </div>
                        )}
                        
                        {!currentLesson && (
                          <p className="text-gray-400 text-sm italic">No lessons scheduled</p>
                        )}
                      </>
                    )}
                    
                    {/* Good Friday Special - Multiple Services with Times */}
                    {service.isGoodFriday && service.goodFridayServices && (
                      <div className="space-y-2">
                        {service.goodFridayServices.map((gf, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => fetchBibleVerse(gf.lesson)}>
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded text-[10px] sm:text-xs">{gf.time}</span>
                                <span className="text-purple-800 font-semibold text-[10px] sm:text-xs">— {gf.heading}</span>
                              </div>
                              <p className="text-gray-800 font-semibold text-xs sm:text-sm">{gf.lesson}</p>
                              <p className="text-purple-500 text-[10px] sm:text-xs font-medium">Click to read →</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Prophet Lessons */}
                    {service.showProphetLesson && (
                      <>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-gray-700 text-[10px] sm:text-xs font-semibold mb-2 sm:mb-3">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-purple-100 rounded flex items-center justify-center">
                            <BookOpen className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                          </div>
                          THIS WEEK'S LESSONS
                          <span className="ml-auto bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded text-[10px] sm:text-xs">{prophetLesson?.friday_date || todayDate}</span>
                        </div>
                        
                        {prophetLesson?.lesson ? (
                          <>
                            {prophetLesson.lesson.prophetic_focus && (
                              <div className="bg-purple-50 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3 border border-purple-100">
                                <p className="text-gray-600 text-xs sm:text-sm">Focus: <span className="text-purple-600 font-medium">{prophetLesson.lesson.prophetic_focus}</span></p>
                              </div>
                            )}
                            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 flex items-start gap-2 sm:gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => fetchBibleVerse(prophetLesson.lesson.bible_lesson)}>
                              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-gray-800 font-semibold text-xs sm:text-sm">{prophetLesson.lesson.bible_lesson}</p>
                                <p className="text-purple-500 text-[10px] sm:text-xs font-medium">Click to read →</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <p className="text-gray-400 text-xs sm:text-sm italic">No lessons scheduled</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Vineyard Events Section */}
      {vineyardEvents.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-2 sm:px-4 mb-6">
          <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-amber-100">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-xl">Vineyard Events</h3>
                <p className="text-sm text-gray-500">Upcoming activities and programs</p>
              </div>
            </div>

            {/* Events Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {vineyardEvents.map((event) => (
                <div 
                  key={event.id} 
                  className="group cursor-pointer"
                  onClick={() => event.flyer_url.toLowerCase().endsWith('.pdf') 
                    ? window.open(event.flyer_url.startsWith('/api') ? `${BACKEND_URL}${event.flyer_url}` : event.flyer_url, '_blank')
                    : setSelectedFlyer(event)
                  }
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
                    {event.flyer_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="w-full h-full bg-gradient-to-br from-red-50 to-orange-50 flex flex-col items-center justify-center">
                        <FileText className="w-16 h-16 text-red-500 mb-2" />
                        <span className="text-sm font-medium text-red-700">PDF Flyer</span>
                        <span className="text-xs text-gray-500 mt-1">Click to view</span>
                      </div>
                    ) : (
                      <img 
                        src={event.flyer_url.startsWith('/api') ? `${BACKEND_URL}${event.flyer_url}` : event.flyer_url} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <div>
                        <p className="text-white font-semibold text-sm line-clamp-2">{event.title}</p>
                        {event.event_date && (
                          <p className="text-amber-300 text-xs mt-1">{event.event_date}</p>
                        )}
                      </div>
                    </div>
                    {/* Click indicator */}
                    <div className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      {event.flyer_url.toLowerCase().endsWith('.pdf') ? (
                        <Download className="w-4 h-4 text-red-600" />
                      ) : (
                        <Eye className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                  </div>
                  {/* Title below image */}
                  <p className="text-gray-700 font-medium text-sm mt-2 line-clamp-1 text-center">{event.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Flyer Viewer Dialog */}
      <Dialog open={!!selectedFlyer} onOpenChange={() => setSelectedFlyer(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          {selectedFlyer && (
            <div>
              <div className="relative">
                <img 
                  src={selectedFlyer.flyer_url.startsWith('/api') ? `${BACKEND_URL}${selectedFlyer.flyer_url}` : selectedFlyer.flyer_url} 
                  alt={selectedFlyer.title}
                  className="w-full h-auto"
                />
              </div>
              <div className="p-4 sm:p-6 bg-gradient-to-r from-amber-50 to-orange-50">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedFlyer.title}</h3>
                {selectedFlyer.event_date && (
                  <p className="text-amber-600 font-medium text-sm mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {selectedFlyer.event_date}
                  </p>
                )}
                {selectedFlyer.description && (
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{selectedFlyer.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="max-w-[1400px] mx-auto px-4 grid md:grid-cols-2 gap-4 mb-6">
        {/* Get Involved - Join Our Ministries - Full Width */}
        <Card className="border-0 shadow-lg overflow-hidden hover-lift md:col-span-2">
          <div className="h-1 bg-gradient-to-r from-blue-600 to-teal-500" />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><Users className="w-6 h-6 text-blue-700" /></div>
              <div>
                <h3 className="font-bold text-blue-800 text-lg">Get Involved – Join Our Ministries</h3>
                <p className="text-sm text-gray-600">We believe every member has a gift to share</p>
              </div>
            </div>
            <p className="text-gray-700 mb-2 leading-relaxed">
              There's a place for you to serve. By joining one of our ministries, you'll grow in faith, build community, and make a lasting impact.
            </p>
            <p className="text-sm text-blue-600 mb-5 font-medium">
              👆 Click on any ministry of interest to provide your details, and one of our department leaders will be in touch.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { name: "Ushering Ministry", icon: Users, gradient: "from-blue-500 to-indigo-600", desc: "Welcome and guide worshippers." },
                { name: "Media Team", icon: Video, gradient: "from-purple-500 to-violet-600", desc: "Sound, visuals, and online outreach." },
                { name: "Prayer Ministry", icon: Heart, gradient: "from-amber-500 to-orange-600", desc: "Intercession and spiritual support." },
                { name: "Welfare Ministry", icon: HandCoins, gradient: "from-emerald-500 to-teal-600", desc: "Care and support for those in need." },
                { name: "Choir Ministry", icon: Music, gradient: "from-pink-500 to-rose-600", desc: "Lead worship through music." },
                { name: "Children's Ministry", icon: BookOpen, gradient: "from-cyan-500 to-blue-600", desc: "Nurture young hearts and minds." }
              ].map((ministry) => (
                <div 
                  key={ministry.name}
                  onClick={() => setMinistryInterest({ open: true, ministry: ministry.name })}
                  className={`flex flex-col items-center text-center p-2 bg-gradient-to-br ${ministry.gradient} rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.03] cursor-pointer`}
                >
                  <div className="w-8 h-8 rounded-md bg-white/20 backdrop-blur flex items-center justify-center mb-1">
                    <ministry.icon className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-white text-[10px] leading-tight">{ministry.name}</h4>
                  <p className="text-[8px] text-white/80 mt-0.5 leading-tight hidden sm:block">{ministry.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg overflow-hidden hover-lift md:col-span-2">
          <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-600" />
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center"><MessageSquare className="w-6 h-6 text-cyan-600" /></div>
              <div><h3 className="font-bold text-gray-800 text-lg">Connect With Us</h3><p className="text-sm text-gray-500">We'd love to hear from you</p></div>
            </div>
            <form onSubmit={handleContact} className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Input placeholder="Your name" value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} className="rounded-xl border-gray-200" />
                <Input placeholder="Email or phone" value={contactForm.contact} onChange={e => setContactForm({...contactForm, contact: e.target.value})} className="rounded-xl border-gray-200" />
              </div>
              <div className="space-y-3">
                <Textarea placeholder="Your message" value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} rows={3} className="rounded-xl border-gray-200" />
                <Button type="submit" className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white h-11" disabled={sending}>
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send Message
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Bible Lesson 2026 Section */}
      <div className="px-4 mt-8 max-w-[1400px] mx-auto">
        <Card 
          className="border-0 shadow-xl overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
          onClick={() => navigate('/bible-lessons/january')}
        >
          <div className="h-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">📖 Bible Lesson 2026</h2>
                  <p className="text-sm text-gray-500">Click to view weekly Bible lessons by month</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-indigo-600">
                <span className="text-sm font-medium hidden sm:block">View Lessons</span>
                <ChevronRight className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charity Registration Notice */}
      <div className="px-4 mt-8 mb-8 max-w-[1400px] mx-auto text-center">
        <p className="text-sm text-black font-medium">
          Celestial Church of Christ Medway Central Parish is a charity registered in England under Charity number 1216525
        </p>
      </div>
    </div>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  useEffect(() => { 
    axios.get(`${API}/events`).then(res => {
      // Sort events by date in ascending order (earliest first)
      const sortedEvents = [...res.data].sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(sortedEvents);
    }).catch(() => {}); 
  }, []);
  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="events-page">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-2xl p-6 mb-6 text-white shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <CalendarIcon className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Events & Calendar</h1>
            <p className="text-white/80 text-sm">Stay connected with upcoming activities</p>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-0 shadow-xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
          <CardContent className="p-4"><Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} className="rounded-lg" /></CardContent>
        </Card>
        <div className="lg:col-span-2 space-y-4">
          {events.length === 0 ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon className="w-10 h-10 text-purple-500" />
                </div>
                <p className="text-gray-500 font-medium">No upcoming events</p>
                <p className="text-sm text-gray-400 mt-1">Check back soon for new activities!</p>
              </CardContent>
            </Card>
          ) : events.map((event, i) => (
            <Card key={i} className="border-0 shadow-xl hover-lift overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex flex-col items-center justify-center text-white shadow-lg">
                    <span className="text-xs uppercase font-medium">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                    <span className="text-xl font-bold">{new Date(event.date).getDate()}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 text-lg">{event.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{event.description}</p>
                    <div className="flex items-center gap-4 text-sm text-purple-600 font-medium">
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {event.time}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const BibleLessonMonthPage = () => {
  const navigate = useNavigate();
  const { month } = useParams();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bibleVerseDialog, setBibleVerseDialog] = useState({ open: false, reference: "", verses: [], loading: false });

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const monthCapitalized = month ? month.charAt(0).toUpperCase() + month.slice(1) : "";
  const monthIndex = monthNames.findIndex(m => m.toLowerCase() === month?.toLowerCase());

  useEffect(() => {
    setLoading(true);
    // Use public endpoint for month-specific lessons
    axios.get(`${API}/bible-lessons/month/${month}?year=2026`).then(res => {
      // Sort by date
      const sorted = [...res.data].sort((a, b) => new Date(a.date) - new Date(b.date));
      setLessons(sorted);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [month]);

  const fetchBibleVerse = async (reference) => {
    if (!reference) return;
    setBibleVerseDialog({ open: true, reference, verses: [], loading: true });
    try {
      // Clean up the reference for the API
      // Handle various formats like "Matthew 26: 20-35" or "Matthew 26:20-35"
      let cleanRef = reference.trim();
      // Remove extra spaces around colons and hyphens
      cleanRef = cleanRef.replace(/\s*:\s*/g, ':').replace(/\s*-\s*/g, '-');
      // Replace remaining spaces with + for URL encoding
      cleanRef = cleanRef.replace(/\s+/g, '+');
      
      const response = await fetch(`https://bible-api.com/${cleanRef}?translation=kjv`);
      const data = await response.json();
      if (data.verses && data.verses.length > 0) {
        setBibleVerseDialog({ open: true, reference, verses: data.verses, loading: false });
      } else if (data.text) {
        setBibleVerseDialog({ open: true, reference, verses: [{ verse: 1, text: data.text }], loading: false });
      } else {
        setBibleVerseDialog({ open: true, reference, verses: [{ verse: 0, text: "Unable to fetch this verse." }], loading: false });
      }
    } catch (err) {
      setBibleVerseDialog({ open: true, reference, verses: [{ verse: 0, text: "Unable to fetch verse. Please check your connection." }], loading: false });
    }
  };

  const getServiceColor = (service) => {
    if (!service) return "from-gray-500 to-gray-600";
    if (service.includes("Sunday Morning") || service.includes("Easter Sunday Morning") || service.includes("Palm Sunday Morning")) return "from-purple-500 to-pink-500";
    if (service.includes("Sunday Evening") || service.includes("Easter Sunday Evening") || service.includes("Palm Sunday Evening")) return "from-indigo-500 to-purple-500";
    if (service.includes("Wednesday") || service.includes("Holy Wednesday")) return "from-blue-500 to-cyan-500";
    if (service.includes("Friday") || service.includes("Good Friday")) return "from-green-500 to-teal-500";
    if (service.includes("New Moon")) return "from-amber-500 to-orange-500";
    if (service.includes("Holy") || service.includes("Washing") || service.includes("Lord's Supper")) return "from-red-500 to-rose-500";
    return "from-gray-500 to-gray-600";
  };

  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="bible-lesson-month-page">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 mb-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">📖 Bible Lessons - {monthCapitalized} 2026</h1>
              <p className="text-white/80 text-sm">Weekly scripture readings for all services</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/")} className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20">
            <Home className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
        {monthNames.map((m, idx) => (
          <Button
            key={m}
            variant={m.toLowerCase() === month?.toLowerCase() ? "default" : "outline"}
            className={`rounded-full flex-shrink-0 ${m.toLowerCase() === month?.toLowerCase() ? "bg-gradient-to-r from-indigo-600 to-purple-600" : ""}`}
            onClick={() => navigate(`/bible-lessons/${m.toLowerCase()}`)}
          >
            {m.slice(0, 3)}
          </Button>
        ))}
      </div>

      {/* Lessons Content - Sequential by Date */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          <span className="ml-3 text-gray-500">Loading lessons...</span>
        </div>
      ) : lessons.length === 0 ? (
        <Card className="border-0 shadow-xl">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-10 h-10 text-indigo-500" />
            </div>
            <p className="text-gray-500 font-medium text-lg">No Bible lessons found for {monthCapitalized} 2026</p>
            <p className="text-sm text-gray-400 mt-2">Check back later or select another month</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
          <CardContent className="p-4">
            <div className="space-y-3">
              {lessons.map((lesson, idx) => (
                <div key={idx} className={`rounded-xl p-4 border transition-all hover:shadow-md bg-gradient-to-r ${getServiceColor(lesson.service_type)} bg-opacity-10`} style={{background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,240,255,0.95) 100%)`}}>
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getServiceColor(lesson.service_type)} flex flex-col items-center justify-center text-white shadow-lg flex-shrink-0`}>
                      <span className="text-[10px] uppercase font-medium">
                        {lesson.day?.slice(0,3)}
                      </span>
                      <span className="text-xl font-bold">
                        {lesson.date ? new Date(lesson.date + 'T12:00:00').getDate() : "-"}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full text-white font-medium bg-gradient-to-r ${getServiceColor(lesson.service_type)}`}>
                          {lesson.service_type}
                        </span>
                        <span className="text-xs text-gray-500">{lesson.time}</span>
                      </div>
                      
                      {lesson.first_lesson && (
                        <div 
                          className="bg-white rounded-lg px-3 py-2 border border-indigo-200 cursor-pointer hover:bg-indigo-50 transition-colors"
                          onClick={() => fetchBibleVerse(lesson.first_lesson)}
                        >
                          <span className="text-xs text-indigo-600 font-semibold">1st Lesson:</span>
                          <p className="text-gray-800 font-medium underline decoration-dotted">{lesson.first_lesson}</p>
                        </div>
                      )}
                      
                      {lesson.second_lesson && (
                        <div 
                          className="bg-white rounded-lg px-3 py-2 border border-purple-200 cursor-pointer hover:bg-purple-50 transition-colors"
                          onClick={() => fetchBibleVerse(lesson.second_lesson)}
                        >
                          <span className="text-xs text-purple-600 font-semibold">2nd Lesson:</span>
                          <p className="text-gray-800 font-medium underline decoration-dotted">{lesson.second_lesson}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bible Verse Dialog */}
      <Dialog open={bibleVerseDialog.open} onOpenChange={(open) => setBibleVerseDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-800">
              <BookOpen className="w-5 h-5" /> 📖 {bibleVerseDialog.reference}
            </DialogTitle>
            <DialogDescription>King James Version (KJV)</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {bibleVerseDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                <span className="ml-3 text-gray-500">Loading verse...</span>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 space-y-3">
                {bibleVerseDialog.verses.map((v, idx) => (
                  <div key={idx} className="flex gap-3">
                    {v.verse > 0 && (
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center">
                        {v.verse}
                      </span>
                    )}
                    <p className="text-gray-800 leading-relaxed font-serif flex-1">
                      {v.text?.trim()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setBibleVerseDialog({ open: false, reference: "", verses: [], loading: false })} className="rounded-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SermonsPage = () => {
  const [sermons, setSermons] = useState([]);
  const [category, setCategory] = useState("all");
  const categories = [{id: "all", name: "All"}, {id: "sunday_service", name: "Sermon"}, {id: "revival", name: "Revival"}, {id: "youth", name: "Youth"}, {id: "women_forum", name: "Women Forum"}];
  
  useEffect(() => { const url = category === "all" ? `${API}/sermons` : `${API}/sermons?category=${category}`; axios.get(url).then(res => setSermons(res.data)).catch(() => {}); }, [category]);
  
  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="sermons-page">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-6 mb-6 text-white shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Video className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">📺 Webcast & Media</h1>
            <p className="text-white/80 text-sm">Watch and listen to church broadcasts and media contents across all ministries and events—streamed and archived for your spiritual growth.</p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
        {categories.map(cat => (
          <Button key={cat.id} variant={category === cat.id ? "default" : "outline"} className={`rounded-full whitespace-nowrap shadow-md ${category === cat.id ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0" : "border-purple-200 text-purple-700 hover:bg-purple-50"}`} onClick={() => setCategory(cat.id)}>{cat.name}</Button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sermons.length === 0 ? (
          <Card className="col-span-full border-0 shadow-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                <Video className="w-10 h-10 text-indigo-500" />
              </div>
              <p className="text-gray-500 font-medium">No content available</p>
              <p className="text-sm text-gray-400 mt-1">New content coming soon!</p>
            </CardContent>
          </Card>
        ) : sermons.map((sermon, i) => (
          <Card key={i} className="border-0 shadow-xl overflow-hidden hover-lift group">
            <div className="h-36 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all"></div>
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-white" />
              </div>
              <span className="absolute top-3 right-3 px-2 py-1 text-xs rounded-full bg-white/20 backdrop-blur-sm text-white capitalize">
                {categories.find(c => c.id === sermon.category)?.name || sermon.category}
              </span>
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 line-clamp-1">{sermon.title}</h3>
              <p className="text-sm text-gray-500 mb-3">{sermon.preacher} {sermon.date && `• ${new Date(sermon.date).toLocaleDateString()}`}</p>
              {sermon.description && <p className="text-xs text-gray-400 mb-3 line-clamp-2">{sermon.description}</p>}
              <div className="flex flex-wrap gap-2">
                {sermon.video_url && (
                  <Button size="sm" className="flex-1 rounded-full bg-gradient-to-r from-red-500 to-red-600 gap-1" onClick={() => window.open(sermon.video_url)}>
                    <Play className="w-3 h-3" /> Watch
                  </Button>
                )}
                {sermon.programme_url && (
                  <Button size="sm" variant="outline" className="flex-1 rounded-full border-blue-300 text-blue-700 hover:bg-blue-50 gap-1" onClick={() => window.open(sermon.programme_url)}>
                    <FileText className="w-3 h-3" /> Programme
                  </Button>
                )}
                {sermon.audio_url && !sermon.video_url && (
                  <Button size="sm" variant="outline" className="flex-1 rounded-full border-purple-300 text-purple-700" onClick={() => window.open(sermon.audio_url)}>
                    Listen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const PrayerPage = () => {
  const [form, setForm] = useState({ name: "", phone: "", request: "", is_private: false });
  const [loading, setLoading] = useState(false);
  const [publicPrayers, setPublicPrayers] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  useEffect(() => { axios.get(`${API}/prayer-requests/public`).then(res => setPublicPrayers(res.data)).catch(() => {}); }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.request) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try { 
      await axios.post(`${API}/prayer-requests`, form); 
      setShowConfirmation(true);
      setForm({ name: "", phone: "", request: "", is_private: false }); 
    } catch { toast.error("Failed to submit"); }
    setLoading(false);
  };
  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="prayer-page">
      {/* Prayer Request Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h2>
            <div className="text-gray-600 space-y-3 text-left bg-gradient-to-r from-rose-50 to-pink-50 p-5 rounded-xl border border-rose-100">
              <p className="font-medium text-gray-800">Thank you for submitting your prayer request.</p>
              <p>We have received your message, and our prayer team will review it shortly.</p>
              <p>To support you more personally, we will respond to your request directly via WhatsApp using the phone number you provided.</p>
              <p>Please look out for a message from us soon.</p>
              <div className="pt-3 border-t border-rose-200 mt-3">
                <p className="font-semibold text-rose-700">Blessings,</p>
                <p className="font-bold text-rose-800">CCC Medway Central Parish</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowConfirmation(false)} 
              className="mt-6 w-full rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 h-12 shadow-lg"
            >
              <Heart className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-red-500 rounded-2xl p-6 mb-6 text-white shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Heart className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Prayer Wall</h1>
            <p className="text-white/80 text-sm">Share your prayers with our community</p>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-rose-500 to-pink-500" />
          <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50">
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <Heart className="w-5 h-5" /> Submit Prayer Request
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label className="text-gray-700 font-medium">Your Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl mt-1 border-rose-200 focus:border-rose-400" data-testid="prayer-name-input" placeholder="Enter your name" /></div>
              <div><Label className="text-gray-700 font-medium">Phone Number <span className="text-gray-400 font-normal">(Optional)</span></Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-xl mt-1 border-rose-200 focus:border-rose-400" data-testid="prayer-phone-input" placeholder="Enter your phone number" type="tel" /></div>
              <div><Label className="text-gray-700 font-medium">Prayer Request</Label><Textarea value={form.request} onChange={e => setForm({...form, request: e.target.value})} rows={4} className="rounded-xl mt-1 border-rose-200 focus:border-rose-400" data-testid="prayer-request-input" placeholder="Share your prayer request..." /></div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100"><Switch checked={form.is_private} onCheckedChange={v => setForm({...form, is_private: v})} /><Label className="text-sm text-gray-700">{form.is_private ? "🔒 Private - Only visible to prayer team" : "🌍 Public - Visible on prayer wall"}</Label></div>
              <Button type="submit" className="w-full rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 h-12 shadow-lg" disabled={loading} data-testid="submit-prayer-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}Submit Prayer Request</Button>
            </form>
          </CardContent>
        </Card>
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-bold text-gray-800">Community Prayers</h3>
          </div>
          <ScrollArea className="h-[400px]">
            <div className="space-y-3 pr-4">
              {publicPrayers.length === 0 ? (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
                      <Heart className="w-8 h-8 text-rose-400" />
                    </div>
                    <p className="text-gray-500">No public prayers yet</p>
                    <p className="text-sm text-gray-400 mt-1">Be the first to share!</p>
                  </CardContent>
                </Card>
              ) : publicPrayers.map((p, i) => (
                <Card key={i} className="border-0 shadow-md bg-gradient-to-r from-rose-50 to-pink-50 hover-lift">
                  <CardContent className="p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">"{p.request}"</p>
                    <p className="text-xs text-rose-500 font-medium mt-3">— {p.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

// Member Registration Page (for public - enables automatic GPS attendance tracking)
const MemberRegistrationPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/members/register`, form);
      
      // Request GPS permission for automatic Sunday attendance tracking
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Successfully got permission - GPS tracking will be automatic on Sundays
            console.log("GPS permission granted - automatic Sunday tracking enabled");
          }, 
          (error) => {
            console.log("GPS permission denied - attendance will need manual entry");
          }, 
          { enableHighAccuracy: true }
        );
      }
      
      // Save member token for automatic GPS tracking
      if (res.data.token) {
        localStorage.setItem("memberToken", res.data.token);
        localStorage.setItem("memberId", res.data.member_id);
        localStorage.setItem("gpsTrackingEnabled", "true");
      }
      
      toast.success("Welcome to our church family! 🙏 Your attendance will be tracked automatically on Sundays.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="pb-24 pt-8 px-4 max-w-md mx-auto">
      <div className="text-center mb-8">
        <img src={CCCLogo} alt="CCC Logo" className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg" />
        <h1 className="text-2xl font-bold text-blue-800">Join Our Church Family</h1>
        <p className="text-gray-500 mt-2">Become a member of CCC Medway Central Parish</p>
      </div>

      <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
        <CardContent className="pt-6">
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label className="text-gray-700 font-medium">Full Name *</Label>
              <Input 
                value={form.full_name} 
                onChange={e => setForm({...form, full_name: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Email *</Label>
              <Input 
                type="email"
                value={form.email} 
                onChange={e => setForm({...form, email: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Phone Number *</Label>
              <Input 
                type="tel"
                value={form.phone} 
                onChange={e => setForm({...form, phone: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="07xxx xxxxxx"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium py-3 mt-4"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Join Our Family
            </Button>
          </form>
          
        </CardContent>
      </Card>

      <p className="text-center text-xs text-gray-400 mt-6 px-4">
        By registering, you agree to receive church updates and your Sunday attendance will be tracked automatically.
      </p>
    </div>
  );
};

// Automatic GPS Attendance Tracker - runs in background for registered members
const AutoGPSTracker = () => {
  useEffect(() => {
    const memberId = localStorage.getItem("memberId");
    const memberToken = localStorage.getItem("memberToken");
    const gpsEnabled = localStorage.getItem("gpsTrackingEnabled");
    
    // Only run for registered members with GPS enabled
    if (!memberId || !memberToken || gpsEnabled !== "true") return;
    
    const checkAndSendLocation = () => {
      const now = new Date();
      const day = now.getDay(); // 0 = Sunday
      const hour = now.getHours();
      
      // Only track on Sundays between 8 AM and 6 PM
      if (day !== 0 || hour < 8 || hour >= 18) return;
      
      // Get GPS location and send to server
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await axios.post(`${API}/attendance/gps/auto-checkin`, {
                member_id: memberId,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              }, {
                headers: { Authorization: `Bearer ${memberToken}` }
              });
              console.log("GPS attendance recorded automatically");
            } catch (err) {
              console.log("GPS auto-checkin failed:", err.message);
            }
          },
          (error) => {
            console.log("GPS location error:", error.message);
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    };
    
    // Check immediately on load
    checkAndSendLocation();
    
    // Check every 15 minutes on Sundays
    const interval = setInterval(checkAndSendLocation, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return null; // This component renders nothing, just runs the tracking logic
};

const AttendancePage = () => {
  // Manual registration form for ushers
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", ministry: "" });
  const [loading, setLoading] = useState(false);

  // Register new member (for ushers)
  const handleRegisterMember = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error("Please enter member's name"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/members/manual-register`, {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        ministry: form.ministry,
        entry_type: "manual"
      });
      toast.success("Member registered successfully!");
      setForm({ full_name: "", phone: "", email: "", ministry: "", service_type: "sunday", is_visitor: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register member");
    }
    setLoading(false);
  };

  return (
    <div className="pb-24 pt-4 px-4 max-w-lg mx-auto" data-testid="attendance-page">
      <h1 className="text-2xl font-bold text-blue-800 mb-2 text-center">Member Registration</h1>
      <p className="text-gray-500 text-center mb-6 text-sm">For ushers to register new church members</p>
      
      {/* Registration Form */}
      <Card className="border-0 shadow-lg rounded-2xl mb-6">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-t-2xl">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Register New Member
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleRegisterMember} className="space-y-4">
            <div>
              <Label className="text-gray-700 font-medium">Full Name *</Label>
              <Input 
                value={form.full_name} 
                onChange={e => setForm({...form, full_name: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="Enter member's full name"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Phone Number</Label>
              <Input 
                value={form.phone} 
                onChange={e => setForm({...form, phone: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="07xxx xxxxxx"
                type="tel"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Email</Label>
              <Input 
                value={form.email} 
                onChange={e => setForm({...form, email: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="member@email.com"
                type="email"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Ministry</Label>
              <Select value={form.ministry} onValueChange={v => setForm({...form, ministry: v})}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Select ministry (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ushering">Ushering</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Prayer">Prayer</SelectItem>
                  <SelectItem value="Welfare">Welfare</SelectItem>
                  <SelectItem value="Choir">Choir</SelectItem>
                  <SelectItem value="Children">Children</SelectItem>
                  <SelectItem value="Youth">Youth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium py-3">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Register Member
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Church Location Info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-xl">
        <a 
          href="https://www.google.com/maps/dir/?api=1&destination=176+Rainham+Rd,+Gillingham,+Kent,+ME5+7EN,+United+Kingdom"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-3 hover:bg-blue-100 rounded-lg p-2 -m-2 transition-colors"
        >
          <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-800">Church Location</p>
            <p className="text-sm text-gray-600 hover:underline">176 Rainham Rd, Gillingham, Kent, ME5 7EN</p>
            <p className="text-xs text-blue-600 mt-1">Tap to get directions →</p>
          </div>
        </a>
      </div>
    </div>
  );
};

const HymnsPage = () => {
  const [todayData, setTodayData] = useState({ is_service_day: false, hymns: [], language: "english", service_type: null });
  const [loading, setLoading] = useState(true);
  const [selectedHymn, setSelectedHymn] = useState(null);
  const [hymnDetails, setHymnDetails] = useState(null);
  const [loadingHymn, setLoadingHymn] = useState(false);
  const [showYoruba, setShowYoruba] = useState(false);

  // Service type display names
  const serviceTypeNames = {
    "sunday": "Sunday Service",
    "wednesday": "Wednesday Service",
    "friday": "Friday Service",
    "friday_prophets": "Prophets Service",
    "first_thursday": "First Thursday Service"
  };

  useEffect(() => { 
    setLoading(true);
    axios.get(`${API}/hymns/today`).then(res => {
      setTodayData(res.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    }); 
  }, []);

  const handleHymnClick = async (hymn) => {
    setSelectedHymn(hymn);
    setHymnDetails(null);
    setLoadingHymn(true);
    setShowYoruba(todayData.language === "yoruba");
    try {
      // Fetch full hymn details including lyrics
      const res = await axios.get(`${API}/hymns/by-number/${hymn.number}`);
      setHymnDetails(res.data);
    } catch (err) {
      // If API fails, use the basic hymn data we have
      setHymnDetails(hymn);
    }
    setLoadingHymn(false);
  };

  if (loading) {
    return (
      <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Non-service day - hide hymns section entirely
  if (!todayData.is_service_day) {
    return (
      <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="hymns-page">
        <Card className="border-0 shadow-lg overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100">
          <CardContent className="p-8 text-center">
            <Music className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-600 mb-2">No Service Today</h2>
            <p className="text-gray-500">Hymns are available on service days:</p>
            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <p>• <strong>Saturdays</strong> - Sunday hymns preview (from midnight)</p>
              <p>• <strong>Sundays</strong> - 7 hymns</p>
              <p>• <strong>Wednesdays</strong> - 5 hymns</p>
              <p>• <strong>Fridays</strong> - 5 hymns + Prophets Service</p>
              <p>• <strong>First Thursday of the month</strong> - 5 hymns</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Service day but no hymns selected by admin
  if (todayData.hymns.length === 0) {
    return (
      <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="hymns-page">
        <Card className="border-0 shadow-lg overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
          <CardContent className="p-8 text-center">
            <Music className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-amber-700 mb-2">{serviceTypeNames[todayData.service_type]}</h2>
            <p className="text-amber-600">No hymns have been selected for today's service yet.</p>
            <p className="text-sm text-gray-500 mt-2">Please check back later or contact the admin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Service day with hymns selected
  const today = new Date();
  const hymnCount = todayData.service_type === "sunday" ? 7 : 5;
  const isYoruba = todayData.language === "yoruba";

  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="hymns-page">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Today's Hymns</h1>
      <p className="text-sm text-purple-600 mb-4">Click on each Hymn for the service day to view its lyrics in English & Yoruba</p>
      
      {/* Today's Hymns Card */}
      <Card className="border-0 shadow-lg overflow-hidden mb-6 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Music className="w-5 h-5 text-purple-600" />
              {serviceTypeNames[todayData.service_type]}
            </span>
            <div className="flex items-center gap-2">
              <Badge className={isYoruba ? "bg-green-500" : "bg-purple-500"}>
                {isYoruba ? "Yoruba" : "English"}
              </Badge>
              <Badge variant="outline">{today.toLocaleDateString()}</Badge>
            </div>
          </CardTitle>
          <CardDescription>
            {todayData.hymns.length} hymn{todayData.hymns.length !== 1 ? 's' : ''} selected for {todayData.service_type === "sunday" ? "Sunday" : "today"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {todayData.hymns.map((hymn, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleHymnClick(hymn)}>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {hymn.number}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">
                    {isYoruba ? (hymn.title_yoruba || hymn.title_english) : hymn.title_english}
                  </p>
                  {!isYoruba && hymn.title_yoruba && <p className="text-xs text-gray-500 truncate">{hymn.title_yoruba}</p>}
                  {isYoruba && hymn.title_english && <p className="text-xs text-gray-500 truncate">{hymn.title_english}</p>}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Prophets Service Hymns Card - Only shown on Fridays */}
      {todayData.service_type === "friday" && todayData.prophets_service && (
        <Card className="border-0 shadow-lg overflow-hidden mb-6 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-600" />
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Music className="w-5 h-5 text-indigo-600" />
                Prophets Service
              </span>
              <div className="flex items-center gap-2">
                <Badge className={todayData.prophets_service.language === "yoruba" ? "bg-green-500" : "bg-indigo-500"}>
                  {todayData.prophets_service.language === "yoruba" ? "Yoruba" : "English"}
                </Badge>
                <Badge variant="outline">9:00 PM</Badge>
              </div>
            </CardTitle>
            <CardDescription>
              {todayData.prophets_service.hymns?.length || 0} hymn{(todayData.prophets_service.hymns?.length || 0) !== 1 ? 's' : ''} selected for Prophets Service
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(!todayData.prophets_service.hymns || todayData.prophets_service.hymns.length === 0) ? (
              <p className="text-center text-gray-500 py-4">No hymns have been selected for Prophets Service yet.</p>
            ) : (
              <div className="space-y-2">
                {todayData.prophets_service.hymns.map((hymn, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleHymnClick(hymn)}>
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {hymn.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">
                        {todayData.prophets_service.language === "yoruba" ? (hymn.title_yoruba || hymn.title_english) : hymn.title_english}
                      </p>
                      {todayData.prophets_service.language !== "yoruba" && hymn.title_yoruba && <p className="text-xs text-gray-500 truncate">{hymn.title_yoruba}</p>}
                      {todayData.prophets_service.language === "yoruba" && hymn.title_english && <p className="text-xs text-gray-500 truncate">{hymn.title_english}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hymn Detail Dialog with Full Lyrics */}
      <Dialog open={!!selectedHymn} onOpenChange={() => { setSelectedHymn(null); setHymnDetails(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedHymn && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center text-xl font-bold flex-shrink-0">
                    {selectedHymn.number}
                  </span>
                  <div className="text-left">
                    <p className="text-lg font-bold text-gray-800">
                      {showYoruba ? (hymnDetails?.title_yoruba || selectedHymn.title_yoruba || selectedHymn.title_english) : (hymnDetails?.title_english || selectedHymn.title_english)}
                    </p>
                    <p className="text-sm font-normal text-gray-500">
                      {showYoruba ? (hymnDetails?.title_english || selectedHymn.title_english) : (hymnDetails?.title_yoruba || selectedHymn.title_yoruba)}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              {/* Language Toggle */}
              <div className="flex gap-2 my-4">
                <Button
                  size="sm"
                  variant={!showYoruba ? "default" : "outline"}
                  className={`rounded-full ${!showYoruba ? "bg-purple-600" : ""}`}
                  onClick={() => setShowYoruba(false)}
                >
                  English
                </Button>
                <Button
                  size="sm"
                  variant={showYoruba ? "default" : "outline"}
                  className={`rounded-full ${showYoruba ? "bg-green-600" : ""}`}
                  onClick={() => setShowYoruba(true)}
                >
                  Yoruba
                </Button>
              </div>

              {/* Lyrics Content */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                {loadingHymn ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={showYoruba ? "bg-green-500" : "bg-purple-500"}>
                        {showYoruba ? "Yoruba" : "English"}
                      </Badge>
                      <span className="text-sm text-gray-500">Hymn #{selectedHymn.number}</span>
                    </div>
                    
                    {/* Display Lyrics */}
                    {showYoruba ? (
                      <div className="space-y-4">
                        {hymnDetails?.lyrics_yoruba ? (
                          <div className="whitespace-pre-wrap text-gray-800 leading-relaxed font-medium">
                            {hymnDetails.lyrics_yoruba}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Music className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Yoruba lyrics not available</p>
                            <p className="text-sm text-gray-400 mt-1">Please check the hymn book for full lyrics</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {hymnDetails?.lyrics_english ? (
                          <div className="whitespace-pre-wrap text-gray-800 leading-relaxed font-medium">
                            {hymnDetails.lyrics_english}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Music className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">English lyrics not available</p>
                            <p className="text-sm text-gray-400 mt-1">Please check the hymn book for full lyrics</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show Both Languages Side by Side on larger screens */}
                    {hymnDetails?.lyrics_english && hymnDetails?.lyrics_yoruba && (
                      <div className="mt-6 pt-4 border-t border-purple-200">
                        <p className="text-xs text-purple-600 text-center">
                          Tap the buttons above to switch between English and Yoruba
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GivePage = () => {
  const [churchInfo, setChurchInfo] = useState(null);
  useEffect(() => { axios.get(`${API}/church-info`).then(res => setChurchInfo(res.data)).catch(() => {}); }, []);
  
  // Bank details for QR code
  const bankDetails = {
    bankName: "HSBC",
    accountName: "Celestial Church of Christ Medway",
    sortCode: "40-31-06",
    accountNumber: "02659778"
  };
  
  const qrCodeData = `Bank: ${bankDetails.bankName}\nAccount: ${bankDetails.accountName}\nSort Code: ${bankDetails.sortCode}\nAcc No: ${bankDetails.accountNumber}`;

  return (
    <div className="pb-24 pt-4 px-4 max-w-lg mx-auto" data-testid="give-page">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">Give to the Church</h1>
      
      {/* Bank Transfer Section */}
      <Card className="border-0 shadow-lg overflow-hidden mb-6">
        <div className="h-1 bg-gradient-to-r from-green-500 to-teal-500" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HandCoins className="w-5 h-5 text-green-600" /> Bank Transfer
          </CardTitle>
          <CardDescription>To support the church through offerings, tithes, and alms, please use our bank details below:</CardDescription>
        </CardHeader>
        <CardContent>
          {/* QR Code Section */}
          <div className="flex flex-col items-center mb-6">
            <div className="p-4 bg-white rounded-2xl shadow-md border-2 border-green-100">
              <QRCodeSVG 
                value={qrCodeData}
                size={180}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#1a1a1a"
              />
            </div>
            <p className="text-sm text-gray-500 mt-3 text-center">
              Scan to view bank details securely
            </p>
          </div>
          
          {/* Bank Details (collapsed by default for security) */}
          <details className="group">
            <summary className="cursor-pointer text-sm text-green-600 font-medium text-center hover:text-green-700 list-none flex items-center justify-center gap-2">
              <Eye className="w-4 h-4" />
              <span>Show bank details</span>
            </summary>
            <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm text-gray-500">Bank Name</p>
                <p className="font-medium text-gray-800">{bankDetails.bankName}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-500">Account Name</p>
                <p className="font-medium text-gray-800">{bankDetails.accountName}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-500">Sort Code</p>
                <p className="font-medium text-gray-800">{bankDetails.sortCode}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-500">Account Number</p>
                <p className="font-medium text-gray-800">{bankDetails.accountNumber}</p>
              </div>
            </div>
          </details>
          <p className="text-xs text-gray-400 mt-4 text-center">Please use your name as the payment reference</p>
        </CardContent>
      </Card>
    </div>
  );
};

const GivingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => { try { const res = await axios.get(`${API}/donations/status/${sessionId}`); setPaymentData(res.data); if (res.data.payment_status === "paid") setStatus("success"); else if (res.data.status === "expired") setStatus("expired"); else if (++attempts < 5) setTimeout(poll, 2000); else setStatus("pending"); } catch { setStatus("error"); } };
    poll();
  }, [sessionId]);
  return (
    <div className="pb-24 pt-20 px-4 flex items-center justify-center min-h-[60vh]" data-testid="giving-success">
      <Card className="max-w-md w-full border-0 shadow-xl text-center"><CardContent className="p-8">
        {status === "checking" && <Loader2 className="w-16 h-16 text-blue-500 mx-auto animate-spin" />}
        {status === "success" && (<><CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2">Thank You!</h2><p className="text-gray-600 mb-4">Your donation has been received.</p>{paymentData && <p className="text-3xl font-bold text-green-500">£{(paymentData.amount_total / 100).toFixed(2)}</p>}<Link to="/"><Button className="mt-6 rounded-full">Return Home</Button></Link></>)}
        {status === "error" && (<><AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2">Error</h2><Link to="/give"><Button className="mt-4 rounded-full">Try Again</Button></Link></>)}
      </CardContent></Card>
    </div>
  );
};

// Meet The Team Page
const MeetTheTeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeamMembers(res.data);
      // Get unique categories
      const cats = [...new Set(res.data.map(m => m.role_category))];
      setCategories(cats);
    } catch (err) {
      console.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'shepherd': return '🙏';
      case 'elders': return '👑';
      case 'deacons': return '⭐';
      case 'ministers': return '✨';
      case 'mother in israel': return '💝';
      case 'choir': return '🎵';
      default: return '👤';
    }
  };

  const getCategoryDescription = (category) => {
    switch (category.toLowerCase()) {
      case 'shepherd': return 'Spiritual leadership and pastoral care';
      case 'elders': return 'Wisdom and guidance for our congregation';
      case 'deacons': return 'Service and ministry support';
      case 'ministers': return 'Spreading the word of God';
      case 'mother in israel': return 'Nurturing and spiritual guidance for women';
      case 'choir': return 'Leading worship through music';
      default: return 'Serving our community';
    }
  };

  const getCategoryGradient = (category) => {
    switch (category.toLowerCase()) {
      case 'shepherd': return 'from-purple-600 to-indigo-700';
      case 'elders': return 'from-amber-500 to-orange-600';
      case 'deacons': return 'from-blue-500 to-cyan-600';
      case 'ministers': return 'from-emerald-500 to-teal-600';
      case 'mother in israel': return 'from-pink-500 to-rose-600';
      case 'choir': return 'from-violet-500 to-purple-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getMembersByCategory = (category) => {
    return teamMembers.filter(m => m.role_category === category);
  };

  return (
    <div className="pb-24 pt-4 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-16 text-center">
          <p className="text-blue-200 text-sm font-medium mb-2">Our Leadership Team</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Meet Our Team</h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Dedicated servants of God leading our parish with faith, wisdom, and love.
          </p>
        </div>
      </div>

      {/* Team Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Team Members Coming Soon</h3>
            <p className="text-gray-500">Our leadership team information will be available shortly.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {categories.map((category) => (
              <div key={category} className="space-y-6">
                {/* Category Header */}
                <div className="text-center">
                  <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${getCategoryGradient(category)} text-white px-6 py-3 rounded-full shadow-lg mb-3`}>
                    <span className="text-2xl">{getCategoryIcon(category)}</span>
                    <h2 className="text-xl font-bold">{category}</h2>
                  </div>
                  <p className="text-gray-600">{getCategoryDescription(category)}</p>
                </div>

                {/* Team Members Grid */}
                <div className={`grid gap-6 ${getMembersByCategory(category).length === 1 ? 'grid-cols-1 max-w-xl mx-auto' : getMembersByCategory(category).length === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                  {getMembersByCategory(category).map((member) => (
                    <div key={member.id} className="group">
                      <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                        <div className={`h-2 bg-gradient-to-r ${getCategoryGradient(member.role_category)}`} />
                        <CardContent className="p-8 text-center">
                          {/* Photo */}
                          <div className="relative w-64 h-64 mx-auto mb-6">
                            <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryGradient(member.role_category)} rounded-xl opacity-20 group-hover:opacity-30 transition-opacity`} />
                            {member.photo_url ? (
                              <img 
                                src={member.photo_url} 
                                alt={member.name}
                                className="w-full h-full object-cover rounded-xl border-4 border-white shadow-xl"
                              />
                            ) : (
                              <div className={`w-full h-full rounded-xl bg-gradient-to-br ${getCategoryGradient(member.role_category)} flex items-center justify-center text-white text-5xl font-bold shadow-lg`}>
                                {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <h3 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h3>
                          <p className={`text-base font-semibold bg-gradient-to-r ${getCategoryGradient(member.role_category)} bg-clip-text text-transparent mb-3`}>
                            {member.title}
                          </p>
                          {member.description && (
                            <p className="text-gray-500 text-base">{member.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const { login, subAdminLogin } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSubAdmin) {
        await subAdminLogin(username, password);
      } else {
        await login(email, password);
      }
      toast.success("Welcome!");
      navigate("/admin");
    } catch {
      toast.error("Invalid credentials");
    }
    setLoading(false);
  };
  
  return (
    <div className="pb-24 pt-20 px-4 flex items-center justify-center min-h-[60vh]" data-testid="login-page">
      <Card className="w-full max-w-md border-0 shadow-xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-orange-400 to-orange-500" />
        <CardHeader className="text-center">
          <img src={CCCLogo} alt="Logo" className="w-20 h-20 mx-auto mb-2 rounded-full shadow-md" style={{imageRendering: 'high-quality'}} />
          <CardTitle>{isSubAdmin ? "Sub Admin Login" : "Main Admin Login"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSubAdmin ? (
              <div>
                <Label>Username</Label>
                <Input type="text" value={username} onChange={e => setUsername(e.target.value)} className="rounded-xl" placeholder="Enter your username" />
              </div>
            ) : (
              <div>
                <Label>Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="rounded-xl" data-testid="login-email" />
              </div>
            )}
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="rounded-xl" data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full rounded-xl bg-gradient-to-r from-orange-400 to-orange-500 h-12" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button 
              type="button" 
              onClick={() => setIsSubAdmin(!isSubAdmin)} 
              className="text-sm text-blue-600 hover:underline"
            >
              {isSubAdmin ? "Login as Main Admin →" : "Login as Sub Admin →"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminPage = () => {
  const { isAdmin, isMainAdmin, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  useEffect(() => { 
    if (!authLoading && !isAdmin) navigate("/login"); 
  }, [authLoading, isAdmin, navigate]);

  // Fetch unread messages count
  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/admin/contact-messages`).then(res => {
        const unread = res.data.filter(m => !m.is_read).length;
        setUnreadMessages(unread);
      }).catch(() => {});
    }
  }, [isAdmin, tab]);
  
  if (authLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!isAdmin) return null;
  
  // Filter tabs based on permissions
  const allTabs = [
    { key: "overview", label: "overview", permission: null },
    { key: "events", label: "events", permission: "events_read" },
    { key: "announcements", label: "announcements", permission: "announcements_read" },
    { key: "sermons", label: "webcast", permission: "sermons_read" },
    { key: "hymns", label: "hymns", permission: "hymns_read" },
    { key: "bible lessons", label: "bible lessons", permission: "bible_lessons_read" },
    { key: "prophet lessons", label: "prophet lessons", permission: "bible_lessons_read" },
    { key: "bible-study", label: "bible study", permission: "bible_lessons_read" },
    { key: "prayers", label: "prayers", permission: "prayers_read" },
    { key: "contact", label: "messages", permission: "contact_read" },
    { key: "ushers", label: "ushers", permission: "ushers_access" },
    { key: "attendance", label: "attendance", permission: "attendance_read" },
    { key: "manual-attendance", label: "manual attendance", permission: "manual_attendance_read" },
    { key: "finance", label: "finance", permission: "finance_read" },
    { key: "monthly-finance", label: "monthly income", permission: "monthly_income_read" },
    { key: "expenses", label: "expenses", permission: "expenses_read" },
    { key: "meetings", label: "meetings", permission: "meetings_read" },
    { key: "anointment", label: "anointment", permission: "anointment_read" },
    { key: "projects", label: "projects", permission: "projects_read" },
    { key: "sunday-school", label: "sunday school", permission: "sunday_school_read" },
    { key: "charity", label: "charity", permission: "charity_read" },
    { key: "harvest", label: "harvest", permission: "harvest_read" },
    { key: "team", label: "team", permission: "members_read" },
    { key: "vineyard", label: "vineyard", permission: "announcements_read" },
    { key: "members", label: "members", permission: "members_read" },
    { key: "birthdays", label: "birthdays", permission: "birthdays_access" },
    { key: "notifications", label: "notifications", permission: "notifications_access" },
    { key: "photo-gallery", label: "photo gallery", permission: "announcements_read" },
  ];
  
  // Main Admin only tabs
  if (isMainAdmin) {
    allTabs.push({ key: "media", label: "media", permission: null });
    allTabs.push({ key: "service-flow", label: "service flow", permission: null });
    allTabs.push({ key: "rules-tenets", label: "rules & tenets", permission: null });
    allTabs.push({ key: "shepherds-box", label: "shepherds box", permission: null });
    allTabs.push({ key: "admin-management", label: "admins", permission: null });
    allTabs.push({ key: "audit-logs", label: "logs", permission: null });
  }
  
  const visibleTabs = allTabs.filter(t => t.permission === null || hasPermission(t.permission));
  
  return (
    <div className="pb-24 pt-4 px-4 max-w-6xl mx-auto" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-blue-800">Admin Panel</h1>
          {!isMainAdmin && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Sub Admin</span>}
        </div>
        <Button variant="outline" onClick={() => navigate("/")} className="rounded-full"><Home className="w-4 h-4 mr-2" /> Back</Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-2 bg-transparent">
          {visibleTabs.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="rounded-full capitalize data-[state=active]:bg-blue-600 data-[state=active]:text-white relative">
              {t.label}
              {t.key === "contact" && unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadMessages > 99 ? "99+" : unreadMessages}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          {isMainAdmin ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Church className="w-12 h-12 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Church Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. As the Main Administrator, you have full access to all church management tools. Use the tabs above to navigate to any section.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button 
                    onClick={() => setTab("members")} 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-full px-6 py-3"
                  >
                    <Users className="w-5 h-5 mr-2" /> Members
                  </Button>
                  <Button 
                    onClick={() => setTab("attendance")} 
                    className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 rounded-full px-6 py-3"
                  >
                    <UserCheck className="w-5 h-5 mr-2" /> Attendance
                  </Button>
                  <Button 
                    onClick={() => setTab("finance")} 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-full px-6 py-3"
                  >
                    <DollarSign className="w-5 h-5 mr-2" /> Finance
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : hasPermission("sunday_school_read") ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-amber-500 to-orange-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <BookOpen className="w-12 h-12 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Sunday School Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. To begin, simply click <strong>"sunday school"</strong> tab above to enter the Sunday School folder and access all your tools.
                </p>
                <Button 
                  onClick={() => setTab("sunday-school")} 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full px-8 py-3 text-lg"
                >
                  <BookOpen className="w-5 h-5 mr-2" /> Go to Sunday School
                </Button>
              </CardContent>
            </Card>
          ) : hasPermission("finance_read") || hasPermission("monthly_income_read") || hasPermission("expenses_read") ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <DollarSign className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Finance Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. To begin, simply click <strong>"finance"</strong> or <strong>"monthly income"</strong> tabs above to access the financial management tools.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  {hasPermission("finance_read") && (
                    <Button 
                      onClick={() => setTab("finance")} 
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-full px-6 py-3"
                    >
                      <DollarSign className="w-5 h-5 mr-2" /> Go to Finance
                    </Button>
                  )}
                  {hasPermission("monthly_income_read") && (
                    <Button 
                      onClick={() => setTab("monthly-finance")} 
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 rounded-full px-6 py-3"
                    >
                      <FileText className="w-5 h-5 mr-2" /> Monthly Income
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : hasPermission("charity_read") ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-pink-500 to-rose-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Heart className="w-12 h-12 text-pink-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Welfare Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. To begin, simply click <strong>"charity"</strong> tab above to manage welfare and charity records for our community.
                </p>
                <Button 
                  onClick={() => setTab("charity")} 
                  className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 rounded-full px-8 py-3 text-lg"
                >
                  <Heart className="w-5 h-5 mr-2" /> Go to Welfare
                </Button>
              </CardContent>
            </Card>
          ) : hasPermission("hymns_read") ? (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-violet-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Music className="w-12 h-12 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Hymns Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. To begin, simply click <strong>"hymns"</strong> tab above to manage the hymn selections for our services.
                </p>
                <Button 
                  onClick={() => setTab("hymns")} 
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 rounded-full px-8 py-3 text-lg"
                >
                  <Music className="w-5 h-5 mr-2" /> Go to Hymns
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500" />
              <CardContent className="p-8 text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Settings className="w-12 h-12 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to the Admin Centre</h2>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  We're glad to have you here. Please select a tab above to access your assigned administrative tools.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="events"><AdminEventsTab /></TabsContent>
        <TabsContent value="announcements"><AdminAnnouncementsTab /></TabsContent>
        <TabsContent value="sermons"><AdminSermonsTab /></TabsContent>
        <TabsContent value="hymns"><AdminHymnsTab /></TabsContent>
        <TabsContent value="bible lessons"><AdminBibleLessonsTab /></TabsContent>
        <TabsContent value="prophet lessons"><AdminProphetLessonsTab /></TabsContent>
        <TabsContent value="bible-study"><AdminBibleStudyTab /></TabsContent>
        <TabsContent value="prayers"><AdminPrayersTab /></TabsContent>
        <TabsContent value="contact"><AdminContactTab /></TabsContent>
        <TabsContent value="ushers"><AdminUshersTab /></TabsContent>
        <TabsContent value="attendance"><AdminAttendanceTab /></TabsContent>
        <TabsContent value="manual-attendance"><AdminManualAttendanceTab /></TabsContent>
        <TabsContent value="finance"><AdminFinanceTab /></TabsContent>
        <TabsContent value="monthly-finance"><AdminMonthlyFinanceTab /></TabsContent>
        <TabsContent value="expenses"><AdminExpensesTab /></TabsContent>
        <TabsContent value="meetings"><AdminMeetingsTab /></TabsContent>
        <TabsContent value="anointment"><AdminAnointmentTab /></TabsContent>
        <TabsContent value="projects"><AdminProjectsTab /></TabsContent>
        <TabsContent value="sunday-school"><AdminSundaySchoolTab /></TabsContent>
        <TabsContent value="charity"><AdminCharityTab /></TabsContent>
        <TabsContent value="harvest"><AdminHarvestTab /></TabsContent>
        <TabsContent value="team"><AdminTeamTab /></TabsContent>
        <TabsContent value="vineyard"><AdminVineyardTab /></TabsContent>
        <TabsContent value="members"><AdminMembersTab /></TabsContent>
        <TabsContent value="birthdays"><AdminBirthdaysTab /></TabsContent>
        <TabsContent value="notifications"><AdminNotificationsTab /></TabsContent>
        <TabsContent value="photo-gallery"><AdminPhotoGalleryTab /></TabsContent>
        {isMainAdmin && <TabsContent value="media"><AdminMediaTab /></TabsContent>}
        {isMainAdmin && <TabsContent value="service-flow"><AdminServiceFlowTab /></TabsContent>}
        {isMainAdmin && <TabsContent value="rules-tenets"><AdminRulesTenetsTab /></TabsContent>}
        {isMainAdmin && <TabsContent value="shepherds-box"><AdminShepherdsBoxTab /></TabsContent>}
        {isMainAdmin && <TabsContent value="admin-management"><AdminManagementTab /></TabsContent>}
        {isMainAdmin && <TabsContent value="audit-logs"><AdminAuditLogsTab /></TabsContent>}
      </Tabs>
    </div>
  );
};

const AdminEventsTab = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", date: "", time: "", category: "service" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  useEffect(() => { 
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {}); 
  }, []);
  
  const save = async () => { 
    if (!form.title || !form.date) {
      toast.error("Please fill in title and date");
      return;
    }
    try { 
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, form);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, form); 
        toast.success("Event created"); 
      }
      setDialogOpen(false); 
      setForm({ title: "", description: "", date: "", time: "", category: "service" }); 
      setEditingId(null);
      axios.get(`${API}/events`).then(r => setEvents(r.data)); 
    } catch { 
      toast.error("Failed"); 
    } 
  };
  
  const del = async (id) => { 
    if (!window.confirm("Delete this event?")) return;
    await axios.delete(`${API}/events/${id}`); 
    setEvents(events.filter(e => e.id !== id)); 
    toast.success("Deleted"); 
  };
  
  const edit = (event) => {
    setForm({
      title: event.title || "",
      description: event.description || "",
      date: event.date || "",
      time: event.time || "",
      category: event.category || "service"
    });
    setEditingId(event.id);
    setDialogOpen(true);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-blue-500" /> Events ({events.length})
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ title: "", description: "", date: "", time: "", category: "service" }); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Event" : "Add Event"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Title *</Label>
                <Input placeholder="Event title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full" />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Time</Label>
                <Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Description</Label>
                <Textarea placeholder="Event description" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <Button onClick={save} className="w-full bg-blue-500 hover:bg-blue-600">
                {editingId ? "Update Event" : "Save Event"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No events scheduled</p>
            <p className="text-gray-400 text-sm">Click "Add Event" to create one</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="font-semibold text-gray-800">{e.title}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="flex items-center gap-2 text-gray-600">
                        <CalendarIcon className="w-4 h-4 text-blue-500" />
                        {formatDate(e.date)}
                      </span>
                      {e.time && <span className="text-sm text-gray-400 ml-6">{e.time}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <p className="text-sm text-gray-500 line-clamp-2">{e.description || "-"}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => edit(e)} title="Edit">
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(e.id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const AdminAnnouncementsTab = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", content: "", date: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  useEffect(() => { 
    axios.get(`${API}/announcements`).then(r => setItems(r.data)).catch(() => {}); 
  }, []);
  
  const save = async () => { 
    if (!form.title || !form.content) {
      toast.error("Please fill in title and content");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/announcements/${editingId}`, form);
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/announcements`, form); 
        toast.success("Announcement created"); 
      }
      setDialogOpen(false); 
      setForm({ title: "", content: "", date: "" }); 
      setEditingId(null);
      axios.get(`${API}/announcements`).then(r => setItems(r.data)); 
    } catch {
      toast.error("Failed to save");
    }
  };
  
  const del = async (id) => { 
    if (!window.confirm("Delete this announcement?")) return;
    await axios.delete(`${API}/announcements/${id}`); 
    setItems(items.filter(i => i.id !== id)); 
    toast.success("Deleted"); 
  };
  
  const edit = (item) => {
    setForm({ title: item.title || "", content: item.content || "", date: item.date || "" });
    setEditingId(item.id);
    setDialogOpen(true);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-yellow-500" /> Announcements ({items.length})
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ title: "", content: "", date: "" }); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-yellow-500 hover:bg-yellow-600">
              <Plus className="w-4 h-4 mr-2" /> Add Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Announcement" : "Add Announcement"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Title *</Label>
                <Input placeholder="Announcement title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Description *</Label>
                <Textarea placeholder="Announcement description/content" rows={4} value={form.content} onChange={e => setForm({...form, content: e.target.value})} />
              </div>
              <Button onClick={save} className="w-full bg-yellow-500 hover:bg-yellow-600">
                {editingId ? "Update Announcement" : "Save Announcement"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No announcements yet</p>
            <p className="text-gray-400 text-sm">Click "Add Announcement" to create one</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}>
                  <TableCell className="font-semibold text-gray-800">{item.title}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2 text-gray-600">
                      <CalendarIcon className="w-4 h-4 text-yellow-500" />
                      {formatDate(item.date)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <p className="text-sm text-gray-500 line-clamp-2">{item.content}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => edit(item)} title="Edit">
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(item.id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const AdminSermonsTab = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", programme_url: "", preacher: "", date: "", category: "sunday_service" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");

  const categories = [
    { id: "all", name: "All" },
    { id: "sunday_service", name: "Sermon" },
    { id: "revival", name: "Revival" },
    { id: "youth", name: "Youth" },
    { id: "women_forum", name: "Women Forum" }
  ];

  useEffect(() => { loadItems(); }, []);

  const loadItems = () => {
    axios.get(`${API}/sermons`).then(r => setItems(r.data)).catch(() => {});
  };

  const save = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/sermons/${editingItem.id}`, form);
        toast.success("Webcast updated!");
      } else {
        await axios.post(`${API}/sermons`, form);
        toast.success("Webcast added!");
      }
      setDialogOpen(false);
      setForm({ title: "", description: "", video_url: "", programme_url: "", preacher: "", date: "", category: "sunday_service" });
      setEditingItem(null);
      loadItems();
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      video_url: item.video_url || "",
      programme_url: item.programme_url || "",
      preacher: item.preacher || "",
      date: item.date || "",
      category: item.category || "sunday_service"
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this webcast?")) return;
    try {
      await axios.delete(`${API}/sermons/${id}`);
      toast.success("Deleted");
      loadItems();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const filteredItems = filterCategory === "all" 
    ? items 
    : items.filter(item => item.category === filterCategory);

  return (
    <div className="space-y-6">
      {/* Header Card - Matching Webcast Page Style */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Video className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Webcast Management</h1>
              <p className="text-white/80 text-sm">Manage sermons, programmes & media content</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingItem(null); setForm({ title: "", description: "", video_url: "", programme_url: "", preacher: "", date: "", category: "sunday_service" }); }}}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-white text-purple-600 hover:bg-white/90 gap-2 font-semibold">
                <Plus className="w-4 h-4" /> Add Webcast
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-600" />
                  {editingItem ? "Edit Webcast" : "Add New Webcast"}
                </DialogTitle>
                <DialogDescription>Add video content with programme links for members</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700 font-medium">Title *</Label>
                  <Input 
                    placeholder="e.g., Sunday Service - January 2026" 
                    value={form.title} 
                    onChange={e => setForm({...form, title: e.target.value})} 
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Category *</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sunday_service">Sermon</SelectItem>
                      <SelectItem value="revival">Revival</SelectItem>
                      <SelectItem value="youth">Youth</SelectItem>
                      <SelectItem value="women_forum">Women Forum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Preacher/Speaker</Label>
                  <Input 
                    placeholder="e.g., Pastor John" 
                    value={form.preacher} 
                    onChange={e => setForm({...form, preacher: e.target.value})} 
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Date</Label>
                  <Input 
                    type="date" 
                    value={form.date} 
                    onChange={e => setForm({...form, date: e.target.value})} 
                    className="rounded-xl mt-1"
                  />
                </div>
                <div className="p-3 bg-purple-50 rounded-xl">
                  <Label className="text-purple-800 font-semibold flex items-center gap-2">
                    <Play className="w-4 h-4" /> Video URL
                  </Label>
                  <Input 
                    placeholder="https://youtube.com/watch?v=..." 
                    value={form.video_url} 
                    onChange={e => setForm({...form, video_url: e.target.value})} 
                    className="rounded-xl mt-2 border-purple-200"
                  />
                  <p className="text-xs text-purple-600 mt-1">YouTube, Facebook, or other video link</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Label className="text-blue-800 font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Programme Link
                  </Label>
                  <Input 
                    placeholder="https://drive.google.com/... or PDF link" 
                    value={form.programme_url} 
                    onChange={e => setForm({...form, programme_url: e.target.value})} 
                    className="rounded-xl mt-2 border-blue-200"
                  />
                  <p className="text-xs text-blue-600 mt-1">Link to programme/bulletin (PDF, Google Doc, etc.)</p>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Description</Label>
                  <Textarea 
                    placeholder="Brief description of this webcast..." 
                    value={form.description} 
                    onChange={e => setForm({...form, description: e.target.value})} 
                    className="rounded-xl mt-1"
                    rows={3}
                  />
                </div>
                <Button onClick={save} className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700">
                  {editingItem ? "Update Webcast" : "Add Webcast"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <Button 
            key={cat.id} 
            variant={filterCategory === cat.id ? "default" : "outline"} 
            className={`rounded-full whitespace-nowrap shadow-md ${filterCategory === cat.id ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0" : "border-purple-200 text-purple-700 hover:bg-purple-50"}`} 
            onClick={() => setFilterCategory(cat.id)}
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.length === 0 ? (
          <Card className="col-span-full border-0 shadow-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                <Video className="w-10 h-10 text-indigo-500" />
              </div>
              <p className="text-gray-500 font-medium">No webcasts in this category</p>
              <p className="text-sm text-gray-400 mt-1">Click "Add Webcast" to create content</p>
            </CardContent>
          </Card>
        ) : (
          filteredItems.map(item => (
            <Card key={item.id} className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-gray-800">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.preacher}</p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 capitalize">
                    {categories.find(c => c.id === item.category)?.name || item.category}
                  </span>
                </div>
                {item.date && (
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" /> {new Date(item.date).toLocaleDateString()}
                  </p>
                )}
                {item.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                )}
                <div className="flex flex-wrap gap-2 mb-3">
                  {item.video_url && (
                    <a href={item.video_url} target="_blank" rel="noopener noreferrer" 
                       className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-medium hover:bg-red-100 transition-colors">
                      <Play className="w-3 h-3" /> Watch Video
                    </a>
                  )}
                  {item.programme_url && (
                    <a href={item.programme_url} target="_blank" rel="noopener noreferrer" 
                       className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors">
                      <FileText className="w-3 h-3" /> View Programme
                    </a>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => handleEdit(item)}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const AdminHymnsTab = () => {
  const [hymns, setHymns] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ number: "", title_english: "", lyrics_english: "", title_yoruba: "", lyrics_yoruba: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingHymn, setViewingHymn] = useState(null);
  const [activeService, setActiveService] = useState("sunday");
  const [activeTab, setActiveTab] = useState("selection"); // "selection" or "management"
  const [selections, setSelections] = useState({
    sunday: { hymn_ids: [], language: "english" },
    wednesday: { hymn_ids: [], language: "english" },
    friday: { hymn_ids: [], language: "english" },
    friday_prophets: { hymn_ids: [], language: "english" },
    first_thursday: { hymn_ids: [], language: "english" }
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cleaningUp, setCleaningUp] = useState(false);

  const serviceConfig = {
    sunday: { name: "Sunday Service", maxHymns: 7, color: "from-purple-500 to-pink-500" },
    wednesday: { name: "Wednesday Service", maxHymns: 5, color: "from-blue-500 to-cyan-500" },
    friday: { name: "Friday Service", maxHymns: 5, color: "from-green-500 to-teal-500" },
    friday_prophets: { name: "Prophets Service", maxHymns: 5, color: "from-indigo-500 to-purple-600" },
    first_thursday: { name: "First Thursday", maxHymns: 5, color: "from-orange-500 to-amber-500" }
  };

  // Cleanup orphan hymn IDs
  const cleanupOrphanIds = async () => {
    setCleaningUp(true);
    try {
      const res = await axios.post(`${API}/admin/hymns/cleanup-orphans`);
      if (res.data.details?.length > 0) {
        toast.success(`Cleaned ${res.data.details.length} service(s). Removed orphan hymn references.`);
        // Refresh selections
        const selectionsRes = await axios.get(`${API}/hymns/selections`);
        const newSelections = {
          sunday: { hymn_ids: [], language: "english" },
          wednesday: { hymn_ids: [], language: "english" },
          friday: { hymn_ids: [], language: "english" },
          friday_prophets: { hymn_ids: [], language: "english" },
          first_thursday: { hymn_ids: [], language: "english" }
        };
        selectionsRes.data.forEach(sel => {
          if (newSelections[sel.service_type]) {
            newSelections[sel.service_type] = {
              hymn_ids: sel.hymn_ids || [],
              language: sel.language || "english"
            };
          }
        });
        setSelections(newSelections);
      } else {
        toast.info("No orphan hymn IDs found. All selections are valid.");
      }
    } catch (err) {
      toast.error("Failed to cleanup orphan IDs");
    }
    setCleaningUp(false);
  };

  // Function to refresh hymn list
  const refreshHymns = async () => {
    try {
      const res = await axios.get(`${API}/hymns?search=${search}`);
      setHymns(res.data);
    } catch (err) {
      console.error("Failed to refresh hymns");
    }
  };

  // Load hymns
  useEffect(() => { 
    axios.get(`${API}/hymns?search=${search}&limit=1000`).then(r => setHymns(r.data)).catch(() => {}); 
  }, [search]);

  // State for selected hymns details (to show hymns that might not be in the main list)
  const [selectedHymnsDetails, setSelectedHymnsDetails] = useState({
    sunday: [], wednesday: [], friday: [], friday_prophets: [], first_thursday: []
  });

  // Load existing selections - only run once on mount
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/hymns/selections`).then(async r => {
      const newSelections = {
        sunday: { hymn_ids: [], language: "english" },
        wednesday: { hymn_ids: [], language: "english" },
        friday: { hymn_ids: [], language: "english" },
        friday_prophets: { hymn_ids: [], language: "english" },
        first_thursday: { hymn_ids: [], language: "english" }
      };
      r.data.forEach(sel => {
        if (newSelections[sel.service_type]) {
          newSelections[sel.service_type] = {
            hymn_ids: sel.hymn_ids || [],
            language: sel.language || "english"
          };
        }
      });
      setSelections(newSelections);
      
      // Fetch hymn details for each service's selected hymns
      const newDetails = { sunday: [], wednesday: [], friday: [], friday_prophets: [], first_thursday: [] };
      for (const [service, sel] of Object.entries(newSelections)) {
        if (sel.hymn_ids.length > 0) {
          try {
            // Fetch hymns by their IDs
            const hymnsRes = await axios.get(`${API}/hymns?limit=1000`);
            const allHymns = hymnsRes.data;
            const hymnMap = Object.fromEntries(allHymns.map(h => [h.id, h]));
            newDetails[service] = sel.hymn_ids.map(id => hymnMap[id]).filter(Boolean);
          } catch (e) {
            console.error(`Failed to load hymn details for ${service}`);
          }
        }
      }
      setSelectedHymnsDetails(newDetails);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  const save = async () => {
    if (!form.number || !form.title_english) {
      toast.error("Please enter hymn number and English title");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/hymns/${editingId}`, { ...form, number: parseInt(form.number) });
        toast.success("Hymn updated!");
      } else {
        await axios.post(`${API}/hymns`, { ...form, number: parseInt(form.number) });
        toast.success("Hymn added! It's now available in the hymn library.");
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ number: "", title_english: "", lyrics_english: "", title_yoruba: "", lyrics_yoruba: "" });
      // Refresh hymn list to show newly added hymn
      refreshHymns();
    } catch (err) {
      toast.error(editingId ? "Failed to update hymn" : "Failed to add hymn");
    }
  };

  const editHymn = (hymn) => {
    setForm({
      number: hymn.number.toString(),
      title_english: hymn.title_english || "",
      lyrics_english: hymn.lyrics_english || "",
      title_yoruba: hymn.title_yoruba || "",
      lyrics_yoruba: hymn.lyrics_yoruba || ""
    });
    setEditingId(hymn.id);
    setDialogOpen(true);
  };

  const deleteHymn = async (id) => {
    if (!window.confirm("Are you sure you want to delete this hymn?")) return;
    try {
      await axios.delete(`${API}/hymns/${id}`);
      toast.success("Hymn deleted");
      setHymns(hymns.filter(h => h.id !== id));
    } catch (err) {
      toast.error("Failed to delete hymn");
    }
  };

  const openAddDialog = () => {
    setEditingId(null);
    setForm({ number: "", title_english: "", lyrics_english: "", title_yoruba: "", lyrics_yoruba: "" });
    setDialogOpen(true);
  };

  const toggleHymnSelection = (hymnId) => {
    const current = selections[activeService].hymn_ids;
    const maxHymns = serviceConfig[activeService].maxHymns;
    const hymn = hymns.find(h => h.id === hymnId);
    
    if (current.includes(hymnId)) {
      // Remove from selections
      setSelections(prev => ({
        ...prev,
        [activeService]: {
          ...prev[activeService],
          hymn_ids: prev[activeService].hymn_ids.filter(id => id !== hymnId)
        }
      }));
      // Also remove from selected hymn details
      setSelectedHymnsDetails(prev => ({
        ...prev,
        [activeService]: prev[activeService].filter(h => h.id !== hymnId)
      }));
    } else if (current.length < maxHymns) {
      // Add to selections
      setSelections(prev => ({
        ...prev,
        [activeService]: {
          ...prev[activeService],
          hymn_ids: [...prev[activeService].hymn_ids, hymnId]
        }
      }));
      // Also add to selected hymn details
      if (hymn) {
        setSelectedHymnsDetails(prev => ({
          ...prev,
          [activeService]: [...prev[activeService], hymn]
        }));
      }
    } else {
      toast.error(`Maximum ${maxHymns} hymns allowed for ${serviceConfig[activeService].name}`);
    }
  };

  const setLanguage = (lang) => {
    setSelections(prev => ({
      ...prev,
      [activeService]: {
        ...prev[activeService],
        language: lang
      }
    }));
  };

  const saveSelection = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/hymns/selections`, {
        service_type: activeService,
        hymn_ids: selections[activeService].hymn_ids,
        language: selections[activeService].language
      });
      toast.success(`Hymns saved for ${serviceConfig[activeService].name}!`);
    } catch (err) {
      toast.error("Failed to save selection");
    }
    setSaving(false);
  };

  // Get selected hymns - prioritize cached details, fallback to main hymns list
  const getSelectedHymns = () => {
    if (!selections[activeService]) return [];
    
    // First try to use cached selected hymns details
    if (selectedHymnsDetails[activeService]?.length > 0) {
      return selectedHymnsDetails[activeService];
    }
    
    // Fallback to finding in main hymns array
    if (!hymns.length) return [];
    return selections[activeService].hymn_ids
      .map(id => hymns.find(h => h.id === id))
      .filter(Boolean);
  };

  // Count valid hymns for any service - use cached details if available
  const getValidHymnCount = (serviceKey) => {
    if (!selections[serviceKey]) return 0;
    
    // First try cached details
    if (selectedHymnsDetails[serviceKey]?.length > 0) {
      return selectedHymnsDetails[serviceKey].length;
    }
    
    // Fallback to main hymns array
    if (!hymns.length) return selections[serviceKey].hymn_ids.length; // Show stored count if hymns not loaded
    return selections[serviceKey].hymn_ids
      .filter(id => hymns.some(h => h.id === id))
      .length;
  };

  const selectedHymnsList = getSelectedHymns();

  return (
    <div className="space-y-6">
      {/* Tab Selector */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={activeTab === "selection" ? "default" : "outline"}
          className={`rounded-full ${activeTab === "selection" ? "bg-purple-600" : ""}`}
          onClick={() => setActiveTab("selection")}
        >
          <Music className="w-4 h-4 mr-2" />
          Service Hymn Selection
        </Button>
        <Button
          variant={activeTab === "management" ? "default" : "outline"}
          className={`rounded-full ${activeTab === "management" ? "bg-blue-600" : ""}`}
          onClick={() => setActiveTab("management")}
        >
          <Edit className="w-4 h-4 mr-2" />
          Manage Hymns
        </Button>
      </div>

      {activeTab === "selection" && (
        <>
      {/* Service Type Selector */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className={`h-1 bg-gradient-to-r ${serviceConfig[activeService].color}`} />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            Hymn Selection for Services
          </CardTitle>
          <CardDescription>
            Select hymns for each service type. Hymns will only appear on service days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap mb-4">
            {Object.entries(serviceConfig).filter(([key]) => key !== 'friday_prophets').map(([key, config]) => (
              <Button
                key={key}
                variant={activeService === key ? "default" : "outline"}
                className={`rounded-full ${activeService === key ? `bg-gradient-to-r ${config.color}` : ""}`}
                onClick={() => setActiveService(key)}
              >
                {config.name} ({getValidHymnCount(key)}/{config.maxHymns})
                {key === 'friday' && ` + Prophets (${getValidHymnCount('friday_prophets')}/${serviceConfig.friday_prophets.maxHymns})`}
              </Button>
            ))}
          </div>
          
          {/* Fix Count Mismatch Button */}
          <div className="mb-6 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-800 font-medium">Hymn count not matching?</p>
              <p className="text-xs text-amber-600">Click "Fix" to remove references to deleted hymns</p>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={cleanupOrphanIds}
              disabled={cleaningUp}
            >
              {cleaningUp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Fix Count
            </Button>
          </div>

          {/* Language Selection */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
            <Label className="font-medium">Language:</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={selections[activeService].language === "english" ? "default" : "outline"}
                className={`rounded-full ${selections[activeService].language === "english" ? "bg-purple-500" : ""}`}
                onClick={() => setLanguage("english")}
              >
                English
              </Button>
              <Button
                size="sm"
                variant={selections[activeService].language === "yoruba" ? "default" : "outline"}
                className={`rounded-full ${selections[activeService].language === "yoruba" ? "bg-green-500" : ""}`}
                onClick={() => setLanguage("yoruba")}
              >
                Yoruba
              </Button>
            </div>
          </div>

          {/* Selected Hymns - Main Service */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 mb-3">
              Selected Hymns for {serviceConfig[activeService].name} ({selectedHymnsList.length}/{serviceConfig[activeService].maxHymns})
            </h4>
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                <span className="ml-2 text-gray-500">Loading selections...</span>
              </div>
            ) : selectedHymnsList.length === 0 ? (
              <p className="text-gray-400 text-sm p-4 bg-gray-50 rounded-xl text-center">
                No hymns selected. Click on hymns below to add them.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedHymnsList.map((hymn, i) => (
                  <div key={hymn.id} className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">#{hymn.number} - {hymn.title_english}</p>
                      {hymn.title_yoruba && <p className="text-xs text-gray-500">{hymn.title_yoruba}</p>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => toggleHymnSelection(hymn.id)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button 
              className={`w-full mt-4 rounded-xl bg-gradient-to-r ${serviceConfig[activeService].color}`}
              onClick={saveSelection}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save {serviceConfig[activeService].name} Hymns
            </Button>
          </div>

          {/* Prophets Service Hymns - Only shown when Friday is selected */}
          {activeService === 'friday' && (
            <div className="mb-6 mt-8 pt-6 border-t-2 border-indigo-200">
              <div className={`h-1 bg-gradient-to-r ${serviceConfig.friday_prophets.color} rounded-full mb-4`} />
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"></span>
                Selected Hymns for {serviceConfig.friday_prophets.name} ({(() => {
                  const prophetsHymnIds = selections.friday_prophets?.hymn_ids || [];
                  const prophetsHymnsList = prophetsHymnIds.map(id => hymns.find(h => h.id === id) || selectedHymnsDetails.friday_prophets?.find(h => h.id === id)).filter(Boolean);
                  return prophetsHymnsList.length;
                })()}/{serviceConfig.friday_prophets.maxHymns})
              </h4>
              {(() => {
                const prophetsHymnIds = selections.friday_prophets?.hymn_ids || [];
                const prophetsHymnsList = prophetsHymnIds.map(id => hymns.find(h => h.id === id) || selectedHymnsDetails.friday_prophets?.find(h => h.id === id)).filter(Boolean);
                
                return prophetsHymnsList.length === 0 ? (
                  <p className="text-gray-400 text-sm p-4 bg-indigo-50 rounded-xl text-center">
                    No hymns selected for Prophets Service. Select hymns from the library below.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {prophetsHymnsList.map((hymn, i) => (
                      <div key={hymn.id} className="flex items-center gap-3 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">#{hymn.number} - {hymn.title_english}</p>
                          {hymn.title_yoruba && <p className="text-xs text-gray-500">{hymn.title_yoruba}</p>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => {
                          const current = selections.friday_prophets.hymn_ids;
                          setSelections(prev => ({
                            ...prev,
                            friday_prophets: {
                              ...prev.friday_prophets,
                              hymn_ids: current.filter(id => id !== hymn.id)
                            }
                          }));
                          setSelectedHymnsDetails(prev => ({
                            ...prev,
                            friday_prophets: (prev.friday_prophets || []).filter(h => h.id !== hymn.id)
                          }));
                        }}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <Button 
                className={`w-full mt-4 rounded-xl bg-gradient-to-r ${serviceConfig.friday_prophets.color}`}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await axios.post(`${API}/hymns/selections/friday_prophets`, {
                      hymn_ids: selections.friday_prophets.hymn_ids,
                      language: selections.friday_prophets.language
                    });
                    toast.success("Prophets Service hymns saved!");
                  } catch (err) {
                    toast.error("Failed to save Prophets Service hymns");
                  }
                  setSaving(false);
                }}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save {serviceConfig.friday_prophets.name} Hymns
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hymn Library for Selection */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hymn Library ({hymns.length})</CardTitle>
          {activeService === 'friday' && (
            <div className="text-sm text-gray-500">
              Click hymn to add to Friday Service. Use "P" button for Prophets Service.
            </div>
          )}
        </CardHeader>
        <CardContent>
          <Input 
            placeholder="Search hymns by number or title..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="rounded-xl mb-4"
          />
          <ScrollArea className="h-[400px]">
            <div className="grid md:grid-cols-2 gap-2 pr-4">
              {hymns.map(h => {
                const isSelectedFriday = selections[activeService].hymn_ids.includes(h.id);
                const isSelectedProphets = activeService === 'friday' && selections.friday_prophets?.hymn_ids?.includes(h.id);
                const isSelected = isSelectedFriday || isSelectedProphets;
                
                return (
                  <div 
                    key={h.id} 
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all border-2 ${
                      isSelectedFriday 
                        ? "border-green-500 bg-green-50" 
                        : isSelectedProphets
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-100 hover:border-purple-200 bg-white"
                    }`}
                  >
                    <div 
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 cursor-pointer ${
                        isSelectedFriday ? "bg-green-500" : isSelectedProphets ? "bg-indigo-500" : "bg-gray-400"
                      }`}
                      onClick={() => toggleHymnSelection(h.id)}
                    >
                      {h.number}
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleHymnSelection(h.id)}>
                      <p className="font-medium text-gray-800 text-sm truncate">{h.title_english}</p>
                      {h.title_yoruba && <p className="text-xs text-gray-500 truncate">{h.title_yoruba}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {activeService === 'friday' && (
                        <Button
                          size="sm"
                          variant={isSelectedProphets ? "default" : "outline"}
                          className={`rounded-full w-8 h-8 p-0 text-xs font-bold ${isSelectedProphets ? "bg-indigo-500" : "hover:bg-indigo-100 hover:border-indigo-400"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = selections.friday_prophets?.hymn_ids || [];
                            const hymn = hymns.find(hh => hh.id === h.id);
                            const maxHymns = serviceConfig.friday_prophets.maxHymns;
                            
                            if (current.includes(h.id)) {
                              // Remove from prophets
                              setSelections(prev => ({
                                ...prev,
                                friday_prophets: {
                                  ...prev.friday_prophets,
                                  hymn_ids: current.filter(id => id !== h.id)
                                }
                              }));
                              setSelectedHymnsDetails(prev => ({
                                ...prev,
                                friday_prophets: (prev.friday_prophets || []).filter(hh => hh.id !== h.id)
                              }));
                            } else {
                              if (current.length >= maxHymns) {
                                toast.error(`Maximum ${maxHymns} hymns allowed for Prophets Service`);
                                return;
                              }
                              setSelections(prev => ({
                                ...prev,
                                friday_prophets: {
                                  ...prev.friday_prophets,
                                  hymn_ids: [...current, h.id]
                                }
                              }));
                              if (hymn) {
                                setSelectedHymnsDetails(prev => ({
                                  ...prev,
                                  friday_prophets: [...(prev.friday_prophets || []), hymn]
                                }));
                              }
                            }
                          }}
                          title="Add to Prophets Service"
                        >
                          P
                        </Button>
                      )}
                      {isSelectedFriday && <CheckCircle className="w-5 h-5 text-green-500" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
        </>
      )}

      {activeTab === "management" && (
        <>
          {/* Hymn Management Section */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Hymn Management
                </CardTitle>
                <CardDescription>
                  Add, edit, or delete hymns. Enter full lyrics in both English and Yoruba.
                </CardDescription>
              </div>
              <Button className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Hymn
              </Button>
            </CardHeader>
            <CardContent>
              <Input 
                placeholder="Search hymns by number or title..." 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
                className="rounded-xl mb-4"
              />
              <div className="space-y-3">
                {hymns.map(h => (
                  <div 
                    key={h.id} 
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white hover:shadow-md transition-all"
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {h.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800">{h.title_english || "No English Title"}</p>
                      <p className="text-sm text-gray-500">{h.title_yoruba || "No Yoruba Title"}</p>
                      <div className="flex gap-3 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${h.lyrics_english ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {h.lyrics_english ? "✓ English Lyrics" : "✗ No English Lyrics"}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${h.lyrics_yoruba ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {h.lyrics_yoruba ? "✓ Yoruba Lyrics" : "✗ No Yoruba Lyrics"}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewingHymn(h)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => editHymn(h)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deleteHymn(h.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Add/Edit Hymn Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Hymn" : "Add New Hymn"}</DialogTitle>
            <DialogDescription>
              Enter the hymn details including full lyrics in both languages.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Hymn Number *</Label>
                <Input 
                  type="number" 
                  placeholder="e.g., 205" 
                  value={form.number} 
                  onChange={e => setForm({...form, number: e.target.value})} 
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-blue-600 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-xs">EN</span>
                English Version
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium mb-1 block">English Title *</Label>
                  <Input 
                    placeholder="e.g., God the King of heavenly light" 
                    value={form.title_english} 
                    onChange={e => setForm({...form, title_english: e.target.value})} 
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">English Lyrics</Label>
                  <Textarea 
                    placeholder="Enter full lyrics with verse numbers (e.g., 1: First verse...)" 
                    rows={8} 
                    value={form.lyrics_english} 
                    onChange={e => setForm({...form, lyrics_english: e.target.value})}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-green-100 flex items-center justify-center text-xs">YO</span>
                Yoruba Version
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium mb-1 block">Yoruba Title</Label>
                  <Input 
                    placeholder="e.g., Ọlọrun Ọba Imọlẹ" 
                    value={form.title_yoruba} 
                    onChange={e => setForm({...form, title_yoruba: e.target.value})} 
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1 block">Yoruba Lyrics</Label>
                  <Textarea 
                    placeholder="Enter full Yoruba lyrics with verse numbers" 
                    rows={8} 
                    value={form.lyrics_yoruba} 
                    onChange={e => setForm({...form, lyrics_yoruba: e.target.value})}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4 border-t">
              <Button onClick={save} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {editingId ? "Update Hymn" : "Add Hymn"}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Hymn Dialog */}
      <Dialog open={!!viewingHymn} onOpenChange={(open) => !open && setViewingHymn(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          {viewingHymn && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                    {viewingHymn.number}
                  </span>
                  <div>
                    <div>{viewingHymn.title_english}</div>
                    {viewingHymn.title_yoruba && <div className="text-sm font-normal text-gray-500">{viewingHymn.title_yoruba}</div>}
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {viewingHymn.lyrics_english && (
                  <div>
                    <h4 className="font-semibold text-blue-600 mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center text-xs">EN</span>
                      English Lyrics
                    </h4>
                    <div className="bg-blue-50 rounded-xl p-4 whitespace-pre-wrap font-serif text-gray-700">
                      {viewingHymn.lyrics_english}
                    </div>
                  </div>
                )}
                {viewingHymn.lyrics_yoruba && (
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded bg-green-100 flex items-center justify-center text-xs">YO</span>
                      Yoruba Lyrics
                    </h4>
                    <div className="bg-green-50 rounded-xl p-4 whitespace-pre-wrap font-serif text-gray-700">
                      {viewingHymn.lyrics_yoruba}
                    </div>
                  </div>
                )}
                {!viewingHymn.lyrics_english && !viewingHymn.lyrics_yoruba && (
                  <p className="text-gray-400 text-center py-8">No lyrics available for this hymn.</p>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                <Button className="flex-1" onClick={() => { setViewingHymn(null); editHymn(viewingHymn); }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit This Hymn
                </Button>
                <Button variant="outline" onClick={() => setViewingHymn(null)}>Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminBibleLessonsTab = () => {
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState({ date: "", day: "Sunday", service_type: "Sunday Morning", time: "10:00 AM", first_lesson: "", second_lesson: "", lesson: "", month: "", year: 2026, dateNum: 1 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { 
    axios.get(`${API}/bible-lessons`).then(r => setLessons(r.data)).catch(() => {}); 
  }, []);

  const filteredLessons = lessons.filter(l => 
    (l.lesson || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.day || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.month || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.service_type || "").toLowerCase().includes(search.toLowerCase())
  );

  const openAddDialog = () => {
    setEditingId(null);
    setForm({ date: "", day: "Sunday", service_type: "Sunday Morning", time: "10:00 AM", first_lesson: "", second_lesson: "", lesson: "", month: "", year: 2026, dateNum: 1 });
    setDialogOpen(true);
  };

  const openEditDialog = (lesson) => {
    setEditingId(lesson.id);
    // Parse the date to get dateNum
    let dateNum = 1;
    if (lesson.date) {
      const parts = lesson.date.split('-');
      if (parts.length === 3) {
        dateNum = parseInt(parts[2]) || 1;
      }
    }
    setForm({
      date: lesson.date || "",
      day: lesson.day || "Sunday",
      service_type: lesson.service_type || "Sunday Morning",
      time: lesson.time || "",
      first_lesson: lesson.first_lesson || lesson.lesson || "",
      second_lesson: lesson.second_lesson || "",
      lesson: lesson.lesson || lesson.first_lesson || "",
      month: lesson.month || "",
      year: lesson.year || 2026,
      dateNum: dateNum
    });
    setDialogOpen(true);
  };

  const saveLessons = async () => {
    try {
      // Build the full date from month, dateNum, and year
      const monthIndex = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].indexOf(form.month);
      const fullDate = form.month && form.dateNum && form.year 
        ? `${form.year}-${String(monthIndex + 1).padStart(2, '0')}-${String(form.dateNum).padStart(2, '0')}`
        : form.date;
      
      const payload = {
        date: fullDate,
        day: form.day,
        service_type: form.service_type,
        time: form.time,
        month: form.month,
        year: form.year,
        // Support both new (first_lesson/second_lesson) and legacy (lesson) fields
        first_lesson: form.first_lesson || form.lesson || "",
        second_lesson: form.second_lesson || "",
        lesson: form.first_lesson || form.lesson || "" // Keep legacy field for backward compatibility
      };
      
      if (editingId) {
        // Update existing lesson
        await axios.put(`${API}/bible-lessons/${editingId}`, payload);
        toast.success("Bible lesson updated!");
      } else {
        // Add new lesson
        await axios.post(`${API}/bible-lessons`, payload);
        toast.success("Bible lesson added!");
      }
      
      setDialogOpen(false);
      setEditingId(null);
      setForm({ date: "", day: "Sunday", service_type: "Sunday Morning", time: "10:00 AM", first_lesson: "", second_lesson: "", lesson: "", month: "", year: 2026, dateNum: 1 });
      axios.get(`${API}/bible-lessons`).then(r => setLessons(r.data));
    } catch (err) {
      toast.error(editingId ? "Failed to update lesson" : "Failed to add lesson");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    await axios.delete(`${API}/bible-lessons/${id}`);
    setLessons(lessons.filter(l => l.id !== id));
    toast.success("Deleted");
  };

  const exportLessons = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/bible-lessons/export-json?year=2026`);
      const data = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bible_lessons_${data.year}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.total} lessons for year ${data.year}`);
    } catch (err) {
      toast.error("Failed to export lessons");
    }
    setExporting(false);
  };

  const clearAndResync = async () => {
    if (!window.confirm("⚠️ This will DELETE all 2026 Bible lessons and re-fetch them from CCCEC. This may take a few minutes. Continue?")) return;
    
    setSyncing(true);
    try {
      // Step 1: Clear existing 2026 lessons
      await axios.delete(`${API}/admin/bible-lessons/clear/2026`);
      toast.info("Cleared existing 2026 lessons. Now re-syncing...");
      
      // Step 2: Trigger fresh scrape
      await axios.post(`${API}/admin/bible-lessons/scrape`);
      toast.success("Bible lessons sync started! Refresh page in 2-3 minutes to see updated lessons.");
      
      // Refresh after delay
      setTimeout(() => {
        axios.get(`${API}/bible-lessons`).then(r => setLessons(r.data));
        toast.success("Lessons refreshed!");
      }, 120000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear and resync");
    }
    setSyncing(false);
  };

  const seedJanuaryData = async () => {
    if (!window.confirm("This will add/replace January 2026 Bible lessons with the correct data. Continue?")) return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/bible-lessons/seed-january-2026`);
      toast.success(`${response.data.message} - Added ${response.data.added} lessons`);
      const lessonsRes = await axios.get(`${API}/bible-lessons`);
      setLessons(lessonsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed January data");
    }
    setSyncing(false);
  };

  const importMayDecData = async () => {
    if (!window.confirm("This will import Bible lessons for May-December 2026 from the uploaded Excel data. Existing lessons for these months will be skipped. Continue?")) return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/bible-lessons/import-2026`);
      const r = response.data;
      toast.success(`${r.message} (Imported: ${r.imported}, Updated: ${r.updated_dates || 0}, Skipped: ${r.skipped})`);
      const lessonsRes = await axios.get(`${API}/bible-lessons`);
      setLessons(lessonsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import May-Dec 2026 data");
    }
    setSyncing(false);
  };

  const syncBibleLessons = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/bible-lessons/scrape`);
      toast.success("Bible lessons sync started! Refresh in 1-2 minutes to see updated lessons.");
      // Refresh after delay
      setTimeout(() => {
        axios.get(`${API}/bible-lessons`).then(r => setLessons(r.data));
      }, 60000);
    } catch (err) {
      toast.error("Failed to start sync");
    }
    setSyncing(false);
  };

  const clearUnavailableMonths = async () => {
    if (!window.confirm("⚠️ This will DELETE Bible lessons for May-December 2026 (months not yet available on CCCEC website).\n\nThe system will automatically re-scrape these months when they become available.\n\nContinue?")) return;
    
    setSyncing(true);
    try {
      const response = await axios.delete(`${API}/admin/bible-lessons/clear-months?year=2026&months=May,June,July,August,September,October,November,December`);
      toast.success(response.data.message);
      // Refresh lessons list
      const lessonsRes = await axios.get(`${API}/bible-lessons`);
      setLessons(lessonsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear unavailable months");
    }
    setSyncing(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      // Parse CSV/Excel file - for now just show message
      toast.info("Bulk upload feature: Please contact admin to upload yearly lessons");
    } catch (err) {
      toast.error("Failed to upload");
    }
    setUploading(false);
  };

  const dayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const serviceTypes = ["Sunday Morning", "Sunday Evening", "Seekers Service", "Wednesday", "Wednesday Morning", "Friday", "Friday Prophets", "New Moon", "Palm Sunday", "Thursday", "Washing of Feet", "Holy Communion", "Lord's Supper", "Good Friday Morning", "Good Friday", "Good Friday Afternoon"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          Bible Lessons ({lessons.length})
        </CardTitle>
        <div className="flex gap-2 items-center flex-wrap">
          <Input 
            placeholder="Search lessons..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="w-48 rounded-full"
          />
          <Button 
            variant="outline" 
            className="rounded-full border-amber-500 text-amber-600 font-semibold" 
            onClick={seedJanuaryData}
            disabled={syncing}
            title="Add correct January 2026 Bible lessons"
            data-testid="seed-january-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Fix January 2026
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full border-teal-500 text-teal-600 font-semibold" 
            onClick={importMayDecData}
            disabled={syncing}
            title="Import May-December 2026 Bible lessons from Excel data"
            data-testid="import-may-dec-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Import May-Dec 2026
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full border-green-500 text-green-600" 
            onClick={syncBibleLessons}
            disabled={syncing}
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Sync from CCCEC
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full border-red-500 text-red-600" 
            onClick={clearUnavailableMonths}
            disabled={syncing}
            title="Remove pre-populated May-Dec 2026 lessons (not yet available on source)"
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Clear May-Dec 2026
          </Button>
          <Button 
            variant="outline" 
            className="rounded-full border-blue-500 text-blue-600" 
            onClick={exportLessons}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Data
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx" onChange={handleBulkUpload} className="hidden" />
            <Button variant="outline" className="rounded-full border-purple-500 text-purple-600" disabled={uploading} asChild>
              <span><Upload className="w-4 h-4 mr-2" /> Upload Yearly</span>
            </Button>
          </label>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700" onClick={openAddDialog}><Plus className="w-4 h-4 mr-2" /> Add Lesson</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Bible Lesson" : "Add Bible Lesson"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Day of Service *</Label>
                    <Select value={form.day} onValueChange={v => setForm({...form, day: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {dayOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Service Type *</Label>
                    <Select value={form.service_type} onValueChange={v => setForm({...form, service_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Month</Label>
                    <Select value={form.month} onValueChange={v => setForm({...form, month: v})}>
                      <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
                      <SelectContent>
                        {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Date</Label>
                    <Select value={form.dateNum?.toString() || ""} onValueChange={v => setForm({...form, dateNum: parseInt(v)})}>
                      <SelectTrigger><SelectValue placeholder="Select date" /></SelectTrigger>
                      <SelectContent>
                        {[...Array(31)].map((_, i) => <SelectItem key={i+1} value={(i+1).toString()}>{i+1}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Year</Label>
                    <Select value={form.year?.toString() || "2026"} onValueChange={v => setForm({...form, year: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Time</Label>
                    <Input placeholder="e.g., 10:00 AM" value={form.time} onChange={e => setForm({...form, time: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-500 mb-1 block">1st Bible Lesson / Scripture *</Label>
                  <Input 
                    placeholder="e.g., Zechariah 9:9-12" 
                    value={form.first_lesson} 
                    onChange={e => setForm({...form, first_lesson: e.target.value, lesson: e.target.value})} 
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-500 mb-1 block">2nd Bible Lesson / Scripture (Optional)</Label>
                  <Input 
                    placeholder="e.g., Luke 19:28-38" 
                    value={form.second_lesson} 
                    onChange={e => setForm({...form, second_lesson: e.target.value})} 
                  />
                </div>
                <Button onClick={saveLessons} className="w-full rounded-full bg-purple-600 hover:bg-purple-700" disabled={!form.first_lesson && !form.lesson}>
                  {editingId ? "Update Lesson" : "Save Lesson"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          {filteredLessons.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No Bible lessons added yet. Add lessons manually or upload a yearly file.</p>
          ) : (
            <div className="space-y-2">
              {filteredLessons.map(l => (
                <div key={l.id} className="p-3 border rounded-lg flex justify-between items-start hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-purple-100 text-purple-700">{l.day}</Badge>
                      <Badge variant="outline">{l.service_type}</Badge>
                      {l.month && <Badge variant="outline" className="border-blue-500 text-blue-600">{l.month}</Badge>}
                      <span className="text-xs text-gray-400">{l.time}</span>
                      {l.date && <span className="text-xs text-gray-400">• {l.date}</span>}
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-800 font-medium">
                        📖 <span className="text-purple-700">1st:</span> {l.first_lesson || l.lesson}
                      </p>
                      {l.second_lesson && (
                        <p className="text-sm text-gray-800 font-medium">
                          📖 <span className="text-purple-700">2nd:</span> {l.second_lesson}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(l)} title="Edit lesson">
                      <Edit className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(l.id)} title="Delete lesson">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

const AdminProphetLessonsTab = () => {
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState({ date: "", bible_lesson: "", prophetic_focus: "", year: 2026 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState(2026);

  const fetchLessons = () => {
    axios.get(`${API}/prophet-lessons?year=${selectedYear}`).then(r => setLessons(r.data)).catch(() => {});
  };

  useEffect(() => { 
    fetchLessons();
  }, [selectedYear]);

  const filteredLessons = lessons.filter(l => 
    (l.bible_lesson || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.prophetic_focus || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.date || "").includes(search)
  );

  const saveLesson = async () => {
    try {
      if (editingId) {
        // For edit, we need to delete and recreate since we don't have a PUT endpoint
        await axios.delete(`${API}/prophet-lessons/${editingId}`);
      }
      await axios.post(`${API}/prophet-lessons`, { ...form, year: selectedYear });
      toast.success(editingId ? "Prophet lesson updated!" : "Prophet lesson added!");
      setDialogOpen(false);
      setForm({ date: "", bible_lesson: "", prophetic_focus: "", year: selectedYear });
      setEditingId(null);
      fetchLessons();
    } catch (err) {
      toast.error("Failed to save lesson");
    }
  };

  const editLesson = (lesson) => {
    setForm({
      date: lesson.date,
      bible_lesson: lesson.bible_lesson,
      prophetic_focus: lesson.prophetic_focus || "",
      year: lesson.year
    });
    setEditingId(lesson.id);
    setDialogOpen(true);
  };

  const deleteLesson = async (id) => {
    if (!window.confirm("Delete this prophet lesson?")) return;
    try {
      await axios.delete(`${API}/prophet-lessons/${id}`);
      setLessons(lessons.filter(l => l.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const clearYear = async () => {
    if (!window.confirm(`⚠️ This will DELETE all prophet lessons for ${selectedYear}. Continue?`)) return;
    try {
      await axios.delete(`${API}/prophet-lessons/clear/${selectedYear}`);
      toast.success(`Cleared all ${selectedYear} prophet lessons`);
      fetchLessons();
    } catch (err) {
      toast.error("Failed to clear lessons");
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const lessonsToUpload = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Handle CSV with quoted values
        const values = [];
        let current = '';
        let inQuotes = false;
        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        
        const dateIdx = headers.findIndex(h => h.includes('week') || h.includes('date'));
        const lessonIdx = headers.findIndex(h => h.includes('bible') || h.includes('lesson'));
        const focusIdx = headers.findIndex(h => h.includes('focus') || h.includes('prophetic'));
        
        const date = values[dateIdx] || '';
        const bible_lesson = values[lessonIdx] || '';
        const prophetic_focus = values[focusIdx] || '';
        
        if (date && bible_lesson) {
          lessonsToUpload.push({
            date: date.replace(/[""]/g, ''),
            bible_lesson: bible_lesson.replace(/[""]/g, '').replace(/�/g, '-'),
            prophetic_focus: prophetic_focus.replace(/[""]/g, '').replace(/�/g, '-') || null,
            year: selectedYear
          });
        }
      }
      
      if (lessonsToUpload.length === 0) {
        toast.error("No valid lessons found in CSV. Make sure columns include: Week/Date, Bible Lesson, Prophetic Focus");
        return;
      }
      
      const response = await axios.post(`${API}/prophet-lessons/bulk-upload`, lessonsToUpload);
      toast.success(response.data.message);
      fetchLessons();
    } catch (err) {
      toast.error("Failed to upload CSV: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
    e.target.value = '';
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Week", "Bible Lesson", "Prophetic Focus"],
      ...lessons.map(l => [l.date, l.bible_lesson, l.prophetic_focus || ""])
    ].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prophet_lessons_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Prophet Service Lessons ({lessons.length})
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">Manage Bible lessons for Friday Prophets Service</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select 
            value={selectedYear} 
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="h-10 px-3 rounded-full border border-gray-200"
          >
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
          </select>
          <Input 
            placeholder="Search..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="w-40 rounded-full"
          />
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
            <Button variant="outline" className="rounded-full border-green-500 text-green-600" disabled={uploading} asChild>
              <span>{uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload CSV</span>
            </Button>
          </label>
          <Button variant="outline" className="rounded-full border-blue-500 text-blue-600" onClick={exportToCSV} disabled={lessons.length === 0}>
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button variant="outline" className="rounded-full border-red-500 text-red-600" onClick={clearYear}>
            <Trash2 className="w-4 h-4 mr-2" /> Clear {selectedYear}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ date: "", bible_lesson: "", prophetic_focus: "", year: selectedYear }); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-2" /> Add Lesson</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit" : "Add"} Prophet Lesson</DialogTitle>
                <DialogDescription>Enter the lesson details for Prophet Service (Friday 9PM)</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Date (DD/MM/YYYY) *</Label>
                  <Input 
                    value={form.date} 
                    onChange={e => setForm({...form, date: e.target.value})} 
                    placeholder="06/02/2026"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Bible Lesson *</Label>
                  <Input 
                    value={form.bible_lesson} 
                    onChange={e => setForm({...form, bible_lesson: e.target.value})} 
                    placeholder="Joel 2:23-29"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Prophetic Focus</Label>
                  <Input 
                    value={form.prophetic_focus} 
                    onChange={e => setForm({...form, prophetic_focus: e.target.value})} 
                    placeholder="Outpouring & restoration"
                    className="rounded-xl mt-1"
                  />
                </div>
                <Button onClick={saveLesson} className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700">
                  {editingId ? "Update" : "Add"} Lesson
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {filteredLessons.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No prophet lessons for {selectedYear}</p>
            <p className="text-sm text-gray-400 mt-2">Upload a CSV or add lessons manually</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Bible Lesson</TableHead>
                  <TableHead>Prophetic Focus</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLessons.map(l => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.date}</TableCell>
                    <TableCell className="text-indigo-600 font-medium">{l.bible_lesson}</TableCell>
                    <TableCell className="text-gray-600 italic">{l.prophetic_focus || "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => editLesson(l)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteLesson(l.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Weekly Bible Study Admin Tab
const AdminBibleStudyTab = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    topic: "",
    scripture: "",
    week_start_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchStudies();
  }, []);

  const fetchStudies = async () => {
    try {
      const res = await axios.get(`${API}/bible-study`);
      setStudies(res.data);
    } catch (err) {
      toast.error("Failed to load Bible Studies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.topic || !form.scripture || !form.week_start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/bible-study/${editingId}`, form);
        toast.success("Bible Study updated");
      } else {
        await axios.post(`${API}/bible-study`, form);
        toast.success("Bible Study created");
      }
      setShowDialog(false);
      setEditingId(null);
      setForm({ topic: "", scripture: "", week_start_date: "", notes: "" });
      fetchStudies();
    } catch (err) {
      toast.error("Failed to save Bible Study");
    }
  };

  const handleEdit = (study) => {
    setForm({
      topic: study.topic,
      scripture: study.scripture,
      week_start_date: study.week_start_date,
      notes: study.notes || ""
    });
    setEditingId(study.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this Bible Study?")) return;
    try {
      await axios.delete(`${API}/bible-study/${id}`);
      toast.success("Bible Study deleted");
      fetchStudies();
    } catch (err) {
      toast.error("Failed to delete Bible Study");
    }
  };

  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    return nextMonday.toISOString().split('T')[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-500" /> Weekly Bible Study
          </CardTitle>
          <CardDescription>Manage weekly Bible Study topics and scripture</CardDescription>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingId(null);
            setForm({ topic: "", scripture: "", week_start_date: "", notes: "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" /> Add Bible Study
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Bible Study" : "Add Bible Study"}</DialogTitle>
              <DialogDescription>Enter the topic and scripture for this week's Bible Study</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Week Starting (Monday) *</label>
                <Input 
                  type="date" 
                  value={form.week_start_date} 
                  onChange={e => setForm({...form, week_start_date: e.target.value})}
                  placeholder="Select week start date"
                />
                <p className="text-xs text-gray-500 mt-1">Select the Monday of the week for this study</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Topic *</label>
                <Input 
                  placeholder="e.g., Faith Through Trials" 
                  value={form.topic} 
                  onChange={e => setForm({...form, topic: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Bible Scripture *</label>
                <Input 
                  placeholder="e.g., James 1:2-8" 
                  value={form.scripture} 
                  onChange={e => setForm({...form, scripture: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Notes (Optional)</label>
                <Textarea 
                  placeholder="Additional notes or study guide..." 
                  rows={3}
                  value={form.notes} 
                  onChange={e => setForm({...form, notes: e.target.value})}
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-orange-500 hover:bg-orange-600">
                {editingId ? "Update Bible Study" : "Save Bible Study"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : studies.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No Bible Studies scheduled yet</p>
            <p className="text-gray-400 text-sm">Click "Add Bible Study" to create one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {studies.map((study) => (
              <div key={study.id} className="p-4 border rounded-xl hover:bg-orange-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-orange-500 text-white">
                        Week of {formatDate(study.week_start_date)}
                      </Badge>
                    </div>
                    <h4 className="font-bold text-gray-800 text-lg">{study.topic}</h4>
                    <div className="flex items-center gap-2 mt-2">
                      <BookOpen className="w-4 h-4 text-orange-600" />
                      <span className="text-orange-600 font-medium">{study.scripture}</span>
                    </div>
                    {study.notes && (
                      <p className="text-gray-500 text-sm mt-2 italic">{study.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(study)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(study.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminPrayersTab = () => {
  const [prayers, setPrayers] = useState([]);
  useEffect(() => { axios.get(`${API}/prayer-requests`).then(r => setPrayers(r.data)).catch(() => {}); }, []);
  const approve = async (id) => { await axios.put(`${API}/prayer-requests/${id}/status?status=approved`); toast.success("Approved"); axios.get(`${API}/prayer-requests`).then(r => setPrayers(r.data)); };
  const del = async (id) => { await axios.delete(`${API}/prayer-requests/${id}`); setPrayers(prayers.filter(p => p.id !== id)); toast.success("Deleted"); };
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader><CardTitle>Prayer Requests</CardTitle></CardHeader>
      <CardContent>
        {prayers.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No prayer requests yet</p>
        ) : prayers.map(p => (
          <div key={p.id} className="p-4 border-b last:border-0 flex justify-between items-start hover:bg-gray-50">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium">{p.name}</p>
                {p.phone && <span className="text-sm text-blue-600 flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                {p.is_private && <Badge variant="outline" className="border-purple-500 text-purple-500">Private</Badge>}
                <Badge className={p.status === "approved" ? "bg-green-500" : "bg-yellow-500"}>{p.status}</Badge>
              </div>
              <p className="text-sm text-gray-600 mt-1">{p.request}</p>
              <p className="text-xs text-gray-400 mt-1">{new Date(p.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-1 ml-4">
              {p.status !== "approved" && <Button size="sm" onClick={() => approve(p.id)}>Approve</Button>}
              <Button size="sm" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const AdminContactTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const loadMessages = () => {
    axios.get(`${API}/admin/contact-messages`)
      .then(r => setMessages(r.data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMessages(); }, []);
  
  const del = async (id) => { 
    try {
      await axios.delete(`${API}/admin/contact-messages/${id}`); 
      setMessages(messages.filter(m => m.id !== id)); 
      toast.success("Message deleted"); 
    } catch { 
      toast.error("Failed to delete"); 
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/read`);
      setMessages(messages.map(m => m.id === id ? {...m, is_read: true} : m));
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/admin/contact-messages/mark-all-read`);
      setMessages(messages.map(m => ({...m, is_read: true})));
      toast.success("All messages marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;
  
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Contact Messages
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">
                  {unreadCount}
                </span>
              )}
            </CardTitle>
            <p className="text-white/80 text-sm">Messages from the "Connect With Us" form and Ministry Interest</p>
          </div>
          {unreadCount > 0 && (
            <Button size="sm" variant="secondary" className="rounded-full" onClick={markAllAsRead}>
              <CheckCircle className="w-4 h-4 mr-1" /> Mark All Read
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No messages yet</p>
            <p className="text-gray-400 text-sm">Messages from visitors will appear here</p>
          </div>
        ) : (
          <div className="divide-y">
            {messages.map(m => (
              <div 
                key={m.id} 
                className={`p-4 hover:bg-gray-50 transition-colors ${!m.is_read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''}`}
                onClick={() => !m.is_read && markAsRead(m.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      {!m.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>}
                      <p className={`font-semibold text-gray-800 ${!m.is_read ? 'font-bold' : ''}`}>{m.name}</p>
                      <span className="text-sm text-blue-600 flex items-center gap-1 bg-blue-50 px-2 py-0.5 rounded-full">
                        {m.contact.includes("@") ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                        {m.contact}
                      </span>
                      {m.message && m.message.includes("MINISTRY INTEREST:") && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Ministry Interest</span>
                      )}
                    </div>
                    <p className="text-gray-600 mt-2 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{m.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {!m.is_read && (
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); markAsRead(m.id); }} className="hover:bg-blue-50">
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(m.id); }} className="hover:bg-red-50">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminAttendanceTab = () => {
  const [tab, setTab] = useState("gps");
  const [manualStats, setManualStats] = useState([]);
  const [gpsLogs, setGpsLogs] = useState([]);
  const [gpsSummary, setGpsSummary] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    axios.get(`${API}/attendance/stats`).then(r => setManualStats(r.data)).catch(() => {});
    axios.get(`${API}/attendance/gps-summary`).then(r => setGpsSummary(r.data)).catch(() => {});
    axios.get(`${API}/attendance/gps-logs?date=${filterDate}`).then(r => setGpsLogs(r.data)).catch(() => {});
    axios.get(`${API}/notifications`).then(r => setNotifications(r.data)).catch(() => {});
  }, [filterDate]);

  const processAbsences = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/attendance/process-absences`);
      toast.success(`Processed! ${res.data.absent_count} members marked absent`);
      // Refresh data
      axios.get(`${API}/attendance/gps-summary`).then(r => setGpsSummary(r.data));
      axios.get(`${API}/attendance/gps-logs?date=${filterDate}`).then(r => setGpsLogs(r.data));
      axios.get(`${API}/notifications`).then(r => setNotifications(r.data));
    } catch (err) {
      toast.error("Failed to process absences");
    }
    setProcessing(false);
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2">
        <Button 
          variant={tab === "gps" ? "default" : "outline"} 
          className={`rounded-full ${tab === "gps" ? "bg-gradient-to-r from-green-500 to-teal-500" : ""}`}
          onClick={() => setTab("gps")}
        >
          <MapPin className="w-4 h-4 mr-2" /> GPS Attendance
        </Button>
        <Button 
          variant={tab === "manual" ? "default" : "outline"} 
          className={`rounded-full ${tab === "manual" ? "bg-gradient-to-r from-blue-500 to-purple-500" : ""}`}
          onClick={() => setTab("manual")}
        >
          <UserCheck className="w-4 h-4 mr-2" /> Manual
        </Button>
        <Button 
          variant={tab === "pastoral" ? "default" : "outline"} 
          className={`rounded-full ${tab === "pastoral" ? "bg-gradient-to-r from-red-500 to-pink-500" : ""}`}
          onClick={() => setTab("pastoral")}
        >
          <Heart className="w-4 h-4 mr-2" /> Pastoral Care
        </Button>
      </div>

      {tab === "gps" && (
        <>
          {/* Summary Cards */}
          {gpsSummary && (
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
                <CardContent className="p-4 text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Total Members</p>
                  <p className="text-3xl font-bold text-blue-600">{gpsSummary.total_members}</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-teal-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Present Today</p>
                  <p className="text-3xl font-bold text-green-600">
                    {gpsSummary.daily_stats?.[0]?.present || 0}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-r from-red-50 to-pink-50">
                <CardContent className="p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">High Priority Care</p>
                  <p className="text-3xl font-bold text-red-600">
                    {gpsSummary.high_priority_members?.length || 0}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Process Absences Button */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">Process Service Absences</h3>
                <p className="text-sm text-gray-500">Mark members who didn't check in as absent and send care notifications</p>
              </div>
              <Button 
                onClick={processAbsences} 
                disabled={processing}
                className="rounded-full bg-gradient-to-r from-red-500 to-pink-500"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                Process Absences
              </Button>
            </CardContent>
          </Card>

          {/* GPS Logs */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>GPS Attendance Logs</CardTitle>
              <Input 
                type="date" 
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                className="w-40 rounded-xl"
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Distance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gpsLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-gray-400 py-8">
                        No attendance records for this date
                      </TableCell>
                    </TableRow>
                  ) : gpsLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.member_name}</TableCell>
                      <TableCell>
                        <Badge className={log.status === "PRESENT" ? "bg-green-500" : "bg-red-500"}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{log.check_in_time || "-"}</TableCell>
                      <TableCell>
                        {log.distance_from_church ? `${log.distance_from_church}m` : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {tab === "manual" && (
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle>Manual Attendance Statistics</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Visitors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {manualStats.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.date}</TableCell>
                    <TableCell>{s.total}</TableCell>
                    <TableCell>{s.visitors}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "pastoral" && (
        <div className="space-y-4">
          {/* High Priority Members */}
          {gpsSummary?.high_priority_members?.length > 0 && (
            <Card className="border-0 shadow-lg border-l-4 border-l-red-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  High Priority Care Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {gpsSummary.high_priority_members.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-800">{member.name}</p>
                        <p className="text-sm text-gray-500">
                          {member.phone || member.email || "No contact info"}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-red-500">{member.absence_count} absences</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Notifications */}
          <Card className="border-0 shadow-lg">
            <CardHeader><CardTitle>Care Notifications Sent</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notifications.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">No notifications sent yet</p>
                ) : notifications.slice(0, 10).map(notif => (
                  <div key={notif.id} className="p-3 border rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{notif.member_name}</span>
                      <Badge variant="outline">{notif.date}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 italic">"{notif.caring_message}"</p>
                    <div className="flex gap-2 mt-2">
                      {notif.admin_notified && <Badge className="bg-blue-100 text-blue-700">Admin Notified</Badge>}
                      {notif.member_notified && <Badge className="bg-green-100 text-green-700">Member Notified</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Admin Manual Attendance Tab - Import CSV, Mark Attendance, View Logs
const AdminManualAttendanceTab = () => {
  const [members, setMembers] = useState([]);
  const [presentIds, setPresentIds] = useState(new Set());
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [logFilterDate, setLogFilterDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeView, setActiveView] = useState("mark"); // "mark", "logs", "import", "reports"
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addingMember, setAddingMember] = useState(null);
  const [removingLog, setRemovingLog] = useState(null);
  const [selectedAbsentMembers, setSelectedAbsentMembers] = useState([]);
  
  // New states for filtering and export
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedMember, setSelectedMember] = useState("");
  const [memberAttendance, setMemberAttendance] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, summaryRes, monthsRes] = await Promise.all([
        axios.get(`${API}/admin/manual-attendance/members`),
        axios.get(`${API}/admin/manual-attendance/summary`),
        axios.get(`${API}/admin/manual-attendance/available-months`)
      ]);
      setMembers(membersRes.data);
      setAttendanceSummary(summaryRes.data);
      setAvailableMonths(monthsRes.data);
      
      // Check if there's existing attendance for the selected date
      const logsRes = await axios.get(`${API}/admin/manual-attendance/logs?date=${attendanceDate}`);
      const existingPresentIds = new Set(
        logsRes.data.filter(l => l.status === "PRESENT").map(l => l.member_id)
      );
      setPresentIds(existingPresentIds);
    } catch (err) {
      console.error("Failed to fetch data", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [attendanceDate]);

  const fetchLogs = async (date = logFilterDate) => {
    try {
      const res = await axios.get(`${API}/admin/manual-attendance/logs${date ? `?date=${date}` : ""}`);
      setAttendanceLogs(res.data);
    } catch (err) {
      toast.error("Failed to fetch logs");
    }
  };

  useEffect(() => {
    if (activeView === "logs") {
      fetchLogs();
    }
  }, [activeView, logFilterDate]);

  const togglePresent = (memberId) => {
    setPresentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setPresentIds(new Set(members.map(m => m.id)));
  };

  const deselectAll = () => {
    setPresentIds(new Set());
  };

  // Toggle highlight status for a member
  const toggleHighlight = async (memberId) => {
    try {
      const res = await axios.put(`${API}/admin/manual-attendance/members/${memberId}/highlight`);
      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, highlighted: res.data.highlighted } : m
      ));
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Failed to toggle highlight");
    }
  };

  const saveAttendance = async (processAbsences = false) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/manual-attendance/submit`, {
        date: attendanceDate,
        present_member_ids: Array.from(presentIds),
        process_absences: processAbsences
      });
      
      toast.success(res.data.message);
      
      if (processAbsences && res.data.high_priority_alerts?.length > 0) {
        toast.info(`${res.data.high_priority_alerts.length} pastoral care alert(s) created`);
      }
      
      // Refresh summary
      const summaryRes = await axios.get(`${API}/admin/manual-attendance/summary`);
      setAttendanceSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to save attendance");
    }
    setSaving(false);
  };

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error("CSV file appears to be empty or invalid");
        setImporting(false);
        return;
      }

      // Parse header
      const headerLine = lines[0].toLowerCase();
      const headers = headerLine.split(',').map(h => h.trim().replace(/"/g, ''));
      
      // Find column indices
      const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('full'));
      const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('tel'));
      const ministryIdx = headers.findIndex(h => h.includes('ministry') || h.includes('role') || h.includes('department'));
      const dobIdx = headers.findIndex(h => h.includes('birth') || h.includes('dob'));

      if (nameIdx === -1) {
        toast.error("CSV must have a 'Name' or 'Full Name' column");
        setImporting(false);
        return;
      }

      // Parse data rows
      const membersData = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const fullName = values[nameIdx]?.trim();
        
        if (fullName) {
          membersData.push({
            full_name: fullName,
            phone: phoneIdx !== -1 ? values[phoneIdx]?.trim() || "" : "",
            ministry: ministryIdx !== -1 ? values[ministryIdx]?.trim() || "Member" : "Member",
            date_of_birth: dobIdx !== -1 ? values[dobIdx]?.trim() || "" : ""
          });
        }
      }

      if (membersData.length === 0) {
        toast.error("No valid members found in CSV");
        setImporting(false);
        return;
      }

      // Send to backend
      const res = await axios.post(`${API}/admin/manual-attendance/import-csv`, {
        members: membersData
      });

      toast.success(res.data.message);
      fetchData(); // Refresh member list
    } catch (err) {
      console.error("CSV Import error:", err);
      toast.error("Failed to import CSV");
    }
    
    setImporting(false);
    event.target.value = ''; // Reset file input
  };

  // Add member to attendance log
  const addMemberToLog = async (memberId, status) => {
    if (!logFilterDate) return;
    setAddingMember(memberId);
    try {
      await axios.post(`${API}/admin/manual-attendance/add-entry`, {
        member_id: memberId,
        date: logFilterDate,
        status: status
      });
      toast.success(`Member added as ${status}`);
      fetchLogs(logFilterDate);
      setAddMemberSearch("");
      // Refresh summary
      const summaryRes = await axios.get(`${API}/admin/manual-attendance/summary`);
      setAttendanceSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to add member");
    }
    setAddingMember(null);
  };

  // Remove member from attendance log
  const removeLogEntry = async (logId) => {
    setRemovingLog(logId);
    try {
      await axios.delete(`${API}/admin/manual-attendance/entry/${logId}`);
      toast.success("Entry removed");
      fetchLogs(logFilterDate);
      // Refresh summary
      const summaryRes = await axios.get(`${API}/admin/manual-attendance/summary`);
      setAttendanceSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to remove entry");
    }
    setRemovingLog(null);
  };

  // Toggle log status (PRESENT <-> ABSENT)
  const toggleLogStatus = async (logId, memberId, currentStatus) => {
    const newStatus = currentStatus === "PRESENT" ? "ABSENT" : "PRESENT";
    try {
      await axios.put(`${API}/admin/manual-attendance/entry/${logId}`, {
        status: newStatus
      });
      toast.success(`Status changed to ${newStatus}`);
      fetchLogs(logFilterDate);
      // Refresh summary
      const summaryRes = await axios.get(`${API}/admin/manual-attendance/summary`);
      setAttendanceSummary(summaryRes.data);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  // Export attendance to Excel
  const exportToExcel = async () => {
    setExporting(true);
    try {
      let url = `${API}/admin/manual-attendance/export`;
      const params = new URLSearchParams();
      if (selectedMonth) params.append('month', selectedMonth);
      if (selectedMember) params.append('member_id', selectedMember);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename
      let filename = 'attendance_report';
      if (selectedMonth) filename += `_${selectedMonth}`;
      if (selectedMember) {
        const member = members.find(m => m.id === selectedMember);
        if (member) filename += `_${member.full_name.replace(/\s+/g, '_')}`;
      }
      filename += '.xlsx';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Attendance report exported successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export attendance');
    }
    setExporting(false);
  };

  // Fetch member's attendance with optional month filter
  const fetchMemberAttendance = async (memberId, month = "") => {
    if (!memberId) {
      setMemberAttendance(null);
      return;
    }
    try {
      let url = `${API}/admin/manual-attendance/member/${memberId}`;
      if (month) url += `?month=${month}`;
      const res = await axios.get(url);
      setMemberAttendance(res.data);
    } catch (err) {
      toast.error("Failed to fetch member attendance");
    }
  };

  // Handle member selection change
  useEffect(() => {
    if (activeView === "reports" && selectedMember) {
      fetchMemberAttendance(selectedMember, selectedMonth);
    }
  }, [selectedMember, selectedMonth, activeView]);

  const filteredMembers = members.filter(m =>
    m.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.phone?.includes(searchTerm) ||
    m.ministry?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={activeView === "mark" ? "default" : "outline"} 
          className={`rounded-full ${activeView === "mark" ? "bg-gradient-to-r from-blue-500 to-purple-500" : ""}`}
          onClick={() => setActiveView("mark")}
        >
          <UserCheck className="w-4 h-4 mr-2" /> Mark Attendance
        </Button>
        <Button 
          variant={activeView === "logs" ? "default" : "outline"} 
          className={`rounded-full ${activeView === "logs" ? "bg-gradient-to-r from-green-500 to-teal-500" : ""}`}
          onClick={() => setActiveView("logs")}
        >
          <FileText className="w-4 h-4 mr-2" /> View Logs
        </Button>
        <Button 
          variant={activeView === "reports" ? "default" : "outline"} 
          className={`rounded-full ${activeView === "reports" ? "bg-gradient-to-r from-indigo-500 to-purple-500" : ""}`}
          onClick={() => setActiveView("reports")}
        >
          <Download className="w-4 h-4 mr-2" /> Reports & Export
        </Button>
        <Button 
          variant={activeView === "import" ? "default" : "outline"} 
          className={`rounded-full ${activeView === "import" ? "bg-gradient-to-r from-amber-500 to-orange-500" : ""}`}
          onClick={() => setActiveView("import")}
        >
          <Upload className="w-4 h-4 mr-2" /> Import Members
        </Button>
      </div>

      {/* Mark Attendance View */}
      {activeView === "mark" && (
        <>
          {/* Date Selector & Stats */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-blue-500" />
                Manual Attendance
              </CardTitle>
              <CardDescription>
                Select a date and mark members who are present
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label className="text-sm text-gray-600">Attendance Date</Label>
                  <Input 
                    type="date" 
                    value={attendanceDate} 
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="w-48"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>
                    <X className="w-4 h-4 mr-1" /> Deselect All
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{presentIds.size}</p>
                  <p className="text-xs text-gray-500">Present</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{members.length - presentIds.size}</p>
                  <p className="text-xs text-gray-500">Absent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{members.length}</p>
                  <p className="text-xs text-gray-500">Total Members</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Member List with Checkboxes */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Member List ({filteredMembers.length})</CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="inline-block w-3 h-3 bg-green-200 rounded mr-1"></span>
                    Highlighted members appear in green
                  </p>
                </div>
                <Input 
                  placeholder="Search members..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Present</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Ministry</TableHead>
                      <TableHead className="w-20 text-center">Highlight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map(member => (
                      <TableRow 
                        key={member.id} 
                        className={`cursor-pointer hover:bg-blue-50 ${
                          member.highlighted 
                            ? 'bg-green-100 hover:bg-green-200' 
                            : presentIds.has(member.id) 
                              ? 'bg-green-50' 
                              : ''
                        }`}
                      >
                        <TableCell onClick={() => togglePresent(member.id)}>
                          <div 
                            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                              presentIds.has(member.id) 
                                ? 'bg-green-500 border-green-500 text-white' 
                                : 'border-gray-300'
                            }`}
                          >
                            {presentIds.has(member.id) && <CheckCircle className="w-4 h-4" />}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium" onClick={() => togglePresent(member.id)}>
                          {member.full_name}
                          {member.highlighted && <Star className="w-3 h-3 inline ml-1 text-green-600 fill-green-600" />}
                        </TableCell>
                        <TableCell onClick={() => togglePresent(member.id)}>{member.phone || "-"}</TableCell>
                        <TableCell onClick={() => togglePresent(member.id)}>
                          <Badge variant="outline" className="text-xs">{member.ministry || "Member"}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className={`p-1 h-8 w-8 ${member.highlighted ? 'text-green-600' : 'text-gray-400'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleHighlight(member.id);
                            }}
                            title={member.highlighted ? "Remove highlight" : "Highlight member"}
                          >
                            <Star className={`w-4 h-4 ${member.highlighted ? 'fill-green-500' : ''}`} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <Button 
              onClick={() => saveAttendance(false)} 
              disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-purple-500"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Attendance
            </Button>
            <Button 
              onClick={() => saveAttendance(true)} 
              disabled={saving}
              variant="destructive"
              className="bg-gradient-to-r from-red-500 to-pink-500"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertCircle className="w-4 h-4 mr-2" />}
              Process Absences & Send Alerts
            </Button>
          </div>
        </>
      )}

      {/* View Logs */}
      {activeView === "logs" && (
        <>
          {/* Attendance Summary */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-green-500 to-teal-500" />
            <CardHeader>
              <CardTitle>Attendance Summary</CardTitle>
              <CardDescription>Historical attendance records by date</CardDescription>
            </CardHeader>
            <CardContent>
              {attendanceSummary.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No attendance records yet</p>
              ) : (
                <div className="space-y-2">
                  {attendanceSummary.map(record => (
                    <div 
                      key={record.date} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => { setLogFilterDate(record.date); fetchLogs(record.date); }}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium">{new Date(record.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-xs text-gray-500">Recorded by: {record.recorded_by}</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Badge className="bg-green-100 text-green-700">{record.present_count} Present</Badge>
                        <Badge className="bg-red-100 text-red-700">{record.absent_count} Absent</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detailed Logs */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle>Detailed Attendance Logs</CardTitle>
                <div className="flex gap-2 items-center flex-wrap">
                  <Input 
                    type="date" 
                    value={logFilterDate} 
                    onChange={(e) => setLogFilterDate(e.target.value)}
                    className="w-48"
                  />
                  <Button variant="outline" size="sm" onClick={() => { setLogFilterDate(""); fetchLogs(""); }}>
                    Clear Filter
                  </Button>
                  {logFilterDate && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-green-500 hover:bg-green-600">
                          <Plus className="w-4 h-4 mr-1" /> Add Member
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Member to Attendance Log</DialogTitle>
                          <DialogDescription>Select a member to add to {logFilterDate}'s attendance</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <Input 
                            placeholder="Search members..." 
                            value={addMemberSearch}
                            onChange={(e) => setAddMemberSearch(e.target.value)}
                          />
                          <ScrollArea className="h-[300px]">
                            <div className="space-y-2">
                              {members
                                .filter(m => 
                                  m.full_name?.toLowerCase().includes(addMemberSearch.toLowerCase()) &&
                                  !attendanceLogs.some(log => log.member_id === m.id)
                                )
                                .slice(0, 20)
                                .map(member => (
                                  <div 
                                    key={member.id}
                                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                                  >
                                    <div>
                                      <p className="font-medium">{member.full_name}</p>
                                      <p className="text-xs text-gray-500">{member.phone || "No phone"}</p>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button 
                                        size="sm" 
                                        className="bg-green-500 hover:bg-green-600"
                                        onClick={() => addMemberToLog(member.id, "PRESENT")}
                                        disabled={addingMember}
                                      >
                                        {addingMember === member.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Present"}
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="destructive"
                                        onClick={() => addMemberToLog(member.id, "ABSENT")}
                                        disabled={addingMember}
                                      >
                                        Absent
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </ScrollArea>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Absent Members Quick Actions */}
              {attendanceLogs.filter(l => l.status === "ABSENT" && l.phone).length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-700 font-medium">
                        {attendanceLogs.filter(l => l.status === "ABSENT" && l.phone).length} absent member(s) with phone numbers
                      </span>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          Send WhatsApp to Absent
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Send WhatsApp to Absent Members
                          </DialogTitle>
                          <DialogDescription>
                            Select members and click "Send to Selected" to open WhatsApp for each
                          </DialogDescription>
                        </DialogHeader>
                        {(() => {
                          const absentWithPhone = attendanceLogs.filter(l => l.status === "ABSENT" && l.phone);
                          
                          // Use the viewed/filtered date for the absence message (defaults to logFilterDate which is the "Absent Viewed" date)
                          const viewedDate = logFilterDate || (absentWithPhone.length > 0 && absentWithPhone[0].date) || "";
                          const sundayDate = viewedDate 
                            ? new Date(viewedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                            : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                          
                          const getMessage = () => {
                            return `Halleluyah,\n\nWe didn't see you in church on Sunday, ${sundayDate}, and we just wanted to check in and let you know you were truly missed. We pray that all is well with you and your family.\n\n"Fear not, for I am with you; be not dismayed, for I am your God. I will strengthen you, yes, I will help you." — Isaiah 41:10\n\nMay the Lord's presence, comfort, and strength surround you throughout the week. We look forward to fellowshipping with you at the next Sunday Service.\n\nMedway Central Parish Welfare`;
                          };
                          
                          const toggleSelect = (id) => {
                            setSelectedAbsentMembers(prev => 
                              prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                            );
                          };
                          
                          const selectAll = () => {
                            if (selectedAbsentMembers.length === absentWithPhone.length) {
                              setSelectedAbsentMembers([]);
                            } else {
                              setSelectedAbsentMembers(absentWithPhone.map(l => l.id));
                            }
                          };
                          
                          const sendToSelected = () => {
                            const message = getMessage();
                            selectedAbsentMembers.forEach((id, index) => {
                              const log = absentWithPhone.find(l => l.id === id);
                              if (log) {
                                const phone = log.phone.replace(/\s+/g, '').replace(/^0/, '44');
                                const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
                                setTimeout(() => window.open(whatsappUrl, '_blank'), index * 500);
                              }
                            });
                          };
                          
                          return (
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 rounded-lg">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={selectedAbsentMembers.length === absentWithPhone.length && absentWithPhone.length > 0}
                                    onChange={selectAll}
                                    className="w-4 h-4 rounded"
                                  />
                                  <span className="text-sm font-medium">Select All ({absentWithPhone.length})</span>
                                </label>
                                <Button 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  disabled={selectedAbsentMembers.length === 0}
                                  onClick={sendToSelected}
                                >
                                  <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                  </svg>
                                  Send to Selected ({selectedAbsentMembers.length})
                                </Button>
                              </div>
                              
                              <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                <p className="text-xs text-green-700 font-medium mb-1">Message Preview:</p>
                                <p className="text-xs text-green-800 whitespace-pre-line">{getMessage()}</p>
                              </div>
                              
                              <ScrollArea className="h-[250px]">
                                <div className="space-y-2">
                                  {absentWithPhone.map(log => (
                                    <div 
                                      key={log.id}
                                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedAbsentMembers.includes(log.id) ? 'bg-green-50 border-green-300' : 'hover:bg-gray-50'}`}
                                      onClick={() => toggleSelect(log.id)}
                                    >
                                      <input 
                                        type="checkbox" 
                                        checked={selectedAbsentMembers.includes(log.id)}
                                        onChange={() => toggleSelect(log.id)}
                                        className="w-4 h-4 rounded"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div className="flex-1">
                                        <p className="font-medium">{log.member_name}</p>
                                        <p className="text-xs text-gray-500">{log.phone}</p>
                                      </div>
                                      <Button 
                                        size="sm"
                                        variant="outline"
                                        className="border-green-500 text-green-600 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const phone = log.phone.replace(/\s+/g, '').replace(/^0/, '44');
                                          const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(getMessage())}`;
                                          window.open(whatsappUrl, '_blank');
                                        }}
                                      >
                                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                        </svg>
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            </div>
                          );
                        })()}
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              )}
              <ScrollArea className="h-[400px]">
                {attendanceLogs.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">Select a date to view logs</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Recorded At</TableHead>
                        <TableHead>Recorded By</TableHead>
                        <TableHead className="w-32">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceLogs.map(log => (
                        <TableRow key={log.id}>
                          <TableCell className="font-medium">{log.member_name}</TableCell>
                          <TableCell>{log.phone || "-"}</TableCell>
                          <TableCell>
                            <Badge 
                              className={`cursor-pointer ${log.status === "PRESENT" ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"}`}
                              onClick={() => toggleLogStatus(log.id, log.member_id, log.status)}
                            >
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(log.recorded_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm">{log.recorded_by}</TableCell>
                          <TableCell>
                            <div className="flex gap-2 items-center">
                              {/* WhatsApp button for absent members */}
                              {log.status === "ABSENT" && (
                                <button
                                  type="button"
                                  data-testid={`whatsapp-btn-${log.id}`}
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${log.phone ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                  disabled={!log.phone}
                                  onClick={() => {
                                    if (!log.phone) {
                                      toast.error("No phone number for this member");
                                      return;
                                    }
                                    // Use the log's date (the actual absence date) first, then fall back to logFilterDate
                                    const absenceDate = log.date || logFilterDate;
                                    const sundayDate = absenceDate ? new Date(absenceDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                                    const message = `Halleluyah,\n\nWe didn't see you in church on Sunday, ${sundayDate}, and we just wanted to check in and let you know you were truly missed. We pray that all is well with you and your family.\n\n"Fear not, for I am with you; be not dismayed, for I am your God. I will strengthen you, yes, I will help you." — Isaiah 41:10\n\nMay the Lord's presence, comfort, and strength surround you throughout the week. We look forward to fellowshipping with you at the next Sunday Service.\n\nMedway Central Parish Welfare`;
                                    const phone = log.phone.replace(/\s+/g, '').replace(/^0/, '44');
                                    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
                                    window.open(whatsappUrl, '_blank');
                                  }}
                                  title={log.phone ? "Send WhatsApp Message" : "No phone number"}
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                  </svg>
                                  WA
                                </button>
                              )}
                              <button
                                type="button"
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                onClick={() => removeLogEntry(log.id)}
                                disabled={removingLog === log.id}
                              >
                                {removingLog === log.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </>
      )}

      {/* Reports & Export */}
      {activeView === "reports" && (
        <>
          {/* Export Card */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-indigo-500" />
                Export Sunday Attendance Report
              </CardTitle>
              <CardDescription>
                Export Sunday attendance data to Excel with optional filtering by month or member
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Month Filter */}
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Filter by Month</Label>
                  <Select value={selectedMonth || "all"} onValueChange={(v) => setSelectedMonth(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All months" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {availableMonths.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Member Filter */}
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Filter by Member</Label>
                  <Select value={selectedMember || "all"} onValueChange={(v) => setSelectedMember(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      {members.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Export Button */}
                <div className="flex items-end">
                  <Button 
                    onClick={exportToExcel} 
                    disabled={exporting}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500"
                  >
                    {exporting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Exporting...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Export Sundays to Excel</>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-700">
                <p><strong>Note:</strong> Export includes <strong>Sunday attendance only</strong>. Select a month to get a summary sheet with attendance percentages for each member.</p>
              </div>
            </CardContent>
          </Card>

          {/* Member Attendance View */}
          {selectedMember && memberAttendance && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-500" />
                      {memberAttendance.member?.full_name || "Member"}'s Attendance
                    </CardTitle>
                    <CardDescription>
                      {selectedMonth ? `Showing ${availableMonths.find(m => m.value === selectedMonth)?.label || selectedMonth}` : "All time records"}
                    </CardDescription>
                  </div>
                  {/* Stats */}
                  <div className="flex gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{memberAttendance.stats?.present || 0}</p>
                      <p className="text-xs text-gray-500">Present</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{memberAttendance.stats?.absent || 0}</p>
                      <p className="text-xs text-gray-500">Absent</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{memberAttendance.stats?.attendance_rate || 0}%</p>
                      <p className="text-xs text-gray-500">Rate</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {memberAttendance.logs?.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No attendance records found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Day</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Recorded By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {memberAttendance.logs?.map((log, idx) => {
                          const dateObj = new Date(log.date + 'T12:00:00');
                          return (
                            <TableRow key={idx}>
                              <TableCell>{dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                              <TableCell>{dateObj.toLocaleDateString('en-GB', { weekday: 'short' })}</TableCell>
                              <TableCell>
                                <Badge className={log.status === "PRESENT" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                                  {log.status}
                                </Badge>
                                {log.auto_marked && <span className="text-xs text-gray-400 ml-1">(auto)</span>}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">{log.recorded_by}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Auto-Absent Info */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Automatic Sunday Absence Marking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">
                  <strong>How it works:</strong> At <strong>12:00 AM Monday</strong> (end of Sunday), the system automatically marks all members who were not marked as "Present" on Sunday as "Absent".
                </p>
                <ul className="mt-3 text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>This only runs if at least one person has been marked present on Sunday</li>
                  <li>Auto-marked entries are labeled "(auto)" in the records</li>
                  <li>You can still modify attendance status after auto-marking</li>
                  <li>Only Sunday attendance is processed for automatic absence marking</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Import Members */}
      {activeView === "import" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-500" />
              Import Members from CSV
            </CardTitle>
            <CardDescription>
              Upload a CSV file with member data to import into the system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li><strong>Required column:</strong> "Full Name" or "Name"</li>
                <li><strong>Optional columns:</strong> "Phone", "Ministry", "Date of Birth"</li>
                <li>First row must be column headers</li>
                <li>Existing members (matched by name) will be updated with missing info</li>
              </ul>
            </div>

            {/* File Upload */}
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-amber-400 transition-colors">
              <Upload className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">Click to select a CSV file or drag and drop</p>
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleCSVImport}
                className="hidden"
                id="csv-upload"
                disabled={importing}
              />
              <label htmlFor="csv-upload">
                <Button asChild disabled={importing}>
                  <span>
                    {importing ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importing...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Select CSV File</>
                    )}
                  </span>
                </Button>
              </label>
            </div>

            {/* Current Members Stats */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Current Members</h4>
              <p className="text-2xl font-bold text-blue-600">{members.length}</p>
              <p className="text-sm text-gray-500">members in the database</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminFinanceTab = () => {
  const { isMainAdmin, hasPermission } = useAuth();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ service_date: "", category: "tithe", amount: "", payment_method: "cash", recorded_by: "", description: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filters, setFilters] = useState({ startDate: "", endDate: "", categories: [], paymentMethod: "all" });
  
  const categories = [
    { value: "tithe", label: "Tithe" },
    { value: "general_collection", label: "General Collection" },
    { value: "building_fund", label: "Building Fund" },
    { value: "gift_of_mercy", label: "Gift of Mercy" },
    { value: "room_rent", label: "Room Rent" },
    { value: "iou", label: "IOU" }
  ];

  const paymentMethods = [
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "iou", label: "IOU" }
  ];

  useEffect(() => { 
    axios.get(`${API}/finance`).then(r => { setRecords(r.data); setFilteredRecords(r.data); }).catch(() => {}); 
    axios.get(`${API}/finance/summary`).then(r => setSummary(r.data)).catch(() => {}); 
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...records];
    if (filters.startDate) {
      filtered = filtered.filter(r => r.service_date >= filters.startDate);
    }
    if (filters.endDate) {
      filtered = filtered.filter(r => r.service_date <= filters.endDate);
    }
    if (filters.categories && filters.categories.length > 0) {
      filtered = filtered.filter(r => filters.categories.includes(r.category));
    }
    if (filters.paymentMethod && filters.paymentMethod !== "all") {
      filtered = filtered.filter(r => (r.payment_method || 'cash') === filters.paymentMethod);
    }
    setFilteredRecords(filtered);
  }, [filters, records]);

  const save = async () => { 
    if (!form.service_date || !form.amount) { toast.error("Please fill in date and amount"); return; }
    try {
      if (editingRecord) {
        await axios.put(`${API}/finance/${editingRecord}`, { ...form, amount: parseFloat(form.amount) });
        toast.success("Record updated");
      } else {
        await axios.post(`${API}/finance`, { ...form, amount: parseFloat(form.amount) }); 
        toast.success("Record added" + (!isMainAdmin ? " (Locked)" : "")); 
      }
      setDialogOpen(false); 
      setEditingRecord(null);
      setForm({ service_date: "", category: "tithe", amount: "", payment_method: "cash", recorded_by: "", description: "" }); 
      axios.get(`${API}/finance`).then(r => { setRecords(r.data); setFilteredRecords(r.data); }); 
      axios.get(`${API}/finance/summary`).then(r => setSummary(r.data)); 
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save record");
    }
  };

  const handleEdit = (record) => {
    setForm({
      service_date: record.service_date,
      category: record.category,
      amount: record.amount.toString(),
      payment_method: record.payment_method || 'cash',
      recorded_by: record.recorded_by || '',
      description: record.description || ''
    });
    setEditingRecord(record.id);
    setDialogOpen(true);
  };

  const handleDelete = async (recordId, isLocked) => {
    if (isLocked && !isMainAdmin) {
      toast.error("This record is locked. Only Main Admin can delete it.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await axios.delete(`${API}/finance/${recordId}`);
      toast.success("Record deleted");
      axios.get(`${API}/finance`).then(r => { setRecords(r.data); setFilteredRecords(r.data); }); 
      axios.get(`${API}/finance/summary`).then(r => setSummary(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleUnlock = async (recordId) => {
    try {
      await axios.put(`${API}/finance/${recordId}/unlock`);
      toast.success("Record unlocked");
      axios.get(`${API}/finance`).then(r => { setRecords(r.data); setFilteredRecords(r.data); });
    } catch (err) {
      toast.error("Failed to unlock");
    }
  };

  const exportToExcel = async () => {
    if (!isMainAdmin) {
      toast.error("Only Main Admin can export data");
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const FileSaver = await import('file-saver');
      
      // Prepare data for export - include ALL records for filtering in Excel
      const exportData = records.map(r => ({
        'Date': r.service_date,
        'Category': r.category.replace(/_/g, ' ').toUpperCase(),
        'Amount (£)': r.amount.toFixed(2),
        'Payment Method': (r.payment_method || 'cash').replace(/_/g, ' ').toUpperCase(),
        'Recorded By': r.recorded_by || '',
        'Description': r.description || ''
      }));

      // Add summary rows by category
      const categoryTotals = {};
      records.forEach(r => {
        const cat = r.category.replace(/_/g, ' ').toUpperCase();
        categoryTotals[cat] = (categoryTotals[cat] || 0) + r.amount;
      });

      exportData.push({ 'Date': '', 'Category': '', 'Amount (£)': '', 'Payment Method': '', 'Recorded By': '', 'Description': '' });
      exportData.push({ 'Date': 'SUMMARY', 'Category': '', 'Amount (£)': '', 'Payment Method': '', 'Recorded By': '', 'Description': '' });
      
      Object.entries(categoryTotals).forEach(([cat, total]) => {
        exportData.push({
          'Date': '',
          'Category': cat,
          'Amount (£)': total.toFixed(2),
          'Payment Method': '',
          'Recorded By': '',
          'Description': ''
        });
      });

      const grandTotal = records.reduce((sum, r) => sum + r.amount, 0);
      exportData.push({
        'Date': '',
        'Category': 'GRAND TOTAL',
        'Amount (£)': grandTotal.toFixed(2),
        'Payment Method': '',
        'Recorded By': '',
        'Description': ''
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Financial Records');

      // Set column widths
      ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 25 }];

      // Generate file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const today = new Date().toISOString().split('T')[0];
      const filename = `CCC_Medway_Finance_Report_${today}.xlsx`;
      
      FileSaver.saveAs(data, filename);
      toast.success("Excel file downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export");
    }
  };

  const clearFilters = () => {
    setFilters({ startDate: "", endDate: "", categories: [], paymentMethod: "all" });
  };

  const toggleCategoryFilter = (categoryValue) => {
    setFilters(prev => {
      const newCategories = prev.categories.includes(categoryValue)
        ? prev.categories.filter(c => c !== categoryValue)
        : [...prev.categories, categoryValue];
      return { ...prev, categories: newCategories };
    });
  };

  const filteredTotal = filteredRecords.reduce((sum, r) => sum + r.amount, 0);

  return (<div className="space-y-6">
    {/* Summary Cards */}
    {summary && summary.categories && (<div className="grid md:grid-cols-4 gap-4">{summary.categories.map((c, i) => (<Card key={i} className="border-0 shadow-lg"><CardContent className="p-4 text-center"><p className="text-sm text-gray-500 capitalize">{c.category.replace(/_/g, ' ')}</p><p className="text-2xl font-bold text-green-600">£{(c.total || 0).toFixed(2)}</p></CardContent></Card>))}</div>)}
    
    {/* Filters */}
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5" /> Filter & Export</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-sm text-gray-600 mb-1 block">From Date</Label>
            <Input type="date" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <Label className="text-sm text-gray-600 mb-1 block">To Date</Label>
            <Input type="date" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <Label className="text-sm text-gray-600 mb-1 block">Payment Method</Label>
            <select value={filters.paymentMethod} onChange={e => setFilters({...filters, paymentMethod: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Payment Methods</option>
              {paymentMethods.map(pm => (<option key={pm.value} value={pm.value}>{pm.label}</option>))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={clearFilters} className="flex-1 rounded-xl h-10">Clear</Button>
            {isMainAdmin && (
              <Button onClick={exportToExcel} className="flex-1 rounded-xl h-10 bg-gradient-to-r from-green-500 to-teal-500" disabled={records.length === 0}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            )}
          </div>
        </div>
        
        {/* Multi-select Categories */}
        <div>
          <Label className="text-sm text-gray-600 mb-2 block">Filter by Categories (select multiple)</Label>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant={filters.categories.includes(cat.value) ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${filters.categories.includes(cat.value) ? "bg-blue-600 text-white" : ""}`}
                onClick={() => toggleCategoryFilter(cat.value)}
              >
                {filters.categories.includes(cat.value) && <CheckCircle className="w-3 h-3 mr-1" />}
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
        
        {(filters.startDate || filters.endDate || filters.categories.length > 0 || filters.paymentMethod !== "all") && (
          <div className="mt-3 p-3 bg-blue-50 rounded-xl">
            <p className="text-sm text-blue-700">
              Showing {filteredRecords.length} record(s) | Filtered Total: <span className="font-bold">£{filteredTotal.toFixed(2)}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Records Table */}
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Financial Records {summary && <span className="text-green-600">Total: £{(summary.total || 0).toFixed(2)}</span>}</CardTitle>
        {hasPermission("finance_write") && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingRecord(null);
              setForm({ service_date: "", category: "tithe", amount: "", payment_method: "cash", recorded_by: "", description: "" });
            }
          }}>
            <DialogTrigger asChild><Button className="rounded-full" onClick={() => { setEditingRecord(null); setForm({ service_date: "", category: "tithe", amount: "", payment_method: "cash", recorded_by: "", description: "" }); }}>Add Record</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{editingRecord ? "Edit Financial Record" : "Add Financial Record"}</DialogTitle></DialogHeader>
              {!isMainAdmin && !editingRecord && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                  <p className="text-sm text-amber-700">As a Sub Admin, records you create will be <strong>locked</strong> immediately. Only Main Admin can edit or delete them.</p>
                </div>
              )}
              <div className="space-y-4">
                <div><Label className="text-sm text-gray-600 mb-2 block">Service Date *</Label><Input type="date" value={form.service_date} onChange={e => setForm({...form, service_date: e.target.value})} className="rounded-xl" /></div>
                <div><Label className="text-sm text-gray-600 mb-2 block">Category *</Label><select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">{categories.map(cat => (<option key={cat.value} value={cat.value}>{cat.label}</option>))}</select></div>
                <div><Label className="text-sm text-gray-600 mb-2 block">Amount (£) *</Label><Input type="number" placeholder="0.00" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-xl" /></div>
                <div><Label className="text-sm text-gray-600 mb-2 block">Payment Method</Label><select value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500">{paymentMethods.map(pm => (<option key={pm.value} value={pm.value}>{pm.label}</option>))}</select></div>
                <div><Label className="text-sm text-gray-600 mb-2 block">Recorded By</Label><Input placeholder="Enter name" value={form.recorded_by} onChange={e => setForm({...form, recorded_by: e.target.value})} className="rounded-xl" /></div>
                <div><Label className="text-sm text-gray-600 mb-2 block">Description (optional)</Label><Input placeholder="Additional notes" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl" /></div>
                <Button onClick={save} className="w-full rounded-xl h-11 bg-gradient-to-r from-green-500 to-teal-500">{editingRecord ? "Update Record" : "Save Record"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Recorded By</TableHead>
              <TableHead>Status</TableHead>
              {hasPermission("finance_write") && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecords.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">No records found</TableCell></TableRow>
            ) : filteredRecords.map(r => (
              <TableRow key={r.id} className={r.is_locked ? "bg-amber-50" : ""}>
                <TableCell>{r.service_date}</TableCell>
                <TableCell className="capitalize">{r.category.replace(/_/g,' ')}</TableCell>
                <TableCell>£{r.amount.toFixed(2)}</TableCell>
                <TableCell className="capitalize">{(r.payment_method || 'cash').replace(/_/g,' ')}</TableCell>
                <TableCell>{r.recorded_by || '-'}</TableCell>
                <TableCell>
                  {r.is_locked ? (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs flex items-center gap-1 w-fit">
                      🔒 Locked
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Open</span>
                  )}
                </TableCell>
                {hasPermission("finance_write") && (
                  <TableCell>
                    <div className="flex gap-1">
                      {isMainAdmin && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleEdit(r)}>
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      )}
                      {r.is_locked && isMainAdmin && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUnlock(r.id)}>Unlock</Button>
                      )}
                      {(isMainAdmin || !r.is_locked) && (
                        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDelete(r.id, r.is_locked)}>Delete</Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </div>);
};

// Admin Expenses Tab
const AdminExpensesTab = () => {
  const { hasPermission } = useAuth();
  return (
    <Tabs defaultValue="general" className="space-y-4">
      <TabsList className="rounded-xl" data-testid="expenses-subtabs">
        <TabsTrigger value="general" data-testid="expenses-subtab-general">General Expenses</TabsTrigger>
        <TabsTrigger value="budget" data-testid="expenses-subtab-budget">Budget Expense</TabsTrigger>
      </TabsList>
      <TabsContent value="general"><AdminGeneralExpenses /></TabsContent>
      <TabsContent value="budget"><BudgetExpenseSection canWrite={hasPermission("expenses_write")} /></TabsContent>
    </Tabs>
  );
};

const AdminGeneralExpenses = () => {
  const { isMainAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ date: "", category: "rent", description: "", amount: "", paid_by: "", receipt_ref: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "", category: "all" });

  const expenseCategories = [
    { value: "rent", label: "Rent" },
    { value: "service_charge", label: "Service Charge" },
    { value: "yearly_insurance", label: "Yearly Insurance" },
    { value: "water", label: "Water" },
    { value: "electricity_gas", label: "Electricity & Gas" },
    { value: "miscellaneous", label: "Miscellaneous" }
  ];

  useEffect(() => { 
    loadExpenses();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...expenses];
    if (filters.startDate) {
      filtered = filtered.filter(e => e.date >= filters.startDate);
    }
    if (filters.endDate) {
      filtered = filtered.filter(e => e.date <= filters.endDate);
    }
    if (filters.category && filters.category !== "all") {
      filtered = filtered.filter(e => e.category === filters.category);
    }
    setFilteredExpenses(filtered);
  }, [filters, expenses]);

  const loadExpenses = () => {
    axios.get(`${API}/expenses`).then(r => { setExpenses(r.data); setFilteredExpenses(r.data); }).catch(() => {});
    axios.get(`${API}/expenses/summary`).then(r => setSummary(r.data)).catch(() => {});
  };

  const saveExpense = async () => {
    if (!form.date || !form.category || !form.amount) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      await axios.post(`${API}/expenses`, { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense added!");
      setDialogOpen(false);
      setForm({ date: "", category: "rent", description: "", amount: "", paid_by: "", receipt_ref: "" });
      loadExpenses();
    } catch (err) {
      toast.error("Failed to save expense");
    }
  };

  const approveExpense = async (id) => {
    await axios.put(`${API}/expenses/${id}/approve`);
    toast.success("Expense approved");
    loadExpenses();
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await axios.delete(`${API}/expenses/${id}`);
    toast.success("Deleted");
    loadExpenses();
  };

  const clearFilters = () => {
    setFilters({ startDate: "", endDate: "", category: "all" });
  };

  const exportToExcel = async () => {
    if (!isMainAdmin) {
      toast.error("Only Main Admin can export data");
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const FileSaver = await import('file-saver');
      
      // Prepare data for export
      const exportData = expenses.map(e => ({
        'Date': e.date,
        'Category': (e.category || 'miscellaneous').replace(/_/g, ' ').toUpperCase(),
        'Description': e.description || '',
        'Amount (£)': e.amount.toFixed(2),
        'Paid By': e.paid_by || '',
        'Receipt Ref': e.receipt_ref || '',
        'Status': e.approved ? 'Approved' : 'Pending'
      }));

      // Add summary rows by category
      const categoryTotals = {};
      expenses.forEach(e => {
        const cat = (e.category || 'miscellaneous').replace(/_/g, ' ').toUpperCase();
        categoryTotals[cat] = (categoryTotals[cat] || 0) + e.amount;
      });

      exportData.push({ 'Date': '', 'Category': '', 'Description': '', 'Amount (£)': '', 'Paid By': '', 'Receipt Ref': '', 'Status': '' });
      exportData.push({ 'Date': 'SUMMARY BY CATEGORY', 'Category': '', 'Description': '', 'Amount (£)': '', 'Paid By': '', 'Receipt Ref': '', 'Status': '' });
      
      Object.entries(categoryTotals).forEach(([cat, total]) => {
        exportData.push({
          'Date': '',
          'Category': cat,
          'Description': '',
          'Amount (£)': total.toFixed(2),
          'Paid By': '',
          'Receipt Ref': '',
          'Status': ''
        });
      });

      const grandTotal = expenses.reduce((sum, e) => sum + e.amount, 0);
      exportData.push({
        'Date': '',
        'Category': 'GRAND TOTAL',
        'Description': '',
        'Amount (£)': grandTotal.toFixed(2),
        'Paid By': '',
        'Receipt Ref': '',
        'Status': ''
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expenses Report');

      // Set column widths
      ws['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 10 }];

      // Generate file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const today = new Date().toISOString().split('T')[0];
      const filename = `CCC_Medway_Expenses_Report_${today}.xlsx`;
      
      FileSaver.saveAs(data, filename);
      toast.success("Excel file downloaded!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export");
    }
  };

  const filteredTotal = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-red-500 to-pink-500 text-white mb-4">
        <CardContent className="p-6 text-center">
          <p className="text-lg opacity-80">Total Expenses</p>
          <p className="text-3xl font-bold">£{(summary?.total || 0).toFixed(2)}</p>
        </CardContent>
      </Card>

      {/* Filter & Export */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5" /> Filter & Export</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="text-sm text-gray-600 mb-1 block">From Date</Label>
              <Input type="date" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-gray-600 mb-1 block">To Date</Label>
              <Input type="date" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-gray-600 mb-1 block">Category</Label>
              <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500">
                <option value="all">All Categories</option>
                {expenseCategories.map(cat => (<option key={cat.value} value={cat.value}>{cat.label}</option>))}
              </select>
            </div>
            <div>
              <Button variant="outline" onClick={clearFilters} className="w-full rounded-xl h-10">Clear Filters</Button>
            </div>
            {/* Only show export button for Main Admin */}
            {isMainAdmin && (
              <div>
                <Button onClick={exportToExcel} className="w-full rounded-xl h-10 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600" disabled={expenses.length === 0}>
                  <Download className="w-4 h-4 mr-2" /> Export All to Excel
                </Button>
              </div>
            )}
          </div>
          {(filters.startDate || filters.endDate || filters.category !== "all") && (
            <div className="mt-3 p-3 bg-red-50 rounded-xl">
              <p className="text-sm text-red-700">
                Showing {filteredExpenses.length} expense(s) | Filtered Total: <span className="font-bold">£{filteredTotal.toFixed(2)}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expenses List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-red-500" />
            Expenses ({filteredExpenses.length})
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-red-500 hover:bg-red-600">
                <Plus className="w-4 h-4 mr-2" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Date *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Category *</Label>
                  <select 
                    value={form.category} 
                    onChange={e => setForm({...form, category: e.target.value})} 
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {expenseCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Description</Label>
                  <Input placeholder="Additional details (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Amount (£) *</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Paid By</Label>
                  <Input placeholder="Who paid for this?" value={form.paid_by} onChange={e => setForm({...form, paid_by: e.target.value})} />
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Receipt Reference</Label>
                  <Input placeholder="Invoice/Receipt number" value={form.receipt_ref} onChange={e => setForm({...form, receipt_ref: e.target.value})} />
                </div>
                <Button onClick={saveExpense} className="w-full rounded-full bg-red-500 hover:bg-red-600">
                  Save Expense
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {filteredExpenses.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{expenses.length === 0 ? "No expenses recorded yet" : "No expenses match your filters"}</p>
            ) : (
              <div className="space-y-2">
                {filteredExpenses.map(exp => (
                  <div key={exp.id} className="p-3 border rounded-lg flex justify-between items-center hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-lg text-red-600">£{exp.amount.toFixed(2)}</span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs capitalize">
                          {(exp.category || 'miscellaneous').replace(/_/g, ' ')}
                        </span>
                        {exp.approved && <Badge className="bg-green-500">Approved</Badge>}
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{exp.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {exp.date} {exp.paid_by && `• Paid by: ${exp.paid_by}`} {exp.receipt_ref && `• Ref: ${exp.receipt_ref}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {!exp.approved && (
                        <Button size="sm" variant="outline" onClick={() => approveExpense(exp.id)} className="text-green-600 border-green-500">
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteExpense(exp.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ADMIN MEETINGS TAB ============
const AdminMeetingsTab = () => {
  const [meetings, setMeetings] = useState([]);
  const [form, setForm] = useState({ 
    meeting_date: "", 
    description: "", 
    start_time: "", 
    end_time: "",
    minutes_pdf_url: "",
    minutes_pdf_name: ""
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { 
    axios.get(`${API}/admin/meetings`).then(r => setMeetings(r.data)).catch(() => {}); 
  }, []);

  const filteredMeetings = meetings.filter(m => 
    (m.description || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.meeting_date || "").includes(search)
  );

  const saveMeeting = async () => {
    try {
      if (editingMeeting) {
        await axios.put(`${API}/admin/meetings/${editingMeeting.id}`, form);
        toast.success("Meeting updated!");
      } else {
        await axios.post(`${API}/admin/meetings`, form);
        toast.success("Meeting created!");
      }
      setDialogOpen(false);
      setForm({ meeting_date: "", description: "", start_time: "", end_time: "", minutes_pdf_url: "", minutes_pdf_name: "" });
      setEditingMeeting(null);
      axios.get(`${API}/admin/meetings`).then(r => setMeetings(r.data));
    } catch (err) {
      toast.error("Failed to save meeting");
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    try {
      await axios.delete(`${API}/admin/meetings/${id}`);
      setMeetings(meetings.filter(m => m.id !== id));
      toast.success("Meeting deleted");
    } catch (err) {
      toast.error("Failed to delete meeting");
    }
  };

  const editMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setForm({
      meeting_date: meeting.meeting_date || "",
      description: meeting.description || "",
      start_time: meeting.start_time || "",
      end_time: meeting.end_time || "",
      minutes_pdf_url: meeting.minutes_pdf_url || "",
      minutes_pdf_name: meeting.minutes_pdf_name || ""
    });
    setDialogOpen(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }
    
    // Convert to base64 for storage (in production, use cloud storage)
    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({
        ...prev,
        minutes_pdf_url: reader.result,
        minutes_pdf_name: file.name
      }));
      toast.success("PDF uploaded: " + file.name);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-indigo-600" />
              Admin Meetings
            </CardTitle>
            <CardDescription>Minutes Taking - Manage meeting documentation</CardDescription>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Input 
              placeholder="Search meetings..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              className="w-48 rounded-full"
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingMeeting(null); setForm({ meeting_date: "", description: "", start_time: "", end_time: "", minutes_pdf_url: "", minutes_pdf_name: "" }); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingMeeting ? "Edit Meeting" : "Add New Meeting"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Meeting Date</label>
                    <Input 
                      type="date" 
                      value={form.meeting_date} 
                      onChange={e => setForm({...form, meeting_date: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <Input 
                      placeholder="Meeting description..." 
                      value={form.description} 
                      onChange={e => setForm({...form, description: e.target.value})} 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Start Time</label>
                      <Input 
                        type="time" 
                        value={form.start_time} 
                        onChange={e => setForm({...form, start_time: e.target.value})} 
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">End Time</label>
                      <Input 
                        type="time" 
                        value={form.end_time} 
                        onChange={e => setForm({...form, end_time: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Minutes PDF</label>
                    <div className="mt-1">
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          accept=".pdf" 
                          onChange={handleFileUpload} 
                          className="hidden" 
                        />
                        <Button type="button" variant="outline" className="w-full" asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" /> 
                            {form.minutes_pdf_name || "Upload PDF"}
                          </span>
                        </Button>
                      </label>
                      {form.minutes_pdf_name && (
                        <p className="text-xs text-green-600 mt-1">✓ {form.minutes_pdf_name}</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={saveMeeting} className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingMeeting ? "Update Meeting" : "Save Meeting"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            {filteredMeetings.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No meetings recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMeetings.map(meeting => (
                  <div key={meeting.id} className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CalendarIcon className="w-4 h-4 text-indigo-600" />
                          <span className="font-semibold text-indigo-800">
                            {new Date(meeting.meeting_date).toLocaleDateString('en-GB', { 
                              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                            })}
                          </span>
                        </div>
                        <p className="text-gray-700 font-medium">{meeting.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {meeting.start_time} - {meeting.end_time}
                          </span>
                          {meeting.minutes_pdf_name && (
                            <span className="flex items-center gap-1 text-green-600">
                              <FileText className="w-3 h-3" /> {meeting.minutes_pdf_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {meeting.minutes_pdf_url && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="rounded-full"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = meeting.minutes_pdf_url;
                              link.download = meeting.minutes_pdf_name || 'meeting_minutes.pdf';
                              link.click();
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => editMeeting(meeting)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => deleteMeeting(meeting.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ADMIN ANOINTMENT RECORDS TAB ============
const AdminAnointmentTab = () => {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    name: "", current_rank: "", last_anointment_date: "",
    new_rank: "", new_anointment_date: "",
    certificate_url: "", anointment_form_url: "", notes: ""
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRecords(); }, []);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/anointment-records`);
      setRecords(res.data);
    } catch (err) {
      toast.error("Failed to load records");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      name: "", current_rank: "", last_anointment_date: "",
      new_rank: "", new_anointment_date: "",
      certificate_url: "", anointment_form_url: "", notes: ""
    });
    setEditingRecord(null);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setForm({
      name: record.name || "",
      current_rank: record.current_rank || "",
      last_anointment_date: record.last_anointment_date || "",
      new_rank: record.new_rank || "",
      new_anointment_date: record.new_anointment_date || "",
      certificate_url: record.certificate_url || "",
      anointment_form_url: record.anointment_form_url || "",
      notes: record.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editingRecord) {
        await axios.put(`${API}/admin/anointment-records/${editingRecord.id}`, form);
        toast.success("Record updated");
      } else {
        await axios.post(`${API}/admin/anointment-records`, form);
        toast.success("Record created");
      }
      setDialogOpen(false);
      resetForm();
      loadRecords();
    } catch (err) {
      toast.error("Failed to save record");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this anointment record?")) return;
    try {
      await axios.delete(`${API}/admin/anointment-records/${id}`);
      toast.success("Record deleted");
      loadRecords();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const ranks = ["Member", "Chorister", "Usher", "Shepherd", "Senior Shepherd", "Evangelist", "Senior Evangelist", "Most Senior Evangelist", "Superior Evangelist", "Woli", "Venerable"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Award className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Anointment Records</h1>
              <p className="text-white/80 text-sm">Track member ranks and anointment history</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-white text-orange-600 hover:bg-white/90 gap-2 font-semibold">
                <Plus className="w-4 h-4" /> Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-orange-600" />
                  {editingRecord ? "Edit Anointment Record" : "Add Anointment Record"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700 font-medium">Name *</Label>
                  <Input placeholder="Member's full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Current Rank</Label>
                    <Select value={form.current_rank} onValueChange={v => setForm({...form, current_rank: v})}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Select rank" /></SelectTrigger>
                      <SelectContent>
                        {ranks.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Last Anointment Date</Label>
                    <Input type="date" value={form.last_anointment_date} onChange={e => setForm({...form, last_anointment_date: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">New Anointment</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">New Rank</Label>
                      <Select value={form.new_rank} onValueChange={v => setForm({...form, new_rank: v})}>
                        <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Select new rank" /></SelectTrigger>
                        <SelectContent>
                          {ranks.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Date of New Anointment</Label>
                      <Input type="date" value={form.new_anointment_date} onChange={e => setForm({...form, new_anointment_date: e.target.value})} className="rounded-xl mt-1" />
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Documents</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-700 font-medium">Certificate URL (PDF)</Label>
                      <Input placeholder="https://drive.google.com/... or PDF link" value={form.certificate_url} onChange={e => setForm({...form, certificate_url: e.target.value})} className="rounded-xl mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Anointment Form URL (PDF)</Label>
                      <Input placeholder="https://drive.google.com/... or PDF link" value={form.anointment_form_url} onChange={e => setForm({...form, anointment_form_url: e.target.value})} className="rounded-xl mt-1" />
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Upload PDFs to Google Drive/Dropbox and paste the shareable link</p>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Notes</Label>
                  <Textarea placeholder="Additional notes..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="rounded-xl mt-1" rows={2} />
                </div>
                <Button onClick={handleSave} className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                  {editingRecord ? "Update Record" : "Save Record"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Records List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
      ) : records.length === 0 ? (
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <Award className="w-16 h-16 text-orange-300 mx-auto mb-4" />
            <p className="text-gray-500">No anointment records yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {records.map(record => (
            <Card key={record.id} className="border-0 shadow-lg overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-gray-800">{record.name}</h3>
                    <p className="text-sm text-orange-600 font-medium">{record.current_rank || "No rank"}</p>
                  </div>
                  {record.new_rank && (
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                      → {record.new_rank}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 space-y-1 mb-3">
                  {record.last_anointment_date && <p>Last Anointment: {new Date(record.last_anointment_date).toLocaleDateString()}</p>}
                  {record.new_anointment_date && <p className="text-green-600">New Anointment: {new Date(record.new_anointment_date).toLocaleDateString()}</p>}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {record.certificate_url && (
                    <a href={record.certificate_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs hover:bg-blue-100">
                      <FileText className="w-3 h-3" /> Certificate
                    </a>
                  )}
                  {record.anointment_form_url && (
                    <a href={record.anointment_form_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-600 rounded-full text-xs hover:bg-purple-100">
                      <FileText className="w-3 h-3" /> Form
                    </a>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => handleEdit(record)}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDelete(record.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ ADMIN PROJECTS TAB ============
const AdminProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", start_date: "", total_cost: "", document_url: "", status: "ongoing" });
  const [contributionForm, setContributionForm] = useState({ contributor_name: "", amount: "", date: "", notes: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contributionDialogOpen, setContributionDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/projects`);
      setProjects(res.data);
    } catch (err) {
      toast.error("Failed to load projects");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ name: "", description: "", start_date: "", total_cost: "", document_url: "", status: "ongoing" });
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setForm({
      name: project.name || "",
      description: project.description || "",
      start_date: project.start_date || "",
      total_cost: project.total_cost || "",
      document_url: project.document_url || "",
      status: project.status || "ongoing"
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Project name is required"); return; }
    try {
      const data = { ...form, total_cost: parseFloat(form.total_cost) || 0 };
      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, data);
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/admin/projects`, data);
        toast.success("Project created");
      }
      setDialogOpen(false);
      resetForm();
      loadProjects();
    } catch (err) {
      toast.error("Failed to save project");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project and all its contributions?")) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`);
      toast.success("Project deleted");
      loadProjects();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const openContributionDialog = (project) => {
    setSelectedProject(project);
    setContributionForm({ contributor_name: "", amount: "", date: new Date().toISOString().split("T")[0], notes: "" });
    setContributionDialogOpen(true);
  };

  const handleAddContribution = async () => {
    if (!contributionForm.contributor_name || !contributionForm.amount) {
      toast.error("Name and amount are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/projects/${selectedProject.id}/contributions`, {
        ...contributionForm,
        amount: parseFloat(contributionForm.amount)
      });
      toast.success("Contribution added");
      setContributionDialogOpen(false);
      loadProjects();
    } catch (err) {
      toast.error("Failed to add contribution");
    }
  };

  const handleDeleteContribution = async (projectId, contributionId) => {
    if (!window.confirm("Delete this contribution?")) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}/contributions/${contributionId}`);
      toast.success("Contribution deleted");
      loadProjects();
    } catch (err) {
      toast.error("Failed to delete contribution");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FolderOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Church Projects</h1>
              <p className="text-white/80 text-sm">Track projects and member contributions</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-white text-teal-600 hover:bg-white/90 gap-2 font-semibold">
                <Plus className="w-4 h-4" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-teal-600" />
                  {editingProject ? "Edit Project" : "Create Project"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-700 font-medium">Project Name *</Label>
                  <Input placeholder="e.g., Church Building Fund" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Total Cost (£)</Label>
                    <Input type="number" placeholder="0.00" value={form.total_cost} onChange={e => setForm({...form, total_cost: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ongoing">Ongoing</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Description</Label>
                  <Textarea placeholder="Project details..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" rows={3} />
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Label className="text-blue-800 font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Document URL (PDF)</Label>
                  <Input placeholder="https://drive.google.com/... or PDF link" value={form.document_url} onChange={e => setForm({...form, document_url: e.target.value})} className="rounded-xl mt-2 border-blue-200" />
                </div>
                <Button onClick={handleSave} className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
                  {editingProject ? "Update Project" : "Create Project"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contribution Dialog */}
      <Dialog open={contributionDialogOpen} onOpenChange={setContributionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Add Contribution to {selectedProject?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-700 font-medium">Contributor Name *</Label>
              <Input placeholder="Member name" value={contributionForm.contributor_name} onChange={e => setContributionForm({...contributionForm, contributor_name: e.target.value})} className="rounded-xl mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Amount (£) *</Label>
                <Input type="number" placeholder="0.00" value={contributionForm.amount} onChange={e => setContributionForm({...contributionForm, amount: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Date</Label>
                <Input type="date" value={contributionForm.date} onChange={e => setContributionForm({...contributionForm, date: e.target.value})} className="rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Notes</Label>
              <Input placeholder="Optional notes" value={contributionForm.notes} onChange={e => setContributionForm({...contributionForm, notes: e.target.value})} className="rounded-xl mt-1" />
            </div>
            <Button onClick={handleAddContribution} className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500">
              Add Contribution
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Projects List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
      ) : projects.length === 0 ? (
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <FolderOpen className="w-16 h-16 text-teal-300 mx-auto mb-4" />
            <p className="text-gray-500">No projects yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {projects.map(project => (
            <Card key={project.id} className="border-0 shadow-xl overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{project.name}</h3>
                    <p className="text-sm text-gray-500">{project.description}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                    project.status === "completed" ? "bg-green-100 text-green-700" :
                    project.status === "paused" ? "bg-yellow-100 text-yellow-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>
                    {project.status}
                  </span>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Contribution Progress</span>
                    <span className="font-bold text-teal-600">
                      £{(project.contribution_total || 0).toLocaleString()} / £{(project.total_cost || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, project.total_cost > 0 ? (project.contribution_total / project.total_cost) * 100 : 0)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {project.contributor_count || 0} contributor(s) • Started: {project.start_date ? new Date(project.start_date).toLocaleDateString() : "N/A"}
                  </p>
                </div>

                {/* Document Link */}
                {project.document_url && (
                  <a href={project.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium hover:bg-blue-100 mb-4">
                    <FileText className="w-3 h-3" /> View Document
                  </a>
                )}

                {/* Contributions */}
                {project.contributions && project.contributions.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" /> Contributions ({project.contributions.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {project.contributions.map(c => (
                        <div key={c.id} className="flex items-center justify-between p-2 bg-white rounded-lg text-sm">
                          <div>
                            <span className="font-medium text-gray-800">{c.contributor_name}</span>
                            <span className="text-gray-400 text-xs ml-2">{c.date ? new Date(c.date).toLocaleDateString() : ""}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-green-600">£{(c.amount || 0).toLocaleString()}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteContribution(project.id, c.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button size="sm" className="flex-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500" onClick={() => openContributionDialog(project)}>
                    <Plus className="w-3 h-3 mr-1" /> Add Contribution
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleEdit(project)}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDelete(project.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ ADMIN MONTHLY FINANCE TAB ============
const AdminMonthlyFinanceTab = () => {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear(),
    income_bank_transfer: "",
    income_cash: "",
    expenses_bank_transfer: "",
    expenses_cash: "",
    notes: ""
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlySummary, setYearlySummary] = useState(null);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Available years from December 2025 onwards
  const availableYears = [2025, 2026, 2027, 2028, 2029, 2030];

  useEffect(() => { 
    loadRecords();
    loadYearlySummary();
  }, [selectedYear]);

  const loadRecords = async () => {
    try {
      const res = await axios.get(`${API}/admin/monthly-finance?year=${selectedYear}`);
      setRecords(res.data);
    } catch (err) {
      console.error("Failed to load records");
    }
  };

  const loadYearlySummary = async () => {
    try {
      const res = await axios.get(`${API}/admin/monthly-finance/summary/${selectedYear}`);
      setYearlySummary(res.data);
    } catch (err) {
      console.error("Failed to load summary");
    }
  };

  const saveRecord = async () => {
    try {
      const submitData = {
        ...form,
        income_bank_transfer: parseFloat(form.income_bank_transfer) || 0,
        income_cash: parseFloat(form.income_cash) || 0,
        expenses_bank_transfer: parseFloat(form.expenses_bank_transfer) || 0,
        expenses_cash: parseFloat(form.expenses_cash) || 0,
        expenses: 0  // Legacy field - set to 0 as we now use split fields
      };
      await axios.post(`${API}/admin/monthly-finance`, submitData);
      toast.success("Finance record saved!");
      setDialogOpen(false);
      setForm({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), income_bank_transfer: "", income_cash: "", expenses_bank_transfer: "", expenses_cash: "", notes: "" });
      setEditingRecord(null);
      loadRecords();
      loadYearlySummary();
    } catch (err) {
      toast.error("Failed to save record");
    }
  };

  const deleteRecord = async (year, month) => {
    if (!window.confirm(`Delete ${months[month - 1]} ${year} record?`)) return;
    try {
      await axios.delete(`${API}/admin/monthly-finance/${year}/${month}`);
      loadRecords();
      loadYearlySummary();
      toast.success("Record deleted");
    } catch (err) {
      toast.error("Failed to delete record");
    }
  };

  const editRecord = (record) => {
    setEditingRecord(record);
    setForm({
      month: record.month,
      year: record.year,
      income_bank_transfer: record.income_bank_transfer?.toString() || "",
      income_cash: record.income_cash?.toString() || "",
      expenses_bank_transfer: record.expenses_bank_transfer?.toString() || "",
      expenses_cash: record.expenses_cash?.toString() || "",
      notes: record.notes || ""
    });
    setDialogOpen(true);
  };

  const totalIncome = parseFloat(form.income_bank_transfer || 0) + parseFloat(form.income_cash || 0);
  const totalExpenses = parseFloat(form.expenses_bank_transfer || 0) + parseFloat(form.expenses_cash || 0);
  const profitLoss = totalIncome - totalExpenses;

  const exportToExcel = async () => {
    if (records.length === 0) {
      toast.error("No records to export");
      return;
    }

    try {
      const XLSX = await import('xlsx');

      // Prepare data for Excel
      const exportData = records.map(record => ({
        'Month': months[record.month - 1],
        'Year': record.year,
        'Income - Bank Transfer (£)': record.income_bank_transfer?.toFixed(2) || '0.00',
        'Income - Cash (£)': record.income_cash?.toFixed(2) || '0.00',
        'Total Income (£)': record.total_income?.toFixed(2) || '0.00',
        'Expenses - Bank Payment (£)': record.expenses_bank_transfer?.toFixed(2) || '0.00',
        'Expenses - Cash Payment (£)': record.expenses_cash?.toFixed(2) || '0.00',
        'Total Expenses (£)': record.total_expenses?.toFixed(2) || '0.00',
        'Profit/Loss (£)': record.profit_loss?.toFixed(2) || '0.00',
        'Status': record.profit_loss >= 0 ? 'Profit' : 'Loss',
        'Notes': record.notes || ''
      }));

      // Add summary row
      const totalIncomeBankTransfer = records.reduce((sum, r) => sum + (r.income_bank_transfer || 0), 0);
      const totalIncomeCash = records.reduce((sum, r) => sum + (r.income_cash || 0), 0);
      const totalIncomeSum = records.reduce((sum, r) => sum + (r.total_income || 0), 0);
      const totalExpensesBankTransfer = records.reduce((sum, r) => sum + (r.expenses_bank_transfer || 0), 0);
      const totalExpensesCash = records.reduce((sum, r) => sum + (r.expenses_cash || 0), 0);
      const totalExpensesSum = records.reduce((sum, r) => sum + (r.total_expenses || 0), 0);
      const totalProfitLoss = records.reduce((sum, r) => sum + (r.profit_loss || 0), 0);

      exportData.push({
        'Month': 'TOTAL',
        'Year': selectedYear,
        'Income - Bank Transfer (£)': totalIncomeBankTransfer.toFixed(2),
        'Income - Cash (£)': totalIncomeCash.toFixed(2),
        'Total Income (£)': totalIncomeSum.toFixed(2),
        'Expenses - Bank Payment (£)': totalExpensesBankTransfer.toFixed(2),
        'Expenses - Cash Payment (£)': totalExpensesCash.toFixed(2),
        'Total Expenses (£)': totalExpensesSum.toFixed(2),
        'Profit/Loss (£)': totalProfitLoss.toFixed(2),
        'Status': totalProfitLoss >= 0 ? 'Net Profit' : 'Net Loss',
        'Notes': ''
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 12 }, // Month
        { wch: 8 },  // Year
        { wch: 22 }, // Income Bank Transfer
        { wch: 18 }, // Income Cash
        { wch: 16 }, // Total Income
        { wch: 24 }, // Expenses Bank Payment
        { wch: 22 }, // Expenses Cash Payment
        { wch: 18 }, // Total Expenses
        { wch: 16 }, // Profit/Loss
        { wch: 10 }, // Status
        { wch: 30 }  // Notes
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `Finance_${selectedYear}`);

      // Generate filename
      const today = new Date().toISOString().split('T')[0];
      const filename = `CCC_Medway_Monthly_Finance_${selectedYear}_${today}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${records.length} records to Excel`);
    } catch (err) {
      toast.error("Failed to export to Excel");
      console.error(err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Yearly Summary Card */}
      {yearlySummary && (
        <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="text-lg text-green-800">📊 {selectedYear} Yearly Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Income (Bank)</p>
                <p className="text-lg font-bold text-blue-600">£{yearlySummary.total_income_bank_transfer?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Income (Cash)</p>
                <p className="text-lg font-bold text-blue-500">£{yearlySummary.total_income_cash?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Total Income</p>
                <p className="text-lg font-bold text-emerald-600">£{yearlySummary.total_income?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Expenses (Bank)</p>
                <p className="text-lg font-bold text-red-600">£{yearlySummary.total_expenses_bank_transfer?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Expenses (Cash)</p>
                <p className="text-lg font-bold text-red-500">£{yearlySummary.total_expenses_cash?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl shadow-sm">
                <p className="text-xs text-gray-500">Total Expenses</p>
                <p className="text-lg font-bold text-red-700">£{yearlySummary.total_expenses?.toFixed(2) || '0.00'}</p>
              </div>
              <div className={`text-center p-3 rounded-xl shadow-sm ${yearlySummary.net_profit_loss >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <p className="text-xs text-gray-500">Net {yearlySummary.net_profit_loss >= 0 ? 'Profit' : 'Loss'}</p>
                <p className={`text-lg font-bold ${yearlySummary.net_profit_loss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  £{Math.abs(yearlySummary.net_profit_loss || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Monthly Income & Expenditure
            </CardTitle>
            <CardDescription>Track monthly financial activity</CardDescription>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32 rounded-full">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
              onClick={exportToExcel}
              disabled={records.length === 0}
            >
              <Download className="w-4 h-4 mr-2" /> Export Excel
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingRecord(null); setForm({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), income_bank_transfer: "", income_cash: "", expenses: "", notes: "" }); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Record
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingRecord ? "Edit Record" : "Add Monthly Record"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Month</label>
                      <Select value={form.month.toString()} onValueChange={(v) => setForm({...form, month: parseInt(v)})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select month" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((m, i) => (
                            <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Year</label>
                      <Select value={form.year.toString()} onValueChange={(v) => setForm({...form, year: parseInt(v)})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableYears.map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-semibold text-blue-800 mb-2">💰 Income</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600">Bank Transfer (£)</label>
                        <Input 
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={form.income_bank_transfer} 
                          onChange={e => setForm({...form, income_bank_transfer: e.target.value})} 
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Cash (£)</label>
                        <Input 
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={form.income_cash} 
                          onChange={e => setForm({...form, income_cash: e.target.value})} 
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-semibold text-red-800 mb-2">📉 Expenses</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600">Bank Payment (£)</label>
                        <Input 
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={form.expenses_bank_transfer} 
                          onChange={e => setForm({...form, expenses_bank_transfer: e.target.value})} 
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Cash Payment (£)</label>
                        <Input 
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={form.expenses_cash} 
                          onChange={e => setForm({...form, expenses_cash: e.target.value})} 
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className={`p-3 rounded-lg ${profitLoss >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                    <p className="text-sm font-semibold mb-1">{profitLoss >= 0 ? '📈 Profit' : '📉 Loss'}</p>
                    <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      £{Math.abs(profitLoss).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Total Income: £{totalIncome.toFixed(2)} | Total Expenses: £{totalExpenses.toFixed(2)}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Notes (Optional)</label>
                    <Input 
                      placeholder="Additional notes..." 
                      value={form.notes} 
                      onChange={e => setForm({...form, notes: e.target.value})} 
                    />
                  </div>
                  
                  <Button onClick={saveRecord} className="w-full bg-green-600 hover:bg-green-700">
                    {editingRecord ? "Update Record" : "Save Record"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {records.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No records for {selectedYear}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {records.map(record => (
                  <div key={`${record.year}-${record.month}`} className="p-4 bg-gradient-to-r from-gray-50 to-green-50 rounded-xl border border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">{months[record.month - 1]} {record.year}</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-2">
                          <div>
                            <p className="text-xs text-gray-500">Income (Bank)</p>
                            <p className="font-semibold text-blue-600">£{record.income_bank_transfer?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Income (Cash)</p>
                            <p className="font-semibold text-blue-500">£{record.income_cash?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Expenses (Bank)</p>
                            <p className="font-semibold text-red-600">£{record.expenses_bank_transfer?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Expenses (Cash)</p>
                            <p className="font-semibold text-red-500">£{record.expenses_cash?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Total Expenses</p>
                            <p className="font-semibold text-red-700">£{record.total_expenses?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{record.profit_loss >= 0 ? 'Profit' : 'Loss'}</p>
                            <p className={`font-semibold ${record.profit_loss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              £{Math.abs(record.profit_loss || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {record.notes && <p className="text-xs text-gray-500 mt-2">📝 {record.notes}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => editRecord(record)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => deleteRecord(record.year, record.month)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Yearly Totals Summary */}
                {records.length > 0 && (
                  <div className="p-5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl border-2 border-indigo-300 shadow-lg mt-4">
                    <h4 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5" /> {selectedYear} Yearly Totals
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xs text-blue-100 font-medium">Income (Bank)</p>
                        <p className="font-bold text-white text-lg">
                          £{records.reduce((sum, r) => sum + (r.income_bank_transfer || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xs text-blue-100 font-medium">Income (Cash)</p>
                        <p className="font-bold text-white text-lg">
                          £{records.reduce((sum, r) => sum + (r.income_cash || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xs text-red-200 font-medium">Expenses (Bank)</p>
                        <p className="font-bold text-white text-lg">
                          £{records.reduce((sum, r) => sum + (r.expenses_bank_transfer || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xs text-red-200 font-medium">Expenses (Cash)</p>
                        <p className="font-bold text-white text-lg">
                          £{records.reduce((sum, r) => sum + (r.expenses_cash || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-3">
                        <p className="text-xs text-red-200 font-medium">Total Expenses</p>
                        <p className="font-bold text-white text-lg">
                          £{records.reduce((sum, r) => sum + (r.total_expenses || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white/30 rounded-lg p-3">
                        <p className="text-xs text-yellow-200 font-medium">
                          {records.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'Total Profit' : 'Total Loss'}
                        </p>
                        <p className={`font-bold text-lg ${records.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          £{Math.abs(records.reduce((sum, r) => sum + (r.profit_loss || 0), 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/20 text-center">
                      <p className="text-white/80 text-sm">
                        Total Income: <span className="font-bold text-green-300">£{records.reduce((sum, r) => sum + (r.income_bank_transfer || 0) + (r.income_cash || 0), 0).toFixed(2)}</span>
                        {" | "}
                        Total Expenses: <span className="font-bold text-red-300">£{records.reduce((sum, r) => sum + (r.total_expenses || 0), 0).toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminSundaySchoolTab = () => {
  const { user } = useAuth();
  const isMainAdmin = user?.role === "admin";
  const [activeSection, setActiveSection] = useState("students");
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ full_name: "", age: "", date_of_birth: "", mothers_name: "", parent_telephone: "", description: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  
  // Officiating Ministers state
  const [ministers, setMinisters] = useState([]);
  const [ministerForm, setMinisterForm] = useState({ child_name: "", role: "", scripture: "", scripture_file_url: "", parent_name: "", parent_contact: "" });
  const [ministerDialogOpen, setMinisterDialogOpen] = useState(false);
  const [editingMinister, setEditingMinister] = useState(null);
  const [uploadingScripture, setUploadingScripture] = useState(false);
  
  // Teachers state
  const [teachers, setTeachers] = useState([]);
  const [teacherForm, setTeacherForm] = useState({ name: "", role: "", telephone: "", email: "" });
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docForm, setDocForm] = useState({ name: "", date_created: "", file_url: "" });
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/sunday-school`).then(r => setStudents(r.data)).catch(() => {});
    axios.get(`${API}/admin/sunday-school/officiating-ministers`).then(r => setMinisters(r.data)).catch(() => {});
    axios.get(`${API}/admin/sunday-school/teachers`).then(r => setTeachers(r.data)).catch(() => {});
    axios.get(`${API}/admin/sunday-school/documents`).then(r => setDocuments(r.data)).catch(() => {});
  }, []);

  const filteredStudents = students.filter(s =>
    (s.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.mothers_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const save = async () => {
    if (!form.full_name || !form.age) {
      toast.error("Please enter Full Name and Age");
      return;
    }
    try {
      const payload = { ...form, age: parseInt(form.age) };
      if (editingId) {
        await axios.put(`${API}/admin/sunday-school/${editingId}`, payload);
        toast.success("Student updated");
      } else {
        await axios.post(`${API}/admin/sunday-school`, payload);
        toast.success("Student added");
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ full_name: "", age: "", date_of_birth: "", mothers_name: "", parent_telephone: "", description: "" });
      axios.get(`${API}/admin/sunday-school`).then(r => setStudents(r.data));
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const editStudent = (student) => {
    setForm({
      full_name: student.full_name || "",
      age: student.age?.toString() || "",
      date_of_birth: student.date_of_birth || "",
      mothers_name: student.mothers_name || "",
      parent_telephone: student.parent_telephone || "",
      description: student.description || ""
    });
    setEditingId(student.id);
    setDialogOpen(true);
  };

  const deleteStudent = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(`${API}/admin/sunday-school/${id}`);
      toast.success("Student deleted");
      setStudents(students.filter(s => s.id !== id));
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const updateHighlightColor = async (studentId, color) => {
    try {
      await axios.put(`${API}/admin/sunday-school/${studentId}/highlight`, { highlight_color: color });
      setStudents(students.map(s => s.id === studentId ? { ...s, highlight_color: color } : s));
      toast.success(color ? "Highlight applied" : "Highlight removed");
    } catch (err) {
      toast.error("Failed to update highlight");
    }
  };

  // Available highlight colors
  const highlightColors = [
    { name: "None", value: "", bg: "bg-white", border: "border-gray-300" },
    { name: "Yellow", value: "yellow", bg: "bg-yellow-100", border: "border-yellow-400" },
    { name: "Green", value: "green", bg: "bg-green-100", border: "border-green-400" },
    { name: "Blue", value: "blue", bg: "bg-blue-100", border: "border-blue-400" },
    { name: "Pink", value: "pink", bg: "bg-pink-100", border: "border-pink-400" },
    { name: "Orange", value: "orange", bg: "bg-orange-100", border: "border-orange-400" },
    { name: "Purple", value: "purple", bg: "bg-purple-100", border: "border-purple-400" }
  ];

  const getHighlightClass = (color) => {
    const colorMap = {
      "yellow": "bg-yellow-100",
      "green": "bg-green-100",
      "blue": "bg-blue-100",
      "pink": "bg-pink-100",
      "orange": "bg-orange-100",
      "purple": "bg-purple-100"
    };
    return colorMap[color] || "";
  };

  const openAddDialog = () => {
    setEditingId(null);
    setForm({ full_name: "", age: "", date_of_birth: "", mothers_name: "", parent_telephone: "", description: "" });
    setDialogOpen(true);
  };

  const syncParentPhones = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/sunday-school/sync-parent-phones`);
      toast.success(res.data.message);
      axios.get(`${API}/admin/sunday-school`).then(r => setStudents(r.data));
    } catch (err) {
      toast.error("Failed to sync phone numbers");
    }
    setSyncing(false);
  };

  const lookupParentPhone = async () => {
    if (!form.mothers_name) {
      toast.error("Please enter mother's name first");
      return;
    }
    setLookingUp(true);
    try {
      const res = await axios.get(`${API}/admin/sunday-school/lookup-phone/${encodeURIComponent(form.mothers_name)}`);
      if (res.data.found) {
        setForm({ ...form, parent_telephone: res.data.phone });
        toast.success(`Found phone number for ${res.data.name}`);
      } else {
        toast.info("No matching member found in Members List");
      }
    } catch (err) {
      toast.error("Failed to lookup");
    }
    setLookingUp(false);
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/sunday-school/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sunday_school_students_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel file downloaded!");
    } catch (err) {
      toast.error("Failed to export");
    }
    setExporting(false);
  };

  // Officiating Ministers handlers
  const saveMinister = async () => {
    if (!ministerForm.child_name || !ministerForm.role) {
      toast.error("Please enter Child Name and Role");
      return;
    }
    try {
      if (editingMinister) {
        await axios.put(`${API}/admin/sunday-school/officiating-ministers/${editingMinister}`, ministerForm);
        toast.success("Minister updated");
      } else {
        await axios.post(`${API}/admin/sunday-school/officiating-ministers`, ministerForm);
        toast.success("Minister added");
      }
      setMinisterDialogOpen(false);
      setEditingMinister(null);
      setMinisterForm({ child_name: "", role: "", scripture: "", scripture_file_url: "", parent_name: "", parent_contact: "" });
      axios.get(`${API}/admin/sunday-school/officiating-ministers`).then(r => setMinisters(r.data));
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const deleteMinister = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API}/admin/sunday-school/officiating-ministers/${id}`);
      toast.success("Deleted");
      setMinisters(ministers.filter(m => m.id !== id));
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleScriptureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingScripture(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData);
      setMinisterForm({ ...ministerForm, scripture_file_url: res.data.url });
      toast.success("File uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploadingScripture(false);
  };

  // Teachers handlers
  const saveTeacher = async () => {
    if (!teacherForm.name) {
      toast.error("Please enter teacher name");
      return;
    }
    try {
      if (editingTeacher) {
        await axios.put(`${API}/admin/sunday-school/teachers/${editingTeacher}`, teacherForm);
        toast.success("Teacher updated");
      } else {
        await axios.post(`${API}/admin/sunday-school/teachers`, teacherForm);
        toast.success("Teacher added");
      }
      setTeacherDialogOpen(false);
      setEditingTeacher(null);
      setTeacherForm({ name: "", role: "", telephone: "", email: "" });
      axios.get(`${API}/admin/sunday-school/teachers`).then(r => setTeachers(r.data));
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const deleteTeacher = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API}/admin/sunday-school/teachers/${id}`);
      toast.success("Deleted");
      setTeachers(teachers.filter(t => t.id !== id));
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Documents handlers
  const saveDocument = async () => {
    if (!docForm.name || !docForm.file_url) {
      toast.error("Please enter document name and upload file");
      return;
    }
    try {
      if (editingDoc) {
        await axios.put(`${API}/admin/sunday-school/documents/${editingDoc}`, docForm);
        toast.success("Document updated");
      } else {
        await axios.post(`${API}/admin/sunday-school/documents`, docForm);
        toast.success("Document added");
      }
      setDocDialogOpen(false);
      setEditingDoc(null);
      setDocForm({ name: "", date_created: "", file_url: "" });
      axios.get(`${API}/admin/sunday-school/documents`).then(r => setDocuments(r.data));
    } catch (err) {
      toast.error("Failed to save");
    }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API}/admin/sunday-school/documents/${id}`);
      toast.success("Deleted");
      setDocuments(documents.filter(d => d.id !== id));
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData);
      setDocForm({ ...docForm, file_url: res.data.url });
      toast.success("File uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploadingDoc(false);
  };

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 bg-amber-50 p-2 rounded-xl">
        {[
          { key: "students", label: "Students", icon: Users },
          { key: "ministers", label: "Officiating Ministers", icon: Award },
          { key: "teachers", label: "Teacher Directory", icon: BookOpen },
          { key: "documents", label: "Documents", icon: FileText }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={activeSection === tab.key ? "default" : "ghost"}
            className={`rounded-full ${activeSection === tab.key ? "bg-amber-600 hover:bg-amber-700" : ""}`}
            onClick={() => setActiveSection(tab.key)}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Students Section */}
      {activeSection === "students" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Sunday School Students ({students.length})
              </CardTitle>
              <CardDescription>Manage Sunday School student records</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" className="rounded-full text-green-600 hover:bg-green-50" onClick={syncParentPhones} disabled={syncing}>
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync Contacts from Members
              </Button>
              <Button variant="outline" className="rounded-full" onClick={exportToExcel} disabled={exporting}>
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export Excel
              </Button>
              <Button className="rounded-full bg-amber-600 hover:bg-amber-700" onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Student
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Search by name or mother's name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="rounded-xl mb-4"
            />
            
            {filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-amber-500" />
                </div>
                <p className="text-gray-500 font-medium">No students found</p>
                <p className="text-sm text-gray-400 mt-1">Click "Add Student" to add your first record</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-amber-50 to-orange-50">
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">S/N</th>
                      <th className="text-center p-3 font-semibold text-gray-700 border-b w-16">Mark</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Full Name</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Age</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Date of Birth</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Mother's Name</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Parent Contact</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Description</th>
                      <th className="text-center p-3 font-semibold text-gray-700 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, idx) => (
                      <tr key={student.id} className={`hover:bg-amber-50/50 transition-colors ${getHighlightClass(student.highlight_color)}`}>
                        <td className="p-3 border-b text-gray-600">{idx + 1}</td>
                        <td className="p-3 border-b">
                          <div className="relative group">
                            <button
                              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all hover:scale-110 ${
                                student.highlight_color 
                                  ? highlightColors.find(c => c.value === student.highlight_color)?.bg || 'bg-white'
                                  : 'bg-white'
                              } ${
                                student.highlight_color 
                                  ? highlightColors.find(c => c.value === student.highlight_color)?.border || 'border-gray-300'
                                  : 'border-gray-300'
                              }`}
                              title="Click to change highlight color"
                            >
                              {student.highlight_color ? (
                                <div className={`w-4 h-4 rounded-full ${highlightColors.find(c => c.value === student.highlight_color)?.bg?.replace('100', '400') || 'bg-gray-400'}`} />
                              ) : (
                                <span className="text-gray-400 text-xs">●</span>
                              )}
                            </button>
                            {/* Color picker dropdown */}
                            <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border p-2 z-50 hidden group-hover:flex flex-wrap gap-1 w-32">
                              {highlightColors.map(color => (
                                <button
                                  key={color.value}
                                  onClick={() => updateHighlightColor(student.id, color.value)}
                                  className={`w-7 h-7 rounded-full border-2 ${color.bg} ${color.border} hover:scale-110 transition-transform flex items-center justify-center`}
                                  title={color.name}
                                >
                                  {student.highlight_color === color.value && (
                                    <Check className="w-3 h-3 text-gray-700" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 border-b font-medium text-gray-800">{student.full_name}</td>
                        <td className="p-3 border-b text-gray-600">{student.age}</td>
                        <td className="p-3 border-b text-gray-600">{student.date_of_birth || "-"}</td>
                        <td className="p-3 border-b text-gray-600">{student.mothers_name || "-"}</td>
                        <td className="p-3 border-b text-gray-600">
                          {student.parent_telephone ? (
                            <a href={`tel:${student.parent_telephone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {student.parent_telephone}
                            </a>
                          ) : "-"}
                        </td>
                        <td className="p-3 border-b text-gray-600 max-w-[200px] truncate">{student.description || "-"}</td>
                        <td className="p-3 border-b">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => editStudent(student)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isMainAdmin && (
                              <Button size="sm" variant="outline" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deleteStudent(student.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Officiating Ministers Section */}
      {activeSection === "ministers" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-600" />
                Officiating Ministers – Juvenile Harvest ({ministers.length})
              </CardTitle>
              <CardDescription>Manage children assigned to minister during Juvenile Harvest</CardDescription>
            </div>
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700" onClick={() => { setEditingMinister(null); setMinisterForm({ child_name: "", role: "", scripture: "", scripture_file_url: "", parent_name: "", parent_contact: "" }); setMinisterDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Minister
            </Button>
          </CardHeader>
          <CardContent>
            {ministers.length === 0 ? (
              <div className="text-center py-12">
                <Award className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                <p className="text-gray-500">No officiating ministers yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-purple-50 to-pink-50">
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">S/N</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Name of Child</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Role</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Scripture</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Parent Name & Contact</th>
                      <th className="text-center p-3 font-semibold text-gray-700 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ministers.map((m, idx) => (
                      <tr key={m.id} className="hover:bg-purple-50/50 transition-colors">
                        <td className="p-3 border-b text-gray-600">{idx + 1}</td>
                        <td className="p-3 border-b font-medium text-gray-800">{m.child_name}</td>
                        <td className="p-3 border-b text-gray-600">{m.role}</td>
                        <td className="p-3 border-b text-gray-600">
                          {m.scripture && <span className="block text-sm">{m.scripture}</span>}
                          {m.scripture_file_url && (
                            <a href={m.scripture_file_url.startsWith('/api') ? `${BACKEND_URL}${m.scripture_file_url}` : m.scripture_file_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 text-xs hover:underline flex items-center gap-1">
                              <Download className="w-3 h-3" /> View File
                            </a>
                          )}
                        </td>
                        <td className="p-3 border-b text-gray-600">
                          <span className="block font-medium">{m.parent_name || "-"}</span>
                          <span className="text-xs text-gray-400">{m.parent_contact}</span>
                        </td>
                        <td className="p-3 border-b">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setMinisterForm({ child_name: m.child_name, role: m.role, scripture: m.scripture || "", scripture_file_url: m.scripture_file_url || "", parent_name: m.parent_name || "", parent_contact: m.parent_contact || "" }); setEditingMinister(m.id); setMinisterDialogOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isMainAdmin && (
                              <Button size="sm" variant="outline" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deleteMinister(m.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Teachers Section */}
      {activeSection === "teachers" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Sunday School Teacher Directory ({teachers.length})
              </CardTitle>
              <CardDescription>Manage Sunday School teacher contacts</CardDescription>
            </div>
            <Button className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => { setEditingTeacher(null); setTeacherForm({ name: "", role: "", telephone: "", email: "" }); setTeacherDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Teacher
            </Button>
          </CardHeader>
          <CardContent>
            {teachers.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-blue-300 mx-auto mb-4" />
                <p className="text-gray-500">No teachers added yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50 to-cyan-50">
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">S/N</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Name</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Role</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Telephone</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Email Address</th>
                      <th className="text-center p-3 font-semibold text-gray-700 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((t, idx) => (
                      <tr key={t.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="p-3 border-b text-gray-600">{idx + 1}</td>
                        <td className="p-3 border-b font-medium text-gray-800">{t.name}</td>
                        <td className="p-3 border-b text-gray-600">{t.role || "-"}</td>
                        <td className="p-3 border-b text-gray-600">{t.telephone || "-"}</td>
                        <td className="p-3 border-b text-gray-600">{t.email || "-"}</td>
                        <td className="p-3 border-b">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setTeacherForm({ name: t.name, role: t.role || "", telephone: t.telephone || "", email: t.email || "" }); setEditingTeacher(t.id); setTeacherDialogOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isMainAdmin && (
                              <Button size="sm" variant="outline" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deleteTeacher(t.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Section */}
      {activeSection === "documents" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-green-500 to-emerald-500" />
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Document Uploads ({documents.length})
              </CardTitle>
              <CardDescription>Manage Sunday School documents and files</CardDescription>
            </div>
            <Button className="rounded-full bg-green-600 hover:bg-green-700" onClick={() => { setEditingDoc(null); setDocForm({ name: "", date_created: new Date().toISOString().split('T')[0], file_url: "" }); setDocDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500">No documents uploaded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-green-50 to-emerald-50">
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">S/N</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Name of Document</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">Date Created</th>
                      <th className="text-left p-3 font-semibold text-gray-700 border-b">File</th>
                      <th className="text-center p-3 font-semibold text-gray-700 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((d, idx) => (
                      <tr key={d.id} className="hover:bg-green-50/50 transition-colors">
                        <td className="p-3 border-b text-gray-600">{idx + 1}</td>
                        <td className="p-3 border-b font-medium text-gray-800">{d.name}</td>
                        <td className="p-3 border-b text-gray-600">{d.date_created || "-"}</td>
                        <td className="p-3 border-b">
                          {d.file_url && (
                            <a href={d.file_url.startsWith('/api') ? `${BACKEND_URL}${d.file_url}` : d.file_url} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center gap-1">
                              <Download className="w-4 h-4" /> Download
                            </a>
                          )}
                        </td>
                        <td className="p-3 border-b">
                          <div className="flex justify-center gap-2">
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setDocForm({ name: d.name, date_created: d.date_created || "", file_url: d.file_url || "" }); setEditingDoc(d.id); setDocDialogOpen(true); }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isMainAdmin && (
                              <Button size="sm" variant="outline" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deleteDocument(d.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Student Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Student" : "Add New Student"}</DialogTitle>
            <DialogDescription>Enter the student's information below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Full Name *</Label>
              <Input
                placeholder="Enter full name"
                value={form.full_name}
                onChange={e => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Age *</Label>
                <Input
                  type="number"
                  placeholder="e.g., 8"
                  value={form.age}
                  onChange={e => setForm({ ...form, age: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Date of Birth</Label>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Mother's Name</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter mother's name"
                  value={form.mothers_name}
                  onChange={e => setForm({ ...form, mothers_name: e.target.value })}
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={lookupParentPhone} disabled={lookingUp} title="Lookup phone from Members List">
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Parent Contact</Label>
              <Input
                placeholder="Phone number"
                value={form.parent_telephone}
                onChange={e => setForm({ ...form, parent_telephone: e.target.value })}
              />
              <p className="text-xs text-gray-400 mt-1">Click the refresh button above to auto-fill from Members List</p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Description</Label>
              <Textarea
                placeholder="Any additional notes about the student..."
                rows={3}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={save} className="flex-1 bg-amber-600 hover:bg-amber-700">
                {editingId ? "Update Student" : "Add Student"}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Minister Dialog */}
      <Dialog open={ministerDialogOpen} onOpenChange={(open) => { setMinisterDialogOpen(open); if (!open) setEditingMinister(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMinister ? "Edit Minister" : "Add Officiating Minister"}</DialogTitle>
            <DialogDescription>Enter the child's ministering details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Name of Child *</Label>
                <Input
                  placeholder="Enter child's name"
                  value={ministerForm.child_name}
                  onChange={e => setMinisterForm({ ...ministerForm, child_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Role *</Label>
                <Input
                  placeholder="e.g., Bible Reader, Song Leader"
                  value={ministerForm.role}
                  onChange={e => setMinisterForm({ ...ministerForm, role: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Scripture Reference</Label>
              <Input
                placeholder="e.g., John 3:16"
                value={ministerForm.scripture}
                onChange={e => setMinisterForm({ ...ministerForm, scripture: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Upload Scripture Copy</Label>
              {ministerForm.scripture_file_url ? (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-purple-700 flex-1">File uploaded</span>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setMinisterForm({ ...ministerForm, scripture_file_url: "" })}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <input type="file" id="scripture-upload" className="hidden" accept="image/*,.pdf" onChange={handleScriptureUpload} />
                  <Button variant="outline" className="w-full" onClick={() => document.getElementById('scripture-upload')?.click()} disabled={uploadingScripture}>
                    {uploadingScripture ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Choose File
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Parent Name</Label>
                <Input
                  placeholder="Enter parent's name"
                  value={ministerForm.parent_name}
                  onChange={e => setMinisterForm({ ...ministerForm, parent_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Parent Contact</Label>
                <Input
                  placeholder="Phone number"
                  value={ministerForm.parent_contact}
                  onChange={e => setMinisterForm({ ...ministerForm, parent_contact: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={saveMinister} className="flex-1 bg-purple-600 hover:bg-purple-700">
                {editingMinister ? "Update" : "Add Minister"}
              </Button>
              <Button variant="outline" onClick={() => setMinisterDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Teacher Dialog */}
      <Dialog open={teacherDialogOpen} onOpenChange={(open) => { setTeacherDialogOpen(open); if (!open) setEditingTeacher(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTeacher ? "Edit Teacher" : "Add Teacher"}</DialogTitle>
            <DialogDescription>Enter teacher's contact information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Name *</Label>
                <Input
                  placeholder="Enter name"
                  value={teacherForm.name}
                  onChange={e => setTeacherForm({ ...teacherForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Role</Label>
                <Input
                  placeholder="e.g., Head Teacher, Assistant"
                  value={teacherForm.role}
                  onChange={e => setTeacherForm({ ...teacherForm, role: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Telephone</Label>
                <Input
                  placeholder="Phone number"
                  value={teacherForm.telephone}
                  onChange={e => setTeacherForm({ ...teacherForm, telephone: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Email Address</Label>
                <Input
                  type="email"
                  placeholder="Email"
                  value={teacherForm.email}
                  onChange={e => setTeacherForm({ ...teacherForm, email: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={saveTeacher} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {editingTeacher ? "Update" : "Add Teacher"}
              </Button>
              <Button variant="outline" onClick={() => setTeacherDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Document Dialog */}
      <Dialog open={docDialogOpen} onOpenChange={(open) => { setDocDialogOpen(open); if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDoc ? "Edit Document" : "Upload Document"}</DialogTitle>
            <DialogDescription>Upload a new document to the Sunday School records</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Name of Document *</Label>
              <Input
                placeholder="Enter document name"
                value={docForm.name}
                onChange={e => setDocForm({ ...docForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Date Created</Label>
              <Input
                type="date"
                value={docForm.date_created}
                onChange={e => setDocForm({ ...docForm, date_created: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Upload File *</Label>
              {docForm.file_url ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700 flex-1">File uploaded</span>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDocForm({ ...docForm, file_url: "" })}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <input type="file" id="doc-upload" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleDocUpload} />
                  <Button variant="outline" className="w-full" onClick={() => document.getElementById('doc-upload')?.click()} disabled={uploadingDoc}>
                    {uploadingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Choose File
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={saveDocument} className="flex-1 bg-green-600 hover:bg-green-700">
                {editingDoc ? "Update" : "Upload Document"}
              </Button>
              <Button variant="outline" onClick={() => setDocDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminCharityTab = () => {
  const [trustees, setTrustees] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [trusteeForm, setTrusteeForm] = useState({ name: "", role: "" });
  const [meetingForm, setMeetingForm] = useState({ meeting_date: "", meeting_time: "", notes: "" });
  const [editingTrustee, setEditingTrustee] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [trusteeDialogOpen, setTrusteeDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState("trustees");
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [newDocName, setNewDocName] = useState("");

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [trusteesRes, meetingsRes, docsRes] = await Promise.all([
        axios.get(`${API}/admin/charity/trustees`),
        axios.get(`${API}/admin/charity/meetings`),
        axios.get(`${API}/admin/charity/documents`)
      ]);
      setTrustees(trusteesRes.data);
      setMeetings(meetingsRes.data);
      setDocuments(docsRes.data);
    } catch (err) {
      console.error("Failed to fetch charity data", err);
    }
  };

  // Trustees functions
  const saveTrustee = async () => {
    if (!trusteeForm.name) {
      toast.error("Please enter trustee name");
      return;
    }
    try {
      if (editingTrustee) {
        await axios.put(`${API}/admin/charity/trustees/${editingTrustee}`, trusteeForm);
        toast.success("Trustee updated");
      } else {
        await axios.post(`${API}/admin/charity/trustees`, trusteeForm);
        toast.success("Trustee added");
      }
      setTrusteeDialogOpen(false);
      setTrusteeForm({ name: "", role: "" });
      setEditingTrustee(null);
      fetchAllData();
    } catch (err) {
      toast.error("Failed to save trustee");
    }
  };

  const deleteTrustee = async (id) => {
    if (!window.confirm("Are you sure you want to remove this trustee?")) return;
    try {
      await axios.delete(`${API}/admin/charity/trustees/${id}`);
      toast.success("Trustee removed");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to remove trustee");
    }
  };

  // Meetings functions
  const saveMeeting = async () => {
    if (!meetingForm.meeting_date || !meetingForm.meeting_time) {
      toast.error("Please enter meeting date and time");
      return;
    }
    try {
      if (editingMeeting) {
        await axios.put(`${API}/admin/charity/meetings/${editingMeeting}`, meetingForm);
        toast.success("Meeting updated");
      } else {
        await axios.post(`${API}/admin/charity/meetings`, meetingForm);
        toast.success("Meeting added");
      }
      setMeetingDialogOpen(false);
      setMeetingForm({ meeting_date: "", meeting_time: "", notes: "" });
      setEditingMeeting(null);
      fetchAllData();
    } catch (err) {
      toast.error("Failed to save meeting");
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm("Are you sure you want to delete this meeting?")) return;
    try {
      await axios.delete(`${API}/admin/charity/meetings/${id}`);
      toast.success("Meeting deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete meeting");
    }
  };

  // Documents functions
  const uploadDocument = async (file, docType) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Only PDF files are allowed");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      await axios.post(`${API}/admin/charity/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${docType === 'minutes' ? 'Minutes' : 'Invoice'} uploaded`);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload document");
    }
    setUploading(false);
  };

  const renameDocument = async (docId) => {
    if (!newDocName.trim()) return;
    try {
      await axios.put(`${API}/admin/charity/documents/${docId}?display_name=${encodeURIComponent(newDocName)}`);
      toast.success("Document renamed");
      setRenamingDoc(null);
      setNewDocName("");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to rename document");
    }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await axios.delete(`${API}/admin/charity/documents/${id}`);
      toast.success("Document deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/admin/charity/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  const minutesDocs = documents.filter(d => d.doc_type === 'minutes');
  const invoiceDocs = documents.filter(d => d.doc_type === 'invoice');

  return (
    <div className="space-y-6" data-testid="admin-charity-tab">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          data-testid="charity-trustees-btn"
          variant={activeSection === "trustees" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "trustees" ? "bg-gradient-to-r from-blue-500 to-purple-500" : ""}`}
          onClick={() => setActiveSection("trustees")}
        >
          <Users className="w-4 h-4 mr-2" /> Board of Trustees
        </Button>
        <Button 
          data-testid="charity-meetings-btn"
          variant={activeSection === "meetings" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "meetings" ? "bg-gradient-to-r from-green-500 to-teal-500" : ""}`}
          onClick={() => setActiveSection("meetings")}
        >
          <CalendarIcon className="w-4 h-4 mr-2" /> Meetings
        </Button>
        <Button 
          data-testid="charity-documents-btn"
          variant={activeSection === "documents" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "documents" ? "bg-gradient-to-r from-amber-500 to-orange-500" : ""}`}
          onClick={() => setActiveSection("documents")}
        >
          <FileText className="w-4 h-4 mr-2" /> Documents
        </Button>
      </div>

      {/* Board of Trustees Section */}
      {activeSection === "trustees" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Board of Trustees
                </CardTitle>
                <CardDescription>Manage charity trustees and their roles</CardDescription>
              </div>
              <Dialog open={trusteeDialogOpen} onOpenChange={setTrusteeDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-500" onClick={() => {
                    setTrusteeForm({ name: "", role: "" });
                    setEditingTrustee(null);
                  }}>
                    <Plus className="w-4 h-4 mr-2" /> Add Trustee
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingTrustee ? "Edit Trustee" : "Add New Trustee"}</DialogTitle>
                    <DialogDescription>
                      {editingTrustee ? "Update the trustee's information below." : "Enter the details for the new trustee."}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input 
                        value={trusteeForm.name} 
                        onChange={e => setTrusteeForm({...trusteeForm, name: e.target.value})}
                        placeholder="Enter trustee name"
                      />
                    </div>
                    <div>
                      <Label>Role (Optional)</Label>
                      <Select value={trusteeForm.role} onValueChange={v => setTrusteeForm({...trusteeForm, role: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="chair">Chair</SelectItem>
                          <SelectItem value="treasurer">Treasurer</SelectItem>
                          <SelectItem value="secretary">Secretary</SelectItem>
                          <SelectItem value="trustee">Trustee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTrusteeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={saveTrustee} className="bg-gradient-to-r from-blue-500 to-purple-500">
                      {editingTrustee ? "Update" : "Add"} Trustee
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {trustees.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No trustees added yet</p>
            ) : (
              <div className="space-y-3">
                {trustees.map(trustee => (
                  <div key={trustee.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{trustee.name}</p>
                        {trustee.role && (
                          <Badge variant="outline" className="text-xs capitalize">{trustee.role}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setTrusteeForm({ name: trustee.name, role: trustee.role || "" });
                        setEditingTrustee(trustee.id);
                        setTrusteeDialogOpen(true);
                      }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteTrustee(trustee.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Meetings Section */}
      {activeSection === "meetings" && (
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-green-500 to-teal-500" />
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-green-500" />
                  Meeting Information
                </CardTitle>
                <CardDescription>Schedule and track charity meetings</CardDescription>
              </div>
              <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-green-500 to-teal-500" onClick={() => {
                    setMeetingForm({ meeting_date: "", meeting_time: "", notes: "" });
                    setEditingMeeting(null);
                  }}>
                    <Plus className="w-4 h-4 mr-2" /> Add Meeting
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingMeeting ? "Edit Meeting" : "Add New Meeting"}</DialogTitle>
                    <DialogDescription>
                      {editingMeeting ? "Update the meeting details below." : "Schedule a new charity meeting."}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Meeting Date *</Label>
                      <Input 
                        type="date"
                        value={meetingForm.meeting_date} 
                        onChange={e => setMeetingForm({...meetingForm, meeting_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Meeting Time *</Label>
                      <Input 
                        type="time"
                        value={meetingForm.meeting_time} 
                        onChange={e => setMeetingForm({...meetingForm, meeting_time: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Notes (Optional)</Label>
                      <Textarea 
                        value={meetingForm.notes} 
                        onChange={e => setMeetingForm({...meetingForm, notes: e.target.value})}
                        placeholder="Any additional notes about the meeting"
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setMeetingDialogOpen(false)}>Cancel</Button>
                    <Button onClick={saveMeeting} className="bg-gradient-to-r from-green-500 to-teal-500">
                      {editingMeeting ? "Update" : "Add"} Meeting
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {meetings.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No meetings scheduled</p>
            ) : (
              <div className="space-y-3">
                {meetings.map(meeting => (
                  <div key={meeting.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex flex-col items-center justify-center">
                        <span className="text-xs text-green-600 font-medium">
                          {new Date(meeting.meeting_date).toLocaleDateString('en-GB', { month: 'short' })}
                        </span>
                        <span className="text-lg font-bold text-green-700">
                          {new Date(meeting.meeting_date).getDate()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {new Date(meeting.meeting_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {meeting.meeting_time}
                          {meeting.notes && <span className="ml-2">• {meeting.notes}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setMeetingForm({ 
                          meeting_date: meeting.meeting_date, 
                          meeting_time: meeting.meeting_time, 
                          notes: meeting.notes || "" 
                        });
                        setEditingMeeting(meeting.id);
                        setMeetingDialogOpen(true);
                      }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteMeeting(meeting.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Section */}
      {activeSection === "documents" && (
        <div className="space-y-6">
          {/* Minutes of Meetings */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Minutes of Meetings
              </CardTitle>
              <CardDescription>Upload and manage meeting minutes (PDF only)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-amber-200 rounded-xl p-6 text-center bg-amber-50">
                <Input 
                  type="file" 
                  accept=".pdf"
                  className="hidden"
                  id="minutes-upload"
                  onChange={e => uploadDocument(e.target.files[0], 'minutes')}
                />
                <label htmlFor="minutes-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-amber-700 font-medium">Click to upload Minutes PDF</p>
                  <p className="text-xs text-amber-500 mt-1">Max file size: 10MB</p>
                </label>
              </div>
              
              {minutesDocs.length > 0 && (
                <div className="space-y-2">
                  {minutesDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-amber-500" />
                        {renamingDoc === doc.id ? (
                          <div className="flex gap-2">
                            <Input 
                              value={newDocName}
                              onChange={e => setNewDocName(e.target.value)}
                              className="w-48 h-8"
                              placeholder="New name"
                            />
                            <Button size="sm" onClick={() => renameDocument(doc.id)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRenamingDoc(null); setNewDocName(""); }}>Cancel</Button>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium">{doc.display_name}</p>
                            <p className="text-xs text-gray-400">{(doc.file_size / 1024).toFixed(1)} KB</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc)} title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setRenamingDoc(doc.id); setNewDocName(doc.display_name); }} title="Rename">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteDocument(doc.id)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Invoices
              </CardTitle>
              <CardDescription>Upload and manage invoices (PDF only)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-purple-200 rounded-xl p-6 text-center bg-purple-50">
                <Input 
                  type="file" 
                  accept=".pdf"
                  className="hidden"
                  id="invoice-upload"
                  onChange={e => uploadDocument(e.target.files[0], 'invoice')}
                />
                <label htmlFor="invoice-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  <p className="text-purple-700 font-medium">Click to upload Invoice PDF</p>
                  <p className="text-xs text-purple-500 mt-1">Max file size: 10MB</p>
                </label>
              </div>
              
              {invoiceDocs.length > 0 && (
                <div className="space-y-2">
                  {invoiceDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-purple-500" />
                        {renamingDoc === doc.id ? (
                          <div className="flex gap-2">
                            <Input 
                              value={newDocName}
                              onChange={e => setNewDocName(e.target.value)}
                              className="w-48 h-8"
                              placeholder="New name"
                            />
                            <Button size="sm" onClick={() => renameDocument(doc.id)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRenamingDoc(null); setNewDocName(""); }}>Cancel</Button>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium">{doc.display_name}</p>
                            <p className="text-xs text-gray-400">{(doc.file_size / 1024).toFixed(1)} KB</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadDocument(doc)} title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setRenamingDoc(doc.id); setNewDocName(doc.display_name); }} title="Rename">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteDocument(doc.id)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            <span>Uploading document...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ HARVEST THANKSGIVING TAB ============
const AdminHarvestTab = () => {
  const { isMainAdmin, hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [activeSection, setActiveSection] = useState("levy");
  
  // Harvest Levy Rates state
  const [levyRates, setLevyRates] = useState([]);
  const [levyForm, setLevyForm] = useState({ role: "Church Member", levy_amount: "" });
  const [levyDialogOpen, setLevyDialogOpen] = useState(false);
  const [editingLevy, setEditingLevy] = useState(null);
  
  // Committee state
  const [committee, setCommittee] = useState([]);
  const [committeeForm, setCommitteeForm] = useState({ full_name: "", role: "Harvest Committee Member" });
  const [committeeDialogOpen, setCommitteeDialogOpen] = useState(false);
  const [editingCommittee, setEditingCommittee] = useState(null);
  
  // Juvenile Harvest state
  const [juvenile, setJuvenile] = useState([]);
  const [juvenileForm, setJuvenileForm] = useState({ full_name: "", child_name: "", harvest_levy: "", amount_paid: "", payment_method: "Cash", payment_received_by: "", receipt_given: false, notes: "" });
  const [juvenileDialogOpen, setJuvenileDialogOpen] = useState(false);
  const [editingJuvenile, setEditingJuvenile] = useState(null);
  
  // Adult Harvest state
  const [adult, setAdult] = useState([]);
  const [adultForm, setAdultForm] = useState({ full_name: "", harvest_levy: "", amount_paid: "", payment_method: "Cash", payment_received_by: "", receipt_given: false, notes: "" });
  const [adultDialogOpen, setAdultDialogOpen] = useState(false);
  const [editingAdult, setEditingAdult] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: "minutes", meeting_name: "", meeting_date: "", minute_taker: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Harvest Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseForm, setExpenseForm] = useState({ item: "", total_amount: "", date: "", amount_paid: "", notes: "" });
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Harvest Summary state (Adult + Juvenile Collections + Day Collections + Expenses + Elder Altar + Balance)
  const [summary, setSummary] = useState({ juvenile_harvest_collections: 0, adult_harvest_collections: 0, adult_harvest_day_collections: 0, adult_harvest_expenses: 0, elder_altar_expenses: 0, balance: 0 });
  const [summaryForm, setSummaryForm] = useState({ adult_harvest_day_collections: "", elder_altar_expenses: "" });
  const [savingSummary, setSavingSummary] = useState(false);

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/admin/harvest-summary/${selectedYear}`);
      setSummary(res.data);
      setSummaryForm({
        adult_harvest_day_collections: (res.data.adult_harvest_day_collections || 0).toString(),
        elder_altar_expenses: (res.data.elder_altar_expenses || 0).toString(),
      });
    } catch (e) {}
  };

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      await axios.put(`${API}/admin/harvest-summary/${selectedYear}`, {
        adult_harvest_day_collections: parseFloat(summaryForm.adult_harvest_day_collections) || 0,
        elder_altar_expenses: parseFloat(summaryForm.elder_altar_expenses) || 0,
      });
      toast.success("Summary saved");
      await fetchSummary();
    } catch (e) { toast.error("Failed to save summary"); }
    setSavingSummary(false);
  };

  const committeeRoles = ["Harvest Committee Member", "Financial", "Treasurer", "Adviser", "Publicity", "Chairperson", "Chairman"];
  const levyRoleOptions = ["Chairman", "Chairperson", "Harvest Committee Member", "Church Member"];

  // Fetch all data
  const fetchData = async () => {
    try {
      const [yearsRes, levyRes, committeeRes, juvenileRes, adultRes, docsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/admin/harvest/years`),
        axios.get(`${API}/admin/harvest/levy?year=${selectedYear}`),
        axios.get(`${API}/admin/harvest/committee?year=${selectedYear}`),
        axios.get(`${API}/admin/harvest/juvenile?year=${selectedYear}`),
        axios.get(`${API}/admin/harvest/adult?year=${selectedYear}`),
        axios.get(`${API}/admin/harvest/documents?year=${selectedYear}`),
        axios.get(`${API}/admin/harvest/expenses?year=${selectedYear}`)
      ]);
      setAvailableYears(yearsRes.data.length > 0 ? yearsRes.data : [currentYear]);
      setLevyRates(levyRes.data);
      setCommittee(committeeRes.data);
      setJuvenile(juvenileRes.data);
      setAdult(adultRes.data);
      setDocuments(docsRes.data);
      setExpenses(expensesRes.data);
    } catch (err) {
      console.error("Failed to fetch harvest data:", err);
    }
  };

  useEffect(() => { fetchData(); fetchSummary(); }, [selectedYear]);

  // Committee handlers
  const saveCommittee = async () => {
    if (!committeeForm.full_name) { toast.error("Please enter full name"); return; }
    try {
      if (editingCommittee) {
        await axios.put(`${API}/admin/harvest/committee/${editingCommittee}`, committeeForm);
        toast.success("Committee member updated");
      } else {
        await axios.post(`${API}/admin/harvest/committee`, { ...committeeForm, year: selectedYear });
        toast.success("Committee member added");
      }
      setCommitteeDialogOpen(false);
      setEditingCommittee(null);
      setCommitteeForm({ full_name: "", role: "Harvest Committee Member" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteCommittee = async (id) => {
    if (!window.confirm("Delete this committee member?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/committee/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Levy Rate handlers
  const saveLevy = async () => {
    if (!levyForm.levy_amount) { toast.error("Please enter levy amount"); return; }
    try {
      if (editingLevy) {
        await axios.put(`${API}/admin/harvest/levy/${editingLevy}`, levyForm);
        toast.success("Levy rate updated");
      } else {
        await axios.post(`${API}/admin/harvest/levy`, { ...levyForm, levy_amount: parseFloat(levyForm.levy_amount), year: selectedYear });
        toast.success("Levy rate added");
      }
      setLevyDialogOpen(false);
      setEditingLevy(null);
      setLevyForm({ role: "Church Member", levy_amount: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteLevy = async (id) => {
    if (!window.confirm("Delete this levy rate?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/levy/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Juvenile handlers
  const saveJuvenile = async () => {
    if (!juvenileForm.full_name || !juvenileForm.child_name || !juvenileForm.harvest_levy) { 
      toast.error("Please fill required fields"); return; 
    }
    try {
      const data = { 
        ...juvenileForm, 
        harvest_levy: parseFloat(juvenileForm.harvest_levy) || 0,
        amount_paid: parseFloat(juvenileForm.amount_paid) || 0,
        year: selectedYear 
      };
      if (editingJuvenile) {
        const response = await axios.put(`${API}/admin/harvest/juvenile/${editingJuvenile}`, data);
        if (response.data.new_receipt_generated) {
          toast.success(`Record updated! New receipt ${response.data.new_receipt_number} generated.`);
        } else {
          toast.success("Record updated");
        }
      } else {
        await axios.post(`${API}/admin/harvest/juvenile`, data);
        toast.success("Record added");
      }
      setJuvenileDialogOpen(false);
      setEditingJuvenile(null);
      setJuvenileForm({ full_name: "", child_name: "", harvest_levy: "", amount_paid: "", payment_method: "Cash", payment_received_by: "", receipt_given: false, notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteJuvenile = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/juvenile/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Adult handlers
  const saveAdult = async () => {
    if (!adultForm.full_name || !adultForm.harvest_levy) { 
      toast.error("Please fill required fields"); return; 
    }
    try {
      const data = { 
        ...adultForm, 
        harvest_levy: parseFloat(adultForm.harvest_levy) || 0,
        amount_paid: parseFloat(adultForm.amount_paid) || 0,
        year: selectedYear 
      };
      if (editingAdult) {
        const response = await axios.put(`${API}/admin/harvest/adult/${editingAdult}`, data);
        if (response.data.new_receipt_generated) {
          toast.success(`Record updated! New receipt ${response.data.new_receipt_number} generated.`);
        } else {
          toast.success("Record updated");
        }
      } else {
        await axios.post(`${API}/admin/harvest/adult`, data);
        toast.success("Record added");
      }
      setAdultDialogOpen(false);
      setEditingAdult(null);
      setAdultForm({ full_name: "", harvest_levy: "", amount_paid: "", payment_method: "Cash", payment_received_by: "", receipt_given: false, notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const deleteAdult = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/adult/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Document handlers
  const uploadDocument = async () => {
    if (!selectedFile) { toast.error("Please select a file"); return; }
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) { toast.error("Only PDF files allowed"); return; }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('year', selectedYear);
      formData.append('doc_type', docForm.doc_type);
      if (docForm.doc_type === 'minutes') {
        formData.append('meeting_name', docForm.meeting_name);
        formData.append('meeting_date', docForm.meeting_date);
        formData.append('minute_taker', docForm.minute_taker);
      }
      
      await axios.post(`${API}/admin/harvest/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded");
      setDocDialogOpen(false);
      setSelectedFile(null);
      setDocForm({ doc_type: "minutes", meeting_name: "", meeting_date: "", minute_taker: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/documents/${id}`);
      toast.success("Document deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/admin/harvest/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Download failed");
    }
  };

  // Export handlers
  const exportJuvenile = async () => {
    try {
      const response = await axios.get(`${API}/admin/harvest/juvenile/export?year=${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `juvenile_harvest_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Juvenile Harvest data exported successfully!');
    } catch (error) {
      toast.error('Failed to export Juvenile Harvest data');
      console.error('Export error:', error);
    }
  };

  const exportAdult = async () => {
    try {
      const response = await axios.get(`${API}/admin/harvest/adult/export?year=${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `adult_harvest_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Adult Harvest data exported successfully!');
    } catch (error) {
      toast.error('Failed to export Adult Harvest data');
      console.error('Export error:', error);
    }
  };

  const exportLevy = async () => {
    try {
      const response = await axios.get(`${API}/admin/harvest/levy/export?year=${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `harvest_levy_rates_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Harvest Levy Rates exported successfully!');
    } catch (error) {
      toast.error('Failed to export Levy Rates');
      console.error('Export error:', error);
    }
  };

  const exportCommittee = async () => {
    try {
      const response = await axios.get(`${API}/admin/harvest/committee/export?year=${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `harvest_committee_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Harvest Committee Members exported successfully!');
    } catch (error) {
      toast.error('Failed to export Committee Members');
      console.error('Export error:', error);
    }
  };

  // Harvest Expense handlers
  const saveExpense = async () => {
    if (!expenseForm.item || !expenseForm.total_amount) {
      toast.error("Item and Total Amount are required");
      return;
    }
    try {
      const data = {
        item: expenseForm.item,
        total_amount: parseFloat(expenseForm.total_amount) || 0,
        date: expenseForm.date || "",
        amount_paid: parseFloat(expenseForm.amount_paid) || 0,
        notes: expenseForm.notes || ""
      };
      if (editingExpense) {
        await axios.put(`${API}/admin/harvest/expenses/${editingExpense}`, data);
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/admin/harvest/expenses`, { ...data, year: selectedYear });
        toast.success("Expense added");
      }
      setExpenseDialogOpen(false);
      setEditingExpense(null);
      setExpenseForm({ item: "", total_amount: "", date: "", amount_paid: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to save expense");
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/admin/harvest/expenses/${id}`);
      setExpenses(expenses.filter(e => e.id !== id));
      toast.success("Expense deleted");
    } catch (err) {
      toast.error("Failed to delete expense");
    }
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense.id);
    setExpenseForm({
      item: expense.item || "",
      total_amount: expense.total_amount?.toString() || "",
      date: expense.date || "",
      amount_paid: expense.amount_paid?.toString() || "",
      notes: expense.notes || ""
    });
    setExpenseDialogOpen(true);
  };

  // Receipt handlers
  const [generatingReceipt, setGeneratingReceipt] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptPaymentMethod, setReceiptPaymentMethod] = useState("Cash");
  const [receiptReceivedBy, setReceiptReceivedBy] = useState("");
  const [receiptRecord, setReceiptRecord] = useState(null);

  const openReceiptDialog = (record, type) => {
    setReceiptRecord({ ...record, type });
    setReceiptPaymentMethod(record.payment_method || "Cash");
    setReceiptReceivedBy(record.payment_received_by || "");
    setReceiptDialogOpen(true);
  };

  const generateReceipt = async () => {
    if (!receiptRecord) return;
    
    setGeneratingReceipt(receiptRecord.id);
    try {
      const formData = new FormData();
      formData.append('record_type', receiptRecord.type);
      formData.append('record_id', receiptRecord.id);
      formData.append('payment_method', receiptPaymentMethod);
      formData.append('received_by', receiptReceivedBy);
      
      const response = await axios.post(`${API}/admin/harvest/receipts/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Receipt ${response.data.receipt_number} generated!`);
      setReceiptDialogOpen(false);
      setReceiptRecord(null);
      fetchData();
      
      // Auto-download the receipt
      downloadReceipt(response.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate receipt");
    } finally {
      setGeneratingReceipt(null);
    }
  };

  const downloadReceipt = async (receiptId) => {
    try {
      const response = await axios.get(`${API}/admin/harvest/receipts/${receiptId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${receiptId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download receipt");
    }
  };

  const viewExistingReceipt = async (record, type) => {
    try {
      const response = await axios.get(`${API}/admin/harvest/receipts/by-record/${type}/${record.id}`);
      if (response.data && response.data.id) {
        downloadReceipt(response.data.id);
      } else {
        toast.info("No receipt found. Generate one first.");
      }
    } catch (err) {
      toast.error("Failed to fetch receipt");
    }
  };

  // Receipt History handlers
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [receiptHistory, setReceiptHistory] = useState({ current_receipt: null, history: [], total_receipts: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);

  const viewReceiptHistory = async (record, type) => {
    setHistoryRecord({ ...record, type });
    setHistoryLoading(true);
    setHistoryDialogOpen(true);
    try {
      const response = await axios.get(`${API}/admin/harvest/receipts/history/${type}/${record.id}`);
      setReceiptHistory(response.data);
    } catch (err) {
      toast.error("Failed to fetch receipt history");
      setReceiptHistory({ current_receipt: null, history: [], total_receipts: 0 });
    } finally {
      setHistoryLoading(false);
    }
  };

  const downloadArchivedReceipt = async (receiptId) => {
    try {
      const response = await axios.get(`${API}/admin/harvest/receipts/history/${receiptId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `archived_receipt_${receiptId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download archived receipt");
    }
  };

  // Calculate totals (split paid by payment method)
  const juvenileTotals = juvenile.reduce((acc, r) => {
    const paid = r.amount_paid || 0;
    const pm = (r.payment_method || '').toLowerCase();
    return {
      levy: acc.levy + (r.harvest_levy || 0),
      paid: acc.paid + paid,
      balance: acc.balance + (r.balance || 0),
      paidCash: acc.paidCash + (pm === 'cash' ? paid : 0),
      paidBank: acc.paidBank + (pm.includes('bank') || pm.includes('transfer') ? paid : 0)
    };
  }, { levy: 0, paid: 0, balance: 0, paidCash: 0, paidBank: 0 });

  const adultTotals = adult.reduce((acc, r) => {
    const paid = r.amount_paid || 0;
    const pm = (r.payment_method || '').toLowerCase();
    return {
      levy: acc.levy + (r.harvest_levy || 0),
      paid: acc.paid + paid,
      balance: acc.balance + (r.balance || 0),
      paidCash: acc.paidCash + (pm === 'cash' ? paid : 0),
      paidBank: acc.paidBank + (pm.includes('bank') || pm.includes('transfer') ? paid : 0)
    };
  }, { levy: 0, paid: 0, balance: 0, paidCash: 0, paidBank: 0 });

  return (
    <div className="space-y-6" data-testid="harvest-tab">
      {/* Header with Year Selector */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Wheat className="w-6 h-6" /> Harvest Thanksgiving
              </CardTitle>
              <p className="text-amber-100 mt-1">Manage harvest records, committee members, and documents</p>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-white font-medium">Year:</Label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="h-10 px-4 rounded-xl border-0 bg-white/20 text-white font-medium focus:ring-2 focus:ring-white/50"
              >
                {availableYears.map(y => <option key={y} value={y} className="text-gray-800">{y}</option>)}
                {!availableYears.includes(currentYear + 1) && <option value={currentYear + 1} className="text-gray-800">{currentYear + 1}</option>}
              </select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          data-testid="harvest-levy-btn"
          variant={activeSection === "levy" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "levy" ? "bg-gradient-to-r from-yellow-500 to-amber-500" : ""}`}
          onClick={() => setActiveSection("levy")}
        >
          <DollarSign className="w-4 h-4 mr-2" /> Harvest Levy
        </Button>
        <Button 
          data-testid="harvest-committee-btn"
          variant={activeSection === "committee" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "committee" ? "bg-gradient-to-r from-amber-500 to-orange-500" : ""}`}
          onClick={() => setActiveSection("committee")}
        >
          <Users className="w-4 h-4 mr-2" /> Committee Members
        </Button>
        <Button 
          data-testid="harvest-juvenile-btn"
          variant={activeSection === "juvenile" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "juvenile" ? "bg-gradient-to-r from-pink-500 to-rose-500" : ""}`}
          onClick={() => setActiveSection("juvenile")}
        >
          <Baby className="w-4 h-4 mr-2" /> Juvenile Harvest
        </Button>
        <Button 
          data-testid="harvest-adult-btn"
          variant={activeSection === "adult" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "adult" ? "bg-gradient-to-r from-blue-500 to-indigo-500" : ""}`}
          onClick={() => setActiveSection("adult")}
        >
          <UserCheck className="w-4 h-4 mr-2" /> Adult Harvest
        </Button>
        <Button 
          data-testid="harvest-documents-btn"
          variant={activeSection === "documents" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "documents" ? "bg-gradient-to-r from-green-500 to-teal-500" : ""}`}
          onClick={() => setActiveSection("documents")}
        >
          <FileText className="w-4 h-4 mr-2" /> Documents
        </Button>
        <Button 
          data-testid="harvest-expenses-btn"
          variant={activeSection === "expenses" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "expenses" ? "bg-gradient-to-r from-red-500 to-rose-500" : ""}`}
          onClick={() => setActiveSection("expenses")}
        >
          <DollarSign className="w-4 h-4 mr-2" /> Harvest Expenses
        </Button>
        <Button 
          data-testid="harvest-summary-btn"
          variant={activeSection === "summary" ? "default" : "outline"}
          className={`rounded-full ${activeSection === "summary" ? "bg-gradient-to-r from-emerald-500 to-teal-500" : ""}`}
          onClick={() => { setActiveSection("summary"); fetchSummary(); }}
        >
          <Award className="w-4 h-4 mr-2" /> Harvest Summary
        </Button>
      </div>

      {/* Harvest Levy Section */}
      {activeSection === "levy" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-yellow-600" /> Harvest Levy Rates</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Define levy amounts by role for {selectedYear}</p>
            </div>
            <div className="flex gap-2">
              {levyRates.length > 0 && <Button variant="outline" className="rounded-full" onClick={exportLevy}><Download className="w-4 h-4 mr-2" /> Export</Button>}
              {hasPermission("harvest_write") && (
                <Dialog open={levyDialogOpen} onOpenChange={(open) => { setLevyDialogOpen(open); if (!open) { setEditingLevy(null); setLevyForm({ role: "Church Member", levy_amount: "" }); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500"><Plus className="w-4 h-4 mr-2" /> Add Levy Rate</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingLevy ? "Edit" : "Add"} Harvest Levy Rate</DialogTitle>
                      <DialogDescription>Set the harvest levy amount for each role in {selectedYear}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div><Label>Role *</Label>
                        <select value={levyForm.role} onChange={e => setLevyForm({...levyForm, role: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1">
                          {levyRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div><Label>Levy Amount (£) *</Label><Input type="number" value={levyForm.levy_amount} onChange={e => setLevyForm({...levyForm, levy_amount: e.target.value})} className="rounded-xl mt-1" placeholder="0.00" /></div>
                      <Button onClick={saveLevy} className="w-full rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500">{editingLevy ? "Update" : "Add"} Levy Rate</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Juvenile Harvest Section */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
                <Baby className="w-5 h-5" />
                JUVENILE HARVEST
              </h3>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-purple-100/50">
                      <TableHead className="font-semibold text-purple-800">Category</TableHead>
                      <TableHead className="font-semibold text-purple-800">Levy Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Children</TableCell>
                      <TableCell className="text-green-600 font-bold">£10.00</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Adult Members</TableCell>
                      <TableCell className="text-green-600 font-bold">£25.00</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Adult Harvest Levy Section */}
            <div>
              <h3 className="text-lg font-bold text-yellow-700 mb-4 flex items-center gap-2">
                <Wheat className="w-5 h-5" />
                ADULT HARVEST LEVY
              </h3>
              {levyRates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No levy rates defined for {selectedYear}. Add rates to get started.</p>
              ) : (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4">
                  <Table>
                    <TableHeader><TableRow className="bg-yellow-100/50"><TableHead className="font-semibold text-yellow-800">Role</TableHead><TableHead className="font-semibold text-yellow-800">Levy Amount</TableHead><TableHead className="font-semibold text-yellow-800">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {levyRates.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.role}</TableCell>
                          <TableCell className="text-green-600 font-bold">£{(r.levy_amount || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            {hasPermission("harvest_write") && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => { setLevyForm({ role: r.role, levy_amount: r.levy_amount?.toString() || "" }); setEditingLevy(r.id); setLevyDialogOpen(true); }}><Edit className="w-3 h-3" /></Button>
                                {isMainAdmin && (
                                  <Button size="sm" variant="destructive" onClick={() => deleteLevy(r.id)}><Trash2 className="w-3 h-3" /></Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Committee Section */}
      {activeSection === "committee" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-600" /> Harvest Committee Members</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{committee.length} members for {selectedYear}</p>
            </div>
            <div className="flex gap-2">
              {committee.length > 0 && <Button variant="outline" className="rounded-full" onClick={exportCommittee}><Download className="w-4 h-4 mr-2" /> Export</Button>}
              {hasPermission("harvest_write") && (
                <Dialog open={committeeDialogOpen} onOpenChange={(open) => { setCommitteeDialogOpen(open); if (!open) { setEditingCommittee(null); setCommitteeForm({ full_name: "", role: "Harvest Committee Member" }); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500"><Plus className="w-4 h-4 mr-2" /> Add Member</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingCommittee ? "Edit" : "Add"} Committee Member</DialogTitle>
                      <DialogDescription>Enter the committee member details for {selectedYear}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div><Label>Full Name *</Label><Input value={committeeForm.full_name} onChange={e => setCommitteeForm({...committeeForm, full_name: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label>Role *</Label>
                        <select value={committeeForm.role} onChange={e => setCommitteeForm({...committeeForm, role: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1">
                          {committeeRoles.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <Button onClick={saveCommittee} className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">{editingCommittee ? "Update" : "Add"} Member</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {committee.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No committee members for {selectedYear}</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Full Name</TableHead><TableHead>Role</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {committee.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.full_name}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-amber-50 text-amber-700">{m.role}</Badge></TableCell>
                      <TableCell>
                        {hasPermission("harvest_write") && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => { setCommitteeForm({ full_name: m.full_name, role: m.role }); setEditingCommittee(m.id); setCommitteeDialogOpen(true); }}><Edit className="w-3 h-3" /></Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteCommittee(m.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Juvenile Harvest Section */}
      {activeSection === "juvenile" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><Baby className="w-5 h-5 text-pink-600" /> Juvenile Harvest</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{juvenile.length} records | Total Levy: £{juvenileTotals.levy.toFixed(2)} | Paid: £{juvenileTotals.paid.toFixed(2)} | Balance: £{juvenileTotals.balance.toFixed(2)}</p>
            </div>
            <div className="flex gap-2">
              {juvenile.length > 0 && <Button variant="outline" className="rounded-full" onClick={exportJuvenile}><Download className="w-4 h-4 mr-2" /> Export</Button>}
              {hasPermission("harvest_write") && (
                <Dialog open={juvenileDialogOpen} onOpenChange={(open) => { setJuvenileDialogOpen(open); if (!open) { setEditingJuvenile(null); setJuvenileForm({ full_name: "", child_name: "", harvest_levy: "", amount_paid: "", receipt_given: false, notes: "" }); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-gradient-to-r from-pink-500 to-rose-500"><Plus className="w-4 h-4 mr-2" /> Add Record</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingJuvenile ? "Edit" : "Add"} Juvenile Harvest Record</DialogTitle>
                      <DialogDescription>Enter the harvest details for {selectedYear}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div><Label>Parent/Guardian Full Name *</Label><Input value={juvenileForm.full_name} onChange={e => setJuvenileForm({...juvenileForm, full_name: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label>Child Name *</Label><Input value={juvenileForm.child_name} onChange={e => setJuvenileForm({...juvenileForm, child_name: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Harvest Levy (£) *</Label><Input type="number" value={juvenileForm.harvest_levy} onChange={e => setJuvenileForm({...juvenileForm, harvest_levy: e.target.value})} className="rounded-xl mt-1" /></div>
                        <div><Label>Amount Paid (£)</Label><Input type="number" value={juvenileForm.amount_paid} onChange={e => setJuvenileForm({...juvenileForm, amount_paid: e.target.value})} className="rounded-xl mt-1" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Payment Method</Label>
                          <select value={juvenileForm.payment_method} onChange={e => setJuvenileForm({...juvenileForm, payment_method: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1">
                            <option value="Cash">Cash</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                          </select>
                        </div>
                        <div><Label>Payment Received By</Label><Input value={juvenileForm.payment_received_by} onChange={e => setJuvenileForm({...juvenileForm, payment_received_by: e.target.value})} className="rounded-xl mt-1" placeholder="Name of receiver" /></div>
                      </div>
                      <div><Label>Notes</Label><Input value={juvenileForm.notes} onChange={e => setJuvenileForm({...juvenileForm, notes: e.target.value})} className="rounded-xl mt-1" /></div>
                      <Button onClick={saveJuvenile} className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-rose-500">{editingJuvenile ? "Update" : "Add"} Record</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {juvenile.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No juvenile harvest records for {selectedYear}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Full Name</TableHead><TableHead>Child Name</TableHead><TableHead>Levy (£)</TableHead><TableHead>Paid (£)</TableHead><TableHead>Balance (£)</TableHead><TableHead>Receipt</TableHead><TableHead>Payment Method</TableHead><TableHead>Notes</TableHead><TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {juvenile.map(r => (
                        <TableRow key={r.id} className={r.balance > 0 ? "bg-red-50" : "bg-green-50"}>
                          <TableCell className="font-medium">{r.full_name}</TableCell>
                          <TableCell>{r.child_name}</TableCell>
                          <TableCell>£{(r.harvest_levy || 0).toFixed(2)}</TableCell>
                          <TableCell>£{(r.amount_paid || 0).toFixed(2)}</TableCell>
                          <TableCell className={r.balance > 0 ? "text-red-600 font-bold" : "text-green-600"}>£{(r.balance || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {r.receipt_given ? (
                                <>
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-green-600" onClick={() => viewExistingReceipt(r, 'juvenile')} title="Download Current Receipt">
                                    <CheckCircle className="w-4 h-4 mr-1" /> <Download className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-blue-600" onClick={() => viewReceiptHistory(r, 'juvenile')} title="View Receipt History">
                                    <Clock className="w-3 h-3" />
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" variant="outline" className="h-6 px-2 text-amber-600" onClick={() => openReceiptDialog(r, 'juvenile')} title="Generate Receipt">
                                  <FileText className="w-3 h-3 mr-1" /> Gen
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {r.payment_method ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                r.payment_method.toLowerCase() === 'cash' ? 'bg-green-100 text-green-700' :
                                r.payment_method.toLowerCase() === 'card' ? 'bg-blue-100 text-blue-700' :
                                r.payment_method.toLowerCase().includes('bank') || r.payment_method.toLowerCase().includes('transfer') ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {r.payment_method}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs italic">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">{r.notes}</TableCell>
                          <TableCell>
                            {hasPermission("harvest_write") && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => { setJuvenileForm({ full_name: r.full_name, child_name: r.child_name, harvest_levy: r.harvest_levy?.toString() || "", amount_paid: r.amount_paid?.toString() || "", payment_method: r.payment_method || "Cash", payment_received_by: r.payment_received_by || "", receipt_given: r.receipt_given, notes: r.notes || "" }); setEditingJuvenile(r.id); setJuvenileDialogOpen(true); }}><Edit className="w-3 h-3" /></Button>
                                {isMainAdmin && (
                                  <Button size="sm" variant="destructive" onClick={() => deleteJuvenile(r.id)}><Trash2 className="w-3 h-3" /></Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Juvenile Harvest Yearly Totals */}
                <div className="mt-4 p-4 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl shadow-lg">
                  <h4 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5" /> {selectedYear} Juvenile Harvest Totals
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-pink-100 font-medium">Total Levy</p>
                      <p className="font-bold text-white text-xl">£{juvenileTotals.levy.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-pink-100 font-medium">Total Paid (Cash)</p>
                      <p className="font-bold text-green-200 text-xl">£{juvenileTotals.paidCash.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-pink-100 font-medium">Total Paid (Bank Transfer)</p>
                      <p className="font-bold text-green-200 text-xl">£{juvenileTotals.paidBank.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-pink-100 font-medium">Total Balance</p>
                      <p className={`font-bold text-xl ${juvenileTotals.balance > 0 ? 'text-yellow-200' : 'text-green-200'}`}>
                        £{juvenileTotals.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20 text-center">
                    <p className="text-white/80 text-sm">
                      {juvenile.length} records | Total Paid: <span className="font-bold text-green-200">£{juvenileTotals.paid.toFixed(2)}</span> | Collection Rate: <span className="font-bold text-green-200">{juvenileTotals.levy > 0 ? ((juvenileTotals.paid / juvenileTotals.levy) * 100).toFixed(1) : 0}%</span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Adult Harvest Section */}
      {activeSection === "adult" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-blue-600" /> Adult Harvest</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{adult.length} records | Total Levy: £{adultTotals.levy.toFixed(2)} | Paid: £{adultTotals.paid.toFixed(2)} | Balance: £{adultTotals.balance.toFixed(2)}</p>
            </div>
            <div className="flex gap-2">
              {adult.length > 0 && <Button variant="outline" className="rounded-full" onClick={exportAdult}><Download className="w-4 h-4 mr-2" /> Export</Button>}
              {hasPermission("harvest_write") && (
                <Dialog open={adultDialogOpen} onOpenChange={(open) => { setAdultDialogOpen(open); if (!open) { setEditingAdult(null); setAdultForm({ full_name: "", harvest_levy: "", amount_paid: "", receipt_given: false, notes: "" }); } }}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"><Plus className="w-4 h-4 mr-2" /> Add Record</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingAdult ? "Edit" : "Add"} Adult Harvest Record</DialogTitle>
                      <DialogDescription>Enter the harvest details for {selectedYear}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div><Label>Full Name *</Label><Input value={adultForm.full_name} onChange={e => setAdultForm({...adultForm, full_name: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Harvest Levy (£) *</Label><Input type="number" value={adultForm.harvest_levy} onChange={e => setAdultForm({...adultForm, harvest_levy: e.target.value})} className="rounded-xl mt-1" /></div>
                        <div><Label>Amount Paid (£)</Label><Input type="number" value={adultForm.amount_paid} onChange={e => setAdultForm({...adultForm, amount_paid: e.target.value})} className="rounded-xl mt-1" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Payment Method</Label>
                          <select value={adultForm.payment_method} onChange={e => setAdultForm({...adultForm, payment_method: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1">
                            <option value="Cash">Cash</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                          </select>
                        </div>
                        <div><Label>Payment Received By</Label><Input value={adultForm.payment_received_by} onChange={e => setAdultForm({...adultForm, payment_received_by: e.target.value})} className="rounded-xl mt-1" placeholder="Name of receiver" /></div>
                      </div>
                      <div><Label>Notes</Label><Input value={adultForm.notes} onChange={e => setAdultForm({...adultForm, notes: e.target.value})} className="rounded-xl mt-1" /></div>
                      <Button onClick={saveAdult} className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500">{editingAdult ? "Update" : "Add"} Record</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {adult.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No adult harvest records for {selectedYear}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Full Name</TableHead><TableHead>Levy (£)</TableHead><TableHead>Paid (£)</TableHead><TableHead>Balance (£)</TableHead><TableHead>Receipt</TableHead><TableHead>Payment Method</TableHead><TableHead>Notes</TableHead><TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {adult.map(r => (
                        <TableRow key={r.id} className={r.balance > 0 ? "bg-red-50" : "bg-green-50"}>
                          <TableCell className="font-medium">{r.full_name}</TableCell>
                          <TableCell>£{(r.harvest_levy || 0).toFixed(2)}</TableCell>
                          <TableCell>£{(r.amount_paid || 0).toFixed(2)}</TableCell>
                          <TableCell className={r.balance > 0 ? "text-red-600 font-bold" : "text-green-600"}>£{(r.balance || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {r.receipt_given ? (
                                <>
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-green-600" onClick={() => viewExistingReceipt(r, 'adult')} title="Download Current Receipt">
                                    <CheckCircle className="w-4 h-4 mr-1" /> <Download className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-blue-600" onClick={() => viewReceiptHistory(r, 'adult')} title="View Receipt History">
                                    <Clock className="w-3 h-3" />
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" variant="outline" className="h-6 px-2 text-amber-600" onClick={() => openReceiptDialog(r, 'adult')} title="Generate Receipt">
                                  <FileText className="w-3 h-3 mr-1" /> Gen
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {r.payment_method ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                r.payment_method.toLowerCase() === 'cash' ? 'bg-green-100 text-green-700' :
                                r.payment_method.toLowerCase() === 'card' ? 'bg-blue-100 text-blue-700' :
                                r.payment_method.toLowerCase().includes('bank') || r.payment_method.toLowerCase().includes('transfer') ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {r.payment_method}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs italic">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">{r.notes}</TableCell>
                          <TableCell>
                            {hasPermission("harvest_write") && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => { setAdultForm({ full_name: r.full_name, harvest_levy: r.harvest_levy?.toString() || "", amount_paid: r.amount_paid?.toString() || "", payment_method: r.payment_method || "Cash", payment_received_by: r.payment_received_by || "", receipt_given: r.receipt_given, notes: r.notes || "" }); setEditingAdult(r.id); setAdultDialogOpen(true); }}><Edit className="w-3 h-3" /></Button>
                                {isMainAdmin && (
                                  <Button size="sm" variant="destructive" onClick={() => deleteAdult(r.id)}><Trash2 className="w-3 h-3" /></Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Adult Harvest Yearly Totals */}
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                  <h4 className="font-bold text-white text-lg mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5" /> {selectedYear} Adult Harvest Totals
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-100 font-medium">Total Levy</p>
                      <p className="font-bold text-white text-xl">£{adultTotals.levy.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-100 font-medium">Total Paid (Cash)</p>
                      <p className="font-bold text-green-200 text-xl">£{adultTotals.paidCash.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-100 font-medium">Total Paid (Bank Transfer)</p>
                      <p className="font-bold text-green-200 text-xl">£{adultTotals.paidBank.toFixed(2)}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-100 font-medium">Total Balance</p>
                      <p className={`font-bold text-xl ${adultTotals.balance > 0 ? 'text-yellow-200' : 'text-green-200'}`}>
                        £{adultTotals.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20 text-center">
                    <p className="text-white/80 text-sm">
                      {adult.length} records | Total Paid: <span className="font-bold text-green-200">£{adultTotals.paid.toFixed(2)}</span> | Collection Rate: <span className="font-bold text-green-200">{adultTotals.levy > 0 ? ((adultTotals.paid / adultTotals.levy) * 100).toFixed(1) : 0}%</span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Section */}
      {activeSection === "documents" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-green-600" /> Harvest Documents</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Meeting minutes and miscellaneous documents for {selectedYear}</p>
            </div>
            {hasPermission("harvest_write") && (
              <Dialog open={docDialogOpen} onOpenChange={(open) => { setDocDialogOpen(open); if (!open) { setSelectedFile(null); setDocForm({ doc_type: "minutes", meeting_name: "", meeting_date: "", minute_taker: "" }); } }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-gradient-to-r from-green-500 to-teal-500"><Upload className="w-4 h-4 mr-2" /> Upload Document</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Harvest Document</DialogTitle>
                    <DialogDescription>Upload meeting minutes or miscellaneous documents (PDF only, max 10MB)</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Document Type *</Label>
                      <select value={docForm.doc_type} onChange={e => setDocForm({...docForm, doc_type: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1">
                        <option value="minutes">Meeting Minutes</option>
                        <option value="miscellaneous">Miscellaneous</option>
                      </select>
                    </div>
                    {docForm.doc_type === "minutes" && (
                      <>
                        <div><Label>Name of Meeting *</Label><Input value={docForm.meeting_name} onChange={e => setDocForm({...docForm, meeting_name: e.target.value})} placeholder="e.g., Harvest Planning Meeting" className="rounded-xl mt-1" /></div>
                        <div><Label>Meeting Date</Label><Input type="date" value={docForm.meeting_date} onChange={e => setDocForm({...docForm, meeting_date: e.target.value})} className="rounded-xl mt-1" /></div>
                        <div><Label>Name of Minute Taker</Label><Input value={docForm.minute_taker} onChange={e => setDocForm({...docForm, minute_taker: e.target.value})} className="rounded-xl mt-1" /></div>
                      </>
                    )}
                    <div>
                      <Label>Select PDF File *</Label>
                      <Input type="file" accept=".pdf" onChange={e => setSelectedFile(e.target.files[0])} className="rounded-xl mt-1" />
                    </div>
                    <Button onClick={uploadDocument} disabled={uploading} className="w-full rounded-xl bg-gradient-to-r from-green-500 to-teal-500">
                      {uploading ? "Uploading..." : "Upload Document"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No documents uploaded for {selectedYear}</p>
            ) : (
              <div className="space-y-4">
                {/* Meeting Minutes */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Meeting Minutes</h4>
                  {documents.filter(d => d.doc_type === 'minutes').length === 0 ? (
                    <p className="text-sm text-gray-400 pl-6">No meeting minutes uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.filter(d => d.doc_type === 'minutes').map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex-1">
                            <p className="font-medium">{doc.meeting_name || doc.display_name}</p>
                            <p className="text-sm text-gray-500">
                              {doc.meeting_date && `Date: ${new Date(doc.meeting_date).toLocaleDateString()}`}
                              {doc.minute_taker && ` | Minute Taker: ${doc.minute_taker}`}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}><Download className="w-3 h-3" /></Button>
                            {hasPermission("harvest_write") && <Button size="sm" variant="destructive" onClick={() => deleteDocument(doc.id)}><Trash2 className="w-3 h-3" /></Button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Miscellaneous Documents */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Miscellaneous Documents</h4>
                  {documents.filter(d => d.doc_type === 'miscellaneous').length === 0 ? (
                    <p className="text-sm text-gray-400 pl-6">No miscellaneous documents uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.filter(d => d.doc_type === 'miscellaneous').map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex-1">
                            <p className="font-medium">{doc.display_name}</p>
                            <p className="text-sm text-gray-500">Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}><Download className="w-3 h-3" /></Button>
                            {hasPermission("harvest_write") && <Button size="sm" variant="destructive" onClick={() => deleteDocument(doc.id)}><Trash2 className="w-3 h-3" /></Button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Harvest Expenses Section */}
      {activeSection === "expenses" && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-red-600" /> Harvest Expenses</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Track harvest-related expenses for {selectedYear}</p>
            </div>
            {hasPermission("harvest_write") && (
              <Dialog open={expenseDialogOpen} onOpenChange={(open) => { setExpenseDialogOpen(open); if (!open) { setEditingExpense(null); setExpenseForm({ item: "", total_amount: "", date: "", amount_paid: "", notes: "" }); } }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-gradient-to-r from-red-500 to-rose-500" data-testid="add-expense-btn" onClick={() => { setEditingExpense(null); setExpenseForm({ item: "", total_amount: "", date: "", amount_paid: "", notes: "" }); }}><Plus className="w-4 h-4 mr-2" /> Add Expense</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingExpense ? "Edit" : "Add"} Harvest Expense</DialogTitle>
                    <DialogDescription>Enter expense details for {selectedYear}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Item *</Label>
                      <Input value={expenseForm.item} onChange={e => setExpenseForm({...expenseForm, item: e.target.value})} placeholder="e.g., Hall Decoration" className="rounded-xl mt-1" data-testid="expense-item-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Total Amount (£) *</Label>
                        <Input type="number" step="0.01" value={expenseForm.total_amount} onChange={e => setExpenseForm({...expenseForm, total_amount: e.target.value})} placeholder="0.00" className="rounded-xl mt-1" data-testid="expense-total-input" />
                      </div>
                      <div>
                        <Label>Amount Paid (£)</Label>
                        <Input type="number" step="0.01" value={expenseForm.amount_paid} onChange={e => setExpenseForm({...expenseForm, amount_paid: e.target.value})} placeholder="0.00" className="rounded-xl mt-1" data-testid="expense-paid-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Balance (auto-calculated)</Label>
                      <div className="h-10 px-3 rounded-xl border border-gray-200 mt-1 flex items-center bg-gray-50 font-semibold text-gray-700">
                        £{((parseFloat(expenseForm.total_amount) || 0) - (parseFloat(expenseForm.amount_paid) || 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={expenseForm.date} onChange={e => setExpenseForm({...expenseForm, date: e.target.value})} className="rounded-xl mt-1" data-testid="expense-date-input" />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input value={expenseForm.notes} onChange={e => setExpenseForm({...expenseForm, notes: e.target.value})} placeholder="Optional notes" className="rounded-xl mt-1" data-testid="expense-notes-input" />
                    </div>
                    <Button onClick={saveExpense} className="w-full rounded-xl bg-gradient-to-r from-red-500 to-rose-500" data-testid="save-expense-btn">
                      {editingExpense ? "Update Expense" : "Add Expense"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {expenses.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No harvest expenses recorded for {selectedYear}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 font-semibold text-gray-600">Item</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-600">Date</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-600">Total (£)</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-600">Paid (£)</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-600">Balance (£)</th>
                        <th className="text-left py-3 px-2 font-semibold text-gray-600">Notes</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map(exp => (
                        <tr key={exp.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`expense-row-${exp.id}`}>
                          <td className="py-3 px-2 font-medium">{exp.item}</td>
                          <td className="py-3 px-2 text-gray-600">{exp.date ? new Date(exp.date + 'T12:00:00').toLocaleDateString() : "-"}</td>
                          <td className="py-3 px-2 text-right">{(exp.total_amount || 0).toFixed(2)}</td>
                          <td className="py-3 px-2 text-right">{(exp.amount_paid || 0).toFixed(2)}</td>
                          <td className={`py-3 px-2 text-right font-semibold ${(exp.balance || 0) > 0 ? "text-red-600" : "text-green-600"}`}>{(exp.balance || 0).toFixed(2)}</td>
                          <td className="py-3 px-2 text-gray-500 max-w-[200px] truncate">{exp.notes || "-"}</td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex gap-1 justify-end">
                              {hasPermission("harvest_write") && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => openEditExpense(exp)} data-testid={`edit-expense-${exp.id}`}><Pencil className="w-3 h-3" /></Button>
                                  <Button size="sm" variant="destructive" onClick={() => deleteExpense(exp.id)} data-testid={`delete-expense-${exp.id}`}><Trash2 className="w-3 h-3" /></Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-bold">
                        <td className="py-3 px-2" colSpan={2}>Totals</td>
                        <td className="py-3 px-2 text-right">{expenses.reduce((s, e) => s + (e.total_amount || 0), 0).toFixed(2)}</td>
                        <td className="py-3 px-2 text-right">{expenses.reduce((s, e) => s + (e.amount_paid || 0), 0).toFixed(2)}</td>
                        <td className={`py-3 px-2 text-right ${expenses.reduce((s, e) => s + (e.balance || 0), 0) > 0 ? "text-red-600" : "text-green-600"}`}>{expenses.reduce((s, e) => s + (e.balance || 0), 0).toFixed(2)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Harvest Summary Section */}
      {activeSection === "summary" && (
        <Card className="border-0 shadow-lg" data-testid="harvest-summary-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-emerald-600" /> Harvest Summary — {selectedYear}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Auto-computed from Adult Harvest & Harvest Expenses. Enter Day Collections & Elder Altar Expenses below.</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-w-2xl mx-auto space-y-4">
              {/* Juvenile Harvest Collections - auto */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div>
                  <p className="font-semibold text-gray-800">Juvenile Harvest Collections</p>
                  <p className="text-xs text-gray-500">Auto: sum of Juvenile Harvest paid amounts</p>
                </div>
                <p className="text-xl font-bold text-emerald-700" data-testid="summary-juvenile-collections">£{(summary.juvenile_harvest_collections || 0).toFixed(2)}</p>
              </div>

              {/* Adult Harvest Collections - auto */}
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div>
                  <p className="font-semibold text-gray-800">Adult Harvest Collections</p>
                  <p className="text-xs text-gray-500">Auto: sum of Adult Harvest paid amounts</p>
                </div>
                <p className="text-xl font-bold text-emerald-700" data-testid="summary-adult-collections">£{(summary.adult_harvest_collections || 0).toFixed(2)}</p>
              </div>

              {/* Adult Harvest Day Collections - editable */}
              <div className="flex items-center justify-between gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">Adult Harvest Day Collections</p>
                  <p className="text-xs text-gray-500">Extra collections received on Harvest day</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-600 font-medium">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={summaryForm.adult_harvest_day_collections}
                    onChange={e => setSummaryForm({ ...summaryForm, adult_harvest_day_collections: e.target.value })}
                    className="w-40 rounded-xl text-right font-bold text-emerald-700"
                    placeholder="0.00"
                    data-testid="summary-day-collections-input"
                  />
                </div>
              </div>

              {/* Less: Adult Harvest Expenses - auto */}
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                <div>
                  <p className="font-semibold text-gray-800">Less: Adult Harvest Expenses</p>
                  <p className="text-xs text-gray-500">Auto: sum of Harvest Expenses paid</p>
                </div>
                <p className="text-xl font-bold text-red-700" data-testid="summary-adult-expenses">£{(summary.adult_harvest_expenses || 0).toFixed(2)}</p>
              </div>

              {/* Less: Elder Altar Expenses - editable */}
              <div className="flex items-center justify-between gap-4 p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">Less: Elder Altar Expenses</p>
                  <p className="text-xs text-gray-500">Amount set aside for Elder / Altar</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-600 font-medium">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={summaryForm.elder_altar_expenses}
                    onChange={e => setSummaryForm({ ...summaryForm, elder_altar_expenses: e.target.value })}
                    className="w-40 rounded-xl text-right font-bold text-red-700"
                    placeholder="0.00"
                    data-testid="summary-elder-altar-input"
                  />
                </div>
              </div>

              {/* Live Preview of Balance (before save) */}
              {(() => {
                const dayC = parseFloat(summaryForm.adult_harvest_day_collections) || 0;
                const altar = parseFloat(summaryForm.elder_altar_expenses) || 0;
                const previewBalance = (summary.adult_harvest_collections || 0) + (summary.juvenile_harvest_collections || 0) + dayC - (summary.adult_harvest_expenses || 0) - altar;
                return (
                  <div className={`flex items-center justify-between p-5 rounded-xl border-2 shadow-sm ${previewBalance >= 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-500 border-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-500 border-red-600'}`}>
                    <div>
                      <p className="font-bold text-white text-lg">Balance</p>
                      <p className="text-xs text-white/80">= Adult + Juvenile + Day Collections − Expenses − Elder Altar</p>
                    </div>
                    <p className="text-3xl font-black text-white" data-testid="summary-balance">£{previewBalance.toFixed(2)}</p>
                  </div>
                );
              })()}

              {hasPermission("harvest_write") && (
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" className="rounded-full" onClick={fetchSummary} disabled={savingSummary} data-testid="summary-refresh-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                  <Button onClick={saveSummary} disabled={savingSummary} className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="summary-save-btn">
                    {savingSummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Save Summary
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Receipt Generation Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Payment Receipt</DialogTitle>
            <DialogDescription>
              Generate a PDF receipt for this harvest payment
            </DialogDescription>
          </DialogHeader>
          {receiptRecord && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                <p><strong>Name:</strong> {receiptRecord.full_name}</p>
                {receiptRecord.child_name && <p><strong>Child:</strong> {receiptRecord.child_name}</p>}
                <p><strong>Category:</strong> {receiptRecord.type === 'juvenile' ? 'Juvenile Harvest' : 'Adult Harvest'}</p>
                <p><strong>Harvest Levy:</strong> £{(receiptRecord.harvest_levy || 0).toFixed(2)}</p>
                <p><strong>Amount Paid:</strong> £{(receiptRecord.amount_paid || 0).toFixed(2)}</p>
                <p className={receiptRecord.balance > 0 ? "text-red-600" : "text-green-600"}>
                  <strong>Balance:</strong> £{(receiptRecord.balance || 0).toFixed(2)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Method</Label>
                  <select 
                    value={receiptPaymentMethod} 
                    onChange={e => setReceiptPaymentMethod(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <Label>Received By *</Label>
                  <Input 
                    value={receiptReceivedBy} 
                    onChange={e => setReceiptReceivedBy(e.target.value)}
                    className="rounded-xl mt-1"
                    placeholder="Name of person"
                  />
                </div>
              </div>
              <Button 
                onClick={generateReceipt} 
                disabled={generatingReceipt === receiptRecord.id || !receiptReceivedBy}
                className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500"
              >
                {generatingReceipt === receiptRecord.id ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileText className="w-4 h-4 mr-2" /> Generate & Download Receipt</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" /> Receipt History
            </DialogTitle>
            <DialogDescription>
              View all receipts generated for {historyRecord?.full_name}
              {historyRecord?.child_name && ` (Child: ${historyRecord.child_name})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : receiptHistory.total_receipts === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No receipts found for this record</p>
              </div>
            ) : (
              <>
                {/* Current Receipt */}
                {receiptHistory.current_receipt && (
                  <div className="border border-green-200 bg-green-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full font-medium">Current</span>
                          <span className="font-bold text-green-800">{receiptHistory.current_receipt.receipt_number}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Generated: {new Date(receiptHistory.current_receipt.generated_at).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-600">
                          Amount: £{(receiptHistory.current_receipt.amount_paid || 0).toFixed(2)} | 
                          Balance: £{(receiptHistory.current_receipt.balance || 0).toFixed(2)} | 
                          Method: {receiptHistory.current_receipt.payment_method}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => downloadReceipt(receiptHistory.current_receipt.id)}
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                  </div>
                )}

                {/* Archived Receipts */}
                {receiptHistory.history.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                      <Archive className="w-4 h-4" /> Archived Receipts ({receiptHistory.history.length})
                    </h4>
                    {receiptHistory.history.map((receipt, index) => (
                      <div key={receipt.id} className="border border-gray-200 bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-gray-400 text-white text-xs rounded-full font-medium">Archived</span>
                              <span className="font-medium text-gray-700">{receipt.receipt_number}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Generated: {new Date(receipt.generated_at).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              Archived: {new Date(receipt.archived_at).toLocaleString()}
                              {receipt.archived_reason && ` (${receipt.archived_reason})`}
                            </p>
                            <p className="text-sm text-gray-500">
                              Amount: £{(receipt.amount_paid || 0).toFixed(2)} | 
                              Balance: £{(receipt.balance || 0).toFixed(2)} | 
                              Method: {receipt.payment_method}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => downloadArchivedReceipt(receipt.id)}
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary */}
                <div className="mt-4 pt-4 border-t text-sm text-gray-500 text-center">
                  Total: {receiptHistory.total_receipts} receipt(s) generated for this record
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Rules & Tenets Tab
const AdminRulesTenetsTab = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [annDialogOpen, setAnnDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingAnn, setEditingAnn] = useState(null);
  const [ruleForm, setRuleForm] = useState({ category: "Members are forbidden to", rule: "" });
  const [annForm, setAnnForm] = useState({ title: "", message: "" });

  const categories = ["Worship & Spiritual Conduct", "Substance Prohibition", "Dietary Laws", "Dress Code & Conduct", "Rules for Female Members", "Moral Purity", "Announcement"];

  const fetchData = async () => {
    try {
      const [rulesRes, annRes] = await Promise.all([
        axios.get(`${API}/admin/rules-tenets`),
        axios.get(`${API}/admin/special-announcements`)
      ]);
      setRules(rulesRes.data);
      setAnnouncements(annRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const saveRule = async () => {
    if (!ruleForm.rule) { toast.error("Rule text is required"); return; }
    try {
      if (editingRule) {
        await axios.put(`${API}/admin/rules-tenets/${editingRule}`, ruleForm);
        toast.success("Rule updated");
      } else {
        const maxOrder = rules.length > 0 ? Math.max(...rules.map(r => r.order || 0)) + 1 : 1;
        await axios.post(`${API}/admin/rules-tenets`, { ...ruleForm, order: maxOrder });
        toast.success("Rule added");
      }
      setRuleDialogOpen(false);
      setEditingRule(null);
      setRuleForm({ category: "Members are forbidden to", rule: "" });
      fetchData();
    } catch (err) { toast.error("Failed to save rule"); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await axios.delete(`${API}/admin/rules-tenets/${id}`);
    toast.success("Rule deleted");
    fetchData();
  };

  const moveRule = async (id, direction) => {
    const idx = rules.findIndex(r => r.id === id);
    if (idx < 0) return;
    const newRules = [...rules];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newRules.length) return;
    [newRules[idx], newRules[swapIdx]] = [newRules[swapIdx], newRules[idx]];
    const orders = newRules.map((r, i) => ({ id: r.id, order: i + 1 }));
    setRules(newRules);
    await axios.put(`${API}/admin/rules-tenets/reorder`, { orders });
  };

  const saveAnnouncement = async () => {
    if (!annForm.title || !annForm.message) { toast.error("Title and Message required"); return; }
    try {
      if (editingAnn) {
        await axios.put(`${API}/admin/special-announcements/${editingAnn}`, annForm);
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/admin/special-announcements`, annForm);
        toast.success("Announcement added");
      }
      setAnnDialogOpen(false);
      setEditingAnn(null);
      setAnnForm({ title: "", message: "" });
      fetchData();
    } catch (err) { toast.error("Failed to save"); }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await axios.delete(`${API}/admin/special-announcements/${id}`);
    toast.success("Deleted");
    fetchData();
  };

  const moveAnnouncement = async (id, direction) => {
    const idx = announcements.findIndex(a => a.id === id);
    if (idx < 0) return;
    const newAnns = [...announcements];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newAnns.length) return;
    [newAnns[idx], newAnns[swapIdx]] = [newAnns[swapIdx], newAnns[idx]];
    const orders = newAnns.map((a, i) => ({ id: a.id, order: i + 1 }));
    setAnnouncements(newAnns);
    try {
      await axios.put(`${API}/admin/special-announcements/reorder`, { orders });
    } catch (e) { toast.error("Failed to reorder"); fetchData(); }
  };

  const startPresentation = () => {
    window.open("/presentation", "_blank");
  };

  const seedRules = async () => {
    try {
      const res = await axios.post(`${API}/admin/rules-tenets/seed`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error("Failed to seed rules"); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6" data-testid="rules-tenets-tab">
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-600" /> Rules & Tenets</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Manage church rules for presentation display</p>
          </div>
          <div className="flex gap-2">
            <Button className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-600" onClick={startPresentation} data-testid="start-presentation-btn">
              <Play className="w-4 h-4 mr-2" /> Start Presentation
            </Button>
            {rules.length === 0 && (
              <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500" onClick={seedRules} data-testid="seed-rules-btn">
                <Download className="w-4 h-4 mr-2" /> Load Default Rules
              </Button>
            )}
            <Dialog open={ruleDialogOpen} onOpenChange={(open) => { setRuleDialogOpen(open); if (!open) { setEditingRule(null); setRuleForm({ category: "Members are forbidden to", rule: "" }); } }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="add-rule-btn"><Plus className="w-4 h-4 mr-2" /> Add Rule</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingRule ? "Edit" : "Add"} Rule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Category</Label>
                    <select value={ruleForm.category} onChange={e => setRuleForm({...ruleForm, category: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1" data-testid="rule-category-select">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Rule Text *</Label>
                    <textarea value={ruleForm.rule} onChange={e => setRuleForm({...ruleForm, rule: e.target.value})} placeholder="Enter rule..." className="w-full h-24 px-3 py-2 rounded-xl border border-gray-200 mt-1 resize-none" data-testid="rule-text-input" />
                  </div>
                  <Button onClick={saveRule} className="w-full rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="save-rule-btn">{editingRule ? "Update" : "Add"} Rule</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No rules added yet</p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, idx) => (
                <div key={rule.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border" data-testid={`rule-item-${rule.id}`}>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveRule(rule.id, "up")} disabled={idx === 0}><ChevronRight className="w-3 h-3 -rotate-90" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveRule(rule.id, "down")} disabled={idx === rules.length - 1}><ChevronRight className="w-3 h-3 rotate-90" /></Button>
                  </div>
                  <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full whitespace-nowrap">{rule.category}</span>
                  <span className="flex-1 text-sm">{rule.rule}</span>
                  <Button size="sm" variant="outline" onClick={() => { setEditingRule(rule.id); setRuleForm({ category: rule.category, rule: rule.rule }); setRuleDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteRule(rule.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Special Announcements Section */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-600" /> Special Announcements</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Displayed after rules during presentation</p>
          </div>
          <Dialog open={annDialogOpen} onOpenChange={(open) => { setAnnDialogOpen(open); if (!open) { setEditingAnn(null); setAnnForm({ title: "", message: "" }); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="add-announcement-btn"><Plus className="w-4 h-4 mr-2" /> Add Announcement</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingAnn ? "Edit" : "Add"} Special Announcement</DialogTitle>
                <DialogDescription>Each line becomes a bullet point. Leave a blank line for spacing.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div><Label>Title *</Label><Input value={annForm.title} onChange={e => setAnnForm({...annForm, title: e.target.value})} placeholder="Announcement title" className="rounded-xl mt-1" /></div>
                <div>
                  <Label>Message * <span className="text-xs text-gray-400 font-normal">(one point per line)</span></Label>
                  <textarea 
                    value={annForm.message} 
                    onChange={e => setAnnForm({...annForm, message: e.target.value})} 
                    placeholder={"First announcement point\nSecond announcement point\nThird announcement point"} 
                    className="w-full h-40 px-3 py-2 rounded-xl border border-gray-200 mt-1 resize-y font-mono text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" 
                  />
                  <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <p className="text-xs text-amber-700 font-semibold mb-1">Preview:</p>
                    {annForm.message ? annForm.message.split('\n').filter(l => l.trim()).map((line, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-amber-500 mt-0.5 flex-shrink-0">&#x2022;</span>
                        <span>{line.trim()}</span>
                      </div>
                    )) : <p className="text-xs text-gray-400 italic">Type your message above...</p>}
                  </div>
                </div>
                <Button onClick={saveAnnouncement} className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">{editingAnn ? "Update" : "Add"} Announcement</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No special announcements</p>
          ) : (
            <div className="space-y-3">
              {announcements.map((ann, idx) => (
                <div key={ann.id} className="flex items-start gap-3 p-5 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-100" onClick={() => moveAnnouncement(ann.id, "up")} disabled={idx === 0} title="Move up" data-testid={`ann-move-up-${ann.id}`}><ChevronRight className="w-4 h-4 -rotate-90" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-100" onClick={() => moveAnnouncement(ann.id, "down")} disabled={idx === announcements.length - 1} title="Move down" data-testid={`ann-move-down-${ann.id}`}><ChevronRight className="w-4 h-4 rotate-90" /></Button>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-800 text-xl mb-3">{ann.title}</p>
                    <div className="space-y-2">
                      {ann.message.split('\n').filter(l => l.trim()).map((line, i) => (
                        <div key={i} className="flex items-start gap-3 text-base sm:text-lg text-gray-700 leading-relaxed">
                          <span className="text-amber-500 mt-1 flex-shrink-0 text-xl">&#x2022;</span>
                          <span>{line.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => { setEditingAnn(ann.id); setAnnForm({ title: ann.title, message: ann.message }); setAnnDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteAnnouncement(ann.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Shepherds Box Tab - Internal messages for main admin
const AdminShepherdsBoxTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", message: "", priority: "Normal", expiry_date: "" });

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/shepherds-box`);
      setMessages(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, []);

  const saveMessage = async () => {
    if (!form.title || !form.message) { toast.error("Title and Message are required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/shepherds-box/${editingId}`, form);
        toast.success("Message updated");
      } else {
        await axios.post(`${API}/admin/shepherds-box`, form);
        toast.success("Message created");
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ title: "", message: "", priority: "Normal", expiry_date: "" });
      fetchMessages();
    } catch (err) { toast.error("Failed to save message"); }
  };

  const toggleStatus = async (msg) => {
    const newStatus = msg.status === "Approved" ? "Draft" : "Approved";
    try {
      await axios.put(`${API}/admin/shepherds-box/${msg.id}`, { status: newStatus });
      toast.success(`Message ${newStatus.toLowerCase()}`);
      fetchMessages();
    } catch (err) { toast.error("Failed to update status"); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/shepherds-box/${id}`);
      toast.success("Message deleted");
      setMessages(messages.filter(m => m.id !== id));
    } catch (err) { toast.error("Failed to delete"); }
  };

  const openEdit = (msg) => {
    setEditingId(msg.id);
    setForm({ title: msg.title, message: msg.message, priority: msg.priority || "Normal", expiry_date: msg.expiry_date || "" });
    setDialogOpen(true);
  };

  const isExpired = (expiry) => expiry && new Date(expiry) < new Date();

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;

  return (
    <Card className="border-0 shadow-lg" data-testid="shepherds-box-tab">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" /> Shepherds Box
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">Internal messages & key updates (Main Admin only)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ title: "", message: "", priority: "Normal", expiry_date: "" }); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="add-shepherds-msg-btn" onClick={() => { setEditingId(null); setForm({ title: "", message: "", priority: "Normal", expiry_date: "" }); }}>
              <Plus className="w-4 h-4 mr-2" /> New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit" : "Create"} Message</DialogTitle>
              <DialogDescription>Compose an internal message for the Shepherds Box</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Message title" className="rounded-xl mt-1" data-testid="shepherds-title-input" />
              </div>
              <div>
                <Label>Message *</Label>
                <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Enter your message..." className="w-full h-32 px-3 py-2 rounded-xl border border-gray-200 mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" data-testid="shepherds-message-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full h-10 px-3 rounded-xl border border-gray-200 mt-1" data-testid="shepherds-priority-select">
                    <option value="Normal">Normal</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} className="rounded-xl mt-1" data-testid="shepherds-expiry-input" />
                </div>
              </div>
              <Button onClick={saveMessage} className="w-full rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="save-shepherds-msg-btn">
                {editingId ? "Update Message" : "Create Message"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No messages in Shepherds Box</p>
            <p className="text-sm text-gray-400 mt-1">Create your first internal message</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map(msg => (
              <div key={msg.id} className={`rounded-xl border p-4 transition-all hover:shadow-md ${msg.priority === "Urgent" ? "border-red-200 bg-red-50/50" : "border-gray-200 bg-white"} ${isExpired(msg.expiry_date) ? "opacity-50" : ""}`} data-testid={`shepherds-msg-${msg.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="font-semibold text-gray-800">{msg.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${msg.priority === "Urgent" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{msg.priority}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${msg.status === "Approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{msg.status || "Draft"}</span>
                      {isExpired(msg.expiry_date) && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">Expired</span>}
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{msg.message}</p>
                    <div className="flex gap-4 mt-2 text-xs text-gray-400">
                      <span>Created: {msg.date || new Date(msg.created_at).toLocaleDateString()}</span>
                      {msg.expiry_date && <span>Expires: {new Date(msg.expiry_date + 'T12:00:00').toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant={msg.status === "Approved" ? "default" : "outline"} className={`rounded-full text-xs ${msg.status === "Approved" ? "bg-green-600 hover:bg-green-700" : ""}`} onClick={() => toggleStatus(msg)} data-testid={`approve-msg-${msg.id}`}>
                      {msg.status === "Approved" ? <><Check className="w-3 h-3 mr-1" /> Approved</> : <><CheckCircle className="w-3 h-3 mr-1" /> Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(msg)} data-testid={`edit-msg-${msg.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteMessage(msg.id)} data-testid={`delete-msg-${msg.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Team Tab - Manage Team Members for Meet the Team page
const AdminTeamTab = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    title: "",
    role_category: "Shepherd",
    description: "",
    photo_url: "",
    order: 0
  });

  const roleCategories = [
    "Shepherd",
    "Elders",
    "Mother in Israel",
    "Deacons",
    "Ministers",
    "Choir",
    "Other"
  ];

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeamMembers(res.data);
    } catch (err) {
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.title || !form.role_category) {
      toast.error("Please fill in name, title, and role category");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/team/${editingId}`, form);
        toast.success("Team member updated");
      } else {
        await axios.post(`${API}/team`, form);
        toast.success("Team member added");
      }
      setShowDialog(false);
      resetForm();
      fetchTeam();
    } catch (err) {
      console.error("Team save error:", err);
      toast.error(err.response?.data?.detail || "Failed to save team member");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: "",
      title: "",
      role_category: "Shepherd",
      description: "",
      photo_url: "",
      order: 0
    });
  };

  const handleEdit = (member) => {
    setForm({
      name: member.name,
      title: member.title,
      role_category: member.role_category,
      description: member.description || "",
      photo_url: member.photo_url || "",
      order: member.order || 0
    });
    setEditingId(member.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    try {
      await axios.delete(`${API}/team/${id}`);
      toast.success("Team member deleted");
      fetchTeam();
    } catch (err) {
      toast.error("Failed to delete team member");
    }
  };

  const getCategoryGradient = (category) => {
    switch (category.toLowerCase()) {
      case 'shepherd': return 'from-purple-600 to-indigo-700';
      case 'elders': return 'from-amber-500 to-orange-600';
      case 'deacons': return 'from-blue-500 to-cyan-600';
      case 'ministers': return 'from-emerald-500 to-teal-600';
      case 'mother in israel': return 'from-pink-500 to-rose-600';
      case 'choir': return 'from-violet-500 to-purple-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" /> Team Management
          </CardTitle>
          <CardDescription>Manage team members for the "Meet the Team" page</CardDescription>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4 mr-2" /> Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
              <DialogDescription>Enter the team member's information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Full Name *</label>
                <Input 
                  placeholder="e.g., Venerable Superior Evangelist John Doe" 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Title/Position *</label>
                <Input 
                  placeholder="e.g., Shepherd-In-Charge, Senior Elder" 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Role Category *</label>
                <Select value={form.role_category} onValueChange={v => setForm({...form, role_category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role category" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description (Optional)</label>
                <Textarea 
                  placeholder="Brief description or role details..." 
                  rows={2}
                  value={form.description} 
                  onChange={e => setForm({...form, description: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Photo</label>
                <div className="space-y-2">
                  {/* Image Preview */}
                  {form.photo_url && (
                    <div className="relative w-20 h-20 mx-auto">
                      <img 
                        src={form.photo_url.startsWith('/api') ? `${BACKEND_URL}${form.photo_url}` : form.photo_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover rounded-full border-2 border-gray-200"
                      />
                      <button 
                        type="button"
                        onClick={() => setForm({...form, photo_url: ""})}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input 
                        type="file" 
                        id="team-photo-upload"
                        accept="image/*" 
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            toast.info("Uploading image...");
                            const res = await axios.post(`${API}/upload/image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setForm({...form, photo_url: res.data.url});
                            toast.success("Image uploaded!");
                          } catch (err) {
                            toast.error("Failed to upload image");
                          }
                        }}
                      />
                      <div 
                        onClick={() => document.getElementById('team-photo-upload')?.click()}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border-2 border-dashed border-gray-300 transition-colors cursor-pointer"
                      >
                        <Upload className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Upload Photo</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Or enter URL manually */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-400">or enter URL</span>
                    </div>
                  </div>
                  <Input 
                    placeholder="https://example.com/photo.jpg" 
                    value={form.photo_url} 
                    onChange={e => setForm({...form, photo_url: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Display Order</label>
                <Input 
                  type="number"
                  placeholder="0" 
                  value={form.order} 
                  onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})}
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>
              <Button type="button" onClick={handleSubmit} className="w-full bg-blue-500 hover:bg-blue-600">
                {editingId ? "Update Team Member" : "Add Team Member"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No team members added yet</p>
            <p className="text-gray-400 text-sm">Click "Add Team Member" to create one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {roleCategories.filter(cat => teamMembers.some(m => m.role_category === cat)).map(category => (
              <div key={category}>
                <h4 className={`text-sm font-bold bg-gradient-to-r ${getCategoryGradient(category)} bg-clip-text text-transparent mb-2`}>
                  {category}
                </h4>
                <div className="space-y-2">
                  {teamMembers.filter(m => m.role_category === category).map((member) => (
                    <div key={member.id} className="flex items-center gap-4 p-3 border rounded-xl hover:bg-gray-50 transition-colors">
                      {/* Avatar */}
                      {member.photo_url ? (
                        <img 
                          src={member.photo_url} 
                          alt={member.name}
                          className="w-12 h-12 object-cover rounded-full border-2 border-gray-200"
                        />
                      ) : (
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getCategoryGradient(member.role_category)} flex items-center justify-center text-white text-lg font-bold`}>
                          {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      
                      {/* Info */}
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-800">{member.name}</h5>
                        <p className="text-sm text-gray-500">{member.title}</p>
                        {member.description && (
                          <p className="text-xs text-gray-400 mt-1">{member.description}</p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-xs">{member.order}</Badge>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(member)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(member.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Vineyard Events Tab
const AdminVineyardTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    flyer_url: "",
    event_date: "",
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/vineyard-events/all`);
      setEvents(res.data);
    } catch (err) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm({...form, flyer_url: res.data.url});
      toast.success("Flyer uploaded!");
    } catch (err) {
      toast.error("Failed to upload flyer");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.flyer_url) {
      toast.error("Please provide a title and upload a flyer");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/vineyard-events/${editingId}`, form);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/vineyard-events`, form);
        toast.success("Event created");
      }
      setShowDialog(false);
      resetForm();
      fetchEvents();
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save event");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      description: "",
      flyer_url: "",
      event_date: "",
      is_active: true,
      order: 0
    });
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title,
      description: event.description || "",
      flyer_url: event.flyer_url,
      event_date: event.event_date || "",
      is_active: event.is_active,
      order: event.order || 0
    });
    setEditingId(event.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/vineyard-events/${id}`);
      toast.success("Event deleted");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  const toggleActive = async (event) => {
    try {
      await axios.put(`${API}/vineyard-events/${event.id}`, {
        ...event,
        is_active: !event.is_active
      });
      toast.success(event.is_active ? "Event hidden" : "Event visible");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to update event");
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-500" /> Vineyard Events
          </CardTitle>
          <CardDescription>Manage event flyers for the homepage Vineyard section</CardDescription>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Event" : "Add Vineyard Event"}</DialogTitle>
              <DialogDescription>Upload a flyer and add event details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Flyer Upload */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Event Flyer *</label>
                {form.flyer_url ? (
                  <div className="relative">
                    {form.flyer_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="w-full h-48 bg-red-50 rounded-lg border border-red-200 flex flex-col items-center justify-center">
                        <FileText className="w-16 h-16 text-red-500 mb-2" />
                        <span className="text-sm font-medium text-red-700">PDF Flyer Uploaded</span>
                        <a 
                          href={form.flyer_url.startsWith('/api') ? `${BACKEND_URL}${form.flyer_url}` : form.flyer_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-1"
                        >
                          Click to view PDF
                        </a>
                      </div>
                    ) : (
                      <img 
                        src={form.flyer_url.startsWith('/api') ? `${BACKEND_URL}${form.flyer_url}` : form.flyer_url} 
                        alt="Flyer preview" 
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    )}
                    <button 
                      type="button"
                      onClick={() => setForm({...form, flyer_url: ""})}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <input 
                      type="file" 
                      id="flyer-upload"
                      accept="image/*,.pdf,application/pdf" 
                      className="hidden"
                      onChange={handleUpload}
                    />
                    <div 
                      onClick={() => document.getElementById('flyer-upload')?.click()}
                      className="flex flex-col items-center justify-center gap-2 p-8 bg-amber-50 hover:bg-amber-100 rounded-lg border-2 border-dashed border-amber-300 cursor-pointer transition-colors"
                    >
                      {uploading ? (
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-amber-500" />
                          <span className="text-sm text-amber-700 font-medium">Click to upload flyer</span>
                          <span className="text-xs text-amber-500">JPG, PNG, WebP, PDF supported</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Event Title *</label>
                <Input 
                  placeholder="e.g., Easter Revival Service" 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Event Date (Optional)</label>
                <Input 
                  placeholder="e.g., Sunday, April 6th 2025" 
                  value={form.event_date} 
                  onChange={e => setForm({...form, event_date: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description (Optional)</label>
                <Textarea 
                  placeholder="Details about the event..." 
                  rows={3}
                  value={form.description} 
                  onChange={e => setForm({...form, description: e.target.value})}
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Show on Homepage</label>
                <Button 
                  type="button"
                  variant={form.is_active ? "default" : "outline"}
                  size="sm"
                  onClick={() => setForm({...form, is_active: !form.is_active})}
                >
                  {form.is_active ? "Active" : "Hidden"}
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Display Order</label>
                <Input 
                  type="number"
                  placeholder="0" 
                  value={form.order} 
                  onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})}
                />
              </div>

              <Button type="button" onClick={handleSubmit} className="w-full bg-amber-500 hover:bg-amber-600">
                {editingId ? "Update Event" : "Add Event"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No vineyard events yet</p>
            <p className="text-gray-400 text-sm">Click "Add Event" to create one</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {events.map((event) => (
              <div key={event.id} className="group relative">
                <div className={`relative aspect-[3/4] rounded-xl overflow-hidden border-2 ${event.is_active ? 'border-green-400' : 'border-gray-300'}`}>
                  {event.flyer_url.toLowerCase().endsWith('.pdf') ? (
                    <div className={`w-full h-full bg-red-50 flex flex-col items-center justify-center ${!event.is_active && 'opacity-50'}`}>
                      <FileText className="w-16 h-16 text-red-500 mb-2" />
                      <span className="text-xs font-medium text-red-700 px-2 text-center">PDF Flyer</span>
                    </div>
                  ) : (
                    <img 
                      src={event.flyer_url.startsWith('/api') ? `${BACKEND_URL}${event.flyer_url}` : event.flyer_url} 
                      alt={event.title}
                      className={`w-full h-full object-cover ${!event.is_active && 'opacity-50'}`}
                    />
                  )}
                  {/* Status badge */}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${event.is_active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                    {event.is_active ? 'Active' : 'Hidden'}
                  </div>
                  {/* Action buttons overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="w-9 h-9 p-0 rounded-full"
                      onClick={() => handleEdit(event)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="w-9 h-9 p-0 rounded-full"
                      onClick={() => toggleActive(event)}
                    >
                      {event.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="w-9 h-9 p-0 rounded-full"
                      onClick={() => handleDelete(event.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-700 mt-2 line-clamp-1">{event.title}</p>
                {event.event_date && (
                  <p className="text-xs text-gray-500">{event.event_date}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminMembersTab = () => {
  const { isMainAdmin, hasPermission } = useAuth();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", ministry: "", date_of_birth: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { 
    axios.get(`${API}/members`).then(r => setMembers(r.data)).catch(() => {}); 
  }, []);

  const filteredMembers = members.filter(m => 
    (m.full_name || m.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.phone || "").includes(search) ||
    (m.ministry || "").toLowerCase().includes(search.toLowerCase())
  );

  const openAddDialog = () => {
    setEditingMember(null);
    setForm({ full_name: "", email: "", phone: "", address: "", ministry: "", date_of_birth: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setForm({
      full_name: member.full_name || member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      address: member.address || "",
      ministry: member.ministry || "",
      date_of_birth: member.date_of_birth || ""
    });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editingMember) {
        // Update existing member
        await axios.put(`${API}/members/${editingMember.id}`, form);
        toast.success("Member updated");
      } else {
        // Add new member
        await axios.post(`${API}/members`, form);
        toast.success("Member added");
      }
      setDialogOpen(false);
      setForm({ full_name: "", email: "", phone: "", address: "", ministry: "", date_of_birth: "" });
      setEditingMember(null);
      axios.get(`${API}/members`).then(r => setMembers(r.data));
    } catch (err) {
      toast.error("Failed to save member");
    }
  };

  const del = async (id) => { 
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    await axios.delete(`${API}/members/${id}`); 
    setMembers(members.filter(m => m.id !== id)); 
    toast.success("Deleted"); 
  };

  const exportMembers = async () => {
    if (!isMainAdmin) {
      toast.error("Only Main Admin can export data");
      return;
    }
    try {
      const response = await axios.get(`${API}/members/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ccc_medway_members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Members exported successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to export members");
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <CardTitle>Members ({members.length})</CardTitle>
        <div className="flex gap-2 items-center">
          <Input 
            placeholder="Search members..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="w-64 rounded-full"
          />
          {/* Only show export button for Main Admin */}
          {isMainAdmin && (
            <Button 
              variant="outline" 
              className="rounded-full border-green-500 text-green-600 hover:bg-green-50"
              onClick={exportMembers}
            >
              <Download className="w-4 h-4 mr-2" /> Export Excel
            </Button>
          )}
          {isMainAdmin && (
            <Button 
              variant="outline" 
              className="rounded-full border-orange-500 text-orange-600 hover:bg-orange-50"
              onClick={async () => {
                if (!window.confirm("This will convert all DOBs to DD MMM format (e.g., 01 FEB). Continue?")) return;
                try {
                  const res = await axios.post(`${API}/admin/migrate-dob-format`);
                  toast.success(res.data.message);
                  axios.get(`${API}/members`).then(r => setMembers(r.data));
                } catch (err) {
                  toast.error("Migration failed");
                }
              }}
            >
              <Edit className="w-4 h-4 mr-2" /> Fix DOB Format
            </Button>
          )}
          {hasPermission("members_write") && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" onClick={openAddDialog}>Add Member</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingMember ? "Edit Member" : "Add Member"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Full Name *</Label>
                    <Input placeholder="Full Name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Phone</Label>
                    <Input placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Email</Label>
                    <Input placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Address</Label>
                    <Input placeholder="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Ministry</Label>
                    <Input placeholder="Ministry" value={form.ministry} onChange={e => setForm({...form, ministry: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500 mb-1 block">Date of Birth (DD MMM)</Label>
                    <Input 
                      type="text" 
                      placeholder="e.g., 01 FEB, 15 APR" 
                      value={form.date_of_birth} 
                      onChange={e => {
                        const val = e.target.value.toUpperCase();
                        setForm({...form, date_of_birth: val});
                      }}
                      pattern="^[0-3]?[0-9]\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)$"
                    />
                    <p className="text-xs text-gray-400 mt-1">Format: DD MMM (e.g., 01 FEB)</p>
                  </div>
                  <Button onClick={save} className="w-full">{editingMember ? "Update Member" : "Add Member"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ministry</TableHead>
                <TableHead>DOB</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map(m => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">{m.full_name || m.name}</TableCell>
                  <TableCell>{m.phone || "-"}</TableCell>
                  <TableCell>{m.email || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-full">{m.ministry || "Member"}</Badge>
                  </TableCell>
                  <TableCell>{m.date_of_birth || "-"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={`text-xs ${m.entry_type === "registered" ? "bg-green-500" : "bg-yellow-500"}`}>
                        {m.entry_type === "registered" ? "Registered" : "Manual Entry"}
                      </Badge>
                      {m.is_registered && <Badge variant="outline" className="text-xs text-green-600">GPS ✓</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(m)}>
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(m.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Birthday Management Tab
const AdminBirthdaysTab = () => {
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [triggeringJob, setTriggeringJob] = useState(null);

  const fetchData = async () => {
    try {
      const [todayRes, upcomingRes, messagesRes, configRes] = await Promise.all([
        axios.get(`${API}/birthdays/today`),
        axios.get(`${API}/birthdays/upcoming?days=30`),
        axios.get(`${API}/birthdays/messages`),
        axios.get(`${API}/birthdays/email-config`)
      ]);
      setTodayBirthdays(todayRes.data);
      setUpcomingBirthdays(upcomingRes.data);
      setSentMessages(messagesRes.data);
      setEmailConfig(configRes.data);
    } catch (err) {
      console.error("Failed to fetch birthday data", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const sendBirthdayMessage = async (memberId) => {
    setSending(memberId);
    try {
      const response = await axios.post(`${API}/birthdays/send-message/${memberId}`);
      toast.success(response.data.message || "Birthday message sent!");
      fetchData();
    } catch (err) {
      toast.error("Failed to send message");
    }
    setSending(null);
  };

  const sendTestEmail = async () => {
    setSending("test");
    try {
      const response = await axios.post(`${API}/birthdays/send-test-email`);
      toast.success(response.data.message);
    } catch (err) {
      toast.error("Failed to send test email");
    }
    setSending(null);
  };

  const triggerJob = async (jobType) => {
    setTriggeringJob(jobType);
    try {
      const endpoint = jobType === "reminder" ? "trigger-reminder-job" : "trigger-wish-job";
      await axios.post(`${API}/birthdays/${endpoint}`);
      toast.success(`${jobType === "reminder" ? "Reminder" : "Wish"} job triggered!`);
      fetchData();
    } catch (err) {
      toast.error("Failed to trigger job");
    }
    setTriggeringJob(null);
  };

  const isMessageSentToday = (memberId) => {
    const today = new Date().toISOString().split('T')[0];
    return sentMessages.some(m => m.member_id === memberId && m.birthday_date === today);
  };

  // Birthday messages with Bible verses - each celebrant gets a unique random combination
  const birthdayMessages = [
    "Wishing you a day filled with love, joy, and God's abundant blessings!",
    "May your special day be as wonderful as the blessing you are to others!",
    "Celebrating you today! May God's grace shine upon you always.",
    "Happy Birthday! May this year bring you closer to your divine purpose.",
    "On your special day, may heaven's blessings overflow in your life!",
    "May your birthday be the beginning of a year filled with miracles!",
    "Rejoicing with you on this blessed day! You are a gift from God.",
    "Wishing you a birthday crowned with God's favor and mercy!",
    "May the Lord bless you and keep you on this special day and always!",
    "Happy Birthday! May your faith grow stronger with each passing year.",
    "Celebrating another year of God's faithfulness in your life!",
    "May your birthday bring you peace, joy, and divine surprises!",
    "On this blessed day, may God's love wrap around you like a warm embrace!",
    "Happy Birthday! You are fearfully and wonderfully made by God!",
    "May your special day be filled with sweet moments and eternal blessings!"
  ];

  const bibleVerses = [
    { text: "The Lord bless you and keep you; the Lord make His face shine upon you.", ref: "Numbers 6:24-26" },
    { text: "For I know the plans I have for you, declares the Lord, plans for welfare and not for evil, to give you a future and a hope.", ref: "Jeremiah 29:11" },
    { text: "May He give you the desire of your heart and make all your plans succeed.", ref: "Psalm 20:4" },
    { text: "This is the day that the Lord has made; let us rejoice and be glad in it.", ref: "Psalm 118:24" },
    { text: "The Lord your God is with you, the Mighty Warrior who saves. He will take great delight in you.", ref: "Zephaniah 3:17" },
    { text: "Every good and perfect gift is from above, coming down from the Father of the heavenly lights.", ref: "James 1:17" },
    { text: "I praise you because I am fearfully and wonderfully made; your works are wonderful.", ref: "Psalm 139:14" },
    { text: "The Lord is my shepherd; I shall not want. He makes me lie down in green pastures.", ref: "Psalm 23:1-2" },
    { text: "Delight yourself in the Lord, and He will give you the desires of your heart.", ref: "Psalm 37:4" },
    { text: "Trust in the Lord with all your heart and lean not on your own understanding.", ref: "Proverbs 3:5" },
    { text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you.", ref: "Joshua 1:9" },
    { text: "I can do all things through Christ who strengthens me.", ref: "Philippians 4:13" },
    { text: "The Lord is my light and my salvation—whom shall I fear?", ref: "Psalm 27:1" },
    { text: "Cast all your anxiety on Him because He cares for you.", ref: "1 Peter 5:7" },
    { text: "And we know that in all things God works for the good of those who love Him.", ref: "Romans 8:28" },
    { text: "But those who hope in the Lord will renew their strength. They will soar on wings like eagles.", ref: "Isaiah 40:31" },
    { text: "The Lord is good, a refuge in times of trouble. He cares for those who trust in Him.", ref: "Nahum 1:7" },
    { text: "Your word is a lamp for my feet, a light on my path.", ref: "Psalm 119:105" }
  ];

  // Generate a consistent random message and verse for each member based on their ID and current date
  const getMemberBirthdayContent = (memberId) => {
    // Create a seed based on member ID and current date for consistency within the day
    const today = new Date().toISOString().split('T')[0];
    const seed = (memberId + today).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    const messageIndex = seed % birthdayMessages.length;
    const verseIndex = (seed * 7) % bibleVerses.length; // Use different multiplier for verse
    
    return {
      message: birthdayMessages[messageIndex],
      verse: bibleVerses[verseIndex]
    };
  };

  // Monthly birthday messages - automatically updates based on current month (for header banner)
  const monthlyBirthdayMessages = {
    0: [ // January
      "🎉 Happy New Year Birthday! May this year bring you closer to God's purpose for your life.",
      "✨ A January birthday! May your new year start with abundant blessings and joy.",
      "🌟 Born in the month of new beginnings! May God's light shine upon you always.",
      "❄️ A winter blessing! May your birthday bring warmth to your heart and those around you."
    ],
    1: [ // February
      "💕 A February birthday! May love surround you today and always.",
      "❤️ Born in the month of love! May God's love overflow in your life.",
      "🌹 Happy Birthday! May your heart be filled with joy and divine blessings.",
      "💝 A special birthday in the month of hearts! May you feel God's endless love."
    ],
    2: [ // March
      "🌸 Spring is here, and so is your special day! May new blessings bloom in your life.",
      "🌷 A March birthday! May this season bring fresh mercies and new opportunities.",
      "🌱 Born as nature awakens! May your life continue to flourish in God's grace.",
      "☘️ Happy Birthday! May good fortune and blessings follow you all year long."
    ],
    3: [ // April
      "🌧️ April showers bring blessings! May your birthday be filled with God's favor.",
      "🌈 A spring birthday! May rainbows of hope color your path ahead.",
      "🐣 Born in the season of renewal! May God renew your strength and joy.",
      "🌻 Happy Birthday! May your life blossom with endless possibilities."
    ],
    4: [ // May
      "🌺 A May birthday! May flowers of blessing bloom in every area of your life.",
      "🌼 Born in the month of beauty! May God's beauty be reflected in all you do.",
      "🌿 Happy Birthday! May this season bring growth and abundant blessings.",
      "💐 A springtime blessing! May your birthday be as beautiful as God's creation."
    ],
    5: [ // June
      "☀️ A June birthday! May the summer sun warm your heart with God's love.",
      "🌞 Born in the month of light! May your path always be illuminated by His grace.",
      "🏵️ Happy Birthday! May this season bring joy, peace, and divine favor.",
      "🌴 A summer blessing! May your birthday be filled with rest and refreshment."
    ],
    6: [ // July
      "🎆 A July birthday! May your celebration be as bright as fireworks in the sky.",
      "🌊 Born in the heart of summer! May waves of blessings wash over you.",
      "⭐ Happy Birthday! May your life shine brightly for God's glory.",
      "🏖️ A mid-year blessing! May your birthday bring joy and celebration."
    ],
    7: [ // August
      "🌻 An August birthday! May you stand tall like sunflowers in God's garden.",
      "🔥 Born in the warmth of summer! May God's fire ignite passion in your heart.",
      "🌾 Happy Birthday! May this harvest season bring abundance to your life.",
      "☀️ A late summer blessing! May your birthday be filled with golden memories."
    ],
    8: [ // September
      "🍂 A September birthday! May new seasons bring new blessings into your life.",
      "📚 Born in the month of learning! May wisdom guide your every step.",
      "🍁 Happy Birthday! May the changing leaves remind you of God's beautiful plans.",
      "🌅 An autumn blessing! May your birthday mark a beautiful new chapter."
    ],
    9: [ // October
      "🎃 An October birthday! May harvest blessings overflow in your life.",
      "🍁 Born in the season of gratitude! May thankfulness fill your heart.",
      "🌙 Happy Birthday! May God's peace be your comfort through every season.",
      "🍂 An autumn blessing! May your birthday be rich with God's goodness."
    ],
    10: [ // November
      "🦃 A November birthday! May gratitude multiply your blessings this year.",
      "🍁 Born in the month of thanksgiving! May your heart overflow with praise.",
      "🙏 Happy Birthday! May this season of thanks bring you closer to God.",
      "🌾 A harvest birthday! May you reap abundant blessings in the coming year."
    ],
    11: [ // December
      "🎄 A December birthday! May the joy of Christmas fill your celebration.",
      "⭐ Born in the season of miracles! May God's wonder shine in your life.",
      "🎁 Happy Birthday! You are a precious gift to our church family.",
      "❄️ A Christmas season birthday! May divine blessings wrap around you like the warmest embrace."
    ]
  };

  // Get a random message for the current month (for header banner)
  const getCurrentMonthMessage = () => {
    const currentMonth = new Date().getMonth();
    const messages = monthlyBirthdayMessages[currentMonth];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Store the random message in state so it doesn't change on every render
  const [monthlyMessage, setMonthlyMessage] = useState('');
  
  useEffect(() => {
    setMonthlyMessage(getCurrentMonthMessage());
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Configuration Status */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📧 Birthday Email System
          </CardTitle>
          <CardDescription>
            Automated birthday email reminders configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${emailConfig?.email_configured ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                <span className="font-medium">Email Service</span>
              </div>
              <p className="text-sm text-gray-600">
                {emailConfig?.email_configured ? '✅ Resend API configured' : '⚠️ Running in mock mode (no API key)'}
              </p>
              <p className="text-xs text-gray-400 mt-1">From: {emailConfig?.sender_email}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${emailConfig?.scheduler_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="font-medium">Scheduler</span>
              </div>
              <p className="text-sm text-gray-600">
                {emailConfig?.scheduler_active ? '✅ Active' : '❌ Inactive'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Admin notifications: {emailConfig?.admin_notification_email}</p>
            </div>
          </div>
          
          {/* Scheduled Jobs */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Scheduled Jobs</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {emailConfig?.jobs?.map(job => (
                <div key={job.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-blue-800">{job.description}</p>
                    <p className="text-xs text-blue-600">{job.schedule}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-blue-600 border-blue-300"
                    onClick={() => triggerJob(job.id.includes("reminder") ? "reminder" : "wish")}
                    disabled={triggeringJob !== null}
                  >
                    {triggeringJob === (job.id.includes("reminder") ? "reminder" : "wish") ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Run Now"
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Test Email Button */}
          <div className="mt-4 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={sendTestEmail}
              disabled={sending === "test"}
              className="w-full md:w-auto"
            >
              {sending === "test" ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <>📧 Send Test Email</>
              )}
            </Button>
            <p className="text-xs text-gray-400 mt-2">
              Sends a test birthday email to the admin email address
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Today's Birthdays */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-pink-500 to-purple-500" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🎂 Today's Birthdays ({todayBirthdays.length})
          </CardTitle>
          <CardDescription>
            Send birthday greetings to members celebrating today
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Monthly Birthday Message */}
          {todayBirthdays.length > 0 && monthlyMessage && (
            <div className="mb-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border border-purple-200">
              <p className="text-center text-purple-800 font-medium italic">
                {monthlyMessage}
              </p>
            </div>
          )}
          
          {todayBirthdays.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No birthdays today</p>
            </div>
          ) : (
            <div className="space-y-4">
              {todayBirthdays.map(member => {
                const sent = isMessageSentToday(member.id);
                const birthdayContent = getMemberBirthdayContent(member.id);
                return (
                  <div key={member.id} className="p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-100">
                    {/* Member Info Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white text-xl">
                          🎂
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{member.full_name}</p>
                          <p className="text-sm text-gray-500">
                            {member.email || member.phone || "No contact"} • DOB: {member.date_of_birth}
                          </p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => sendBirthdayMessage(member.id)}
                        disabled={sending === member.id || sent}
                        className={`rounded-full ${sent ? "bg-green-500" : "bg-pink-500 hover:bg-pink-600"}`}
                      >
                        {sending === member.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : sent ? (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Sent</>
                        ) : (
                          "Send Email"
                        )}
                      </Button>
                    </div>
                    
                    {/* Personal Birthday Message */}
                    <div className="mt-3 p-3 bg-white/70 rounded-lg border border-purple-100">
                      <p className="text-sm text-purple-700 font-medium mb-2">
                        🎉 {birthdayContent.message}
                      </p>
                      <div className="flex items-start gap-2 text-xs">
                        <span className="text-purple-500">📖</span>
                        <div>
                          <p className="text-gray-600 italic">"{birthdayContent.verse.text}"</p>
                          <p className="text-purple-600 font-medium mt-1">— {birthdayContent.verse.ref}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Birthdays */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📅 Upcoming Birthdays ({upcomingBirthdays.length})
          </CardTitle>
          <CardDescription>
            Birthdays in the next 30 days - Each celebrant has a unique birthday message
          </CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingBirthdays.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No upcoming birthdays in the next 30 days</p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBirthdays.map(member => {
                const birthdayContent = getMemberBirthdayContent(member.id);
                return (
                  <div key={member.id} className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                    {/* Member Info Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                          member.days_until_birthday === 0 ? 'bg-pink-500' :
                          member.days_until_birthday === 1 ? 'bg-orange-500' :
                          member.days_until_birthday <= 7 ? 'bg-amber-500' :
                          'bg-amber-400'
                        }`}>
                          {member.days_until_birthday === 0 ? '🎂' : member.days_until_birthday}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{member.full_name}</p>
                          <p className="text-sm text-gray-500">{member.date_of_birth}</p>
                        </div>
                      </div>
                      <Badge className={`${
                        member.days_until_birthday === 0 ? 'bg-pink-100 text-pink-700' :
                        member.days_until_birthday === 1 ? 'bg-orange-100 text-orange-700' :
                        member.days_until_birthday <= 7 ? 'bg-amber-100 text-amber-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {member.days_until_birthday === 0 ? 'Today! 🎉' :
                         member.days_until_birthday === 1 ? 'Tomorrow! 🌟' :
                         `${member.days_until_birthday} days`}
                      </Badge>
                    </div>
                    
                    {/* Personal Birthday Message Preview */}
                    <div className="mt-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                      <p className="text-sm text-amber-700 font-medium mb-2">
                        🎁 {birthdayContent.message}
                      </p>
                      <div className="flex items-start gap-2 text-xs">
                        <span className="text-amber-500">📖</span>
                        <div>
                          <p className="text-gray-600 italic">"{birthdayContent.verse.text}"</p>
                          <p className="text-amber-600 font-medium mt-1">— {birthdayContent.verse.ref}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sent Messages History */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Birthday Messages History</CardTitle>
          <CardDescription>Recent birthday emails sent</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {sentMessages.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No messages sent yet</p>
            ) : (
              <div className="space-y-3">
                {sentMessages.map(msg => (
                  <div key={msg.id} className="p-4 border rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-gray-800">{msg.member_name}</p>
                      <Badge variant="outline" className="text-xs">
                        {new Date(msg.sent_at).toLocaleDateString()}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{msg.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      📧 {msg.email || msg.phone || "No contact"} • Status: {
                        msg.status === "sent" ? "✅ Email Sent" :
                        msg.status === "mocked" ? "⚠️ Mocked (No API Key)" :
                        msg.status === "no_email" ? "📝 Logged (No Email)" :
                        "❌ Failed"
                      }
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Notifications Tab
const AdminNotificationsTab = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/notifications`)
      .then(res => {
        setNotifications(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const markAsRead = async (id) => {
    await axios.put(`${API}/admin/notifications/${id}/read`);
    setNotifications(notifications.map(n => n.id === id ? {...n, is_read: true} : n));
  };

  const markAllRead = async () => {
    await axios.put(`${API}/admin/notifications/mark-all-read`);
    setNotifications(notifications.map(n => ({...n, is_read: true})));
    toast.success("All notifications marked as read");
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            🔔 Notifications
            {unreadCount > 0 && (
              <Badge className="bg-red-500">{unreadCount} new</Badge>
            )}
          </CardTitle>
          <CardDescription>Admin alerts and notifications</CardDescription>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllRead} className="rounded-full">
            Mark All Read
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          {notifications.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No notifications yet</p>
          ) : (
            <div className="space-y-3">
              {notifications.map(notif => (
                <div 
                  key={notif.id} 
                  className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                    notif.is_read ? "bg-white" : "bg-blue-50 border-blue-200"
                  }`}
                  onClick={() => !notif.is_read && markAsRead(notif.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      notif.notification_type === "birthday" ? "bg-pink-100" : "bg-blue-100"
                    }`}>
                      {notif.notification_type === "birthday" ? "🎂" : "🔔"}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-800">{notif.title}</p>
                        {!notif.is_read && <Badge className="bg-blue-500 text-xs">New</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Admin Ushers Tab - Member Registration (moved from public page)
const AdminUshersTab = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", ministry: "", date_of_birth: "" });
  const [loading, setLoading] = useState(false);
  const [recentMembers, setRecentMembers] = useState([]);

  useEffect(() => {
    // Fetch recently added members
    axios.get(`${API}/members`).then(res => {
      const sorted = res.data.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setRecentMembers(sorted.slice(0, 5));
    }).catch(() => {});
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please fill in Name and Phone Number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/members/register/manual`, form);
      toast.success("Member registered successfully!");
      setForm({ full_name: "", email: "", phone: "", address: "", ministry: "", date_of_birth: "" });
      // Refresh recent members
      axios.get(`${API}/members`).then(res => {
        const sorted = res.data.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setRecentMembers(sorted.slice(0, 5));
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to register member");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-blue-400 to-purple-500" />
        <CardHeader className="text-center">
          <CardTitle className="text-xl flex items-center justify-center gap-2">
            <UserCheck className="w-6 h-6 text-blue-500" /> Usher - Member Registration
          </CardTitle>
          <CardDescription>Register new church members manually</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4 max-w-md mx-auto">
            <div>
              <Label className="text-gray-700 font-medium">Full Name *</Label>
              <Input 
                value={form.full_name} 
                onChange={e => setForm({...form, full_name: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="Enter member's full name"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Phone Number *</Label>
              <Input 
                type="tel"
                value={form.phone} 
                onChange={e => setForm({...form, phone: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="07xxx xxxxxx"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Date of Birth (DD MMM)</Label>
              <Input 
                type="text"
                value={form.date_of_birth} 
                onChange={e => {
                  const val = e.target.value.toUpperCase();
                  setForm({...form, date_of_birth: val});
                }}
                className="rounded-xl mt-1" 
                placeholder="e.g., 01 FEB, 15 APR"
              />
              <p className="text-xs text-gray-400 mt-1">Format: DD MMM (e.g., 01 FEB)</p>
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Email</Label>
              <Input 
                type="email"
                value={form.email} 
                onChange={e => setForm({...form, email: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="email@example.com (optional)"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Address</Label>
              <Input 
                value={form.address} 
                onChange={e => setForm({...form, address: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="Home address (optional)"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Ministry</Label>
              <Input 
                value={form.ministry} 
                onChange={e => setForm({...form, ministry: e.target.value})} 
                className="rounded-xl mt-1" 
                placeholder="e.g., Choir, Ushering (optional)"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 h-12" 
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Register Member"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recently Added Members */}
      {recentMembers.length > 0 && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Recently Added Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentMembers.map((m, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium">{m.full_name || m.name}</p>
                    <p className="text-sm text-gray-500">{m.phone || 'No phone'}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// ============ Photo Gallery ============

const AdminPhotoGalleryTab = () => {
  const { hasPermission } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAlbum, setFilterAlbum] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", description: "", album: "" });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", album: "", active: true });

  const canWrite = hasPermission("announcements_write");

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/photo-gallery`);
      setPhotos(res.data);
    } catch (e) { toast.error("Failed to load photos"); }
    setLoading(false);
  };

  useEffect(() => { fetchPhotos(); }, []);

  const albums = Array.from(new Set(photos.map(p => p.album || "General")));
  const visible = filterAlbum === "all" ? photos : photos.filter(p => (p.album || "General") === filterAlbum);

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length) {
      toast.error(`${oversized.length} file(s) exceed 5MB — please compress and retry`);
      return;
    }
    const readers = files.map(f => new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(f);
    }));
    const dataUrls = await Promise.all(readers);
    setUploadFiles(files);
    setUploadPreviews(dataUrls);
  };

  const submitUpload = async () => {
    if (!uploadForm.title.trim()) return toast.error("Title required");
    if (!uploadPreviews.length) return toast.error("Select at least one photo");
    setUploading(true);
    try {
      await axios.post(`${API}/admin/photo-gallery/upload`, {
        title: uploadForm.title,
        description: uploadForm.description,
        album: uploadForm.album || "General",
        images: uploadPreviews,
      });
      toast.success(`Uploaded ${uploadPreviews.length} photo(s)`);
      setUploadOpen(false);
      setUploadForm({ title: "", description: "", album: "" });
      setUploadFiles([]);
      setUploadPreviews([]);
      fetchPhotos();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const savePhoto = async () => {
    try {
      await axios.put(`${API}/admin/photo-gallery/${editing.id}`, editForm);
      toast.success("Photo updated");
      setEditing(null);
      fetchPhotos();
    } catch (e) { toast.error("Update failed"); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/photo-gallery/${id}`);
    toast.success("Deleted");
    fetchPhotos();
  };

  const toggleActive = async (photo) => {
    await axios.put(`${API}/admin/photo-gallery/${photo.id}`, { active: !photo.active });
    fetchPhotos();
  };

  const movePhoto = async (photo, direction) => {
    const sameAlbum = photos.filter(p => (p.album || "General") === (photo.album || "General")).sort((a, b) => a.display_order - b.display_order);
    const idx = sameAlbum.findIndex(p => p.id === photo.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sameAlbum.length) return;
    const orders = [...sameAlbum];
    [orders[idx], orders[swapIdx]] = [orders[swapIdx], orders[idx]];
    const payload = orders.map((p, i) => ({ id: p.id, order: i + 1 }));
    await axios.put(`${API}/admin/photo-gallery/reorder`, { orders: payload });
    fetchPhotos();
  };

  return (
    <div className="space-y-4" data-testid="admin-photo-gallery">
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><ImageIcon className="w-5 h-5 text-purple-600" /> Photo Gallery</CardTitle>
            <CardDescription>Upload, organise, and manage photos shown on the public gallery page.</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterAlbum} onValueChange={setFilterAlbum}>
              <SelectTrigger className="w-44 rounded-full" data-testid="gallery-filter-album"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All albums ({photos.length})</SelectItem>
                {albums.map(a => (<SelectItem key={a} value={a}>{a} ({photos.filter(p => (p.album || "General") === a).length})</SelectItem>))}
              </SelectContent>
            </Select>
            {canWrite && (
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500" data-testid="gallery-upload-btn"><Plus className="w-4 h-4 mr-1" /> Upload Photos</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Upload Photo(s)</DialogTitle><DialogDescription>Multiple photos will share the same title, description &amp; album.</DialogDescription></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="Title *" value={uploadForm.title} onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })} data-testid="upload-title" />
                    <Textarea placeholder="Description (optional)" value={uploadForm.description} onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} rows={3} data-testid="upload-description" />
                    <Input placeholder="Album name (e.g. Harvest 2026, Convention)" value={uploadForm.album} onChange={e => setUploadForm({ ...uploadForm, album: e.target.value })} list="album-suggestions" data-testid="upload-album" />
                    <datalist id="album-suggestions">{albums.map(a => <option key={a} value={a} />)}</datalist>
                    <div className="border-2 border-dashed rounded-xl p-4 text-center hover:border-purple-400 transition-colors">
                      <Input type="file" accept="image/*" multiple onChange={onFilesSelected} className="hidden" id="photo-upload-input" />
                      <label htmlFor="photo-upload-input" className="cursor-pointer inline-flex flex-col items-center gap-2">
                        <Upload className="w-8 h-8 text-purple-500" />
                        <span className="text-sm font-medium">{uploadFiles.length ? `${uploadFiles.length} photo(s) selected` : "Click to select photos"}</span>
                        <span className="text-xs text-gray-400">Max 5MB per file — JPG, PNG, WebP</span>
                      </label>
                    </div>
                    {uploadPreviews.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                        {uploadPreviews.map((src, i) => (<img key={i} src={src} alt="" className="w-full h-16 object-cover rounded" />))}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
                    <Button onClick={submitUpload} disabled={uploading} className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="upload-submit-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" /><p>No photos yet. Upload some to get started.</p></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {visible.map(p => {
                const sameAlbum = photos.filter(x => (x.album || "General") === (p.album || "General")).sort((a, b) => a.display_order - b.display_order);
                const idx = sameAlbum.findIndex(x => x.id === p.id);
                return (
                  <div key={p.id} className={`group border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all ${!p.active ? "opacity-50" : ""}`} data-testid={`photo-card-${p.id}`}>
                    <div className="relative aspect-square bg-gray-100">
                      <img src={p.image_data} alt={p.title} className="w-full h-full object-cover" />
                      {!p.active && (<span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">HIDDEN</span>)}
                      <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-mono">{idx + 1}</span>
                    </div>
                    <div className="p-2 space-y-1">
                      <p className="text-xs font-bold truncate">{p.title}</p>
                      <p className="text-[10px] text-purple-600 uppercase tracking-wide truncate">{p.album || "General"}</p>
                      {p.description && <p className="text-[11px] text-gray-500 line-clamp-2">{p.description}</p>}
                      {canWrite && (
                        <div className="flex items-center justify-between pt-1 border-t">
                          <div className="flex gap-0.5">
                            <button onClick={() => movePhoto(p, "up")} disabled={idx === 0} className="text-gray-400 hover:text-purple-600 disabled:opacity-30 p-1" title="Move up" data-testid={`photo-up-${p.id}`}><ChevronRight className="w-3 h-3 -rotate-90" /></button>
                            <button onClick={() => movePhoto(p, "down")} disabled={idx === sameAlbum.length - 1} className="text-gray-400 hover:text-purple-600 disabled:opacity-30 p-1" title="Move down" data-testid={`photo-down-${p.id}`}><ChevronRight className="w-3 h-3 rotate-90" /></button>
                          </div>
                          <div className="flex gap-0.5">
                            <button onClick={() => toggleActive(p)} className="text-gray-400 hover:text-purple-600 p-1" title={p.active ? "Hide" : "Show"} data-testid={`photo-toggle-${p.id}`}>
                              {p.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </button>
                            <button onClick={() => { setEditing(p); setEditForm({ title: p.title, description: p.description || "", album: p.album || "General", active: p.active }); }} className="text-gray-400 hover:text-indigo-600 p-1" title="Edit" data-testid={`photo-edit-${p.id}`}><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => deletePhoto(p.id)} className="text-gray-400 hover:text-red-600 p-1" title="Delete" data-testid={`photo-delete-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Photo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <img src={editing.image_data} alt="" className="w-full max-h-48 object-cover rounded" />
              <Input placeholder="Title" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title" />
              <Textarea placeholder="Description" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3} data-testid="edit-description" />
              <Input placeholder="Album" value={editForm.album} onChange={e => setEditForm({ ...editForm, album: e.target.value })} list="album-suggestions-edit" data-testid="edit-album" />
              <datalist id="album-suggestions-edit">{albums.map(a => <option key={a} value={a} />)}</datalist>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} /> Visible on public gallery</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={savePhoto} className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="edit-save-btn"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Public Photo Gallery Page — album cards
const PhotoGalleryPage = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/photo-gallery/albums`).then(r => setAlbums(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pb-24 pt-6 px-4 max-w-6xl mx-auto" data-testid="photo-gallery-page">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-2"><ImageIcon className="w-8 h-8 text-purple-500" /></div>
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 bg-clip-text text-transparent">Photo Gallery</h1>
        <p className="text-gray-500 mt-2">Moments captured from our parish life</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
      ) : albums.length === 0 ? (
        <div className="text-center py-20 text-gray-400"><ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-40" /><p className="text-lg">No albums yet. Check back soon!</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map(a => (
            <button
              key={a.album}
              onClick={() => navigate(`/gallery/${encodeURIComponent(a.album)}`)}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`gallery-album-${a.album}`}
            >
              <img src={a.cover_image} alt={a.album} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                <h3 className="text-white text-xl font-bold mb-1 drop-shadow-lg">{a.album}</h3>
                <p className="text-white/80 text-sm flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {a.photo_count} {a.photo_count === 1 ? 'photo' : 'photos'}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Public Album Page — photos within an album, with lightbox
const PhotoAlbumPage = () => {
  const { album } = useParams();
  const decoded = decodeURIComponent(album || "");
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/photo-gallery?album=${encodeURIComponent(decoded)}`).then(r => setPhotos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [decoded]);

  const prev = () => setLightboxIdx((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setLightboxIdx((i) => (i < photos.length - 1 ? i + 1 : 0));

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxIdx(null); if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, photos.length]);

  return (
    <div className="pb-24 pt-6 px-4 max-w-6xl mx-auto" data-testid="photo-album-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <Button variant="outline" onClick={() => navigate("/gallery")} className="rounded-full mb-3"><ChevronRight className="w-4 h-4 mr-1 rotate-180" /> All albums</Button>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">{decoded}</h1>
          <p className="text-gray-500 text-sm mt-1">{photos.length} {photos.length === 1 ? 'photo' : 'photos'}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
      ) : photos.length === 0 ? (
        <div className="text-center py-20 text-gray-400"><ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-40" /><p>No photos in this album yet.</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <button key={p.id} onClick={() => setLightboxIdx(i)} className="group relative aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all" data-testid={`album-photo-${p.id}`}>
              <img src={p.image_data} alt={p.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                <p className="text-white text-xs font-semibold drop-shadow">{p.title}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)} data-testid="lightbox">
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }} className="absolute top-4 right-4 text-white/70 hover:text-white z-10" aria-label="Close"><X className="w-8 h-8" /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white/70 hover:text-white z-10" aria-label="Previous"><ChevronRight className="w-10 h-10 rotate-180" /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white/70 hover:text-white z-10" aria-label="Next"><ChevronRight className="w-10 h-10" /></button>
          <div className="max-w-5xl w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img src={photos[lightboxIdx].image_data} alt={photos[lightboxIdx].title} className="max-h-[75vh] w-auto object-contain rounded-lg" />
            <div className="text-center text-white max-w-2xl">
              <h3 className="text-2xl font-bold mb-2">{photos[lightboxIdx].title}</h3>
              {photos[lightboxIdx].description && <p className="text-white/80 leading-relaxed">{photos[lightboxIdx].description}</p>}
              <p className="text-white/50 text-sm mt-3 font-mono">{lightboxIdx + 1} / {photos.length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Admin Media Library Tab
const AdminMediaTab = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/uploads`);
      setFiles(res.data);
    } catch (err) {
      toast.error("Failed to load media files");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file(s)`);
      fetchFiles();
    }
    setUploading(false);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/uploads/${filename}`);
      toast.success("File deleted");
      fetchFiles();
    } catch (err) {
      toast.error("Failed to delete file");
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success("URL copied to clipboard!");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-purple-500" /> Media Library
          </CardTitle>
          <CardDescription>Upload and manage images for team photos and other content</CardDescription>
        </div>
        <div>
          <input 
            type="file" 
            id="media-upload-input"
            accept="image/*" 
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <Button 
            className="bg-purple-500 hover:bg-purple-600" 
            disabled={uploading}
            onClick={() => document.getElementById('media-upload-input')?.click()}
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload Images</>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-8">
            <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No files uploaded yet</p>
            <p className="text-gray-400 text-sm">Click "Upload Images" to add photos</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {files.map((file) => (
              <div key={file.filename} className="group relative">
                <div className="aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <img 
                    src={`${BACKEND_URL}${file.url}`} 
                    alt={file.filename}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="w-8 h-8 p-0 rounded-full"
                    onClick={() => copyUrl(file.url)}
                    title="Copy URL"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    className="w-8 h-8 p-0 rounded-full"
                    onClick={() => handleDelete(file.filename)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {/* File info */}
                <div className="mt-2">
                  <p className="text-xs text-gray-600 truncate">{file.filename}</p>
                  <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Management Tab (Main Admin Only)
const AdminManagementTab = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const defaultPermissions = {
    events_read: false, events_write: false,
    announcements_read: false, announcements_write: false,
    sermons_read: false, sermons_write: false,
    hymns_read: false, hymns_write: false,
    bible_lessons_read: false, bible_lessons_write: false,
    prayers_read: false, prayers_write: false,
    contact_read: false, contact_write: false,
    ushers_access: false,
    attendance_read: false, attendance_write: false,
    manual_attendance_read: false, manual_attendance_write: false,
    finance_read: false, finance_write: false,
    monthly_income_read: false, monthly_income_write: false,
    expenses_read: false, expenses_write: false,
    meetings_read: false, meetings_write: false,
    anointment_read: false, anointment_write: false,
    projects_read: false, projects_write: false,
    sunday_school_read: false, sunday_school_write: false,
    charity_read: false,
    harvest_read: false, harvest_write: false,
    members_read: false, members_write: false,
    birthdays_access: false,
    notifications_access: false,
    exports_download: false
  };
  const [form, setForm] = useState({
    username: "", display_name: "", password: "",
    permissions: {...defaultPermissions}
  });

  const fetchSubAdmins = () => {
    setLoading(true);
    axios.get(`${API}/admin/sub-admins`)
      .then(res => { setSubAdmins(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSubAdmins(); }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/sub-admins`, form);
      toast.success("Sub Admin created successfully");
      setDialogOpen(false);
      setForm({ username: "", display_name: "", password: "", permissions: {...defaultPermissions} });
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  const handleUpdate = async (adminId, updates) => {
    try {
      await axios.put(`${API}/admin/sub-admins/${adminId}`, updates);
      toast.success("Updated successfully");
      fetchSubAdmins();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this sub admin?")) return;
    try {
      await axios.delete(`${API}/admin/sub-admins/${adminId}`);
      toast.success("Sub Admin deleted");
      fetchSubAdmins();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleResetPassword = async (adminId) => {
    const newPassword = prompt("Enter new password for this sub admin:");
    if (!newPassword) return;
    try {
      await axios.put(`${API}/admin/sub-admins/${adminId}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error("Failed to reset password");
    }
  };

  const permissionLabels = {
    // Events & Announcements
    events_read: "View Events", events_write: "Edit Events",
    announcements_read: "View Announcements", announcements_write: "Edit Announcements",
    // Sermons
    sermons_read: "View Sermons", sermons_write: "Edit Sermons",
    // Hymns
    hymns_read: "View Hymns", hymns_write: "Edit Hymns",
    // Bible Lessons
    bible_lessons_read: "View Bible Lessons", bible_lessons_write: "Edit Bible Lessons",
    // Prayers
    prayers_read: "View Prayers", prayers_write: "Edit Prayers",
    // Contact Messages
    contact_read: "View Messages", contact_write: "Delete Messages",
    // Ushers
    ushers_access: "Ushers (Register Members)",
    // Attendance
    attendance_read: "View Attendance", attendance_write: "Edit Attendance",
    // Manual Attendance
    manual_attendance_read: "View Manual Attendance", manual_attendance_write: "Edit Manual Attendance",
    // Finance
    finance_read: "View Finance", finance_write: "Edit Finance",
    // Monthly Income
    monthly_income_read: "View Monthly Income", monthly_income_write: "Edit Monthly Income",
    // Expenses
    expenses_read: "View Expenses", expenses_write: "Edit Expenses",
    // Meetings
    meetings_read: "View Meetings", meetings_write: "Edit Meetings",
    // Anointment
    anointment_read: "View Anointment", anointment_write: "Edit Anointment",
    // Projects
    projects_read: "View Projects", projects_write: "Edit Projects",
    // Sunday School
    sunday_school_read: "View Sunday School", sunday_school_write: "Edit Sunday School",
    // Charity
    charity_read: "View Charity",
    // Harvest Thanksgiving
    harvest_read: "View Harvest", harvest_write: "Edit Harvest",
    // Members
    members_read: "View Members", members_write: "Edit Members",
    // Birthdays
    birthdays_access: "View Birthdays",
    // Notifications
    notifications_access: "View Notifications"
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">👥 Sub Admin Management</CardTitle>
          <CardDescription>Create and manage sub admin accounts</CardDescription>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-gradient-to-r from-blue-500 to-purple-500">
              <Plus className="w-4 h-4 mr-2" /> Add Sub Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Sub Admin</DialogTitle>
              <DialogDescription>Sub admins can only use features you grant them access to</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Username *</Label>
                <Input value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="e.g., john_doe" className="rounded-xl" />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="e.g., John Doe" className="rounded-xl" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Create a strong password" className="rounded-xl" />
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-semibold">Permissions</Label>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-full border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => {
                        const allTrue = {...defaultPermissions};
                        Object.keys(allTrue).forEach(k => { if (k !== 'exports_download') allTrue[k] = true; });
                        setForm({...form, permissions: allTrue});
                      }}
                    >
                      ✓ Select All
                    </Button>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="text-xs rounded-full border-red-500 text-red-600 hover:bg-red-50"
                      onClick={() => setForm({...form, permissions: {...defaultPermissions}})}
                    >
                      ✗ Clear All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {Object.entries(permissionLabels).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.permissions[key]}
                        onChange={e => setForm({...form, permissions: {...form.permissions, [key]: e.target.checked}})}
                        className="rounded"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-amber-600 mt-2">⚠️ Exports/Downloads are restricted to Main Admin only</p>
              </div>
              <Button onClick={handleCreate} className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-purple-500">Create Sub Admin</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {subAdmins.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No sub admins created yet</p>
        ) : (
          <div className="space-y-4">
            {subAdmins.map(admin => (
              <div key={admin.id} className="p-4 rounded-xl border bg-white shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{admin.display_name}</h3>
                    <p className="text-sm text-gray-500">@{admin.username}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${admin.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {admin.is_active ? "Active" : "Disabled"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {Object.entries(admin.permissions || {}).filter(([k, v]) => v && k !== "exports_download").map(([key]) => (
                    <span key={key} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">{permissionLabels[key] || key}</span>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleUpdate(admin.id, { is_active: !admin.is_active })}>
                    {admin.is_active ? "Disable" : "Enable"}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleResetPassword(admin.id)}>Reset Password</Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditingAdmin(admin)}>Edit Permissions</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Edit Permissions for {admin.display_name}</DialogTitle></DialogHeader>
                      <div className="flex gap-2 mb-4">
                        <Button 
                          size="sm" 
                          className="flex-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                          onClick={() => {
                            const allPerms = {...defaultPermissions};
                            Object.keys(allPerms).forEach(k => { if (k !== 'exports_download') allPerms[k] = true; });
                            handleUpdate(admin.id, { permissions: allPerms });
                          }}
                        >
                          ✓ Grant All Permissions
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 rounded-full border-red-500 text-red-600 hover:bg-red-50"
                          onClick={() => handleUpdate(admin.id, { permissions: {...defaultPermissions} })}
                        >
                          ✗ Remove All
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(permissionLabels).map(([key, label]) => (
                          <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              defaultChecked={admin.permissions?.[key]}
                              onChange={e => {
                                const newPerms = {...(admin.permissions || {}), [key]: e.target.checked};
                                handleUpdate(admin.id, { permissions: newPerms });
                              }}
                              className="rounded"
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleDelete(admin.id)}>Delete</Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Last login: {admin.last_login || "Never"}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Audit Logs Tab (Main Admin Only)
const AdminAuditLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ admin_id: "", resource_type: "" });

  const fetchLogs = () => {
    setLoading(true);
    let url = `${API}/admin/audit-logs?limit=200`;
    if (filter.admin_id) url += `&admin_id=${filter.admin_id}`;
    if (filter.resource_type) url += `&resource_type=${filter.resource_type}`;
    axios.get(url)
      .then(res => { setLogs(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); }, [filter]);

  const getActionColor = (action) => {
    const colors = {
      create: "bg-green-100 text-green-700",
      update: "bg-blue-100 text-blue-700",
      delete: "bg-red-100 text-red-700",
      login: "bg-purple-100 text-purple-700",
      export: "bg-amber-100 text-amber-700",
      unlock: "bg-cyan-100 text-cyan-700"
    };
    return colors[action] || "bg-gray-100 text-gray-700";
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">📋 Audit Logs</CardTitle>
        <CardDescription>Track all admin actions</CardDescription>
        <div className="flex gap-2 mt-4">
          <select
            value={filter.resource_type}
            onChange={e => setFilter({...filter, resource_type: e.target.value})}
            className="px-3 py-2 rounded-xl border text-sm"
          >
            <option value="">All Resources</option>
            <option value="finance">Finance</option>
            <option value="members">Members</option>
            <option value="attendance">Attendance</option>
            <option value="hymns">Hymns</option>
            <option value="sub_admin">Sub Admins</option>
            <option value="auth">Authentication</option>
          </select>
          <Button variant="outline" className="rounded-full" onClick={fetchLogs}>Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          {logs.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No logs found</p>
          ) : (
            <div className="space-y-2">
              {logs.map(log => (
                <div key={log.id} className="p-3 rounded-xl border bg-white text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action_type)}`}>
                        {log.action_type}
                      </span>
                      <span className="font-medium">{log.actor_name}</span>
                      <span className="text-xs text-gray-400">({log.actor_role})</span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-gray-600">{log.description}</p>
                  {log.resource_type && (
                    <span className="text-xs text-blue-500">Resource: {log.resource_type}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// Floating Social Links Component
const FloatingSocialLinks = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleZoomClick = () => {
    // Try to open Zoom app first, fallback to web
    const zoomAppUrl = "zoommtg://zoom.us/join?confno=6118707792&pwd=123456";
    const zoomWebUrl = "https://zoom.us/j/6118707792?pwd=123456";
    
    // Try app first
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = zoomAppUrl;
    document.body.appendChild(iframe);
    
    // Fallback to web after short delay
    setTimeout(() => {
      window.open(zoomWebUrl, '_blank');
      document.body.removeChild(iframe);
    }, 500);
  };

  return (
    <div className="fixed bottom-24 right-4 z-50 flex flex-col items-end gap-2">
      {/* Expanded Icons */}
      {isExpanded && (
        <div className="flex flex-col gap-2 mb-2 animate-in slide-in-from-bottom-2 duration-200">
          {/* Instagram */}
          <a
            href="https://www.instagram.com/ccc_medwaycentral?igsh=MXNxajVwaDJobDhmeg=="
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            aria-label="Instagram"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          
          {/* Facebook */}
          <a
            href="https://www.facebook.com/share/1K9u3KUH5r/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            aria-label="Facebook"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          
          {/* Zoom */}
          <button
            onClick={handleZoomClick}
            className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            aria-label="Join Zoom Meeting"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4.585 6.857h8.196c1.089 0 1.972.883 1.972 1.972v6.857c0 1.089-.883 1.972-1.972 1.972h-8.196c-1.089 0-1.972-.883-1.972-1.972V8.829c0-1.089.883-1.972 1.972-1.972zM17.857 9.429l3.857-2.571v10.286l-3.857-2.572V9.429z"/>
            </svg>
          </button>
        </div>
      )}
      
      {/* Main Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          isExpanded 
            ? "bg-gray-700 rotate-45" 
            : "bg-gradient-to-r from-blue-600 to-purple-600"
        }`}
        aria-label={isExpanded ? "Close social links" : "Open social links"}
      >
        {isExpanded ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Share2 className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
};

// Protected Route Wrapper - requires member login
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !user) {
      toast.error("Please register or login to access this feature");
      navigate("/");
    }
  }, [loading, user, navigate]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!user) return null;
  
  return children;
};

// =============== SERVICE FLOW - HYMN PRESENTATION ===============

// Helper: Split hymn lyrics into stanzas
const splitIntoStanzas = (lyrics) => {
  if (!lyrics) return [];
  const lines = lyrics.split('\n').map(l => l.trim()).filter(Boolean);
  const stanzas = [];
  let current = [];
  
  for (const line of lines) {
    // Stanza marker: "1:", "2:", "3:", "Chorus:", etc.
    const stanzaMatch = line.match(/^(\d+)\s*[:.](.*)$/);
    if (stanzaMatch && current.length > 0) {
      stanzas.push(current.join('\n'));
      const rest = stanzaMatch[2].trim();
      current = rest ? [rest] : [];
    } else if (stanzaMatch && current.length === 0) {
      const rest = stanzaMatch[2].trim();
      current = rest ? [rest] : [];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) stanzas.push(current.join('\n'));
  
  // If a single stanza has too many lines, split it into chunks of 6
  const result = [];
  for (const s of stanzas) {
    const sLines = s.split('\n');
    if (sLines.length > 8) {
      for (let i = 0; i < sLines.length; i += 6) {
        result.push(sLines.slice(i, i + 6).join('\n'));
      }
    } else {
      result.push(s);
    }
  }
  
  return result.length > 0 ? result : [lyrics.trim()];
};

// Inline controller for admin Service Flow page
const AdminInlineController = ({ sessionId, items }) => {
  const [session, setSession] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [quickRef, setQuickRef] = useState("");
  const [quickLoading, setQuickLoading] = useState(false);
  const [bibleSuggestions, setBibleSuggestions] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [hymnSearch, setHymnSearch] = useState("");
  const [hymnResults, setHymnResults] = useState([]);

  useEffect(() => { axios.get(`${API}/bible-books`).then(r => setAllBooks(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const poll = setInterval(async () => {
      try { const r = await axios.get(`${API}/service-flow/session/${sessionId}`); setSession(r.data); } catch(e) {}
    }, 800);
    return () => clearInterval(poll);
  }, [sessionId]);

  const update = async (changes) => {
    const u = { ...session, ...changes };
    setSession(u);
    await axios.put(`${API}/service-flow/session/${sessionId}`, u);
  };

  if (!session) return <div className="text-center py-4 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const cur = session.current_slide || 0;
  const slidesData = session.slides_data || [];
  const slideMap = session.slide_map || [];
  const totalSlides = session.total_slides || slidesData.length;
  const currentItemIdx = slidesData[cur]?.itemIndex ?? -1;
  const getItemSlides = (idx) => slidesData.map((s, si) => ({ ...s, slideIdx: si })).filter(s => s.itemIndex === idx);
  const goTo = (si) => update({ current_slide: si });

  const itemTypeIcons = { hymn: "♪", bible_reading: "📖", prayer: "🙏", announcement: "📢", song: "🎵", sermon: "📝", liturgy: "📜", blank: "▢", rules: "⚖" };

  return (
    <div className="space-y-2">
      {/* Prev/Next + Slide Counter */}
      <div className="flex items-center gap-2 mb-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => update({ current_slide: Math.max(cur - 1, 0) })} disabled={cur === 0}><ChevronRight className="w-4 h-4 rotate-180" /></Button>
        <span className="text-sm font-bold text-gray-600 flex-1 text-center">{totalSlides > 0 ? `${cur + 1} / ${totalSlides}` : '-'}</span>
        <Button size="sm" className="rounded-full bg-indigo-600" onClick={() => update({ current_slide: Math.min(cur + 1, totalSlides - 1) })} disabled={cur >= totalSlides - 1}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Service items with sub-slides */}
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {items.map((item, itemIdx) => {
          const isActive = currentItemIdx === itemIdx;
          const isExpanded = expandedItem === itemIdx || isActive;
          const subSlides = getItemSlides(itemIdx);
          const icon = itemTypeIcons[item.type] || "?";
          return (
            <div key={itemIdx} className={`rounded-xl overflow-hidden border ${isActive ? "border-indigo-400 bg-indigo-50" : "border-gray-200"}`}>
              <button onClick={() => { setExpandedItem(expandedItem === itemIdx ? null : itemIdx); const first = slideMap.find(m => m.itemIndex === itemIdx); if (first) goTo(first.startSlide); }} className={`w-full flex items-center gap-2 p-2 text-left text-sm ${isActive ? "bg-indigo-50" : "hover:bg-gray-50"}`}>
                <span className="text-base">{icon}</span>
                <span className={`flex-1 truncate ${isActive ? "font-semibold" : ""}`}>{item.title || item.type}</span>
                <span className="text-[10px] text-gray-400">{subSlides.length}</span>
                <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
              </button>
              {isExpanded && subSlides.length > 0 && (
                <div className="bg-gray-50 border-t px-1 py-1 space-y-0.5">
                  {subSlides.map((sub, si) => (
                    <button key={si} onClick={() => goTo(sub.slideIdx)} className={`w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${sub.slideIdx === cur ? "bg-indigo-500 text-white" : "hover:bg-gray-100 text-gray-600"}`}>
                      <span className={`w-4 h-4 rounded-full text-center text-[10px] leading-4 flex-shrink-0 ${sub.slideIdx === cur ? "bg-white text-indigo-600 font-bold" : "bg-gray-200"}`}>{si + 1}</span>
                      <span className="truncate">{sub.label ? `${sub.label}: ` : ''}{sub.text || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Scripture */}
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <Input value={quickRef} onChange={e => { setQuickRef(e.target.value); if (e.target.value.length >= 2) setBibleSuggestions(allBooks.filter(b => b.toLowerCase().startsWith(e.target.value.toLowerCase())).slice(0, 5)); else setBibleSuggestions([]); }} onKeyDown={e => { if (e.key === 'Enter' && quickRef) { setQuickLoading(true); const newItems = [...items, { type: "bible_reading", title: "Scripture", reference: quickRef, content: "", hymn_id: "", hymn_number: 0, order: items.length }]; update({ items: newItems }).then(() => setTimeout(async () => { try { const r = await axios.get(`${API}/service-flow/session/${sessionId}`); const t = (r.data.slide_map||[]).find(m => m.itemIndex === items.length); if (t) update({ ...r.data, current_slide: t.startSlide }); } catch(e){} setQuickLoading(false); }, 2500)); setQuickRef(""); setBibleSuggestions([]); } }} placeholder="Quick Scripture (e.g. John 3:16)" className="rounded-lg text-xs h-8" />
          {bibleSuggestions.length > 0 && <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50">{bibleSuggestions.map(b => <button key={b} onClick={() => { setQuickRef(b + " "); setBibleSuggestions([]); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 border-b">{b}</button>)}</div>}
        </div>
        <Button size="sm" className="rounded-lg bg-emerald-600 h-8 px-3 text-xs" disabled={!quickRef || quickLoading} onClick={() => { setQuickLoading(true); const newItems = [...items, { type: "bible_reading", title: "Scripture", reference: quickRef, content: "", hymn_id: "", hymn_number: 0, order: items.length }]; update({ items: newItems }).then(() => setTimeout(async () => { try { const r = await axios.get(`${API}/service-flow/session/${sessionId}`); const t = (r.data.slide_map||[]).find(m => m.itemIndex === items.length); if (t) update({ ...r.data, current_slide: t.startSlide }); } catch(e){} setQuickLoading(false); }, 2500)); setQuickRef(""); setBibleSuggestions([]); }}>{quickLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Go"}</Button>
      </div>

      {/* Quick Hymn */}
      <div className="relative">
        <Input value={hymnSearch} onChange={async e => { setHymnSearch(e.target.value); if (e.target.value.length >= 2) { const r = await axios.get(`${API}/hymns/search?q=${encodeURIComponent(e.target.value)}&limit=5`); setHymnResults(r.data); } else setHymnResults([]); }} placeholder="Quick Hymn (number, title, lyrics)" className="rounded-lg text-xs h-8" />
        {hymnResults.length > 0 && <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">{hymnResults.map(h => <button key={h.id} onClick={() => { const newItems = [...items, { type: "hymn", title: `Hymn #${h.number} - ${h.title_english || h.title_yoruba}`, hymn_id: h.id, hymn_number: h.number, content: "", reference: "", order: items.length }]; update({ items: newItems }).then(() => setTimeout(async () => { try { const r = await axios.get(`${API}/service-flow/session/${sessionId}`); const t = (r.data.slide_map||[]).find(m => m.itemIndex === items.length); if (t) update({ ...r.data, current_slide: t.startSlide }); } catch(e){} }, 2000)); setHymnSearch(""); setHymnResults([]); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 border-b flex gap-2"><span className="font-bold text-indigo-600">#{h.number}</span><span className="truncate">{h.title_english || h.title_yoruba}</span></button>)}</div>}
      </div>

      {/* Display Mode */}
      <div className="flex gap-1 flex-wrap">
        {[{v:"english",l:"EN"},{v:"yoruba",l:"YO"},{v:"both",l:"Both"},{v:"alternating",l:"Alt"}].map(m => (
          <button key={m.v} onClick={() => update({ display_mode: m.v })} className={`px-2 py-1 rounded-lg text-[10px] font-bold ${session.display_mode === m.v ? "bg-indigo-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{m.l}</button>
        ))}
      </div>
    </div>
  );
};

// Admin Service Flow Tab - Full EasyWorship-style service builder
const AdminServiceFlowTab = () => {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [items, setItems] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [serviceType, setServiceType] = useState("Sunday Morning");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addType, setAddType] = useState("hymn");
  const [hymnSearch, setHymnSearch] = useState("");
  const [hymnResults, setHymnResults] = useState([]);
  const [itemForm, setItemForm] = useState({ title: "", content: "", reference: "" });
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => 'sf-' + Math.random().toString(36).substring(2, 8));
  const [presenting, setPresenting] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [rulesPreviewData, setRulesPreviewData] = useState([]);

  // Preload rules data once for live preview of "rules" item type
  useEffect(() => {
    axios.get(`${API}/presentation/data`).then(res => setRulesPreviewData(res.data.rules || [])).catch(() => {});
  }, []);

  const serviceTypes = ["Sunday Morning", "Sunday Evening", "Wednesday", "Friday", "Holy Communion", "Special Service", "Harvest", "New Moon"];
  const itemTypes = [
    { value: "hymn", label: "Hymn", icon: "♪", color: "bg-indigo-500" },
    { value: "bible_reading", label: "Bible Reading", icon: "📖", color: "bg-emerald-500" },
    { value: "prayer", label: "Prayer", icon: "🙏", color: "bg-purple-500" },
    { value: "announcement", label: "Announcement", icon: "📢", color: "bg-amber-500" },
    { value: "song", label: "Song/Worship", icon: "🎵", color: "bg-pink-500" },
    { value: "sermon", label: "Sermon", icon: "📝", color: "bg-blue-500" },
    { value: "liturgy", label: "Liturgy", icon: "📜", color: "bg-teal-500" },
    { value: "blank", label: "Title Slide", icon: "▢", color: "bg-gray-500" },
    { value: "rules", label: "Rules & Tenets", icon: "⚖", color: "bg-red-500" },
  ];

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/service-flow/templates`);
      setTemplates(res.data);
    } catch (e) {}
    setLoading(false);
  };

  const saveTemplate = async () => {
    if (!templateName) { toast.error("Template name required"); return; }
    try {
      if (currentTemplate) {
        await axios.put(`${API}/service-flow/templates/${currentTemplate.id}`, { name: templateName, service_type: serviceType, items });
        toast.success("Template updated");
      } else {
        const res = await axios.post(`${API}/service-flow/templates`, { name: templateName, service_type: serviceType, items });
        setCurrentTemplate(res.data);
        toast.success("Template saved");
      }
      fetchTemplates();
    } catch (e) { toast.error("Failed to save"); }
  };

  const loadTemplate = (t) => {
    setCurrentTemplate(t);
    setTemplateName(t.name);
    setServiceType(t.service_type || "Sunday Morning");
    setItems(t.items || []);
  };

  const deleteTemplate = async (id) => {
    const password = window.prompt("Enter admin password to delete this template:");
    if (!password) return;
    try {
      await axios.post(`${API}/auth/login`, { email: "info@cccmedwaycentralparish.com", password });
      await axios.delete(`${API}/service-flow/templates/${id}`);
      if (currentTemplate?.id === id) { setCurrentTemplate(null); setItems([]); setTemplateName(""); }
      fetchTemplates();
      toast.success("Template deleted");
    } catch (err) {
      toast.error("Incorrect password");
    }
  };

  const newTemplate = () => {
    setCurrentTemplate(null);
    setItems([]);
    setTemplateName("");
    setServiceType("Sunday Morning");
  };

  const searchHymns = async (q) => {
    setHymnSearch(q);
    if (!q) { setHymnResults([]); return; }
    const res = await axios.get(`${API}/hymns/search?q=${encodeURIComponent(q)}&limit=10`);
    setHymnResults(res.data);
  };

  const addItem = (item) => {
    setItems([...items, { ...item, order: items.length }]);
    setAddDialogOpen(false);
    setItemForm({ title: "", content: "", reference: "" });
    setHymnSearch("");
    setHymnResults([]);
  };

  const addHymn = (h) => {
    addItem({ type: "hymn", title: `Hymn #${h.number} - ${h.title_english || h.title_yoruba}`, hymn_id: h.id, hymn_number: h.number, content: "", reference: "" });
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const newItems = [...items];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newItems.length) return;
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    setItems(newItems);
  };

  // Drag and drop reordering
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers require setData for drag to initiate
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) {}
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };
  const handleDragLeave = () => setDragOverIdx(null);
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }
    const newItems = [...items];
    const [moved] = newItems.splice(draggedIdx, 1);
    newItems.splice(idx, 0, moved);
    setItems(newItems);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const [editingIdx, setEditingIdx] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "", reference: "" });

  const openEditItem = (idx) => {
    const item = items[idx];
    setEditingIdx(idx);
    setEditForm({ title: item.title || "", content: item.content || "", reference: item.reference || "" });
  };

  const saveEditItem = () => {
    const newItems = [...items];
    newItems[editingIdx] = { ...newItems[editingIdx], title: editForm.title, content: editForm.content, reference: editForm.reference };
    setItems(newItems);
    setEditingIdx(null);
    toast.success("Item updated");
  };

  const startPresentation = async () => {
    await axios.put(`${API}/service-flow/session/${sessionId}`, {
      items, current_item: 0, current_slide: 0,
      display_mode: "english", font_size: 100, bold: false,
      high_contrast: false, focus_mode: false, accessibility_mode: false, background: "default"
    });
    window.open(`/service-flow/display/${sessionId}`, '_blank');
    setPresenting(true);
  };

  const controlUrl = `${window.location.origin}/service-flow/control/${sessionId}`;

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-4" data-testid="service-flow-tab">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-bold">Service Flow</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={newTemplate} data-testid="new-template-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
          <Button className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-600" onClick={startPresentation} disabled={items.length === 0} data-testid="present-btn">
            <Play className="w-4 h-4 mr-1" /> Present
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left - Templates List */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-600">Saved Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No saved templates</p>
            ) : templates.map(t => (
              <div key={t.id} className={`p-3 rounded-xl border cursor-pointer transition-all ${currentTemplate?.id === t.id ? "border-indigo-400 bg-indigo-50" : "hover:bg-gray-50"}`} onClick={() => loadTemplate(t)}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.service_type} • {(t.items || []).length} items</p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Middle - Service Order Builder */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-2 flex-1">
                <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name..." className="rounded-xl max-w-[200px]" />
                <select value={serviceType} onChange={e => setServiceType(e.target.value)} className="h-10 px-3 rounded-xl border border-gray-200 text-sm">
                  {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Button size="sm" className="rounded-full bg-indigo-600" onClick={saveTemplate}><Check className="w-3 h-3 mr-1" /> Save</Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Add Item Buttons */}
            <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b">
              {itemTypes.map(t => (
                <button key={t.value} onClick={() => { setAddType(t.value); setAddDialogOpen(true); }} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-white ${t.color} hover:opacity-90 transition-opacity`}>
                  <span>{t.icon}</span> {t.label}
                </button>
              ))}
            </div>

            {/* Service Order Items */}
            {items.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Music className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Add items to build your service order</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {items.map((item, idx) => {
                  const typeInfo = itemTypes.find(t => t.value === item.type) || { icon: "?", color: "bg-gray-400", label: item.type };
                  const isDragging = draggedIdx === idx;
                  const isDropTarget = dragOverIdx === idx && draggedIdx !== null && draggedIdx !== idx;
                  return (
                    <div
                      key={idx}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, idx)}
                      onDragEnd={handleDragEnd}
                      onClick={() => setPreviewIdx(idx)}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border bg-white hover:shadow-sm transition-all cursor-pointer ${
                        previewIdx === idx ? "ring-2 ring-indigo-400 border-indigo-300" : ""
                      } ${
                        isDragging ? "opacity-40 scale-[0.98]" : ""
                      } ${
                        isDropTarget ? (draggedIdx > idx ? "border-t-4 border-t-indigo-500" : "border-b-4 border-b-indigo-500") : ""
                      }`}
                      data-testid={`flow-item-${idx}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-indigo-500 flex-shrink-0 touch-none"
                        title="Drag to reorder"
                        aria-label="Drag handle"
                        data-testid={`flow-item-drag-${idx}`}
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={(e) => { e.stopPropagation(); moveItem(idx, -1); }} disabled={idx === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronRight className="w-3 h-3 -rotate-90" /></button>
                        <button onClick={(e) => { e.stopPropagation(); moveItem(idx, 1); }} disabled={idx === items.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronRight className="w-3 h-3 rotate-90" /></button>
                      </div>
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs ${typeInfo.color}`}>{typeInfo.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title || typeInfo.label}</p>
                        {item.reference && <p className="text-xs text-gray-400">{item.reference}</p>}
                        {item.content && <p className="text-xs text-gray-400 truncate">{item.content.substring(0, 60)}...</p>}
                      </div>
                      <span className="text-[10px] text-gray-300 font-mono">{idx + 1}</span>
                      <button onClick={(e) => { e.stopPropagation(); openEditItem(idx); }} className="text-gray-300 hover:text-indigo-500"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="text-gray-300 hover:text-red-500"><XCircle className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Live Slide Preview Pane */}
            {items.length > 0 && (
              <div className="border-t mt-4 pt-4">
                <SlidePreview item={items[previewIdx] || items[0]} allRulesData={rulesPreviewData} />
                <p className="text-[10px] text-gray-400 text-center mt-1">Tip: Click any item above to preview its slides here</p>
              </div>
            )}

            {/* QR Code + Laptop Control */}
            {items.length > 0 && (
              <div className="border-t mt-4 pt-4 flex items-center gap-4 flex-wrap">
                <div className="bg-white p-2 rounded-xl border shadow-sm">
                  <QRCodeSVG value={controlUrl} size={80} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-700">Remote Control</p>
                  <p className="text-xs text-gray-400 mb-2">Scan QR on phone or open in new tab</p>
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => window.open(controlUrl, '_blank')}>
                    <ExternalLink className="w-3 h-3 mr-1" /> Open Controller
                  </Button>
                </div>
              </div>
            )}

            {/* Inline Slide Controller */}
            {presenting && items.length > 0 && (
              <div className="border-t mt-4 pt-4 space-y-3">
                <p className="font-semibold text-sm text-gray-700 flex items-center gap-2"><Play className="w-4 h-4 text-emerald-600" /> Live Controller</p>
                <AdminInlineController sessionId={sessionId} items={items} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {itemTypes.find(t => t.value === addType)?.label || "Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {addType === "hymn" ? (
              <div className="space-y-3">
                <Input value={hymnSearch} onChange={e => searchHymns(e.target.value)} placeholder="Search hymn by number or title..." className="rounded-xl" autoFocus />
                {hymnResults.length > 0 && (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {hymnResults.map(h => (
                      <button key={h.id} onClick={() => addHymn(h)} className="w-full text-left p-2.5 rounded-xl border hover:bg-indigo-50 transition-colors flex items-center gap-2">
                        <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">#{h.number}</span>
                        <span className="text-sm font-medium">{h.title_english || h.title_yoruba}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : addType === "bible_reading" ? (
              <div className="space-y-3">
                <div><Label>Title (e.g., "1st Lesson")</Label><Input value={itemForm.title} onChange={e => setItemForm({...itemForm, title: e.target.value})} placeholder="1st Lesson" className="rounded-xl mt-1" /></div>
                <div><Label>Bible Reference</Label><Input value={itemForm.reference} onChange={e => setItemForm({...itemForm, reference: e.target.value})} placeholder="e.g. John 3:16-18" className="rounded-xl mt-1" /></div>
                <Button onClick={() => addItem({ type: "bible_reading", title: itemForm.title || "Bible Reading", reference: itemForm.reference, content: "", hymn_id: "", hymn_number: 0 })} className="w-full rounded-xl bg-emerald-600">Add Bible Reading</Button>
              </div>
            ) : addType === "liturgy" ? (
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={itemForm.title} onChange={e => setItemForm({...itemForm, title: e.target.value})} placeholder="e.g. Opening Prayers" className="rounded-xl mt-1" /></div>
                <div><Label>Content (use "M:" for Minister, "C:" for Congregation)</Label><textarea value={itemForm.content} onChange={e => setItemForm({...itemForm, content: e.target.value})} placeholder={"M: The Lord be with you\nC: And also with you\nM: Let us pray"} className="w-full h-32 px-3 py-2 rounded-xl border mt-1 font-mono text-sm resize-y" /></div>
                <Button onClick={() => addItem({ type: "liturgy", title: itemForm.title || "Liturgy", content: itemForm.content, reference: "", hymn_id: "", hymn_number: 0 })} className="w-full rounded-xl bg-teal-600">Add Liturgy</Button>
              </div>
            ) : addType === "rules" ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">This will add the Rules & Tenets slides to the service flow.</p>
                <Button onClick={() => addItem({ type: "rules", title: "Rules & Tenets", content: "", reference: "", hymn_id: "", hymn_number: 0 })} className="w-full rounded-xl bg-red-600">Add Rules & Tenets</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={itemForm.title} onChange={e => setItemForm({...itemForm, title: e.target.value})} placeholder={addType === "sermon" ? "Sermon Topic" : addType === "song" ? "Song Title" : "Title"} className="rounded-xl mt-1" /></div>
                <div><Label>Content (each line shown separately)</Label><textarea value={itemForm.content} onChange={e => setItemForm({...itemForm, content: e.target.value})} placeholder="Enter content... Each line will be displayed clearly" className="w-full h-32 px-3 py-2 rounded-xl border mt-1 text-sm resize-y" /></div>
                <Button onClick={() => addItem({ type: addType, title: itemForm.title || itemTypes.find(t => t.value === addType)?.label, content: itemForm.content, reference: "", hymn_id: "", hymn_number: 0 })} className="w-full rounded-xl bg-indigo-600">Add {itemTypes.find(t => t.value === addType)?.label}</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editingIdx !== null} onOpenChange={(open) => { if (!open) setEditingIdx(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editingIdx !== null ? (items[editingIdx]?.type || '').replace('_', ' ') : ''}</DialogTitle>
          </DialogHeader>
          {editingIdx !== null && (
            <div className="space-y-4 py-2">
              <div><Label>Title</Label><Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} className="rounded-xl mt-1" /></div>
              {items[editingIdx]?.type === "bible_reading" && (
                <div><Label>Bible Reference</Label><Input value={editForm.reference} onChange={e => setEditForm({...editForm, reference: e.target.value})} placeholder="e.g. John 3:16-18" className="rounded-xl mt-1" /></div>
              )}
              {items[editingIdx]?.type !== "hymn" && items[editingIdx]?.type !== "rules" && (
                <div><Label>Content</Label><textarea value={editForm.content} onChange={e => setEditForm({...editForm, content: e.target.value})} className="w-full h-32 px-3 py-2 rounded-xl border mt-1 text-sm resize-y" /></div>
              )}
              <Button onClick={saveEditItem} className="w-full rounded-xl bg-indigo-600">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// AutoFitText - binary-searches the largest font size that fits the parent container.
// Reacts to text changes, container resizes, and a user multiplier (default 1.0).
// `targetFillRatio` (0.5–1.0) controls how aggressively to fill (1.0 = right to edges).
const AutoFitText = ({
  text,
  italic = false,
  color = "text-white",
  maxPx = 240,
  minPx = 20,
  multiplier = 1,
  lineHeight = 1.2,
  targetFillRatio = 0.94,
  className = "",
  style = {},
}) => {
  const wrapperRef = React.useRef(null);
  const measureRef = React.useRef(null);
  const [size, setSize] = React.useState(96);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    if (!wrapper || !measure) return;

    let raf = 0;
    const compute = () => {
      const W = wrapper.clientWidth * targetFillRatio;
      const H = wrapper.clientHeight * targetFillRatio;
      if (W <= 0 || H <= 0) return;
      measure.style.maxWidth = W + "px";
      measure.style.lineHeight = String(lineHeight);
      let lo = minPx;
      let hi = maxPx;
      let best = minPx;
      for (let i = 0; i < 16 && lo <= hi; i++) {
        const mid = Math.floor((lo + hi) / 2);
        measure.style.fontSize = mid + "px";
        const fitsH = measure.scrollHeight <= H + 1;
        const fitsW = measure.scrollWidth <= W + 1;
        if (fitsH && fitsW) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      setSize(Math.max(minPx, Math.round(best * multiplier)));
    };

    raf = requestAnimationFrame(compute);
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    });
    ro.observe(wrapper);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [text, multiplier, maxPx, minPx, targetFillRatio, lineHeight]);

  return (
    <div ref={wrapperRef} className={`w-full h-full flex items-center justify-center overflow-hidden ${className}`}>
      {/* visible rendered text */}
      <p
        className={`font-black ${color} ${italic ? "italic" : ""} text-center w-full`}
        style={{ fontSize: size + "px", whiteSpace: "pre-line", lineHeight, ...style }}
      >
        {text}
      </p>
      {/* hidden measurement copy - same content & style, off-screen */}
      <p
        ref={measureRef}
        aria-hidden
        className={`font-black ${italic ? "italic" : ""} text-center`}
        style={{
          position: "absolute",
          left: "-99999px",
          top: 0,
          visibility: "hidden",
          pointerEvents: "none",
          whiteSpace: "pre-line",
          lineHeight,
          ...style,
        }}
      >
        {text}
      </p>
    </div>
  );
};

// SlidePreview - Mini 16:9 preview pane that builds the same slides the display would
// render for a given Service Flow item, with the same auto-fit logic. Used in the
// admin Service Flow editor so presenters can verify each item before going live.
const SlidePreview = ({ item, allRulesData = [] }) => {
  const [slides, setSlides] = React.useState([]);
  const [idx, setIdx] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  // Build slides for this item using the same logic as the display
  React.useEffect(() => {
    if (!item) { setSlides([]); return; }
    setIdx(0);
    let cancelled = false;
    const build = async () => {
      setLoading(true);
      const out = [];
      try {
        if (item.type === "hymn" && item.hymn_id) {
          try {
            const res = await axios.get(`${API}/hymns/search?q=&limit=1000`);
            const hymn = res.data.find(h => h.id === item.hymn_id);
            if (hymn) {
              const enS = splitIntoStanzas(hymn.lyrics_english);
              const yoS = splitIntoStanzas(hymn.lyrics_yoruba);
              out.push({ itemType: "hymn_title", title: `Hymn ${hymn.number}`, subtitle: hymn.title_english });
              const maxStanzas = Math.max(enS.length, yoS.length);
              for (let s = 0; s < maxStanzas; s++) {
                out.push({ itemType: "hymn", english: enS[s] || "", yoruba: yoS[s] || "" });
              }
            }
          } catch (e) {}
        } else if (item.type === "bible_reading" && item.reference) {
          out.push({ itemType: "bible_title", title: item.title || "Bible Reading", reference: item.reference });
          try {
            const res = await axios.get(`${API}/bible-verse/${encodeURIComponent(item.reference)}`);
            const verses = res.data.verses || [];
            if (verses.length > 0) {
              verses.forEach(v => out.push({ itemType: "bible_text", reference: `${item.reference}:${v.verse}`, text: (v.text || '').trim() }));
            } else {
              out.push({ itemType: "bible_text", reference: item.reference, text: res.data.text || "Verse not found" });
            }
          } catch (e) {
            out.push({ itemType: "bible_text", reference: item.reference, text: "Preview unavailable" });
          }
        } else if (item.type === "rules") {
          (allRulesData || []).slice(0, 30).forEach(r => {
            out.push({ itemType: "rule", category: r.category, rule: r.rule });
          });
          if (out.length === 0) out.push({ itemType: "default", title: "Rules & Tenets", lines: [] });
        } else if (item.type === "liturgy") {
          const lines = (item.content || "").split('\n').filter(l => l.trim());
          out.push({ itemType: "liturgy", title: item.title || "Liturgy", lines });
        } else {
          // Generic: prayer, announcement, song, sermon, blank, etc.
          const lines = (item.content || "").split('\n').filter(l => l.trim());
          out.push({ itemType: "default", title: item.title || "", lines });
        }
      } catch (e) {}
      if (!cancelled) {
        setSlides(out);
        setLoading(false);
      }
    };
    build();
    return () => { cancelled = true; };
  }, [item, allRulesData]);

  const slide = slides[idx];
  const total = slides.length;

  const renderMini = () => {
    if (!slide) {
      return (
        <div className="text-white/60 text-center">
          <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-xs">{loading ? "Building preview..." : "No preview available"}</p>
        </div>
      );
    }
    const lh = 1.2;
    const style = { textShadow: '0 2px 8px rgba(0,0,0,0.6)', whiteSpace: 'pre-line' };
    switch (slide.itemType) {
      case "hymn_title":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <p className="text-indigo-300/80 text-[8px] uppercase tracking-[0.3em] mb-1 flex-shrink-0">Hymn</p>
            <div className="flex-1 w-full min-h-0">
              <AutoFitText text={slide.title} maxPx={64} minPx={8} lineHeight={lh} style={style} />
            </div>
            {slide.subtitle && <p className="text-[8px] text-white/60 mt-1 flex-shrink-0 px-1 text-center truncate w-full">{slide.subtitle}</p>}
          </div>
        );
      case "bible_title":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <p className="text-emerald-300/80 text-[8px] uppercase tracking-[0.3em] mb-1 flex-shrink-0">{slide.title || "Bible Reading"}</p>
            <div className="flex-1 w-full min-h-0">
              <AutoFitText text={slide.reference} maxPx={64} minPx={8} lineHeight={lh} style={style} />
            </div>
          </div>
        );
      case "hymn":
        return (
          <div className="w-full h-full">
            {slide.english && slide.yoruba ? (
              <div className="h-full flex gap-1.5">
                <div className="flex-1 min-h-0"><AutoFitText text={slide.english} maxPx={44} minPx={6} lineHeight={lh} style={style} /></div>
                <div className="w-px self-stretch bg-white/20 flex-shrink-0" />
                <div className="flex-1 min-h-0"><AutoFitText text={slide.yoruba} italic color="text-amber-100" maxPx={44} minPx={6} lineHeight={lh} style={style} /></div>
              </div>
            ) : (
              <div className="h-full"><AutoFitText text={slide.english || slide.yoruba || ""} italic={!slide.english} color={slide.english ? "text-white" : "text-amber-100"} maxPx={60} minPx={6} lineHeight={lh} style={style} /></div>
            )}
          </div>
        );
      case "bible_text":
        return (
          <div className="w-full h-full flex flex-col">
            <p className="text-emerald-300 text-[8px] uppercase tracking-[0.2em] mb-1 text-center flex-shrink-0">{slide.reference}</p>
            <div className="flex-1 min-h-0"><AutoFitText text={slide.text} maxPx={48} minPx={6} lineHeight={lh} style={style} /></div>
          </div>
        );
      case "rule":
        return (
          <div className="w-full h-full flex flex-col">
            <p className="text-indigo-300/60 text-[8px] uppercase tracking-[0.3em] mb-1 text-center flex-shrink-0">{slide.category}</p>
            <div className="flex-1 min-h-0"><AutoFitText text={slide.rule} maxPx={52} minPx={6} lineHeight={lh} style={style} /></div>
          </div>
        );
      case "liturgy": {
        const combined = (slide.lines || []).join("\n");
        return (
          <div className="w-full h-full flex flex-col">
            {slide.title && <p className="text-teal-300/60 text-[8px] uppercase tracking-[0.3em] mb-1 text-center flex-shrink-0">{slide.title}</p>}
            <div className="flex-1 min-h-0"><AutoFitText text={combined} maxPx={44} minPx={6} lineHeight={lh} style={style} /></div>
          </div>
        );
      }
      default: {
        const lines = (slide.lines || []).join("\n");
        const onlyTitle = (slide.lines || []).length === 0;
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            {slide.title && !onlyTitle && <p className="text-amber-300/80 text-[8px] uppercase tracking-[0.2em] mb-1 flex-shrink-0">{slide.title}</p>}
            {onlyTitle && slide.title && (
              <div className="flex-1 w-full min-h-0"><AutoFitText text={slide.title} maxPx={60} minPx={6} lineHeight={lh} style={style} /></div>
            )}
            {!onlyTitle && (
              <div className="flex-1 w-full min-h-0"><AutoFitText text={lines || "(empty)"} maxPx={48} minPx={6} lineHeight={lh} style={style} /></div>
            )}
          </div>
        );
      }
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl border p-3" data-testid="slide-preview-pane">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Live Preview</p>
        {total > 0 && <p className="text-[10px] text-gray-400 font-mono">{idx + 1} / {total}</p>}
      </div>
      {/* 16:9 aspect frame */}
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <div className="absolute inset-0 rounded-lg overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-3 shadow-inner">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          {renderMini()}
        </div>
      </div>
      {total > 1 && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="preview-prev-btn">
            <ChevronRight className="w-3 h-3 rotate-180" />
          </Button>
          <div className="flex gap-1 max-w-[160px] overflow-x-auto">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors ${i === idx ? 'bg-indigo-500' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setIdx(Math.min(total - 1, idx + 1))} disabled={idx >= total - 1} data-testid="preview-next-btn">
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};



const HymnDisplayPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [slides, setSlides] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bibleCache, setBibleCache] = useState({});
  const [rulesData, setRulesData] = useState([]);

  // Load rules data once
  useEffect(() => {
    axios.get(`${API}/presentation/data`).then(res => setRulesData(res.data.rules || []));
  }, []);

  // Poll session
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/service-flow/session/${sessionId}`);
        setSession(prev => {
          const itemsChanged = JSON.stringify(prev?.items) !== JSON.stringify(res.data?.items);
          const modeChanged = prev?.display_mode !== res.data?.display_mode;
          if (itemsChanged || modeChanged || !prev) {
            buildAllSlides(res.data.items || [], res.data.display_mode || "english", res.data);
          }
          return res.data;
        });
      } catch (e) {}
    }, 600);
    return () => clearInterval(poll);
  }, [sessionId]);

  const fetchBible = async (ref) => {
    if (bibleCache[ref]) return bibleCache[ref];
    try {
      const res = await axios.get(`${API}/bible-verse/${encodeURIComponent(ref)}`);
      const verses = res.data.verses || [];
      const result = verses.length > 0 
        ? verses.map(v => ({ num: v.verse, text: (v.text || '').trim() }))
        : [{ num: 0, text: res.data.text || "Verse not found" }];
      setBibleCache(prev => ({ ...prev, [ref]: result }));
      return result;
    } catch (e) { return [{ num: 0, text: "Unable to fetch verse" }]; }
  };

  const buildAllSlides = async (items, mode, sess) => {
    const allSlides = [];
    for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
      const item = items[itemIdx];
      if (item.type === "hymn" && item.hymn_id) {
        try {
          const res = await axios.get(`${API}/hymns/search?q=&limit=1000`);
          const hymn = res.data.find(h => h.id === item.hymn_id);
          if (hymn) {
            const enS = splitIntoStanzas(hymn.lyrics_english);
            const yoS = splitIntoStanzas(hymn.lyrics_yoruba);
            allSlides.push({ itemType: "hymn_title", itemIndex: itemIdx, title: `Hymn #${hymn.number}`, subtitle: hymn.title_english || hymn.title_yoruba, label: "Title" });
            if (mode === "english") enS.forEach((s, si) => allSlides.push({ itemType: "hymn", itemIndex: itemIdx, english: s, label: `Stanza ${si + 1}` }));
            else if (mode === "yoruba") yoS.forEach((s, si) => allSlides.push({ itemType: "hymn", itemIndex: itemIdx, yoruba: s, label: `Stanza ${si + 1}` }));
            else { const max = Math.max(enS.length, yoS.length); for (let i = 0; i < max; i++) allSlides.push({ itemType: "hymn", itemIndex: itemIdx, english: enS[i] || "", yoruba: yoS[i] || "", label: `Stanza ${i + 1}` }); }
          }
        } catch (e) {}
      } else if (item.type === "bible_reading") {
        allSlides.push({ itemType: "bible_title", itemIndex: itemIdx, title: item.title || "Bible Reading", reference: item.reference, label: "Title" });
        if (item.reference) {
          const verses = await fetchBible(item.reference);
          if (Array.isArray(verses) && verses.length > 0) {
            for (let i = 0; i < verses.length; i++) {
              const v = verses[i];
              const text = v.num > 0 ? `${v.num}. ${v.text}` : v.text;
              allSlides.push({ itemType: "bible_text", itemIndex: itemIdx, reference: item.reference, text, label: `v${v.num || (i + 1)}` });
            }
          }
        }
      } else if (item.type === "rules") {
        rulesData.forEach((r, ri) => allSlides.push({ itemType: "rule", itemIndex: itemIdx, category: r.category, rule: r.rule, label: `Rule ${ri + 1}` }));
      } else if (item.type === "liturgy") {
        const lines = (item.content || "").split('\n').filter(l => l.trim());
        for (let i = 0; i < lines.length; i += 4) {
          allSlides.push({ itemType: "liturgy", itemIndex: itemIdx, title: item.title, lines: lines.slice(i, i + 4), label: `Part ${Math.floor(i / 4) + 1}` });
        }
      } else {
        if (item.content) {
          const lines = item.content.split('\n').filter(l => l.trim());
          for (let i = 0; i < lines.length; i += 5) {
            allSlides.push({ itemType: item.type, itemIndex: itemIdx, title: item.title, lines: lines.slice(i, i + 5), label: lines.length > 5 ? `Part ${Math.floor(i / 5) + 1}` : '' });
          }
        } else {
          allSlides.push({ itemType: item.type, itemIndex: itemIdx, title: item.title, lines: [], label: '' });
        }
      }
    }
    setSlides(allSlides);
    // Store slide map in session so controller knows
    const slideMap = [];
    let lastItemIdx = -1;
    allSlides.forEach((s, si) => {
      if (s.itemIndex !== lastItemIdx) {
        slideMap.push({ itemIndex: s.itemIndex, startSlide: si });
        lastItemIdx = s.itemIndex;
      }
    });
    axios.put(`${API}/service-flow/session/${sessionId}`, { ...sess, slide_map: slideMap, total_slides: allSlides.length, slides_data: allSlides.map(s => ({ itemIndex: s.itemIndex, itemType: s.itemType, label: s.label, text: (s.english || s.yoruba || s.text || s.rule || s.title || '').substring(0, 60) })) });
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.();
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") goSlide(1);
      if (e.key === "ArrowLeft") goSlide(-1);
      if (e.key === "f" || e.key === "F") toggleFullscreen();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [session, slides.length]);

  const goSlide = (dir) => {
    if (!session) return;
    const next = Math.max(0, Math.min((session.current_slide || 0) + dir, slides.length - 1));
    axios.put(`${API}/service-flow/session/${sessionId}`, { ...session, current_slide: next });
  };

  const cur = session?.current_slide || 0;
  const slide = slides[cur];
  const fs = session?.font_size || 100;
  const isBold = session?.bold;
  const hc = session?.high_contrast;
  const focus = session?.focus_mode;
  const accessibility = session?.accessibility_mode;
  const bg = session?.background || "default";
  const scaleMult = accessibility ? 1.4 : focus ? 1.3 : 1;
  const scaledSize = fs * scaleMult;
  const transSpeed = accessibility ? '1s' : '0.4s';

  const bgStyles = { default: "from-[#1a1a2e] via-[#16213e] to-[#0f3460]", black: "from-black via-black to-black", navy: "from-[#0a0a23] via-[#101030] to-[#0a0a23]", warm: "from-[#1a0f0a] via-[#2a1810] to-[#1a0f0a]", forest: "from-[#0a1a0f] via-[#0f2818] to-[#0a1a0f]" };
  const bgClass = hc ? "from-black via-black to-black" : (bgStyles[bg] || bgStyles.default);

  const renderSlide = () => {
    if (!slide) return <div className="text-white text-center"><Music className="w-16 h-16 mx-auto mb-4 opacity-50" /><p className="text-2xl opacity-70">Waiting for service...</p></div>;

    const textStyle = { textShadow: '0 4px 30px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.4)', whiteSpace: 'pre-line' };
    // User multiplier (60–160%) plus accessibility/focus bumps
    const mult = scaledSize / 100;
    // Accessibility / focus modes give slightly more line spacing for clarity
    const lh = accessibility ? 1.5 : 1.2;

    switch (slide.itemType) {
      case "hymn_title":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <p className="text-indigo-300/80 text-3xl uppercase tracking-[0.3em] mb-6 flex-shrink-0">Hymn</p>
            <div className="flex-1 w-full min-h-0">
              <AutoFitText text={slide.title} multiplier={mult} maxPx={260} lineHeight={lh} style={textStyle} />
            </div>
            {slide.subtitle && <p className="text-3xl md:text-4xl text-white/70 font-bold mt-4 flex-shrink-0">{slide.subtitle}</p>}
          </div>
        );
      case "bible_title":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <p className="text-emerald-300/80 text-3xl uppercase tracking-[0.3em] mb-6 flex-shrink-0">{slide.title || "Bible Reading"}</p>
            <div className="flex-1 w-full min-h-0">
              <AutoFitText text={slide.reference} multiplier={mult} maxPx={300} lineHeight={lh} style={textStyle} />
            </div>
          </div>
        );
      case "hymn":
        return (
          <div className="w-full h-full flex flex-col">
            {slide.english && slide.yoruba ? (
              <div className="flex-1 flex gap-8 lg:gap-14 items-stretch justify-center min-h-0">
                <div className="flex-1 flex flex-col min-w-0">
                  <p className="text-indigo-300/60 text-sm uppercase tracking-[0.25em] mb-3 text-center flex-shrink-0">English</p>
                  <div className="flex-1 min-h-0">
                    <AutoFitText text={slide.english} multiplier={mult} maxPx={180} lineHeight={lh} style={textStyle} />
                  </div>
                </div>
                <div className="w-px self-stretch bg-white/20 flex-shrink-0" />
                <div className="flex-1 flex flex-col min-w-0">
                  <p className="text-amber-300/60 text-sm uppercase tracking-[0.25em] mb-3 text-center flex-shrink-0">Yoruba</p>
                  <div className="flex-1 min-h-0">
                    <AutoFitText text={slide.yoruba} italic color="text-amber-100" multiplier={mult} maxPx={180} lineHeight={lh} style={textStyle} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col min-h-0">
                {slide.english && (
                  <div className="flex-1 min-h-0">
                    <AutoFitText text={slide.english} multiplier={mult} maxPx={220} lineHeight={lh} style={textStyle} />
                  </div>
                )}
                {slide.yoruba && (
                  <div className="flex-1 min-h-0">
                    <AutoFitText text={slide.yoruba} italic color="text-amber-100" multiplier={mult} maxPx={220} lineHeight={lh} style={textStyle} />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case "bible_text":
        return (
          <div className="w-full h-full flex flex-col">
            <p className="text-emerald-300 text-2xl md:text-3xl font-bold uppercase tracking-[0.2em] mb-6 text-center flex-shrink-0">{slide.reference}</p>
            <div className="flex-1 min-h-0">
              <AutoFitText text={slide.text} multiplier={mult} maxPx={200} lineHeight={lh} style={textStyle} />
            </div>
          </div>
        );
      case "rule":
        return (
          <div className="w-full h-full flex flex-col">
            <p className="text-indigo-300/60 text-2xl uppercase tracking-[0.3em] mb-6 text-center flex-shrink-0">{slide.category}</p>
            <div className="flex-1 min-h-0">
              <AutoFitText text={slide.rule} multiplier={mult} maxPx={220} lineHeight={lh} style={textStyle} />
            </div>
          </div>
        );
      case "liturgy": {
        const combined = (slide.lines || []).join("\n");
        return (
          <div className="w-full h-full flex flex-col">
            {slide.title && <p className="text-teal-300/60 text-xl uppercase tracking-[0.3em] mb-4 text-center flex-shrink-0">{slide.title}</p>}
            <div className="flex-1 min-h-0">
              <AutoFitText text={combined} multiplier={mult} maxPx={180} lineHeight={lh} style={textStyle} />
            </div>
          </div>
        );
      }
      default: {
        const linesText = (slide.lines || []).join("\n");
        const onlyTitle = (slide.lines || []).length === 0;
        return (
          <div className="w-full h-full flex flex-col items-center justify-center">
            {slide.title && !onlyTitle && (
              <p className="text-amber-300/80 text-2xl uppercase tracking-[0.2em] mb-6 flex-shrink-0">{slide.title}</p>
            )}
            {onlyTitle && slide.title && (
              <div className="flex-1 w-full min-h-0">
                <AutoFitText text={slide.title} multiplier={mult} maxPx={260} lineHeight={lh} style={textStyle} />
              </div>
            )}
            {!onlyTitle && (
              <div className="flex-1 w-full min-h-0">
                <AutoFitText text={linesText} multiplier={mult} maxPx={200} lineHeight={lh} style={textStyle} />
              </div>
            )}
          </div>
        );
      }
    }
  };

  return (
    <div className={`fixed inset-0 bg-gradient-to-br ${bgClass} flex items-center justify-center overflow-hidden`} onDoubleClick={toggleFullscreen} data-testid="hymn-display-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.12),transparent_50%)]" />
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img src={CCCLogo} alt="" className="w-[45vh] h-[45vh] rounded-full opacity-[0.04] object-contain" />
      </div>

      {!focus && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 text-center pointer-events-none w-full px-20 z-20">
          <h1 className="text-3xl md:text-4xl font-bold" style={{color: '#c4b5fd', fontFamily: 'Georgia, serif', textShadow: '0 2px 15px rgba(139,92,246,0.3)'}}>Celestial Church of Christ</h1>
        </div>
      )}

      <div className="relative z-10 w-[94vw] h-[80vh] px-6 flex flex-col items-center justify-center" style={{transition: `opacity ${transSpeed} ease`}}>
        {slide && <div key={cur} className="animate-fade-in w-full h-full" style={{animationDuration: transSpeed}}>{renderSlide()}</div>}
        {!slide && renderSlide()}
      </div>

      {!isFullscreen && slides.length > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Button onClick={(e) => { e.stopPropagation(); goSlide(-1); }} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/10 rounded-full h-10 w-10" disabled={cur === 0}><ChevronRight className="w-6 h-6 rotate-180" /></Button>
          <span className="text-white/40 text-sm font-bold">{cur + 1} / {slides.length}</span>
          <Button onClick={(e) => { e.stopPropagation(); goSlide(1); }} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/10 rounded-full h-10 w-10" disabled={cur >= slides.length - 1}><ChevronRight className="w-6 h-6" /></Button>
          <Button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} variant="ghost" className="text-white/40 hover:text-white hover:bg-white/10 rounded-full h-10 w-10 ml-2" title="Fullscreen (or double-click anywhere)">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
          </Button>
        </div>
      )}
    </div>
  );
};

// Service Flow Phone Controller
const HymnControlPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState({ current_slide: 0, items: [], slides_data: [], slide_map: [], display_mode: "english", font_size: 100, bold: false, high_contrast: false, focus_mode: false, accessibility_mode: false, background: "default" });
  const [expandedItem, setExpandedItem] = useState(null);
  const [quickRef, setQuickRef] = useState("");
  const [quickLoading, setQuickLoading] = useState(false);
  const [bibleSuggestions, setBibleSuggestions] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [hymnSearch, setHymnSearch] = useState("");
  const [hymnResults, setHymnResults] = useState([]);

  useEffect(() => { axios.get(`${API}/bible-books`).then(r => setAllBooks(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/service-flow/session/${sessionId}`);
        setSession(res.data);
      } catch (e) {}
    }, 800);
    return () => clearInterval(poll);
  }, [sessionId]);

  const update = async (changes) => {
    const updated = { ...session, ...changes };
    setSession(updated);
    await axios.put(`${API}/service-flow/session/${sessionId}`, updated);
  };

  const cur = session?.current_slide || 0;
  const items = session?.items || [];
  const slidesData = session?.slides_data || [];
  const slideMap = session?.slide_map || [];
  const totalSlides = session?.total_slides || slidesData.length;

  const next = () => update({ current_slide: Math.min(cur + 1, totalSlides - 1) });
  const prev = () => update({ current_slide: Math.max(cur - 1, 0) });
  const goTo = (slideIdx) => update({ current_slide: slideIdx });

  const currentItemIdx = slidesData[cur]?.itemIndex ?? -1;
  const getItemSlides = (itemIdx) => slidesData.map((s, si) => ({ ...s, slideIdx: si })).filter(s => s.itemIndex === itemIdx);

  const handleQuickRefChange = (val) => {
    setQuickRef(val);
    if (val.length >= 2) {
      const matches = allBooks.filter(b => b.toLowerCase().startsWith(val.toLowerCase()));
      setBibleSuggestions(matches.slice(0, 6));
    } else {
      setBibleSuggestions([]);
    }
  };

  const selectBibleBook = (book) => {
    setQuickRef(book + " ");
    setBibleSuggestions([]);
  };

  const addQuickScripture = async () => {
    if (!quickRef.trim()) return;
    setQuickLoading(true);
    const newItems = [...items, { type: "bible_reading", title: "Scripture", reference: quickRef.trim(), content: "", hymn_id: "", hymn_number: 0, order: items.length }];
    // Find what slide index the new item will start at
    const newItemIdx = items.length;
    await update({ items: newItems });
    // Wait for slides to rebuild, then jump to the new item's title slide
    setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/service-flow/session/${sessionId}`);
        const sm = res.data.slide_map || [];
        const target = sm.find(m => m.itemIndex === newItemIdx);
        if (target != null) {
          await update({ ...res.data, current_slide: target.startSlide });
        }
      } catch (e) {}
      setQuickLoading(false);
    }, 2500);
    setQuickRef("");
    setBibleSuggestions([]);
  };

  const searchHymns = async (q) => {
    setHymnSearch(q);
    if (q.length < 2) { setHymnResults([]); return; }
    try {
      const res = await axios.get(`${API}/hymns/search?q=${encodeURIComponent(q)}&limit=8`);
      setHymnResults(res.data);
    } catch (e) { setHymnResults([]); }
  };

  const addQuickHymn = async (h) => {
    const newItems = [...items, { type: "hymn", title: `Hymn #${h.number} - ${h.title_english || h.title_yoruba}`, hymn_id: h.id, hymn_number: h.number, content: "", reference: "", order: items.length }];
    const newItemIdx = items.length;
    await update({ items: newItems });
    setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/service-flow/session/${sessionId}`);
        const sm = res.data.slide_map || [];
        const target = sm.find(m => m.itemIndex === newItemIdx);
        if (target != null) {
          await update({ ...res.data, current_slide: target.startSlide });
        }
      } catch (e) {}
    }, 2000);
    setHymnSearch(""); setHymnResults([]);
  };

  const itemTypeIcons = { hymn: "♪", bible_reading: "📖", prayer: "🙏", announcement: "📢", song: "🎵", sermon: "📝", liturgy: "📜", blank: "▢", rules: "⚖" };
  const itemTypeColors = { hymn: "bg-indigo-500", bible_reading: "bg-emerald-500", prayer: "bg-purple-500", announcement: "bg-amber-500", song: "bg-pink-500", sermon: "bg-blue-500", liturgy: "bg-teal-500", blank: "bg-gray-500", rules: "bg-red-500" };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] p-3 pb-32" data-testid="hymn-control-page">
      <div className="max-w-md mx-auto space-y-3">
        {/* Header */}
        <div className="flex items-center gap-3">
          <img src={CCCLogo} alt="CCC" className="w-9 h-9 rounded-full border-2 border-white/20" />
          <div className="flex-1">
            <h1 className="text-white font-bold text-base">Service Flow Control</h1>
            <p className="text-white/40 text-[10px]">Tap any item to expand. Tap sub-slides to jump.</p>
          </div>
        </div>

        {/* Service Order - Interactive List */}
        <div className="space-y-1.5">
          {items.map((item, itemIdx) => {
            const isActive = currentItemIdx === itemIdx;
            const isExpanded = expandedItem === itemIdx || isActive;
            const subSlides = getItemSlides(itemIdx);
            const icon = itemTypeIcons[item.type] || "?";
            const color = itemTypeColors[item.type] || "bg-gray-500";

            return (
              <div key={itemIdx} className={`rounded-2xl overflow-hidden transition-all ${isActive ? "ring-2 ring-indigo-400 shadow-lg shadow-indigo-500/20" : "border border-white/10"}`}>
                {/* Item header - clickable */}
                <button
                  onClick={() => {
                    setExpandedItem(expandedItem === itemIdx ? null : itemIdx);
                    // Jump to first slide of this item
                    const firstSlide = slideMap.find(m => m.itemIndex === itemIdx);
                    if (firstSlide != null) goTo(firstSlide.startSlide);
                  }}
                  className={`w-full flex items-center gap-2.5 p-3 text-left transition-all ${isActive ? "bg-indigo-500/20" : "bg-white/5 hover:bg-white/10"}`}
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs flex-shrink-0 ${color}`}>{icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isActive ? "text-white" : "text-white/70"}`}>{item.title || item.type.replace('_', ' ')}</p>
                    {item.reference && <p className="text-[10px] text-amber-300/70">{item.reference}</p>}
                  </div>
                  <span className="text-[10px] text-white/30">{subSlides.length} slides</span>
                  <ChevronRight className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </button>

                {/* Sub-slides - expandable */}
                {isExpanded && subSlides.length > 0 && (
                  <div className="bg-black/20 px-2 py-1.5 space-y-1">
                    {subSlides.map((sub, si) => {
                      const isCurrentSlide = sub.slideIdx === cur;
                      return (
                        <button
                          key={si}
                          onClick={() => goTo(sub.slideIdx)}
                          className={`w-full text-left p-2 rounded-xl text-xs transition-all flex items-center gap-2 ${isCurrentSlide ? "bg-indigo-500/40 ring-1 ring-indigo-400/60 text-white" : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"}`}
                        >
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${isCurrentSlide ? "bg-indigo-500 text-white" : "bg-white/10 text-white/40"}`}>{si + 1}</span>
                          <span className="truncate flex-1">
                            {sub.label ? <span className="text-indigo-300/70 mr-1">{sub.label}:</span> : null}
                            {sub.text || ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Scripture Lookup */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 space-y-2">
          <p className="text-emerald-300/70 text-xs font-bold uppercase tracking-wider">Quick Scripture</p>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input value={quickRef} onChange={e => handleQuickRefChange(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addQuickScripture(); }} placeholder="e.g. John 3:16" className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/30 text-sm" />
              {bibleSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-[#1a2040] border border-white/20 rounded-xl overflow-hidden z-50 shadow-xl">
                  {bibleSuggestions.map(b => (
                    <button key={b} onClick={() => selectBibleBook(b)} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-indigo-500/30 border-b border-white/5">{b}</button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" className="rounded-xl bg-emerald-600 text-white px-4" disabled={!quickRef.trim() || quickLoading} onClick={addQuickScripture}>
              {quickLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Go"}
            </Button>
          </div>
          <p className="text-[10px] text-white/30">Type book name for suggestions, add chapter:verse, press Go</p>
        </div>

        {/* Quick Hymn Search */}
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-3 space-y-2">
          <p className="text-indigo-300/70 text-xs font-bold uppercase tracking-wider">Quick Hymn</p>
          <Input value={hymnSearch} onChange={e => searchHymns(e.target.value)} placeholder="Number, title, or lyrics (e.g. Egbe, Yio)" className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/30 text-sm" />
          {hymnResults.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {hymnResults.map(h => (
                <button key={h.id} onClick={() => addQuickHymn(h)} className="w-full text-left p-2 rounded-lg bg-white/5 hover:bg-indigo-500/30 text-xs flex items-center gap-2">
                  <span className="text-indigo-300 font-bold flex-shrink-0">#{h.number}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 truncate">{h.title_english || 'Untitled'}</p>
                    {h.title_yoruba && h.title_yoruba !== h.title_english && <p className="text-white/40 truncate text-[10px]">{h.title_yoruba}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-white/30">Search by number, English/Yoruba title, or lyrics content</p>
        </div>

        {/* Settings panel - collapsible */}
        <details className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <summary className="p-3 text-white/50 text-xs uppercase tracking-wider cursor-pointer hover:text-white/70">Display Settings</summary>
          <div className="p-3 pt-0 space-y-3">
            <div className="grid grid-cols-3 gap-1.5">
              {[{v:"english",l:"EN"},{v:"yoruba",l:"YO"},{v:"both",l:"Both"},{v:"side-by-side",l:"Side"},{v:"alternating",l:"Alt"}].map(m => (
                <button key={m.v} onClick={() => update({ display_mode: m.v })} className={`p-1.5 rounded-lg text-[10px] font-bold ${session.display_mode === m.v ? "bg-indigo-500 text-white" : "bg-white/5 text-white/50"}`}>{m.l}</button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {[{v:"default",c:"bg-gradient-to-r from-[#1a1a2e] to-[#0f3460]"},{v:"black",c:"bg-black"},{v:"navy",c:"bg-[#0a0a23]"},{v:"warm",c:"bg-gradient-to-r from-[#1a0f0a] to-[#2a1810]"},{v:"forest",c:"bg-gradient-to-r from-[#0a1a0f] to-[#0f2818]"}].map(b => (
                <button key={b.v} onClick={() => update({ background: b.v })} className={`rounded-lg p-1 ${(session.background||"default")===b.v?"ring-2 ring-indigo-400":""}`}><div className={`w-full h-6 rounded ${b.c} border border-white/20`}/></button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="text-white/60 rounded-full h-7 w-7 p-0" onClick={() => update({ font_size: Math.max(60, (session.font_size||100)-10) })}>A-</Button>
              <span className="text-white font-bold text-xs flex-1 text-center">{session.font_size||100}%</span>
              <Button size="sm" variant="ghost" className="text-white/60 rounded-full h-7 w-7 p-0" onClick={() => update({ font_size: Math.min(160, (session.font_size||100)+10) })}>A+</Button>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              <button onClick={() => update({bold:!session.bold})} className={`p-1.5 rounded-lg text-[10px] font-bold ${session.bold?"bg-indigo-500 text-white":"bg-white/5 text-white/50"}`}>Bold</button>
              <button onClick={() => update({high_contrast:!session.high_contrast})} className={`p-1.5 rounded-lg text-[10px] font-bold ${session.high_contrast?"bg-indigo-500 text-white":"bg-white/5 text-white/50"}`}>HiCon</button>
              <button onClick={() => update({focus_mode:!session.focus_mode})} className={`p-1.5 rounded-lg text-[10px] font-bold ${session.focus_mode?"bg-amber-500 text-white":"bg-white/5 text-white/50"}`}>Focus</button>
              <button onClick={() => update({accessibility_mode:!session.accessibility_mode})} className={`p-1.5 rounded-lg text-[10px] font-bold ${session.accessibility_mode?"bg-green-500 text-white":"bg-white/5 text-white/50"}`}>A11y</button>
            </div>
          </div>
        </details>
      </div>

      {/* Floating Prev/Next bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0f1629]/95 backdrop-blur-lg border-t border-white/10 p-3">
        <div className="max-w-md mx-auto flex gap-3">
          <Button onClick={prev} disabled={cur===0} className="flex-1 h-12 rounded-2xl bg-white/10 hover:bg-white/20 text-white border border-white/10 font-bold"><ChevronRight className="w-6 h-6 rotate-180" /> Prev</Button>
          <div className="flex items-center px-3 text-white/50 font-bold text-sm">{totalSlides > 0 ? `${cur+1}/${totalSlides}` : '-'}</div>
          <Button onClick={next} disabled={cur>=totalSlides-1} className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold">Next <ChevronRight className="w-6 h-6" /></Button>
        </div>
      </div>
    </div>
  );
};

// =============== PRESENTATION MODE ===============
const PresentationPage = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sessionId] = useState(() => Math.random().toString(36).substring(2, 10));
  const [showQR, setShowQR] = useState(true);

  useEffect(() => {
    axios.get(`${API}/presentation/data`).then(res => {
      const allSlides = [];
      // Each rule is its own slide
      res.data.rules.forEach((r, idx) => {
        allSlides.push({ type: "rule", number: idx + 1, total: res.data.rules.length, category: r.category, rule: r.rule });
      });
      // Special announcements after rules
      res.data.announcements.forEach(a => {
        allSlides.push({ type: "announcement", title: a.title, message: a.message });
      });
      setSlides(allSlides);
      axios.put(`${API}/presentation/session/${sessionId}`, { current_slide: 0, total_slides: allSlides.length });
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/presentation/session/${sessionId}`);
        if (res.data.current_slide !== currentSlide) {
          setCurrentSlide(res.data.current_slide);
        }
      } catch (e) {}
    }, 800);
    return () => clearInterval(interval);
  }, [sessionId, currentSlide]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "f" || e.key === "F") toggleFullscreen();
      if (e.key === "q" || e.key === "Q") setShowQR(v => !v);
      if (e.key === "Escape") { if (document.fullscreenElement) document.exitFullscreen(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [slides.length, currentSlide]);

  const next = () => {
    const n = Math.min(currentSlide + 1, slides.length - 1);
    setCurrentSlide(n);
    axios.put(`${API}/presentation/session/${sessionId}`, { current_slide: n, total_slides: slides.length });
  };
  const prev = () => {
    const n = Math.max(currentSlide - 1, 0);
    setCurrentSlide(n);
    axios.put(`${API}/presentation/session/${sessionId}`, { current_slide: n, total_slides: slides.length });
  };

  const controlUrl = `${window.location.origin}/presentation/control/${sessionId}`;
  const slide = slides[currentSlide];
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center overflow-hidden" data-testid="presentation-page" onDoubleClick={toggleFullscreen}>
      {/* Glossy background overlays */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.1),transparent_50%)]" />
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

      {/* Full-screen Church Logo watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img src={CCCLogo} alt="" className="w-[50vh] h-[50vh] rounded-full opacity-[0.06] object-contain" />
      </div>

      {/* Church Name at top */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center pointer-events-none">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-wide" style={{color: '#c4b5fd', fontFamily: 'Georgia, serif', textShadow: '0 2px 20px rgba(139,92,246,0.4)'}}>
          Celestial Church of Christ
        </h1>
        <p className="text-white/50 text-lg md:text-xl mt-2 tracking-[0.4em] uppercase font-medium">Medway Central Parish</p>
      </div>

      {/* Slide Content */}
      <div className="relative z-10 w-full max-w-[90vw] px-12 text-center flex flex-col items-center justify-center">
        {!slide ? (
          <div className="text-white">
            <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" />
            <p className="text-2xl">Loading presentation...</p>
          </div>
        ) : slide.type === "rule" ? (
          <div className="animate-fade-in w-full">
            <p className="text-indigo-300 text-lg md:text-xl font-bold mb-8 uppercase tracking-[0.3em]">{slide.category}</p>
            <p className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-snug" style={{textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'}}>
              {slide.rule}
            </p>
            <p className="text-white/30 text-base mt-10 font-medium tracking-wider">{currentSlide + 1} / {slides.length}</p>
          </div>
        ) : (
          <div className="animate-fade-in w-full">
            <Bell className="w-14 h-14 text-amber-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-amber-300 mb-8" style={{textShadow: '0 4px 25px rgba(245,158,11,0.4)'}}>
              {slide.title}
            </h2>
            <div className="space-y-4 text-left max-w-4xl mx-auto">
              {slide.message.split('\n').filter(l => l.trim()).map((line, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="text-amber-400 text-3xl md:text-4xl leading-none mt-1 flex-shrink-0">&#x2022;</span>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-snug" style={{textShadow: '0 3px 20px rgba(0,0,0,0.4)'}}>
                    {line.trim()}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-white/30 text-base mt-10 font-medium tracking-wider text-center">{currentSlide + 1} / {slides.length}</p>
          </div>
        )}
      </div>

      {/* Slide counter & nav - hidden in fullscreen */}
      {!isFullscreen && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4">
          <Button onClick={(e) => { e.stopPropagation(); prev(); }} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-12 w-12" disabled={currentSlide === 0}>
            <ChevronRight className="w-7 h-7 rotate-180" />
          </Button>
          <span className="text-white/50 text-lg font-bold">{currentSlide + 1} / {slides.length}</span>
          <Button onClick={(e) => { e.stopPropagation(); next(); }} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-12 w-12" disabled={currentSlide === slides.length - 1}>
            <ChevronRight className="w-7 h-7" />
          </Button>
        </div>
      )}

      {/* QR Code - hidden in fullscreen */}
      {showQR && !isFullscreen && (
        <div className="absolute top-8 right-8 bg-white rounded-2xl p-4 shadow-2xl cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowQR(false); }}>
          <QRCodeSVG value={controlUrl} size={140} />
          <p className="text-xs text-center text-gray-500 mt-2 font-semibold">Scan to control</p>
        </div>
      )}

      {/* Hint - hidden in fullscreen */}
      {!isFullscreen && (
        <div className="absolute bottom-8 right-8 text-white/20 text-xs hidden md:block text-right">
          Double-click for fullscreen<br/>← → Navigate | Q Toggle QR
        </div>
      )}
    </div>
  );
};

// Presentation Remote Controller (phone)
const PresentationControlPage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState({ current_slide: 0, total_slides: 0 });
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    axios.get(`${API}/presentation/data`).then(res => {
      const allSlides = [];
      res.data.rules.forEach((r, idx) => {
        allSlides.push({ type: "rule", number: idx + 1, total: res.data.rules.length, category: r.category, rule: r.rule });
      });
      res.data.announcements.forEach(a => {
        allSlides.push({ type: "announcement", title: a.title, message: a.message });
      });
      setSlides(allSlides);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/presentation/session/${sessionId}`);
        setSession(res.data);
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const goTo = async (idx) => {
    await axios.put(`${API}/presentation/session/${sessionId}`, { current_slide: idx, total_slides: slides.length });
    setSession(prev => ({ ...prev, current_slide: idx }));
  };

  const next = () => goTo(Math.min(session.current_slide + 1, slides.length - 1));
  const prev = () => goTo(Math.max(session.current_slide - 1, 0));

  const currentSlideData = slides[session.current_slide];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] p-4" data-testid="presentation-control-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src={CCCLogo} alt="CCC" className="w-10 h-10 rounded-full border-2 border-white/20" />
          <div>
            <h1 className="text-white font-bold text-lg">Presentation Control</h1>
            <p className="text-white/50 text-xs">CCC Medway Central Parish</p>
          </div>
        </div>

        {/* Current slide preview */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 mb-6">
          <p className="text-white/50 text-xs mb-2 uppercase tracking-wider">Now Showing ({session.current_slide + 1}/{slides.length})</p>
          {currentSlideData ? (
            currentSlideData.type === "rule" ? (
              <div>
                <h3 className="text-indigo-300 font-bold text-xs mb-1">({currentSlideData.number}) {currentSlideData.category}</h3>
                <p className="text-white/90 text-sm">{currentSlideData.rule}</p>
              </div>
            ) : (
              <div>
                <h3 className="text-amber-300 font-bold text-sm mb-1">{currentSlideData.title}</h3>
                <p className="text-white/90 text-sm">{currentSlideData.message}</p>
              </div>
            )
          ) : (
            <p className="text-white/50 text-sm">Loading...</p>
          )}
        </div>

        {/* Navigation controls */}
        <div className="flex gap-3 mb-6">
          <Button onClick={prev} disabled={session.current_slide === 0} className="flex-1 h-16 rounded-2xl bg-white/10 hover:bg-white/20 text-white border border-white/10 text-lg font-bold">
            <ChevronRight className="w-8 h-8 rotate-180" /> Prev
          </Button>
          <Button onClick={next} disabled={session.current_slide >= slides.length - 1} className="flex-1 h-16 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-lg font-bold">
            Next <ChevronRight className="w-8 h-8" />
          </Button>
        </div>

        {/* Slide list */}
        <div className="space-y-2">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-2">All Slides</p>
          {slides.map((s, idx) => (
            <button key={idx} onClick={() => goTo(idx)} className={`w-full text-left p-3 rounded-xl transition-all ${idx === session.current_slide ? "bg-indigo-500/30 border border-indigo-400/50" : "bg-white/5 border border-white/5 hover:bg-white/10"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === session.current_slide ? "bg-indigo-500 text-white" : "bg-white/10 text-white/50"}`}>{idx + 1}</span>
                <span className={`text-sm font-medium ${idx === session.current_slide ? "text-white" : "text-white/60"}`}>
                  {s.type === "rule" ? `(${s.number}) ${s.rule.substring(0, 40)}...` : `📢 ${s.title}`}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const isPresentation = location.pathname.startsWith("/presentation") || location.pathname.startsWith("/service-flow/");
  
  if (isPresentation) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/presentation" element={<PresentationPage />} />
          <Route path="/presentation/control/:sessionId" element={<PresentationControlPage />} />
          <Route path="/service-flow/display/:sessionId" element={<HymnDisplayPage />} />
          <Route path="/service-flow/control/:sessionId" element={<HymnControlPage />} />
        </Routes>
      </>
    );
  }
  
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      <Header />
      <Toaster position="top-center" richColors />
      <AutoGPSTracker />
      <main className="pt-2">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/sermons" element={<SermonsPage />} />
          <Route path="/prayer" element={<PrayerPage />} />
          <Route path="/hymns" element={<HymnsPage />} />
          <Route path="/give" element={<GivePage />} />
          <Route path="/giving/success" element={<GivingSuccessPage />} />
          <Route path="/team" element={<MeetTheTeamPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<MemberRegistrationPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/announcements" element={<EventsPage />} />
          <Route path="/bible-lessons/:month" element={<BibleLessonMonthPage />} />
          <Route path="/gallery" element={<PhotoGalleryPage />} />
          <Route path="/gallery/:album" element={<PhotoAlbumPage />} />
        </Routes>
      </main>
      <BottomNav />
      <FloatingSocialLinks />
    </div>
  );
}

export default App;
// Build timestamp: 1769557605
